/**
 * 전투 시뮬레이션 본체.
 *
 * 30Hz 고정 타임스텝으로 돈다. 렌더링은 이 안을 들여다보되 절대 건드리지 않는다.
 * 모든 상태는 정수 배열(SoA)이고, 유닛 순회는 언제나 인덱스 오름차순이다.
 * 이 두 가지가 지켜지는 한 같은 명부 + 같은 입력은 어디서 돌려도 같은 결과를 낸다.
 *
 * 수치는 병종 표를 공유하지 않고 병사마다 배열로 들고 있다. 장비와 개체 성장이
 * 붙으면 같은 병종이라도 병사마다 수치가 달라지기 때문이다. 지금은 같은 병종이면
 * 같은 값이 들어가지만, 자리를 미리 만들어 두면 나중에 갈아엎지 않아도 된다.
 */
import {
  FP_ONE,
  clamp,
  distSq,
  fp,
  fpClampLen,
  fpMul,
  fpNormalize,
  isqrt,
} from './fixed.ts';
import { Rng } from './rng.ts';
import { Cell, FORD_DEF_MALUS, TILE_FP, TILE_PX, TerrainGrid, movePct } from './grid.ts';
import { SpatialHash } from './spatialhash.ts';
import { FlowField, UNREACHABLE as UNREACHED } from './flowfield.ts';
import { hashArray, hashInit, hashNumber } from './checksum.ts';
import { MAX_PROJECTILES, MAX_UNITS, TICK_HZ, Team } from './units.ts';
import {
  ARROW_ARMOR_PCT,
  BranchCode,
  arrowLeadPct,
  COUNTER_TABLE,
  GUARD_RADIUS_PX,
  HUNT_RADIUS_PX,
  PACE_PCT,
  fieldHp,
  BOW_LOB,
  BOW_MELEE,
  BOW_SPREAD,
  MELEE_SPREAD,
  FAVOR_CAP,
  FOE_SQUADS,
  foeHeadSquad,
  Duty,
  ELITE_DUTY,
  shieldedAtk,
  shieldedCd,
  arrowsFor,
  RETURN_HP_PCT,
  Role,
  WITHDRAW_BY_BRANCH,
  clampCaptain,
} from './troops.ts';
import type { CaptainBonus, CombatantSpec } from './troops.ts';
import { ALL_SQUADS, CmdType, MAX_SQUADS, MAX_WAYPOINTS, Order } from './commands.ts';
import type { Command } from './commands.ts';
import { PHALANX_SPEED_PCT, SKILL_CODE, VOLLEY_RATE_PCT } from './skills.ts';
import { MATE_HURT_PCT, MATE_LOSS_PCT, MateTrigger } from './mate-skills.ts';
import type { SkillParams } from './skills.ts';

/** 적을 찾아보는 반경. 이보다 멀면 명령에 따라 움직인다. */
const SENSE_RADIUS = fp(170);
/**
 * 공격 명령의 아군 병사(궁수 빼고)가 표적을 찾는 반경. 제 둘레(`SENSE_RADIUS`)보다 넓다 — 공격은 저마다 가장 가까운
 * 적에게 달려드는 명령이다. 플레이한 사람이 짚었다: "공격 명령 상태에서도 부대원끼리 뭉쳐 있으려는 성향이 있네? 각자
 * 가장 가까운 적을 공격하려는 성향을 더 크게 해줘." 170px 안에 적이 없으면 병사는 지휘관 곁 끈(`ATTACK_LEASH`)을 따라
 * 적 무게중심 쪽 흐름을 탔다 — 다들 같은 곳으로 모여들었다.
 */
const ATTACK_SENSE = fp(240);

/** 병종 본능의 반경(`HUNT_RADIUS_PX`, `GUARD_RADIUS_PX`). 기병은 적 궁수를, 창은 제 편 궁수를 노리는 기병을 먼저 본다. */
const HUNT_RADIUS = fp(HUNT_RADIUS_PX);
const GUARD_RADIUS = fp(GUARD_RADIUS_PX);
/**
 * 궁수에게 달려드는 기병이 적의 벽(`wallBox`)을 돌아갈 때 벽에서 띄우는 거리. 돌 때는 벽 가장자리에서 이것의 두 배
 * 떨어진 세로줄을 탄다.
 */
const FLANK_MARGIN = fp(48);
/** 곧장 가는 길이 벽을 스치는지 볼 때 벽 상자를 넓히는 몫(`flankPoint`). */
const FLANK_GRAZE = fp(16);
/**
 * 제 둘레(`SENSE_RADIUS`) 밖, `ATTACK_SENSE` 안의 적은 **덜 붐비는 이만** 표적으로 잡는다: 그 적의 둘레(`CROWD_RADIUS`)에
 * 같은 편이 이만큼 이하일 때. 반경만 넓혔더니 병사가 한둘씩 먼저 뛰어나가 적 떼에 홀로 박혀 쪼개져 죽었다 — 깊이 17
 * 전군 공격 전과 3065 → 2847, 장수 전사 4 → 5/15, 깊이 15 승 11 → 8/15. 떼의 가장자리·낙오한 적을 먼저 치러 나가
 * 앞이 넓어지고, 떼는 예전처럼 본대가 함께 부딪친다.
 */
const CROWD_RADIUS = fp(70);
const CROWD_MAX = 2;
/**
 * 대형 명령에서 나가 싸우는 범위. 부대원은 **지휘관에서**, 지휘관은 진의 자리에서 잰다.
 *
 * 자기 자리에서 재면 진 가장자리에 선 병사가 반경만큼 더 나가, 진이 적 쪽으로
 * 조금씩 끌려 나간다. 진의 중심이 지휘관이므로 지휘관에서 잰다.
 *
 * **값에 매우 민감하다.** 깊이 3에서 대형만 걸고 장수가 죽은 판(15판 중):
 *
 *   반경 150 → 15판, 200 → 13판, 250 → 5판   (옛 추종 태세 8판)
 *
 * 150은 부대가 스스로 펼쳐진 폭(지휘관에서 120)과 거의 같아, 진 가장자리에 붙은
 * 적을 절반쯤 못 본 척했다. 병사는 맞으면서 서 있고, 진이 녹은 뒤 장수가 홀로
 * 남았다. "가까이 온 적만 친다"의 가까이는 진의 폭보다 넓어야 한다.
 */
/**
 * 대형의 장수가 진을 끌고 나아가다 서는 거리. 적이 이 안에 보이면 서서 맞는다(`steerByOrder`).
 * 궁수 사거리(250px 남짓)보다 넓어야 궁수가 다가오는 적을 쏠 틈이 남는다. tactic(깊이 17, 시드 15)으로
 * 골랐다: 300이면 '대형 · 기병 망치'의 장수 전사 7/15, 380이면 5/15, 450이면 2/15(예전 서 있던 대형 1/15).
 * '전군 대형'은 450에서 장수 전사 1/15로 예전과 같다. 가까이 서면 적이 궁수 사격을 덜 받고 닿는다.
 */
const ADVANCE_HALT = fp(450);
/**
 * **적장은 제 병사들과 함께 온다.** 맞서는 적이 이 거리 밖이면 적 병사 가운데 가장 느린 걸음(`foePace` — 병종의 걸음에서
 * 지친 만큼 깎은 값)으로 가고, 안에 들면 제 걸음으로 싸운다. 우리 지휘관의 "멀면 함께, 가까우면 돌격"(`ATTACK_CHARGE`)과 같은 꼴이다.
 *
 * 플레이한 사람이 짚었다: "적이 수가 많은데도 금방 도망가는 판이 많았거든." 재 보니 말 탄 적장이 제 병사 앞줄보다
 * 30~90px 앞서 달려와, 첫 피해가 난 지 4~6초 뒤에 **거의 모든 판에서**(1,440판 가운데 1,432판) 혼자 쓰러졌다 — 그때 적
 * 병사는 80~95%가 남아 있었다. 적장이 쓰러지면 모든 적 병사가 기개의 40%를 잃으므로(`CHIEF_FALL_PCT`) 적은 싸움
 * 대부분을 사기 절반으로 치렀고, 판의 절반 넘게에서 살아 있는 적의 절반이 한꺼번에 달아났다. "적장을 노려 베면 판이
 * 뒤집힌다"는 한 수가 판마다 공짜로 주어지고 있었다.
 *
 * 우리 지휘관과 다른 점 둘. (1) 첫 피해가 난 뒤에도 건다(`joined`를 보지 않는다) — 적장은 화살이 날기 시작한 뒤에 앞서
 * 나와 쓰러졌다. 우리 쪽은 추격이 굼떠지지 않게 첫 피해까지만 걸었는데, 적장은 쫓을 때도 맞서는 적이 가까워야 달린다.
 * (2) 거리가 짧다 — `ATTACK_CHARGE`(450px)에서 말이 달리면 보병보다 200px 앞서 닿는다.
 *
 * 토벌에는 걸지 않는다(`chiefMarch`) — 장수 혼자 싸우는 판이라 두령과 졸개가 한꺼번에 닿으면 다른 싸움이 된다.
 */
const CHIEF_CHARGE = fp(120);
/**
 * **적 병사는 횡대로 온다.** 아군 병사 무게중심의 줄에서 이 거리보다 멀리(위에) 선 적 병사는, 표적이 없는 동안 제 옆자리를
 * 지킨 채 **곧장 아래로** 내려오고 적 병사 가운데 가장 느린 걸음(`foePace`)에 맞춘다. 바위 뒤에서만 흐름장(아군
 * 가장자리 줄이 목표)을 타고 돌아 나온다. 이 안에 들면 예전처럼 가장 가까운 아군 병사 쪽으로(`flowEnemy`) 제 걸음으로
 * 달려든다.
 *
 * 플레이한 사람이 짚었다: "적들이 일렬로 다가오고 있어. 횡대로 대형을 갖춰서 쳐들어오게 해줘." 찍어 보니 판을 연 지
 * 5초면 폭 370px로 선 적이 **한 칸 너비의 줄 둘**이 되어 있었다(깊이 12·20, `npm run spread`: 11초에 폭 135~170 ×
 * 길이 420~550px). 까닭 둘. (1) 흐름장은 칸마다 "가장 가까운 목표로 가는 한 방향"이다 — 목표(아군 병사 칸들)의 폭 밖에
 * 선 적은 모두 대각선으로 목표 폭의 끝 열에 들어선 뒤 그 열을 따라 내려온다. 30칸 밖에서 출발하니 거의 전원이 양 끝의
 * 두 열로 모였다. `FRONT_RADIUS_CELLS`로 목표를 넓힌 것은 맞붙는 폭을 넓혔을 뿐 오는 길의 모임은 그대로였다.
 * (2) 저마다 제 걸음이라 기병·도수·창·궁이 차례로 늘어섰다.
 *
 * **흐름장으로는 횡대가 되지 않는다.** 처음에는 "아군 가장자리 줄 전체"가 목표인 흐름장을 타게 했다 — 평지에서는 곧장
 * 내려오지만, 숲·언덕이 길에 있으면 30칸 앞의 싼 길(지형 사이의 골)을 향해 출발하자마자 옆으로 모여 줄 셋이 됐다.
 * 흐름장은 같은 값의 길 가운데 하나를 고르고 모두가 같은 것을 고른다. 그래서 방향은 흐름장에 묻지 않고 곧장 아래로
 * 둔다 — 숲과 늪은 느려질 뿐 지나간다.
 *
 * **바위를 도는 것은 칸으로 정한다.** 처음에는 한 칸 반 앞이 막혔는지를 유닛의 자리에서 쟀다(막혔으면 흐름, 아니면
 * 곧장). 오목한 바위 위에 선 적은 흐름을 따라 위로 한 걸음 물러나면 앞이 열려 다시 내려오고, 내려오면 다시 막혀 —
 * 제자리에서 떨었다. 깊이 1의 한 판에서 적 넷이 그렇게 서 있고 대형의 아군은 450px 밖의 적을 기다려, 판이 끝나지
 * 않았다(`npm run length`의 최장 180초). 지금은 선 칸과 바로 아래 칸의 흐름이 둘 다 아래를 볼 때만 곧장 간다 — 바위
 * 뒤의 칸은 흐름이 옆이나 위를 보므로 거기 들어서지 않고, 들어선 뒤에는 흐름을 따라 끝까지 돌아 나온다.
 *
 * 토벌에는 걸지 않는다(`lineMarch`, `chiefMarch`와 같은 까닭). 매복처럼 아군 아래에 나타난 적은 예전대로다 — 줄보다
 * 위에 선 적만 건다.
 */
const LINE_RELEASE = fp(300);
/**
 * 공격 명령의 지휘관과 병사(109부터 — `chargeHeld`)가 **돌격을 시작하는 거리**. 첫 피해(화살 포함, `joined`) 전에, 이보다 먼 적을 향해서는 제 분대의
 * 가장 느린 병사 걸음으로 함께 가고(`sqPace`), 이 안에 맞서는 적이 들면 제 걸음으로 돌격한다. 첫 피해 뒤로는 늘 제
 * 걸음이다 — 무너져 달아나는 적을 쫓을 때 병사 걸음으로 느려지면 안 된다(사용자: "우리가 이겨서 적을 추격하는 중에는
 * 방해가 될 것 같은데?").
 *
 * 처음에는 공격의 지휘관이 처음부터 제 걸음으로 갔다("속도 공조는 대형일 때만, 공격은 최대 속도로"). 전장 세로를 늘린
 * 뒤(출발 거리 684 → 1452px) 말 탄 장수가 병사보다 245px 앞서 닿아 "멀면 함께, 가까우면 돌격"이 됐고(2026-10-02,
 * 450px), 기본 명령이 공격이 되면서 150px로 줄였다(2026-10-07, 사용자가 정한 값 — 맥궁 명사수 사거리의 절반쯤).
 */
const ATTACK_CHARGE = fp(150);
/**
 * **따로 공격하러 보낸 분대는 첫 칼이 닿기 전까지 주력과 나란히 간다.** 공격 명령을 받은 부하 부대(1~3번)의 사람은 —
 * 지휘관도 병사도 — 주력의 맨 앞 병사보다 군의 앞쪽(`frontX`)으로 이만큼 넘게 앞서 나가지 않는다. 주력은 장수를 따르는
 * 무리다(`countSquads`의 `follows`: 내 부대와 장수 곁에 진을 짠 대형 분대). 분대의 맨 앞 사람을 기준으로 재어 분대가
 * 함께 서고 함께 걷는다 — 다 왔으면 서고, 덜 왔으면 가는 쪽은 그대로 두고 걸음만 줄인다. **한 번 칼이 닿으면
 * (`meleeJoined`) 풀린다** — 그 뒤로는 예전처럼 제 걸음으로 달린다.
 *
 * 플레이한 사람이 청했다(2026-10-06): 기병 부대를 따로 편성해 공격 명령으로 적 궁수를 치게 하되 "도착하는 타이밍이
 * 너무 빠르면 안돼. 우리 주력 부대와 적 주력부대가 만나는 시점에 같이 도착하게 끔 속도를 조절해야해. 원래 공격
 * 명령에서는 최고 속도로 움직이는게 기본이지만, 아직 칼을 섞기 전에는 속도를 조절하면서 이동하게 해줘. 칼을 섞은
 * 후에는 예전처럼 최고속도로 이동하고." 그 전에는 공격의 지휘관이 제 분대의 가장 느린 걸음에만 맞췄다(`sqPace`) —
 * 기병만의 분대는 말의 걸음으로 가서 주력보다 몇 초 먼저 적 앞줄에 닿아 혼자 맞았다.
 *
 * 걸지 않는 때: 주력이 서 있으라는 명령(대기·후퇴)일 때(기다릴 주력이 오지 않는다), 그 분대가 경로를 걷는 중일 때(경로는
 * 플레이어가 때를 정한 것이다), 플레이어가 직접 모는 사람, 무너졌거나 다쳐 물러나는 사람, 주력에 병사가 없을 때.
 * 적에게는 없다(적 병사는 횡대로 온다, `LINE_RELEASE`).
 *
 * 값: 나란히(0)보다 조금 앞. 0이면 주력의 맨 앞 병사가 한 걸음 물러날 때마다 서고, 크면 적 앞줄의 옆구리에 먼저 닿는다.
 */
const ATTACK_ABREAST = fp(24);
/** 나란히 가는 걸음을 고르게 하는 몫(`ATTACK_ABREAST`): 한 틱의 앞쪽 걸음은 남은 거리의 이 분의 일까지다. */
const ATTACK_ABREAST_EASE = 6;
/**
 * **공격 명령을 받은 기병 분대는 통째로 돌아간다.** 병사의 절반 넘게가 기병인 부하 부대(1~3번)가 공격 명령을 받으면,
 * 지휘관이 적의 벽(`wallBox` — 궁수가 아닌 몸의 둘레 상자)의 **가까운 쪽 끝 바깥**을 지나 적 궁수의 높이까지 가고
 * 병사는 지휘관을 따른다. 경로를 걷는 부대와 같은 꼴이다 — 가는 동안 닿는 적만 치고(`Sense.Reach`) 치는 동안에도 걷는다.
 * 궁수의 높이에 닿으면 끝나고, 그 뒤는 여느 공격 명령이다(곁에 온 궁수는 병종 본능이 쥔다, `HUNT_RADIUS_PX`).
 *
 * 플레이한 사람이 청했다(2026-10-06): "기병 부대는 통째로 돌아가면 좋겠어. 굳이 플레이어가 경로를 그려주지 않더라도,
 * 얼추 가까운쪽 구석으로 돌아가게 해줘." 그 전에는 공격의 지휘관이 적 군대를 향해 곧장 갔고(흐름장), 벽을 도는 것은
 * 궁수가 330px 안에 든 병사 저마다의 일이었다(`flankPoint`). 왼쪽에 선 기병 분대는 적 앞줄의 왼쪽 끝에 부딪쳐 절반이
 * 첫 칼과 함께 앞줄 싸움에 붙들렸고, 궁수를 처음 치는 것은 맞붙고 7~10초 뒤였다(`npm run horse`).
 *
 * - `outPx`: 벽의 끝에서 이만큼 바깥의 세로줄을 탄다(130 → 78, REPLAY 111 — 플레이한 사람: "지금 너무 멀리 우회하네.
 *   우회 거리를 지금의 60프로 정도로 줄여줘." 130은 진의 폭(±60px)에 맞춰 안쪽 병사가 적 줄 끝에 닿지 않게 잡은
 *   값이었다 — 100px일 때는 기병 쓰러짐이 2~7 → 11~21%였다. 그 뒤로 요격에 맞서기·기본 공격이 들어와 판이 달라졌다). 병사 하나가 도는 줄(`FLANK_MARGIN`의 두 배)과 비슷하다. **줄은
 *   쪽을 고를 때 한 번 정하고 바꾸지 않는다**(`sqSweepLane`). 처음에는 틱마다 지금의 벽에서 다시 쟀는데, 칼이 닿으면
 *   적이 기병 쪽으로 번져 벽이 넓어지고 줄이 따라 밀려났다 — 분대가 옆으로만 230px을 달리고 올라가지 못했다(깊이 21의
 *   한 판에서 16초).
 * - `mixedPx`: 적 궁수의 가운데가 벽의 앞에서 이만큼 안이면 "섞여 있다"고 보고 돌지 않는다. 처음에는 벽 상자 안인지를
 *   봤는데(`flankPoint`와 같이), 벽 상자는 궁수 뒤에 선 적장까지 감싸 궁수가 늘 "안"이었다 — 칼이 닿기 전에는 한 번도
 *   돌지 않았다.
 * - `besidePx`: 그 줄에서 이만큼 안이면 "줄에 섰다"고 보고 궁수의 높이로 올라간다.
 * - `arrivePx`: 궁수의 높이(적 궁수의 가운데)에서 이만큼 안이면 다 돌았다.
 *
 * **도는 것은 기병과 지휘관뿐이다**(`rides`). 플레이한 사람이 정했다(2026-10-06): "기병부대에 보병이나 궁수가 몇
 * 딸려있으면 공격 모드 이동 속도가 너무 느려. 보병은 제외해서 우회 기동이 동작하게 해줘." 처음에는 분대의 병사가 모두
 * 함께 돌았고 지휘관이 가장 느린 병사의 걸음에 맞췄다 — 보병 하나가 끼면 기병 무리가 보병 걸음으로 돌았다. 이제 걸음도
 * 진의 자리도 무게중심도 기병만으로 잰다. 딸린 보병·궁수는 돌지 않고 여느 공격 분대의 병사처럼 싸운다(첫 칼 전에는
 * 주력과 나란히, `ATTACK_ABREAST`).
 *
 * **어디서든 칼이 닿으면 그만 돈다**(`meleeJoined`). 같은 날 정했다: "어디서든 근접 공격이 오가면 우회기동 멈추고 일반
 * 공격을 하게 해줘." 처음에는 칼이 닿은 뒤에도 궁수의 높이에 닿을 때까지(길게는 15초, 옛 `maxTicks`) 닿는 적만 치며
 * 계속 돌았다. 이제 첫 칼이 난 다음 틱의 `planSweeps`에서 다 돈 것으로 치고(그 틱의 나머지 유닛은 이미 정한 갈 곳을 쓴다), 그 뒤로는 여느 공격으로 싸운다. 칼이 닿은 뒤에 받은 공격
 * 명령으로는 돌지 않는다.
 *
 * 쪽과 줄은 한 번 고르면 바꾸지 않는다(`sqSweep`·`sqSweepLane` — 틱을 넘는 상태라 해시에 든다). 지휘관과 궁수를 잇는 가로 거리가 짧은
 * 쪽이고(같으면 왼쪽), 전장 가장자리 밖이면 다른 쪽, 둘 다 안 되면 돌지 않는다. 명령을 다시 내리면 처음부터 다시 고른다.
 * 경로를 그려 주면 경로가
 * 먼저다. 적 궁수가 없거나 벽 안에 섞여 있으면 돌 것이 없다. 적에게는 없다.
 */
const SWEEP = { outPx: 78, besidePx: 30, arrivePx: 40, mixedPx: 60, stepPx: 96, meetPx: 160, slantPct: 50 } as const;
/**
 * **요격이 다가오면 돌기를 그만두고 싸운다.** 플레이한 사람이 짚었다(2026-10-06): "우회중이었는데 적의 요격이 다가오면
 * 좀 더 적극적으로 싸우게 해줘. 지금은 잘 안싸우려고 하네." 도는 무리는 닿는 적만 치며 제 자리를 따라 걷는다
 * (`Sense.Reach`) — 적이 저를 노리고 달려와도 칼이 닿을 때까지 진을 지키며 걸었고, 맞고 나서야 돌아섰다.
 * 이제 도는 무리(기병 병사·지휘관)를 **표적으로 쥔 적의 근접 병사**가 `meetPx` 안에 들면 돌기를 끝내고 여느 공격으로
 * 맞선다. 그 무리는 첫 칼 전의 걸음 맞추기(`ATTACK_ABREAST`)에서도 풀린다(`sqSweepFree`) — 묶인 채로는 다가오는 적을
 * 서서 기다린다. 궁수가 겨누는 것은 요격이 아니다(화살은 돌면서 맞는다). 표적으로 쥔 적만 세므로, 줄 밖 130px을
 * 지나며 스치는 적 벽의 끝은 세지 않는다.
 */
const SWEEP_MEET = fp(SWEEP.meetPx);
const SWEEP_OUT = fp(SWEEP.outPx);
const SWEEP_BESIDE = fp(SWEEP.besidePx);
const SWEEP_ARRIVE = fp(SWEEP.arrivePx);
const SWEEP_MIXED = fp(SWEEP.mixedPx);
const SWEEP_STEP = fp(SWEEP.stepPx);
/**
 * **돌아가는 분대는 지휘관 둘레의 걷는 진으로 간다.** 병사는 대형의 자리(`placeSlots` — 지휘관 좌우로 줄을 지어, 기병
 * 간격으로)를 받아 자리와 같은 걸음으로 따라 걷는다(`SLOT_MARCH`). 진은 지휘관보다 `aheadPx` 앞에서 시작해 지휘관이 첫
 * 줄이 아니라 줄 사이에 선다. 진이 보는 쪽은 군의 앞(`frontX`) 그대로다 — 옆걸음하는 동안에도 적을 본 채 게걸음한다.
 * 지휘관은 기병 병사의 가장 느린 걸음을 넘지 않고, 기병 병사의 무게중심이 제 뒤 16px(`lagPx`)보다 처져 있으면 `waitPct`%로 늦춘다.
 *
 * 플레이한 사람이 두 번 짚었다(2026-10-06). 처음: "부하 장수가 부대원 기병들에비해 너무 앞으로 나가네. 그래서 혼자
 * 화살을 다 맞고 있어. … 모두 말 타고 있었는데 왜 부하장수만 멀리 나갔지?" — 걸음 탓이 아니라 병사가 "지휘관 곁
 * 120px(`FOLLOW_RADIUS`) 안이면 선다"로 따라와 늘 100px 뒤였다(경로를 걷는 부대의 따라가기를 빌린 탓). 그래서 병사가
 * 지휘관 앞 50px의 **한 점** 둘레 40px로 모이게 고쳤더니, 다음: "이제 기병들이 덜덜 떨면서 이동하는데? 이거 근본적으로
 * 고칠 수 없어?" — 열세 명이 한 점을 겨누면 틱마다 원 안팎을 드나들며 서고 걷고, 서로 밀려 가는 쪽이 꺾인다(재 보니
 * 걷는 틱의 13%가 60도 넘게 꺾이고 걸음이 틱마다 20%씩 바뀌었다). 한 점이 아니라 **저마다의 자리**여야 한다 — 그것은
 * 대형이 이미 푼 문제다(걷는 진의 따라 걷기·붙들기·바위 돌아가기·줄 뒤 빈 칸). 새 규칙을 짓지 않고 그 길을 그대로 탄다.
 */
const SWEEP_PACK = { aheadPx: 40, lagPx: -16, waitPct: 70 } as const;
const SWEEP_AHEAD = fp(SWEEP_PACK.aheadPx);
const SWEEP_LAG = fp(SWEEP_PACK.lagPx);
/** `sqSweep`의 값. */
const Sweep = { None: 0, Left: 1, Right: 2, Done: 3 } as const;
const FORMATION_RADIUS = fp(250);

/**
 * 공격 명령의 부대원이 지휘관보다 앞서 나가도 되는 거리.
 *
 * 예전에는 부대원이 지휘관을 **따라가기만** 했다. 곁(120px)에 들면 멈추고, 지휘관은
 * 부대 무게중심이 곁(110px)에 올 때까지 기다렸다. 그러면 병사는 늘 지휘관 뒤 120px에
 * 서 있고, 지휘관은 한 걸음 가고 서기를 되풀이했다. 플레이어 눈에는 장수가 앞으로
 * 가려는데 병사들이 안 따라오는 것으로 보였다.
 *
 * 이제 부대원이 이 거리 안에서는 스스로 적 군대 쪽으로 나아간다. 앞서 나가 이 거리를
 * 넘으면(`ATTACK_LEASH_MAX`까지) 서서 지휘관을 기다리고, 그보다 멀거나 뒤처지면
 * 예전처럼 지휘관을 따라간다. 장수를 뒤로 몰면 병사도 결국 따라온다.
 *
 * **이 끈은 첫 칼이 닿기 전까지만 맨다**(`meleeJoined`). 한 번 칼이 닿은 뒤로는 공격 명령의 부대원이 지휘관과의 거리를
 * 재지 않고 적 군대 쪽으로 나아간다 — 앞서 나갔다고 서지도, 멀어졌다고 돌아오지도 않는다. 옆 끈(`ATTACK_FLANK`)도
 * 같이 푼다. 처음에는 판 내내 맸다: 싸움이 붙은 뒤에도 병사가 지휘관 150px 앞에서 서서 기다리고 220px을 넘으면
 * 되돌아와, 눈앞의 싸움을 두고 줄을 맞췄다. 플레이한 사람이 짚었다: "공격상태인데 너무 대형을 따지는 거 같아. 혼자
 * 튀어나가지 못하게 막는 기능도 첫 칼을 교환한 후에는 동작하지 않게 해줘." 궁수의 뒤 거리(`BOW_REAR`)를 같은 때에
 * 푸는 것과 같은 뜻이다("공격 상태면 무조건 가서 싸우는거야"). 끈은 맞붙기 전에 한 덩어리로 닿게 하려는 것이라
 * 그 뒤에는 할 일이 없다. 병사를 물리려면 대형이나 후퇴 명령을 쓴다 — 공격인 채 장수를 뒤로 몰아도 따라오지 않는다.
 * 경로를 걷는 지휘관을 따르는 병사(`followsWalker`)는 그대로 지휘관을 따른다.
 */
const ATTACK_LEASH = fp(150);
const ATTACK_LEASH_MAX = fp(220);
/**
 * 공격 명령의 부대원이 지휘관 **옆으로** 벌어져도 되는 거리. 위 두 거리는 적 쪽(앞뒤)으로만 잰다.
 *
 * 처음에는 지휘관 둘레의 원(150px)으로 쟀다. 병사는 전장 폭 ±300px에 흩어져 서는데 지휘관은 모두 가운데에
 * 서니, 날개의 병사가 원 밖이라 지휘관 쪽으로 모여들어 부대가 종대가 됐다(플레이한 사람이 짚었다: "너무 종대로
 * 전투가 진행돼"). 앞뒤는 짧게, 옆은 넓게 매어 횡대로 나아가게 한다. 처음 선 폭(±300px)을 넉넉히 덮는다.
 * 뒤로는 묶지 않는다 — 뒤처진 병사도 제 흐름을 타면 적 쪽으로 오며 따라잡는다. 뒤를 150px에서 끊어 지휘관 쪽으로
 * 걷게 하면 공격 모드가 더 나빠졌다(전군 공격 장수 전사 13/15).
 */
const ATTACK_FLANK = fp(340);
/**
 * 공격 명령의 **궁수**가 지휘관 뒤에 두는 거리 — **근접 교전이 시작되기 전까지만**(`meleeJoined`). 그때까지 궁수는
 * 지휘관보다 이만큼 뒤에 있는 동안만 적 쪽으로 나아가고, 그보다 앞이면 서서 지휘관이 앞서 가기를 기다린다 — 칼 든
 * 병사처럼 지휘관보다 앞서 나가면 먼저 맞붙는다. 한 번 칼이 닿은 뒤로는 궁수도 다른 병사와 같은 끈
 * (`ATTACK_LEASH`)으로 적을 찾아 나아간다. 플레이한 사람이 정했다: "이건 근접 교전이 시작되기 전까지만 적용하고, 그
 * 다음부터는 적용하지마. 공격 상태면 무조건 가서 싸우는거야."
 *
 * 처음에는 궁수만 예전 규칙으로 두었다: 지휘관 곁 160px 안이면 서고, 벗어나면 지휘관을 향해 곧장 걷는다. 그러면
 * 궁수는 지휘관이 160px 멀어질 때까지 서 있다가 뒤늦게 걷고, 지휘관이 맞붙은 뒤에는 그 뒤 160px에서 서 버려 적이
 * 사거리 밖에 남았다. 플레이한 사람이 짚었다(Todo11): "공격을 누르면 내 장수 부대의 궁수들이 잘 이동을 못해. 속도도
 * 오히려 대형보다 느려지고. 적도 잘 못찾는 듯." 재 보니(장수 분대에 도수 10·궁수 10, 시드 12개) 맞붙기 전 궁수의
 * 걸음이 대형 50%에 공격 10%, 맞붙은 뒤 표적 없는 틱이 깊이 14에서 대형 26%에 공격 46%였다.
 */
const BOW_REAR = fp(50);
/** 대형의 지휘관이 진의 자리에 돌아왔다고 보는 거리. 딱 그 점에 서려고 떨지 않게 한다. */
const HOME_ARRIVE = fp(12);
/** 닿는 적만 치는 명령(대기)에서 표적을 고르는 여유. 표적의 몸 반지름 몫이다. */
const REACH_SLACK = fp(16);

/**
 * 이 유닛이 표적을 어떻게 고르는가. 명령과 역할에서 한 번 정해 표적 고르기와
 * 표적 검사가 같은 규칙을 쓰게 한다. 둘이 따로 정하면 고르자마자 버리는 표적이
 * 생겨 매 틱 다시 찾는다.
 */
const Sense = {
  /** 자기 둘레 SENSE_RADIUS. 공격과 적군. */
  Free: 0,
  /** 지휘관 둘레 FORMATION_RADIUS 안의 적만. 대형의 부대원. */
  Anchor: 1,
  /** 닿는 거리만. 대기, 그리고 명령이 서라고 한 지휘관. */
  Reach: 2,
  /** 고르지 않는다. 후퇴하는 병력은 멈춰서 싸우지 않는다. */
  None: 3,
} as const;
type Sense = (typeof Sense)[keyof typeof Sense];

/** 병종 본능으로 노리는 표적인가(`HUNT_RADIUS_PX`). `Hunt`는 기병 → 적 궁수, `Guard`는 창 → 제 편 궁수를 노리는 기병. */
const Instinct = {
  None: 0,
  Hunt: 1,
  Guard: 2,
} as const;
type Instinct = (typeof Instinct)[keyof typeof Instinct];

/** 표적 재탐색 주기(틱). 유닛마다 다른 틱에 흩어서 돌린다. */
const RETARGET_INTERVAL = 6;
/** 부대원이 지휘관 곁에 머무는 거리. 이보다 멀어지면 따라간다. */
const FOLLOW_RADIUS = fp(120);

/*
 * **대형의 자리.** 대형 명령의 병사는 제 지휘관을 기준으로 정해진 자리에 선다. 자리는
 * 군의 앞(`frontX`)으로 돌린다 — 앞은 장수를 몬 마지막 방향이다.
 *
 * 예전 대형에는 자리가 없었다. 부대원은 지휘관 곁 120px 안에 들면 서기만 했다. 장수를
 * 몰면 병사는 뭉친 채 서 있다가 멀어진 뒤에야 몰려왔고, 플레이한 사람 눈에는 "대형이
 * 대기 같다"였다. 그 사람이 정한 모양: 보병은 지휘관 양옆으로 늘어서고, 궁수는 조금
 * 뒤에, 기병은 왼쪽에 거리를 두고 따로 선다(기병의 자리는 뒤에 바뀌었다 — 아래).
 *
 * 보병은 오른쪽·왼쪽을 번갈아 한 명씩 바깥으로 채운다(한쪽 줄에 셋, 넘치면 뒷줄).
 * 몸 반지름이 5~6px이라 15px 간격이면 어깨가 닿지 않고 밀어내기에 흩어지지 않는다.
 *
 * **기병의 자리는 기병의 몸에 맞춘다.** 기병 둘은 가운데끼리 18px(반경 8 + 8 + 같은 편 틈 `CROWD_GAP` 2)를 벌려야 서로
 * 밀지 않는다. 처음에는 옆 19px, 동료 분대의 기병은 앞뒤 줄이 보병과 같은 16px이었고, 장수 부대의 기병은 왼쪽 90px 밖에
 * 세 줄로 섰다 — 출전 위치가 생긴 뒤로 그 자리는 **좌익 분대의 자리**(옆 140px, `DEPLOY_COL_PX`)와 같은 땅이다. 자리가
 * 겹치면 같은 편 떼어 놓기(`relaxCrowd`)가 병사를 자리 밖(`SLOT_ARRIVE`)으로 밀고, 병사는 한 걸음 되돌아와 다시 겹치고, 또
 * 밀린다. 플레이한 사람이 짚었다: "대형 명령 상태에서 기병들이 덜덜 떨리고 있어 제자리에서. 필요하면 간격을 좀 넓혀."
 * 장수가 서 있는 동안 장수 부대 기병의 90%, 좌익 기병 분대의 70%가 매 틱 움직였고, 밀린 동료가 흔들리면 그 분대의 자리가
 * 통째로 흔들렸다(`npm run jitter`).
 *
 * - 옆·앞뒤 간격 20px(`SLOT_HORSE_STEP`). 간격만 넓혀서는 멎지 않았다(28px에서도 60%) — 두 분대가 같은 땅에 서 있어서다.
 * - **기병은 제 분대의 줄 안에서 보병 다음 자리에 선다**(장수 부대도 동료 분대도, `placeSlots`). 보병이 줄의 안쪽을
 *   채우고 기병은 그 바깥·뒷줄에 모인다. 분대가 차지하는 땅이 지휘관 둘레 한 덩어리가 되어, 옆 칸의 분대와 겹칠지는
 *   분대의 크기만으로 정해진다.
 * - 한 번 틀렸다: 처음 고칠 때는 장수 부대의 기병을 대열 양 끝 66px의 세로줄에 세웠다(보병 줄 끝과 좌익 분대 사이의 골).
 *   기본 배치(좌익 앞)의 떨림은 멎었지만, 기병이 많으면 세로줄이 뒤로 120px까지 늘어나 **중군 뒤**의 분대와, 기병 분대가
 *   **우익 앞**에 서면 그 안쪽 끝(140 − 60px)과 겹쳐 거기서 새로 떨었다(리뷰가 열린 땅에서 재현했다: 0 → 28px). 분대 밖의
 *   고정 자리는 여섯 출전 칸 가운데 어느 칸과는 겹친다.
 * - 잰 것(`npm run jitter`, 한 사람이 100틱에 제자리걸음한 거리): 장수 부대 기병 8 + 좌익 기병 분대 12에서 103 → 7px ·
 *   66 → 4px, 기병만 둘(장수 부대 14 + 좌익 앞 12)에서 77 → 0px · 75 → 0px. 중군 뒤·우익 앞·좌익 뒤도 전보다 같거나 작다.
 *   남은 값은 바위 때문에 동료의 자리가 장수 쪽으로 당겨져 두 분대가 한 땅에 선 시드의 것이다.
 * - 한번 선 병사를 붙들어 두는 규칙(조금 밀려도 되돌아 걷지 않게)도 재 봤다. 자리가 겹친 채로는 떨림이 남았고(83%),
 *   자리를 고친 뒤에는 더할 것이 없어 넣지 않았다 — 틱을 넘는 상태만 는다.
 */
const SLOT_INF_GAP = fp(20);
const SLOT_INF_STEP = fp(15);
const SLOT_INF_PER_ROW = 3;
const SLOT_ROW_STEP = fp(16);
const SLOT_BOW_BACK = fp(36);
const SLOT_BOW_STEP = fp(15);
const SLOT_BOW_PER_ROW = 6;
const SLOT_HORSE_STEP = fp(20);
/**
 * 궁수 줄은 그 분대의 마지막 줄(보병·기병)에서 이만큼 뒤에 선다 — `SLOT_BOW_BACK`(36px)보다 가깝지는 않다. 처음에는 늘
 * 36px 뒤였다: 줄이 하나둘인 분대에서는 맞지만, 기병이 뒷줄(20px·40px 뒤)을 채우면 궁수 줄과 같은 높이가 되어 기병과
 * 궁수의 몸(반경 8 + 5 + 틈 2)이 닿았다. 기병을 줄 안으로 들인 뒤 QA가 쟀다: 기병 7 · 보병 6 · 궁수 5인 장수 부대의
 * 궁수가 서 있는 동안 움직인 틱이 13% → 27%. 보병만 셋 넘는 줄을 채운 분대도 같은 땅을 밟고 있었다(전부터).
 */
const SLOT_BOW_CLEAR = fp(20);
/** 자리에 닿았다고 보는 거리. 한 틱 걸음(보병 2px, 기병 4.4px)보다 작으면 자리 둘레에서 떤다. */
const SLOT_ARRIVE = fp(5);
/**
 * 지휘관이 지난 틱에 이만큼 넘게 걸었으면 진이 걷는 중으로 본다(px/틱). 그동안 병사는 자리와 같은 걸음으로 따라 걷는다.
 *
 * 처음에는 진이 걷든 서든 "자리에서 5px 안이면 선다, 밖이면 제 걸음으로 걷는다"였다. 진은 가장 느린 병사의 걸음(1px/틱
 * 남짓)으로 걷는데 기병은 3~4px/틱이라, 자리에 닿아 서고 → 자리가 5px 멀어지면 한두 걸음 달리고 → 또 서기를 되풀이했다.
 * 플레이한 사람이 짚었다: "대형 이동 할 때 기병들이 덜덜 떨리는 문제는 수정이 되었나?" — 서 있을 때의 떨림만 고쳤던 것이다.
 * 장수가 걷는 틱에 기병이 서 있던 비율이 32~47%, 틱 사이 걸음 변화가 2.2~2.5px이었다(보병은 3~8% · 0.1~0.5px).
 *
 * 문턱을 두는 까닭: 제 병사에게 조금 밀린 동료(`relaxCrowd`)도 "움직였다". 그것까지 걷는 것으로 보면 서 있는 진의 병사가
 * 그 흔들림을 따라 미세하게 걷는다. 걷는 지휘관은 한 틱에 1px 넘게 간다.
 */
const SLOT_MARCH = fp(0.5);
/**
 * 자리가 바위에 가려 돌아가기 시작한 병사는, 자리가 다시 보여도 돌 곳에 닿을 때까지 계속 돌아간다. 길어야 이 틱 동안이다
 * (닿지 못하는 판에서 영영 돌지 않게 — 3초).
 *
 * 처음에는 틱마다 "가렸으면 지휘관 쪽 흐름장, 보이면 자리로 곧장"을 다시 골랐다. 바위 모서리에서는 한 걸음에 가림이
 * 뒤바뀌어 두 방향을 오갔다 — 리뷰가 쟀다: 장수가 선 45틱 동안 한 기병이 가림이 19번 바뀌며 171px을 걷고도 자리에 닿지
 * 못했다(시드 20261012 좌익 뒤 24번, 걷는 진을 고친 뒤에는 20261006의 25번).
 */
const SLOT_DETOUR_TICKS = 90;
/** 상한까지 돌고도 돌 곳에 닿지 못한 병사가 다시 돌기 전에 예전 규칙으로 걷는 틱(1초). */
const SLOT_DETOUR_REST = 30;
/** 돌 곳: 지휘관에게서 자리 쪽으로 이만큼 나간 점. 지휘관의 몸(반경 8) + 기병의 몸(8) + 틈보다 조금 넓게. */
const SLOT_DETOUR_OUT = fp(20);
/** 돌 곳에 이만큼 다가왔으면 그만 돌고 자리로 간다. */
const SLOT_DETOUR_NEAR = fp(6);
/**
 * 서 있는 진에서 한번 자리에 닿은 병사(`SLOT_ARRIVE` 5px)는 자리에서 이만큼 벗어나기 전에는 되돌아 걷지 않는다.
 *
 * 두 분대의 자리가 같은 땅을 밟으면(바위 때문에 분대의 자리가 장수 쪽으로 당겨진 판) 같은 편 떼어 놓기가 병사를 5px
 * 밖으로 밀고, 병사는 한 걸음 되돌아와 다시 겹치고, 또 밀렸다 — 장수가 서 있는데 병사가 틱마다 앞뒤로 움직였다(45틱에
 * 방향을 스물몇 번 뒤집었다). 밀려난 자리는 이미 서로 닿지 않는 자리다. 거기 서 있으면 다음 틱에 밀 사람이 없다.
 *
 * 기병의 자리를 고칠 때(REPLAY 89) 같은 규칙을 재 보고 넣지 않았다 — 그때는 분대끼리 자리가 통째로 겹친 것이 원인이라
 * 붙들어도 떨림이 남았고, 자리를 고친 뒤에는 표에 차이가 없었다. 남은 것이 바로 이 경우(지형이 만든 겹침)였다.
 *
 * 10px보다 줄이면 떨림이 돌아온다(`npm run wander`: 7px 65명 · 8px 43명 · 10px 20명). 값은 줄의 가지런함이다 — 자리에서
 * 5~10px 떨어져 서는 병사가 는다(QA가 쟀다: 2~8% → 6~13%).
 */
const SLOT_LEAVE = fp(10);
/** 제 자리가 막힌 병사에게 보아 주는 줄 뒤의 빈 칸 수(두 줄 — `placeSlots`). */
const SLOT_ALT_CELLS = 12;
/** 줄 뒤의 빈 칸은 같은 분대의 다른 자리에서 이만큼은 떨어져야 한다 — 기병 둘이 서로 밀지 않는 거리(8 + 8 + 틈 2). */
const SLOT_ALT_GAP = fp(18);
/** 자리에서 이보다 멀면 달려서 붙는다. 장수를 몰 때 병사가 뒤처지지 않게. */
const SLOT_SPRINT = fp(48);

/*
 * **동료 분대도 장수 곁 제 자리를 따라간다.** 분대의 주력 가지로 자리를 정한다 —
 * 보병 분대는 옆, 궁수 분대는 뒤, 기병 분대는 왼쪽 멀리. 같은 가지 분대가 여럿이면
 * 표의 다음 칸이다. [옆(오른쪽 +), 앞(+)] 순서, px.
 *
 * 예전에는 대형을 명령한 순간 동료가 선 곳이 진이었고 동료는 거기를 지켰다. 그러면
 * 장수가 움직여도 군이 따라오지 않는다. 한 자리를 지키게 하려면 경로를 그려 두면 된다 —
 * 경로가 끝난 곳이 그 분대의 진이 되고 장수를 따르지 않는다(`sqStation`).
 */
const STATION_INF: readonly (readonly [number, number])[] = [
  [fp(130), 0],
  [-fp(150), -fp(60)],
  [fp(200), -fp(60)],
];
const STATION_BOW: readonly (readonly [number, number])[] = [
  [0, -fp(100)],
  [-fp(80), -fp(110)],
  [fp(80), -fp(110)],
];
const STATION_HORSE: readonly (readonly [number, number])[] = [
  [-fp(240), 0],
  [-fp(240), -fp(70)],
  [fp(240), 0],
];
/** 군의 앞이 될 수 있는 여덟 방향(고정소수점 단위 벡터). 181 = 256/√2. */
const OCTANT_X = [256, 181, 0, -181, -256, -181, 0, 181];
const OCTANT_Y = [0, 181, 256, 181, 0, -181, -256, -181];
/** 새 방향이 지금 앞과 이만큼 가까우면(내적, cos 35° × 256²) 앞을 바꾸지 않는다. */
const FRONT_KEEP_DOT = 53673;

/** 자리 가지. 보병(창·도검·민병)·궁·기병. */
const SLOT_INF = 0;
const SLOT_BOW = 1;
const SLOT_HORSE = 2;

function slotKind(branch: number): number {
  if (branch === BranchCode.Bow) return SLOT_BOW;
  if (branch === BranchCode.Horse) return SLOT_HORSE;
  return SLOT_INF;
}
/**
 * 밀어내기 반응 속도. 걸음 배율(`PACE_PCT`)을 같이 탄다.
 *
 * 걸음만 낮추고 이 값을 그대로 두었더니 적의 몸이 막는 세기가 반으로 줄었다 — 여섯 겹 창벽이 장수를 늦추는 비가
 * 3.14배에서 1.79배로(`combat.test.ts` "적의 몸"). 막는 몫(`BODY_BLOCK_PCT`)은 겹친 깊이에 비례하는데, 겹친 깊이는
 * 걸어 들어가는 빠르기와 밀려 나오는 빠르기의 균형이다. 걸음만 느려지면 덜 겹치고, 덜 막힌다. 그래서 둘의 비를
 * 지킨다. 몸싸움이 예전과 똑같아지는 것은 아니다 — 같이 낮춘 지금 그 비는 2.06배다(공격 간격·같은 편 떼어 놓기 같은
 * 틱 기준 규칙은 그대로라서다). "여섯 겹은 두 배 넘게 세운다"는 처음의 목표(`BODY_BLOCK_PCT`)는 지킨다.
 */
const SEPARATION_SPEED = Math.floor((fp(2.2) * PACE_PCT) / 100);

/**
 * 적의 몸이 얼마나 단단히 막는가(백분율). `separation`의 전진 깎기에 곱한다.
 *
 * 100이면 질량 비와 겹친 깊이 그대로다. 그 값에서는 창벽이 장수를 못 세웠다 —
 * 장수 질량이 12, 장창병이 4라 한 명당 몫이 작아서, 장창병 여섯 겹이 장수를
 * 1.16배밖에 늦추지 못했다. 순수한 질량 물리로는 "창벽이 장수를 몸으로 막는다"가
 * 나오지 않는다.
 *
 * 처음 세운 목표는 "창 분대 하나(세 겹)가 장수를 절반으로 늦추고, 민병 하나는
 * 거의 못 막는다"였다. **이 방식 하나로는 둘을 함께 못 맞춘다.** 장수가 적 대열을
 * 지나는 시간(빈 땅 대비)을 세기별로 재 보면:
 *
 *   세기   민병 1겹   장창병 3겹   장창병 6겹
 *   100      1.02        1.08         1.16
 *   300      1.04        1.32         2.26
 *   400      1.06        1.61         4.44
 *   600      1.12        2.25        11.91
 *
 * (300 줄만 아래 trunc 수정 뒤에 다시 쟀다. 나머지는 floor 시절 값이다.)
 *
 * 세 겹이 2배가 되는 600에서는 민병 하나가 장수를 막기 시작하고 여섯 겹은 벽이
 * 된다. 곡선이 매우 가파르다. 그래서 목표를 **여섯 겹**으로 물려 300을 골랐다 —
 * 민병 하나에는 거의 안 막히고, 여섯 겹 창벽은 두 배 넘게 세운다. 데이터를 보고
 * 목표를 바꾼 것이므로 그렇다고 적어 둔다.
 *
 * 이 표는 장수가 멈춰 선 덩어리에 곧장 걸어 들어가는 합성 시험이다. '민병 1겹'은
 * 한 명이 아니라 열네 명이 한 줄로 선 것이다. 실제 전투의 판정은 sweep·tactic·duel의
 * 전후 비교로 한다. 장수가 한 명에 막히면 일당백이 아니다.
 *
 * 전후 비교는 공짜가 아니었다. 난이도 사다리는 대체로 지켰지만 깊이 3 돌격 한 판이
 * 섬멸에서 장수 전사로 떨어졌다. tactic에서는 우회 대 정면이 +137%에서 +114%로
 * 줄었다. 새 명령 체계가 들어오면 "뭉쳐 밀기만 하는 것이 늘 최선은 아니다"를
 * 합격선으로 다시 잰다.
 *
 * **한때 이 값이 +54%였고 기병 후방 강타가 전군 추종 아래로 내려갔다.** 적 대열이
 * 우회 기병을 세워서라고 읽었다. 아니었다 — 아래 깎기가 음수를 floor로 잘라 매 틱
 * 한쪽으로 1단위씩 기울었다. trunc로 고치자 기병 후방 강타가 2224·전사 6/15로
 * 돌아왔다. 1단위 편향이 전술 순위를 뒤집을 수 있다.
 */
const BODY_BLOCK_PCT = 300;
/**
 * 같은 편끼리 겹친 몸을 한 틱에 얼마나 떼어 놓는가(겹친 깊이의 백분율). `relaxCrowd`.
 *
 * 밀어내기(`separation`)는 부드러운 힘이라 반쯤 겹친 두 몸을 틱에 0.55px쯤 떼는데, 보병 걸음은 2.2px다(30Hz).
 * 앞줄이 적에 막혀 서면 뒷줄이 계속 걸어 들어와 몸이 포개진 채로 균형을 잡았다 — 첫 충돌 10초에 아군
 * 97%가 옆 사람과 겹치고 55%는 깊이(40% 넘게) 겹쳤다(`npm run crowd`, 플레이한 사람이 짚었다).
 */
const CROWD_PUSH_PCT = 50;
/** `relaxCrowd`가 한 틱에 한 유닛을 옮기는 거리의 상한. 떨림을 막는다. */
const CROWD_MAX_STEP = fp(1.5);
/** 틱 시작에 지은 격자를 쓰므로 그 뒤 움직인 만큼 넉넉히 찾는다. */
const CROWD_SLACK = fp(6);
/** 가장 큰 몸의 반경(기병·장수 8px). 조회 반경을 잡는 데 쓴다. */
const CROWD_BODY_MAX = fp(8);
/**
 * 같은 편 사이에 두는 틈(몸 반경 합에 더한다). 그림(월드 폭 14px)이 몸(지름 10px)보다 넓어 몸만 떼면 화면에서는
 * 여전히 포개져 보인다. 4px이면 더 잘 풀렸지만 한 적에게 몰려드는 둘이 서로 밀려 표적을 양쪽에서 감싸, 나란히
 * 선 둘이 두 면이 되었다(`combat.test.ts` 포위 — 포위 보정이 쉬워진다). 2px은 그 시험을 지킨다.
 */
const CROWD_GAP = fp(2);
/**
 * 공격하러 가다 앞이 아군에 막혀 **나아가지 못하면** 제 왼쪽으로 비껴 돈다. 플레이한 사람이 청했다: "공격 모드에서
 * 아군에 막혀서 적에게 다가가지 못할 때는 왼편으로 돌아가게 해줘. 적도 마찬가지야. 그러면 전체적으로 시계 방향으로
 * 돌면서 싸우겠지." 아군은 위로 가니 왼쪽이 서쪽, 적은 아래로 오니 왼쪽이 동쪽 — 서쪽으로 올라가고 동쪽으로
 * 내려오니 화면에서 시계 방향으로 돈다. 앞줄 뒤에 막혀 서 있던 병사가 옆으로 빠져 적의 옆구리에 닿는다.
 *
 * - `FLANK_GAP`: 몸 반경 합에 이만큼 더한 거리 안의 아군을 '앞을 막는다'고 본다. 같은 편 틈(`CROWD_GAP` 2px)보다
 *   넉넉해야 떼어 놓인 앞사람도 보인다.
 * - **앞사람이 싸우고 있어야(표적이 있어야) 막힌 것이다.** 처음에는 앞에 누가 있기만 하면 막혔다고 봤다. 같은 편
 *   틈이 2px이라 행군하는 뒷줄은 늘 그 안에 들어, 접촉하기 전부터 아군 셋에 하나, 적 둘에 하나가 60° 비껴
 *   걸었다(리뷰가 짚었다). 그것이 공격 모드 장수 전사(0 → 12/15)의 큰 몫이었다 — 적이 싸움과 무관하게 옆으로
 *   퍼져 들어왔다. 같이 걸어가는 앞사람은 막는 것이 아니다.
 * - `FLANK_COS`: 가는 쪽에서 이 각(코사인, FP) 안에 있어야 앞이다. 45°.
 * - `FLANK_TURN`: 왼쪽 성분의 무게(FP). 앞 1 : 왼 4 — 약 76°를 튼다.
 * - **정말 막혔을 때만 돈다**(`STUCK_TICKS`). 가려는 쪽으로 한 틱에 제 걸음의 절반도 못 나아간 틱이 이만큼 이어지고,
 *   그때 앞에 싸우는 같은 편이 있어야 돈다. 막힘이 풀리면(앞이 비거나 다시 나아가면) 그 틱에 바로 앞으로 돌아온다.
 *   처음에는 앞에 싸우는 같은 편이 보이기만 하면 틀고 1.5초를 이어 돌았다 — 축은 15초에 68° 돌았지만, 플레이한
 *   사람이 짚었다: "이렇게 미리 돌지 말고 정말 막혔을 때만 돌아. 지금은 앞이 막히지 않았는데도 도네. 정말 갈 길이
 *   없을 때만 돌면 돼."
 */
const FLANK_GAP = fp(6);
const FLANK_COS = 181;
const FLANK_TURN = 1024;
/** 나아가지 못한 틱이 이만큼 이어져야 막힌 것이다(1/3초). 한두 틱 멈칫한 것은 막힌 것이 아니다. */
const STUCK_TICKS = 10;
/**
 * 공격·적군 흐름장의 목표로 삼는 상대 병사의 범위(칸, 한 칸 32px). 상대 무게중심에서 이 안에 선 병사가 모두
 * 목표다(`armyCells`). 이 안에 아무도 없으면(흩어졌다) 무게중심은 빼고 병사 전원이 목표다.
 *
 * 0이면 예전처럼 무게중심 한 점 — 흐름선이 모여 종대로 부딪친다. 끝없이 넓히면 저마다 가장 가까운 적에게 가
 * 전장 폭 그대로 부딪친다. 5칸(160px)이면(옆 끈 `ATTACK_FLANK`, 왼쪽 돌기와 함께) 깊이 17 전군 공격의 첫 충돌
 * 5초에 적군 폭이 135px에서 271px로, 접전 폭이 79px에서 123px로 넓어진다(`npm run spread`). 플레이한 사람이
 * "어느 정도 퍼져서"를 청했다.
 */
const FRONT_RADIUS_CELLS = 5;
/**
 * 물러난 병사가 추스르려면 이 안에 적이 없어야 한다(`WITHDRAW_BY_BRANCH`). 적 표적 반경(`SENSE_RADIUS` 170px)보다
 * 넓어야, 추스르는 병사를 둘레의 적이 곧장 표적으로 잡지 않는다.
 */
const WITHDRAW_CLEAR = fp(200);
/** 물러나는 병사가 이만큼(틱) 나아가지 못하면 몰린 것이다 — 돌아서 싸운다. 1초. */
const CORNERED_TICKS = 30;
/** 추스르는 주기(틱). 이 틱마다 초당 몫의 절반을 한 번에 더한다 — 정수 체력에서 작은 몫이 0으로 잘리지 않게. */
const REGEN_EVERY = 15;
/**
 * 궁수가 물러서는 거리. 활이 아닌 적(칼·창·기병·민병)이 이 안에 들면 쏘지 않고 그 적에게서 물러선다. 벌어지면
 * 다시 쏜다. 플레이한 사람이 청했다: "궁수는 적이 다가오면 도망가게 해줘." 궁수 사거리(250px 남짓)보다 한참
 * 짧아, 물러서도 곧 쏠 거리다. 대기 명령과 경로를 걷는 궁수는 물러서지 않는다 — 제자리·제 길을 지키라는 명령이다.
 */
/** 궁수가 활을 놓고 근접으로 싸우는 거리와 닿는 거리(`BOW_MELEE`). */
const BOW_SPREAD_STEP = fp(BOW_SPREAD.perAimerPx);
const BOW_STICK = fp(BOW_SPREAD.stickPx);
const MELEE_SPREAD_STEP = fp(MELEE_SPREAD.perChaserPx);
const DUTY_RADIUS = fp(ELITE_DUTY.radiusPx);
const DUTY_LEASH = fp(ELITE_DUTY.leashPx);
const DUTY_NEAR = fp(ELITE_DUTY.nearPx);
const DUTY_SPRINT = fp(ELITE_DUTY.sprintPx);
const MELEE_STICK = fp(MELEE_SPREAD.stickPx);
const LOB_SCATTER = fp(BOW_LOB.scatterPx);
const LOB_SPEED = fp(BOW_LOB.speedPx);
const BOW_CLOSE = fp(BOW_MELEE.closePx);
const BOW_MELEE_RANGE = fp(BOW_MELEE.rangePx);
/** 화살통의 상태(`quiver`). */
const Quiver = {
  /** 화살이 남았다. */
  Full: 0,
  /** 다 쏘았고 이기는 판이었다 — 근접으로 싸운다. */
  Melee: 1,
  /** 다 쏘았고 지는 판이었다 — 제 가장자리로 빠져나간다. */
  Leave: 2,
} as const;
/**
 * 잔병 소탕: 적 병사가 가장 많던 때의 1/4 이하로 줄었고, 남은 수가 이 수 이하이거나 전장의 아군 병사의 1/4
 * 이하면 대형도 쫓아간다. 처음부터 적이 한 줌인 싸움(시험의 작은 판)은 잔병이 아니다 — 줄어든 것이어야 한다.
 *
 * 플레이한 사람이 짚었다: "적 잔병이 남았을 때 아군 장수와 병사들이 어디로 가야할지 결정을 못하고 멈추거나 머뭇거리는
 * 모습이 보여." 대형은 장수 곁에 온 적만 치고(`Sense.Anchor`), 장수는 적이 450px(`ADVANCE_HALT`) 안에 보이면 서서
 * 맞는다. 물러나 추스르는 적이나 물러서며 쏘는 궁수처럼 다가오지 않는 잔병 앞에서는 모두가 서서 기다렸다 — 깊이 12
 * 기본 대형에서 적이 다섯 이하로 남은 뒤 판이 끝나기까지 평균 28초, 그동안 아군 63%가 서 있었다(한 판은 106초).
 */
const MOPUP_MAX = 5;
/** 흐름장 재계산 최소 간격(틱). */
const FLOW_INTERVAL = 10;
/** 사기 충격 반경. */
const MORALE_RADIUS = fp(90);
/**
 * 붕괴한 병사가 무작정 달아나는 시간(틱). 그 뒤에는 제 부대의 평균 사기(`rallyPct`)를 보고 추스를지 끝까지 달아날지
 * 정한다(`RALLY_PCT`). 한때는 이 시간이 지나면 누구나 사기를 반쯤 되찾아 다시 싸웠다 — 가장자리까지 가던 병사가 끝에서
 * 멈춰 서 버텼고, 장수가 거기까지 가서 베어야 판이 끝났다(플레이한 사람: "도망을 안가더라고. 플레이어 장수가 가서 끝에
 * 있는 병사를 죽이고 있어"). 늘 끝까지 달아나게 하자 이번에는 부대가 멀쩡한데 한 번 흔들린 병사까지 전장을 떴다 —
 * 플레이한 사람이 청했다: "부대의 평균 사기를 고려해서 도망갈지 회복할지 결정하는게 좋겠어."
 */
const ROUT_TICKS = 90;
/**
 * 무너진 병사가 추스르는 부대 평균 사기(기개 대비 백분율, `rallyPct`). 이보다 낮으면 부대가 흔들리는 것이라 끝까지
 * 달아나 전장을 빠져나간다(`tryExit`). 제 편 장수의 함성(`roar`)은 이와 무관하게 곧장 추스른다.
 */
const RALLY_PCT = 25;
/**
 * 출구(제 편 가장자리)에서 이 줄 수 안까지 달아난 병사는 부대 사기와 무관하게 빠져나간다. 가장자리 바로 앞에서 추슬러
 * 서는 병사가 "끝에 서서 버티는" 바로 그 모습이었다.
 */
const RALLY_EDGE_ROWS = 5;

/**
 * **피로**(Todo6). 병사마다 따로 쌓인다(`fatigue`, 0~`FATIGUE_MAX`). 치면(`FATIGUE_SWING`) 맞으면(`FATIGUE_HIT`) 조금씩
 * 차오르고, 쌓인 만큼 치는 세기와 걸음이 준다(가득이면 `FATIGUE_ATK_PCT`·`FATIGUE_SPEED_PCT`%). 한 번에 몰아 맞아도
 * 1초(`FATIGUE_WINDOW`)에 오르는 몫은 `FATIGUE_WINDOW_CAP`까지다 — 둘러싸여 두들겨 맞은 병사가 한순간에 지쳐 버리지
 * 않게. 출정 안에서 깊이를 넘어 이어지고(`RunState.fatigue`, 명부의 `RosterEntry.fatigue`), 철수하면 풀린다. 병사만
 * 지친다(장수·동료·적장은 아니다). 적 병사도 판 안에서는 같은 규칙으로 지친다(판마다 새로 온다).
 *
 * 플레이한 사람이 청했다: "실제로 병사마다 따로 피로도 수치가 관리되면 좋겠어. 공격을 하거나 피격을 당하면 조금씩
 * 차오르게. 한 번에 공격을 몰아 받아도 올라가는 피로도에는 제한이 있어야 하고."
 */
export const FATIGUE_MAX = 1000;
export const FATIGUE_SWING = 10;
export const FATIGUE_HIT = 12;
export const FATIGUE_WINDOW = 30;
export const FATIGUE_WINDOW_CAP = 30;
export const FATIGUE_ATK_PCT = 30;
export const FATIGUE_SPEED_PCT = 20;
/** 기병이 이만큼 연속으로 돌진하면 충격 보너스가 붙는다. */
const CHARGE_TICKS = 22;
/** 돌격이 실릴 때의 피해 배율(백분율). */
const CHARGE_PCT = 210;
/** 장수의 휩쓸기가 한 번에 치는 최대 인원. */
const CLEAVE_MAX = 8;
/**
 * 장수의 함성(`leaderRoar`). 둘레의 적 병사 사기를 제 기개(`grit`)의 이만큼(백분율) 깎는다. 기개에 비례하므로
 * 갓 나선 적은 흔들리기만 하고, 전우가 쓰러져 이미 사기가 꺾인 적이 무너져 달아난다 — **언제** 외치느냐가 값이다.
 *
 * 까닭: 판 안에서 플레이어가 결과를 바꾸는 한 수가 없었다. `tactic` 깊이 17에서 손대지 않은 기본 대형(3292)이
 * 가장 좋은 쪽이었고, 장수는 스스로 싸워 휩쓸기를 누를 일도 드물었다(평가에서 짚었다). 적 사기 몫만으로는 판이
 * 거의 안 바뀌어(`ROAR_ATK_PCT`에 잰 값) 둘레 아군의 기세를 함께 준다. docs/design.md "장수의 함성".
 */
const ROAR_PCT = 60;
/**
 * 적장이 쓰러지면 전장의 모든 적 병사가 잃는 사기(기개의 %). 이미 흔들리던 적은 그 자리에서 무너진다.
 * 플레이한 사람이 짚었다: "적장을 잡으면 적 사기가 떨어지지?" — 떨어지지 않았다. 적장의 죽음도 병사 하나의 죽음처럼
 * 둘레(`MORALE_RADIUS`)만 흔들었다. 적장을 노려 베는 것이 판을 뒤집는 한 수가 되도록 전군에 건다. 함성(`ROAR_PCT`
 * 60%, 둘레만)보다 약하고 넓다.
 */
const CHIEF_FALL_PCT = 40;
/** 적의 부대 수의 상한(`FOE_SQUADS.maxSquads`). 적 부대의 배열은 우리 분대의 배열(`MAX_SQUADS`)과 따로 둔다. */
const MAX_FOE_SQUADS = FOE_SQUADS.maxSquads;
/**
 * 장수가 쓰러진 뒤 후퇴에 주는 시간(틱, 30초). 그때까지 빠져나가지 못한 병력은 잃는다. 끝이 없으면 막힌 병사 하나 때문에
 * 판이 끝나지 않는다. 전장 한가운데에서 아래 가장자리까지 걸어도 넉넉한 시간이다.
 *
 * 30초였다. 전장 세로를 늘리고 걸음을 낮춘 뒤(`FIELD_ROWS`·`PACE_PCT`)로는 넉넉하지 않았다 — 리뷰가 온통 늪인 전장에서
 * 적진 줄(위에서 170px)의 민병이 맞지도 않고 30초에 출구 340px 앞에서 낙오하는 것을 보였다(옛 전장에서는 20초에
 * 나갔다). 가운데에서 가장자리까지가 512px에서 896px, 걸음이 85%라 같은 여유를 두려면 30 × (896/512) / 0.85 ≈ 62초인데,
 * 지는 판의 뒤처리를 그만큼 늘리지는 않고 50초로 두었다 — 적진 줄에서 맨땅으로 걸어 나오는 데 27~30초쯤이었다.
 *
 * **전장 세로를 32칸으로 되돌리며(Todo11) 30초로 되돌렸다.** 가운데에서 가장자리까지가 다시 512px이다. 걸음은 85%
 * 그대로라 옛 30초보다 여유가 조금 준다(512 ÷ 0.85 ≈ 602px 몫) — 맨땅의 병사가 가운데에서 걸어 나오는 데 10초 안쪽이다.
 */
export const FALL_RETREAT_TICKS = 900;
/** 함성이 닿는 반경. 장수를 적이 뭉친 곳까지 몰고 가야 크게 먹힌다. */
export const ROAR_RADIUS = fp(120);
/** 함성의 재사용 대기(틱, 30Hz). 한 판에 한두 번 — 첫 충돌에 쓸지 무너질 때 쓸지 고른다. */
export const ROAR_CD = 600;
/**
 * 함성을 들은 아군 병사가 더 세게 치는 몫(백분율)과 그 틱 수(5초).
 *
 * 처음에는 적의 사기만 깎았다. 재 보니 판이 거의 안 바뀌었다(깊이 17 생환 24.5 → 25.1) — 부딪치는 순간의 적은
 * 사기가 가득해 60%를 깎아도 무너지지 않고, 100%로 무너뜨려도 아군은 달아나는 적을 쫓지 않아(`acquireTarget`이
 * 무너진 적을 건너뛴다) 적은 3초 뒤 돌아왔다. 벤 격이 줄어 오히려 손해였다. 기세를 아군에 주자 부딪치는 순간의
 * 한 수가 됐다: +40%·5초에 생환 24.5 → 29.5. +80%는 30.9로 더 오르지만 한 번에 판이 기우는 쪽이라 40으로 둔다.
 */
export const ROAR_ATK_PCT = 40;
export const ROAR_TICKS = 150;
/**
 * 경로 지점에 이만큼 다가간 병사는 멈춘다. 지점 위에 겹쳐 서려고 밀치지 않게 한다.
 *
 * 옛 군기는 85px였다. 깃발 둘레를 지키는 것이라 넓어도 됐다. 경로는 부대의
 * 무게중심이 지점에 닿아야(`PATH_ARRIVE`) 다음으로 넘어가는데, 병사가 저마다 85px
 * 앞에서 서면 무게중심이 영영 70px 안으로 못 들어와 경로가 끝나지 않았다.
 * 병사가 멈추는 거리는 부대가 닿았다고 보는 거리보다 좁아야 한다.
 */
const BANNER_ARRIVE = fp(30);
/**
 * 부대(병사 무게중심)가 경로 지점에 이만큼 닿으면 다음 지점으로 넘어간다.
 * 병사 하나하나가 아니라 부대로 재야 앞선 몇이 먼저 닿았다고 나머지가 끌려가지 않는다.
 */
const PATH_ARRIVE = fp(70);
/**
 * 배후 공격 배율(백분율).
 *
 * 이게 없으면 병력을 나눌 이유가 없다. 검산에서 우회가 정면보다 언제나 나빴던
 * 이유가 이것이었다. 한 덩어리로 뭉치는 것이 항상 옳으면 분대도 깃발도 조작만
 * 늘린 장식이 된다. 옆구리를 찌른 보람이 숫자로 남아야 진형이 의미를 갖는다.
 */
const FLANK_PCT = 145;
/** 배후에서 맞으면 사기도 함께 깎인다. 포위당한 대열이 무너지는 이유다. */
const FLANK_MORALE = 4;

/**
 * 포위. 적에게 잡힌 면이 하나 늘 때마다 들어오는 피해가 이만큼 오른다.
 *
 * **수가 아니라 면을 센다.** 정면에 둘이 달라붙는 것은 포위가 아니다. 같은 쪽에서
 * 밀고 있으면 막는 손은 한 방향만 쓰면 된다. 수를 세면 한 덩어리로 몰려가는 것이
 * 곧 포위가 되어, 병력을 나눌 이유가 오히려 사라진다.
 *
 * 면은 방어자가 바라보는 쪽을 기준으로 정면·좌·우·후 넷이다. 넷을 다 잡으면
 * 최대 +45%다. 배후 판정(`FLANK_PCT`)이 "이 타격이 뒤에서 왔는가"를 본다면
 * 이쪽은 "지금 몇 면이 잡혀 있는가"를 본다.
 */
const SURROUND_PCT = 15;

/** 방어자 기준 네 면. 비트 하나씩 쓴다. */
const SIDE_FRONT = 1;
const SIDE_RIGHT = 2;
const SIDE_REAR = 4;
const SIDE_LEFT = 8;

/**
 * 높은 데서 치면 더 아프다.
 *
 * 언덕을 **차지할 값어치가 있는 자리**로 만드는 유일한 장치다. 오르는 데
 * 속도를 깎기만 하면 아무도 안 올라간다. 양쪽 다 언덕이면 보정이 없다.
 * 언덕 위 난전에서 서로 +15%를 주고받는 것은 아무것도 바꾸지 않는다.
 */
const HILL_PCT = 115;

/**
 * 숲에 선 표적에게 화살이 받는 값(백분율).
 *
 * 나무가 가려 주는 몫이다. 궁수가 사거리에서 일방적으로 이기는 구도에
 * 대응할 자리를 준다. 근접에는 적용하지 않는다 — 나무가 칼을 막아 주지는
 * 않고, 적용하면 숲이 그냥 방어력 지형이 되어 들어가는 쪽이 늘 이득이 된다.
 */
const FOREST_COVER_PCT = 50;

/** 돌격이 붙지 않는 땅. 나무 사이를, 진창을, 물살을, 말뚝 사이를 뚫고 속도를 붙일 수는 없다. */
function roughGround(cell: Cell): boolean {
  return cell === Cell.Forest || cell === Cell.Marsh || cell === Cell.Ford || cell === Cell.Stakes;
}

const NO_TARGET = -1;

export interface BattleStats {
  kills: Int32Array;
  /** team별로 처치한 적의 격 총합. 전과 산정의 기본값이다. */
  killScore: Int32Array;
  losses: Int32Array;
  damage: Int32Array;
  /** team별로 전장을 빠져나간 병사 수. 병사만 센다(`losses`와 같은 기준). */
  exited: Int32Array;
}

export interface SpawnOrder {
  tick: number;
  spec: CombatantSpec;
  team: Team;
  count: number;
  x: number;
  y: number;
  spread: number;
  uidBase: number;
  squad?: number;
}

/** 초당 픽셀 속도를 틱당 고정소수점으로. 걸음 배율(`PACE_PCT`)을 여기서 한 번 곱한다 — 모든 유닛이 이 문을 지난다. */
function speedToFp(pxPerSec: number): number {
  return Math.round((pxPerSec * PACE_PCT * FP_ONE) / (100 * TICK_HZ));
}

/**
 * 소리 전용 셈(`World.fx`)의 종류. 근접은 친 쪽의 병종으로 가른다: 창과 기병은 찌르고, 나머지(칼·민병·궁수가 활을 놓고
 * 칠 때·장수)는 벤다.
 */
export const FX = { Cut: 0, Thrust: 1, ArrowOut: 2, ArrowHit: 3, Fall: 4 } as const;
export const FX_KINDS = 5;

export class World {
  readonly grid: TerrainGrid;
  readonly rng: Rng;
  readonly seed: number;
  /**
   * 이 판의 깊이(`ScenarioConfig.depth` — `createBattle`이 적는다, 그 밖에는 0). **화면만 읽는다**: 적의 대장이 누구인지를
   * 깊이로 가른다(관문이면 이름난 사람 — `meta/foe-leaders.ts`). 시뮬레이션은 이 값을 읽지 않고 해시에도 넣지 않는다
   * (명부와 같은 불변 입력이고, 판정에 쓰이지 않는다).
   */
  depth = 0;
  tick = 0;

  // ---- 위치와 상태 ----
  readonly x = new Int32Array(MAX_UNITS);
  readonly y = new Int32Array(MAX_UNITS);
  /** 직전 틱 위치. 렌더링 보간 전용이며 시뮬레이션은 읽지 않는다. */
  readonly prevX = new Int32Array(MAX_UNITS);
  readonly prevY = new Int32Array(MAX_UNITS);
  readonly knockX = new Int32Array(MAX_UNITS);
  readonly knockY = new Int32Array(MAX_UNITS);
  readonly faceX = new Int32Array(MAX_UNITS);
  readonly faceY = new Int32Array(MAX_UNITS);
  readonly hp = new Int32Array(MAX_UNITS);
  readonly alive = new Uint8Array(MAX_UNITS);
  readonly cd = new Int16Array(MAX_UNITS);
  readonly target = new Int32Array(MAX_UNITS);
  readonly morale = new Int16Array(MAX_UNITS);
  readonly routed = new Int16Array(MAX_UNITS);
  /** 함성을 듣고 기세가 오른 남은 틱(아군 병사). 0이면 없다. */
  readonly rallied = new Int16Array(MAX_UNITS);
  readonly charge = new Uint8Array(MAX_UNITS);
  readonly hitFlash = new Uint8Array(MAX_UNITS);
  /**
   * 렌더링 전용: 이 병사가 장수에게서 받은 피해의 누계. 렌더러가 체력 감소분 중 장수 몫을 크게 띄운다.
   * 체력 차분만으로는 누가 쳤는지 알 수 없다. 규칙은 읽지 않고 체크섬에도 넣지 않는다(`checksum` 머리말).
   */
  readonly leaderDealt = new Int32Array(MAX_UNITS);
  /**
   * 렌더링 전용: 장수의 한 방에 쓰러졌으면 1. 렌더러가 그 장수 숫자를 흰빛으로 번쩍인다. 같은 프레임에 장수와
   * 병사가 함께 쳤을 때 누가 쓰러뜨렸는지는 체력 차분으로 알 수 없어 따로 남긴다. 규칙은 읽지 않고 체크섬에도
   * 넣지 않는다(`checksum` 머리말).
   */
  readonly leaderKilled = new Uint8Array(MAX_UNITS);
  /**
   * 이 유닛이 벤 적의 수. 결과 화면의 병사별 전공이 읽는다. 규칙은 읽지 않는다 — 누가 베었는지는
   * 피해 귀속(`kill`의 `by`)이 이미 정하고, 이 수는 그 결과를 셀 뿐이다.
   */
  readonly killCount = new Uint16Array(MAX_UNITS);
  /**
   * 결과 전용: 이 유닛이 벤 적의 격(`sPower`) 합, 친 횟수, 맞은 횟수. 전투 성과 경험치(`meta/run.ts`)가 읽는다 — 플레이한
   * 사람이 청했다: "전투 성과를 낸 경우(적을 처치, 적을 공격, 피격당함) 해당 병사(장수)에게 추가 경험치를 주자." 규칙은
   * 읽지 않는다. 틱마다 셀 뿐이라 리플레이를 다시 돌리면 같은 값이 나온다(서버도 같은 경험치를 센다).
   */
  readonly killPower = new Int32Array(MAX_UNITS);
  readonly hitsDealt = new Int32Array(MAX_UNITS);
  readonly hitsTaken = new Int32Array(MAX_UNITS);
  /** 병사의 피로(0~`FATIGUE_MAX`). 치는 세기·걸음이 준다. 틱을 넘는 규칙 상태라 해시에 든다. */
  readonly fatigue = new Uint16Array(MAX_UNITS);
  /** 이번 1초(`FATIGUE_WINDOW`)에 오른 피로. `FATIGUE_WINDOW_CAP`까지. 창이 바뀔 때 비운다. 해시에 든다. */
  readonly fatGain = new Uint16Array(MAX_UNITS);
  readonly team = new Uint8Array(MAX_UNITS);
  /** 소속 분대. 0번은 장수 직할대다. */
  readonly squad = new Uint8Array(MAX_UNITS);
  /** 동료가 쓰러졌는지. 동료는 전사하지 않고 다음 출정 몇 번을 쉰다. */
  readonly downed = new Uint8Array(MAX_UNITS);
  /**
   * 전장을 빠져나갔는지. 빠져나간 유닛은 `alive`가 0이지만 죽은 것이 아니다.
   *
   * `alive`를 1로 두고 따로 숨기지 않은 이유: 전장 위의 모든 판정(이웃 조회,
   * 표적, 화살, 적의 무게중심)이 `alive`만 본다. 빠져나간 병사가 그 판정에 하나라도
   * 남으면 보이지 않는 병사가 싸움에 끼어든다. 전장 밖이라는 점에서는 죽은 것과
   * 같고, 계정에 돌아갈 때만 다르다.
   */
  readonly exited = new Uint8Array(MAX_UNITS);
  /** 계정에 영속하는 병사 식별자. 영구 사망 추적에 쓴다. */
  readonly uid = new Int32Array(MAX_UNITS);
  count = 0;

  // ---- 병사별 전투 수치 ----
  readonly maxHp = new Int32Array(MAX_UNITS);
  readonly sAtk = new Int32Array(MAX_UNITS);
  readonly sDef = new Int32Array(MAX_UNITS);
  /** 사거리(고정소수점). */
  readonly sRange = new Int32Array(MAX_UNITS);
  /** 궁수가 움직이는 적의 앞을 내다보는 몫(%, `arrowLeadPct`). 궁수가 아니면 0. 스폰 때 정해진다. */
  readonly sLead = new Uint8Array(MAX_UNITS);
  /**
   * 지난 틱에 옮긴 몫(고정소수점 px/틱). 틱 끝에 `x − prevX`로 잰다(`step`). 궁수가 적의 앞을 내다볼 때 읽는다
   * (`fireArrow`) — 규칙이 읽으므로 체크섬에 넣는다.
   */
  readonly velX = new Int32Array(MAX_UNITS);
  readonly velY = new Int32Array(MAX_UNITS);
  /** 틱당 이동 거리(고정소수점). */
  readonly sSpeed = new Int32Array(MAX_UNITS);
  readonly sCd = new Int16Array(MAX_UNITS);
  readonly sRadius = new Int32Array(MAX_UNITS);
  readonly sMass = new Int16Array(MAX_UNITS);
  readonly sGrit = new Int16Array(MAX_UNITS);
  readonly sPierce = new Int16Array(MAX_UNITS);

  /**
   * 이 유닛이 적에게 잡힌 면. 정면·좌·우·후 네 비트다. 포위 판정에 쓴다.
   *
   * 수가 아니라 면인 이유는 `SURROUND_PCT`에 적었다. 방어자가 바라보는 쪽이
   * 기준이므로, 같은 자리에 선 적이라도 방어자가 돌아서면 다른 면이 된다.
   *
   * 값은 **지난 틱에 센 것**이다. 같은 틱에 센 값을 바로 읽으면, 색인이 앞선
   * 유닛과 뒤선 유닛이 서로 다른 시점의 숫자를 보게 된다. 지연을 두면 한 틱
   * 안에서는 누가 읽든 같은 값이다. 30Hz에서 한 틱은 33ms라 보이지 않는다.
   *
   * 지연이 고치는 것은 **읽는 쪽**의 일관성뿐이다. 세는 쪽은 여전히 색인
   * 순서를 탄다. 위치 갱신이 제자리라 색인이 앞선 적은 이번 틱 자리에서,
   * 뒤선 적은 지난 틱 자리에서 사거리를 따진다. 밀어내기가 원래 그랬고 포위
   * 판정이 그대로 물려받았다. 결정론은 지켜지지만 공짜로 얻은 것은 아니다.
   */
  readonly pressSides = new Uint8Array(MAX_UNITS);
  private readonly pressSidesNext = new Uint8Array(MAX_UNITS);

  /**
   * 틱이 시작할 때의 방향. 면 분류에만 쓴다.
   *
   * 방향을 제자리에서 갱신하면 방어자가 재탐색으로 돌아설 때, 색인이 앞선
   * 공격자는 옛 방향으로 뒤선 공격자는 새 방향으로 분류된다. 그러면 **정면에
   * 나란히 선 둘이 정면과 우측면으로 갈려** 없던 면이 생긴다. 다음 틱이면
   * 한 면으로 돌아오지만, 그 틱의 피해는 이미 더 들어갔다.
   *
   * 배후 판정도 같은 제자리 방향을 읽는다. 다만 거기서는 타격 하나의 방향이
   * 어긋날 뿐이고, 여기서는 "같은 쪽에서 밀면 한 면"이라는 규칙 자체가
   * 깨진다. 재 보니 면 분류 46만 번 중 1만 번(2.2%)이 달랐다.
   */
  private readonly faceSnapX = new Int32Array(MAX_UNITS);
  private readonly faceSnapY = new Int32Array(MAX_UNITS);
  readonly sPower = new Int32Array(MAX_UNITS);
  readonly branch = new Uint8Array(MAX_UNITS);
  readonly role = new Uint8Array(MAX_UNITS);
  /** 이 전투에 등장한 명부 종류의 인덱스. 렌더링과 집계가 참조한다. */
  readonly specIdx = new Uint16Array(MAX_UNITS);
  /** 이 전투에 등장한 명부 종류 목록. */
  readonly specs: CombatantSpec[] = [];
  private readonly specLookup = new Map<string, number>();

  // ---- 투사체 ----
  readonly pjX = new Int32Array(MAX_PROJECTILES);
  readonly pjY = new Int32Array(MAX_PROJECTILES);
  readonly pjPrevX = new Int32Array(MAX_PROJECTILES);
  readonly pjPrevY = new Int32Array(MAX_PROJECTILES);
  readonly pjVX = new Int32Array(MAX_PROJECTILES);
  readonly pjVY = new Int32Array(MAX_PROJECTILES);
  readonly pjTtl = new Int16Array(MAX_PROJECTILES);
  /**
   * 화살 한 대의 피해. Int32다 — 깊이 30 너머의 적은 단련으로 공격이 수만이 된다(`boostSpec`). Int16이던 때는 깊이 67
   * 남짓의 단련된 궁수(공격 37,380)가 쏜 화살이 −28,156으로 저장되어, 단련할수록 화살이 약해졌다(리뷰가 짚었다).
   * 체크섬(`hashArray`)은 원소 값을 섞으므로 폭을 넓혀도 같은 값이면 해시가 같다.
   */
  readonly pjDmg = new Int32Array(MAX_PROJECTILES);
  readonly pjTeam = new Uint8Array(MAX_PROJECTILES);
  /** 곡사인가(`BOW_LOB`). 날아가는 동안에는 아무도 맞지 않고, 떨어지는 틱에 그 자리의 적만 맞는다. 체크섬에 든다. */
  readonly pjLob = new Uint8Array(MAX_PROJECTILES);
  /** 쏠 때의 수명(틱). 그리기 전용 — 곡사 화살이 얼마나 날았는지(포물선의 높이)를 화면이 읽는다. 규칙은 읽지 않는다. */
  readonly pjLife = new Int16Array(MAX_PROJECTILES);
  readonly pjOwner = new Int32Array(MAX_PROJECTILES);
  readonly pjAlive = new Uint8Array(MAX_PROJECTILES);
  private pjCursor = 0;

  // ---- 지휘 상태 ----
  leaderIdx = NO_TARGET;
  /**
   * 렌더링 전용: 장수가 휩쓸기를 휘두른 횟수와 그 순간 바라본 방향. 렌더러가 칼바람을 그린다.
   * 규칙은 읽지 않고 체크섬에도 넣지 않는다(`checksum` 머리말). 렌더러가 틱 끝의 `faceX/Y`를 쓰면
   * 수동 휩쓸기(틱 첫머리에 판정)의 방향이 틀렸다 — 같은 틱 뒤에서 표적 쪽으로 몸을 돌린다(리뷰가 짚었다).
   * 횟수로 세므로 한 프레임에 틱이 여럿 지나도 휘두른 것을 놓치지 않는다.
   */
  strikeCount = 0;
  strikeFaceX = 0;
  strikeFaceY = 0;
  /** 렌더링 전용: 마지막으로 휩쓸기를 휘두른 사람(장수 또는 조종하는 동료, `controlled`). 칼바람을 그 자리에 그린다. */
  strikeBy = NO_TARGET;
  /**
   * 이 틱에 플레이어가 조종하는 사람(`controlled`). 틱 첫머리에 명령을 받은 뒤 한 번 정한다 — 유닛마다 다시 재면 그 틱
   * 안에서 동료가 쓰러질 때 앞뒤 유닛이 다른 사람을 조종하는 셈이 된다. 고른 분대(`selectedMask`)와 삶·죽음에서 나오는
   * 파생값이라 해시에 넣지 않는다.
   */
  private ctrl = NO_TARGET;
  /**
   * 장수 기술을 다시 쓸 수 있을 때까지 남은 틱. 0이면 쓸 수 있다. 판은 준비된 채 시작한다. 이름은 기술이 함성 하나뿐이던
   * 때의 것이다(Todo10에서 기술 칸이 생겼다, `sim/skills.ts`).
   */
  roarCd = 0;
  /** 장수 기술을 쓴 횟수. 그리기 전용(퍼지는 고리) — `strikeCount`와 같은 까닭으로 횟수로 센다. */
  roarCount = 0;
  /**
   * 이번 틱에 난 일의 수와 자리 — **소리 전용**(`audio/observer.ts`). 종류(`FX`)마다 [수, x의 합(px), y의 합(px)].
   * 틱 첫머리에 비우고 판정이 난 자리에서 더한다. 판정은 이것을 읽지 않고 체크섬에도 넣지 않는다 — 있든 없든 판은 같다.
   *
   * 화면의 피해 숫자(`render/damage-floats.ts`)는 프레임 사이의 체력 차이를 본다. 그 길로는 칼인지 화살인지, 쏜 것인지
   * 맞은 것인지 모르고, 살아서 빠져나간 병사(`tryExit`)도 쓰러진 것으로 보인다 — 그래서 판정이 난 자리에서 직접 센다.
   * 하나하나를 적지 않고 종류별로 모으는 까닭: 소리는 어차피 묶어서 낸다(수백 명의 타격을 하나씩 낼 수 없다). 자리의
   * 합을 수로 나누면 그 틱에 싸움이 난 한가운데다(카메라에서의 거리와 좌우를 정한다).
   */
  readonly fx = new Int32Array(FX_KINDS * 3);
  /** 장수 기술(`SKILL_CODE`)과 레벨. 판을 세울 때 정한다(`setSkill`) — 정하지 않으면 함성 레벨 1, 예전 판이다. */
  skillCode = 0;
  skillLevel = 1;
  /** 기술의 재사용 대기(틱)·지속(틱)·몫(%)·반경. 화면이 읽는다(단추가 차오르는 몫, 고리 크기). */
  skillCdMax = ROAR_CD;
  skillTicks = ROAR_TICKS;
  skillPct = ROAR_ATK_PCT;
  skillRadius = ROAR_RADIUS;
  /** 방진(`Skill.Phalanx`)이 남은 틱. 이 동안 받는 피해가 줄고(`skillPct`) 밀리지 않으며 느리다(`PHALANX_SPEED_PCT`). */
  readonly guard = new Int16Array(MAX_UNITS);
  /** 일제사격(`Skill.Volley`)이 남은 틱. 이 동안 쏘는 화살이 `skillPct`% 더 아프다. */
  readonly volley = new Uint8Array(MAX_UNITS);
  /**
   * 장수가 쓰러진 틱. 쓰러지지 않았으면 0. 그 뒤로 남은 아군은 모두 후퇴하고(명령을 받지 않는다), 아래 가장자리로
   * 빠져나간 병사는 살아 돌아온다. 판은 전장에 아군이 남지 않거나 `FALL_RETREAT_TICKS`가 지나면 끝난다(`fallOver`).
   * 플레이한 사람이 청했다: "플레이어 장수가 죽으면 바로 끝나게 하지말고 후퇴 절차를 거쳐줘. 무사히 하단으로 빠져나온
   * 병사들은 살아돌아오게해줘." 예전에는 장수가 쓰러지는 틱에 판이 끝나고 데려간 병력을 전부 잃었다.
   */
  leaderFellAt = 0;
  /**
   * 렌더링 전용: 이 유닛의 사기가 크게 꺾인 마지막 틱(함성, 적장의 죽음). 0이면 없다. 렌더러가 그 순간 머리 위에 ▼를
   * 그린다. 처음에는 화면이 그리는 때의 반경·무너짐으로 함성 대상을 다시 셌는데, 외친 틱에 반경을 벗어난 적은 사기가
   * 꺾이고도 표시가 없었다 — 한 프레임에 틱이 여럿 돌고 유닛은 외친 뒤 움직인다(리뷰가 짚었다). 전우 하나의 죽음(-9)이나
   * 옆구리 몰림은 잦고 작아 적지 않는다 — 난전이 ▼로 뒤덮인다. 규칙은 읽지 않고 체크섬에도 없다.
   */
  readonly shakenTick = new Int32Array(MAX_UNITS);
  chiefIdx = NO_TARGET;
  private inputDx = 0;
  private inputDy = 0;

  /** 분대별 지휘관. 0번 분대는 장수가 맡는다. 쓰러지면 NO_TARGET이 된다. */
  readonly sqCommander = new Int32Array(MAX_SQUADS).fill(NO_TARGET);
  /**
   * 부대 명령. **기본은 공격이다**(REPLAY_VERSION 108). 플레이한 사람이 정했다(2026-10-07): "기본 명령상태를 모두
   * 공격으로 해줘." 맨 처음에도 공격이 기본이었고, 30부터 107까지는 대형이었다("기본 전장 명령을 대형으로, 이
   * 상태에서 부대장들은 자동전투" — 대형의 장수는 손을 놓으면 진을 끌고 적 쪽으로 나아간다, `steerByOrder`).
   * 되돌린 까닭: 기병의 분대가 주력을 기다리지 않고 돌아 들어가게 된 뒤(107) 대형으로 남은 주력이 한참 늦게 닿아
   * 기병이 혼자 녹았다(손 안 댄 판의 기병 쓰러짐 5~10 → 21~35%, 관문 25의 이김 고·백·신 77·75·66 → 59·62·41/90).
   * 모두 공격이면 주력도 함께 들어간다. 대형은 이제 플레이어가 고르는 명령이다 — 대형의 기병 분대가 스스로
   * 나서는 규칙(`sqAuto`)은 대형을 내린 적 없는 분대에만 걸리므로, 기본이 공격인 판에서는 쓰일 일이 없다.
   */
  readonly sqOrder = new Uint8Array(MAX_SQUADS).fill(Order.Attack);
  /**
   * 부대원(병사)의 무게중심과 수. 틱 시작에 한 번 센다. 지휘관이 없는 부대가 경로 지점에
   * 닿았는지 재는 데 쓴다(`advancePaths`). 예전에는 공격 명령의 지휘관이 부대를 기다릴지도
   * 이것으로 정했다(없앴다 — `countSquads` 뒤의 주석).
   */
  private readonly sqCx = new Int32Array(MAX_SQUADS);
  private readonly sqCy = new Int32Array(MAX_SQUADS);
  private readonly sqN = new Int32Array(MAX_SQUADS);
  /**
   * 편·분대마다 버티는 병사의 사기 합과 산 병사 모두의 기개 합(`tallyMorale`, 색인 `team * MAX_SQUADS + squad`). 틱
   * 시작에 센 파생값이라 해시에 넣지 않는다.
   */
  private readonly grpMorale = new Int32Array(2 * MAX_SQUADS);
  private readonly grpGrit = new Int32Array(2 * MAX_SQUADS);
  /**
   * 틱 시작에 센다(`countSquads`): 아군에서 가장 느린 병사·동료의 걸음, 그리고 표적을 쥔 아군 병사 수.
   * 대형의 장수가 진을 끌고 나아갈 때 걸음을 맞추고(`armyPace`), 누가 싸우는 동안에는 서는 데(`armyEngaged`)
   * 쓴다. 매 틱 상태에서 다시 세므로 체크섬에 넣지 않는다.
   */
  private armyPace = 0;
  /**
   * 주력의 맨 앞(`ATTACK_ABREAST`): 장수를 따르는 무리의 병사 가운데 군의 앞쪽(`frontX`)으로 가장 나간 자리(그 축에
   * 내린 값, 고정소수점 px)와, 그런 병사가 하나라도 있는가. 틱 시작에 센 파생값이라 해시에 넣지 않는다(`armyPace`와 같다).
   */
  private mainFront = 0;
  private mainAny = false;
  /**
   * 부하 부대마다 맨 앞 사람의 자리(`measureMain`): 군의 앞축에 내린 값(`sqFront`). 지휘관도
   * 센다. 걸음을 맞출 때 분대가 함께 서고 함께 걷게 하는 기준이다(`ATTACK_ABREAST`). 파생값.
   */
  private readonly sqFront = new Int32Array(MAX_SQUADS);
  private readonly sqFrontAny = new Uint8Array(MAX_SQUADS);
  /**
   * 기병 분대의 돌아가기(`SWEEP`). `sqSweep`은 고른 쪽(`Sweep`), `sqSweepLane`은 그때 정한 세로줄(x) — 틱을 넘는 상태라 해시에 든다. `sweepOn`·`sweepX`·`sweepY`는 틱 시작에 정한 이번 틱의 갈 곳이다(`planSweeps`, 파생값).
   * `sqHorse`는 분대의 기병 병사 수(`countSquads`).
   */
  readonly sqSweep = new Uint8Array(MAX_SQUADS);
  readonly sqSweepLane = new Int32Array(MAX_SQUADS);
  /**
   * 줄에 섰는가(0·1) — 한 번 서면 되돌아가지 않는다. 틱을 넘는 상태라 해시에 든다.
   *
   * 처음에는 틱마다 "지금 줄 곁인가"를 다시 봤다. 줄 위에 바위가 하나 있으면 올라가는 길이 막혀 적 쪽 흐름을 타는데
   * (비스듬히 안쪽으로), 그러면 줄에서 벗어나 다시 옆걸음이 되고, 줄에 서면 다시 올라가려다 흐름을 탔다 — 지휘관이 두
   * 단계를 오가며 무리 전체를 흔들었다. 플레이한 사람: "이제 기병들이 덜덜 떨면서 이동하는데?" 재 보니 판의 대부분은
   * 가는 쪽이 꺾이는 틱이 1%인데 그런 판은 16~28%였고, 24판 가운데 둘은 끝내 다 돌지 못했다(리뷰도 무게중심이 줄의
   * 문턱을 넘나드는 판을 짚었다 — 9번 바뀌고 끝났다).
   */
  readonly sqSweepUp = new Uint8Array(MAX_SQUADS);
  /**
   * 돌기를 마친 분대(0·1): 궁수의 높이에 닿았거나 요격을 만나 그만뒀다(`SWEEP_MEET`). 그 분대의 기병과 지휘관은 첫 칼
   * 전의 걸음 맞추기(`ATTACK_ABREAST`)에 묶이지 않는다 — 돌아 들어간 자리에서 주력을 기다리며 서 있지 않고 곧장 친다.
   * 돌 줄을 찾지 못해 돌지 않은 분대는 0이다(여느 공격 분대다). 틱을 넘는 상태라 해시에 든다.
   */
  readonly sqSweepFree = new Uint8Array(MAX_SQUADS);
  /**
   * 대형의 기병 분대가 스스로 공격에 나설 수 있는가(1·0). 판을 열면 1이고, 스스로 나섰거나 플레이어가 그 분대에 어떤
   * 명령이든 내리면 0이 된다 — 다시 1이 되지 않는다. 틱을 넘는 상태라 해시에 든다.
   *
   * 플레이한 사람이 골랐다(2026-10-06): "대형 명령에서도 기병 분대가 때맞춰 돌아가기 이거 해보자." 공격 명령을 받은 기병
   * 분대는 통째로 돌아 적 궁수를 치는데(`SWEEP`), 손 안 댄 판에서는 모든 부대가 대형이라 그 규칙에 닿지 않았다 — 대형의
   * 기병은 지휘관 둘레 250px의 끈에 묶여 앞줄에서 창에게 깎였다(세 나라 모두 맨 아래 가지). 이제 기병의 분대는 판이
   * 열리고 적 궁수가 줄 뒤에 서면 스스로 공격으로 바꿔 돌아간다. 붙들어 두려면 그 분대에 대형을 다시 내린다 — 한 번
   * 명령을 받은 분대는 스스로 나서지 않는다.
   */
  readonly sqAuto = new Uint8Array(MAX_SQUADS).fill(1);
  /**
   * 그 분대가 스스로 나섰는가(0·1, 한 번 켜지면 그대로). 화면이 알림을 띄우는 데 읽는다 — 플레이어가 공격을 누른 것과
   * 가른다. 읽는 규칙이 없는 결과 전용 값이라 해시에 넣지 않는다(`killCount`와 같다).
   */
  readonly sqAutoOut = new Uint8Array(MAX_SQUADS);
  private readonly sweepOn = new Uint8Array(MAX_SQUADS);
  private readonly sweepX = new Int32Array(MAX_SQUADS);
  private readonly sweepY = new Int32Array(MAX_SQUADS);
  /** 돌아가는 분대의 지휘관이 가는 쪽(단위 벡터, `planSweeps`가 채운다). 지휘관이 기병의 무게중심을 앞뒤로 재는 축이다(`SWEEP_PACK`). 파생값. */
  private readonly sweepDirX = new Int32Array(MAX_SQUADS);
  private readonly sweepDirY = new Int32Array(MAX_SQUADS);
  private readonly sqHorse = new Int32Array(MAX_SQUADS);
  /** 분대의 기병 병사만 센 무게중심의 합(칸)과 가장 느린 걸음 — 돌아가는 무리는 기병뿐이다(`rides`). 파생값. */
  private readonly sqHorseCx = new Int32Array(MAX_SQUADS);
  private readonly sqHorseCy = new Int32Array(MAX_SQUADS);
  private readonly sqHorsePace = new Int32Array(MAX_SQUADS);
  /**
   * 적 병사 가운데 가장 느린 걸음(무너져 달아나는 병사는 빼고, 병사가 없으면 0). 적장이 맞서는 적이 멀 때 맞추는
   * 걸음이다(`CHIEF_CHARGE`). 틱 시작에 센 파생값이라 해시에 넣지 않는다(`sqPace`와 같다).
   */
  private foePace = 0;
  /** 적장이 제 병사들과 함께 오는가(`CHIEF_CHARGE`). 판을 세울 때 정한다 — 토벌이 아니면 켠다(`createBattle`). */
  chiefMarch = false;
  /** 적 병사가 횡대로 오는가(`LINE_RELEASE`). 시나리오가 켠다. */
  lineMarch = false;
  /**
   * 아군 병사 무게중심의 줄(y, 칸 한가운데). 없으면 -1. 흐름장을 다시 볼 때(`updateFlowFields`) 같이 고친다 — 틱을
   * 넘는 상태라 해시에 든다.
   */
  private marchY = -1;
  private armyEngaged = 0;
  /**
   * 대형의 자리. 대형을 명령한 순간 지휘관이 선 곳이다.
   *
   * 처음에는 지휘관을 그냥 세워 두고 닿는 적만 치게 했다. 그러면 가장 센 사람(장수와
   * 동료)이 진 한가운데 서서 놀았다. 깊이 3에서 대형만 걸면 장수가 15판 모두
   * 죽었다 — 옛 추종(장수는 서 있고 병사가 제 둘레를 치던 것)은 8판이었다. 진의
   * 자리를 따로 두면 지휘관도 나가 싸우고 돌아올 곳이 생긴다. 장수를 몰고 있는
   * 동안은 자리가 장수를 따라간다.
   */
  readonly sqHomeX = new Int32Array(MAX_SQUADS);
  readonly sqHomeY = new Int32Array(MAX_SQUADS);
  /**
   * 1이면 대형의 동료 분대가 진의 자리를 장수 곁 제 자리(`STATION_*`)로 매 틱 옮긴다.
   * 대형을 명령하면 1, 경로를 걸어 끝에 진을 짜면 0이다 — 그 자리를 지킨다.
   *
   * **명령한 대형과 같게 1로 시작한다**(0번은 장수 자신이라 0) — 대형이 기본이던 때(REPLAY 30~107)의 초기값이고,
   * 기본이 공격인 지금은 대형을 명령할 때 다시 1로 놓인다. 처음에는 `sqOrder`만 대형으로
   * 채워, 명령을 한 번도 내리지 않은 첫 판에서 동료 분대가 스폰 자리를 지키며 장수를 따르지 않았다 —
   * 장수만 진군하고 분대가 흩어져 녹았다(리뷰가 짚었다).
   */
  readonly sqStation = new Uint8Array(MAX_SQUADS).fill(1).fill(0, 0, 1);
  /**
   * 출전 위치에서 정한 동료 분대의 자리(`setPost`, `sim/deploy.ts`): 장수에게서 본 [옆(오른쪽 +), 앞(+)], 고정소수점.
   * `sqPostSet`이 1이면 대형의 자리를 병종 표(`STATION_*`)가 아니라 이것으로 잡는다 — 고른 칸을 지킨다. 배치를 싣지 않은
   * 판(옛 명부, 도구)은 0이라 예전처럼 병종으로 고른다.
   */
  private readonly sqPostX = new Int32Array(MAX_SQUADS);
  private readonly sqPostY = new Int32Array(MAX_SQUADS);
  private readonly sqPostSet = new Uint8Array(MAX_SQUADS);
  /**
   * 군의 앞(단위 벡터, 고정소수점). 대형의 자리를 이 축으로 돌린다. 장수를 몬 마지막
   * 방향이고, 처음에는 적 쪽(위)이다. 아군은 아래에서 올라간다.
   */
  frontX = 0;
  frontY = -FP_ONE;
  /** 대형 병사의 자리. 틱 시작에 `placeSlots`가 짠다. `slotOk`가 0이면 자리가 없다. */
  private readonly slotX = new Int32Array(MAX_UNITS);
  private readonly slotY = new Int32Array(MAX_UNITS);
  private readonly slotOk = new Uint8Array(MAX_UNITS);
  /**
   * 바위를 돌아가는 남은 틱(1~`SLOT_DETOUR_TICKS`), 또는 그 뒤 쉬는 남은 틱(`SLOT_DETOUR_TICKS` 초과 —
   * `SLOT_DETOUR_REST`). 0이면 돌지 않는다. 틱을 넘는 상태라 체크섬에 든다.
   */
  private readonly slotDetour = new Uint8Array(MAX_UNITS);
  /**
   * 제 자리가 막혀 줄 뒤의 빈 칸에 섰는가: 0이면 제 자리(또는 자리 없음), n이면 n번째 빈 칸(`SLOT_ALT_CELLS`). 지난
   * 틱의 칸을 먼저 보므로 틱을 넘는 상태다 — 체크섬에 든다.
   */
  private readonly slotAlt = new Uint8Array(MAX_UNITS);
  /** 제 자리가 막힌 병사의 명단(`placeSlots` 안에서만 쓴다 — 틱마다 새로 채운다). */
  private readonly slotLate = new Int32Array(MAX_UNITS);
  /** 자리에 닿아 서 있는가(`SLOT_LEAVE`). 틱을 넘는 상태라 체크섬에 든다. */
  private readonly slotHeld = new Uint8Array(MAX_UNITS);
  /** `placeSlots`가 세는 칸. 분대 × 가지(`SLOT_INF` 등). */
  private readonly slotCount = new Int32Array(MAX_SQUADS * 3);
  /** 분대의 보병 수(`placeSlots`가 틱마다 다시 센다). 기병은 줄에서 이 다음 칸부터 선다. */
  private readonly slotInfOf = new Int32Array(MAX_SQUADS);
  /** 분대의 궁수 줄이 서는 깊이(`SLOT_BOW_CLEAR`). 틱마다 다시 낸다. */
  private readonly slotBowBack = new Int32Array(MAX_SQUADS);
  /**
   * 대형 분대를 겨누고 있는 적 가운데 진에 가장 가까운 하나. 틱 시작에 `placeSlots`가 찾는다.
   *
   * 대형은 가까이 온 적만 친다. 그런데 적 궁수는 진 반경(250px) 바로 밖(250~320px)에서
   * 쏜다. 진이 모이고 나니 대형만 건 깊이 4에서 궁수 일곱이 280px 밖에서 서 있는 장수를
   * 쏘아 죽였다 — 전에는 동료가 제각기 흩어져 서 있어 누군가의 반경에 걸렸다. 진을 쏘는
   * 적에게는 나가서 벤다. 반경 안에 적이 있으면 그쪽이 먼저다.
   */
  private readonly sqThreat = new Int32Array(MAX_SQUADS).fill(NO_TARGET);
  /**
   * 분대별 이동경로. `sq * MAX_WAYPOINTS + k`번째 지점이다. `sqPathLen`이 0이면 없다.
   * `sqPathAt`은 지금 향하는 지점이다. 끝점에 닿으면 경로가 지워지고 명령으로 돌아간다.
   */
  readonly sqPathX = new Int32Array(MAX_SQUADS * MAX_WAYPOINTS);
  readonly sqPathY = new Int32Array(MAX_SQUADS * MAX_WAYPOINTS);
  readonly sqPathLen = new Uint8Array(MAX_SQUADS);
  readonly sqPathAt = new Uint8Array(MAX_SQUADS);
  /** 지휘 보정. 지휘관이 살아 있을 때만 적용된다. */
  readonly sqAtkPct = new Int16Array(MAX_SQUADS).fill(100);
  readonly sqDefBonus = new Int16Array(MAX_SQUADS);
  readonly sqGritBonus = new Int16Array(MAX_SQUADS);
  /**
   * 분대의 지휘관이 잘 부리는 병종(`Favor`, troops.ts)과 그 병종에만 붙는 몫. 병종은 -1이면 없다. 판을 세울 때 정해지고
   * 바뀌지 않는다(지휘 보정과 같다).
   */
  readonly sqFavBranch = new Int8Array(MAX_SQUADS).fill(-1);
  readonly sqFavAtk = new Int16Array(MAX_SQUADS);
  readonly sqFavDef = new Int16Array(MAX_SQUADS);
  readonly sqFavGrit = new Int16Array(MAX_SQUADS);
  readonly sqUsed = new Uint8Array(MAX_SQUADS);
  /**
   * 동료 고유 기술(Todo10 4단계, `sim/mate-skills.ts`). 분대마다 하나 — 그 분대를 맡은 동료의 것, 0번 분대는 장수 곁의
   * 동료(부관)의 것. `mateWho`가 그 동료(없으면 NO_TARGET)이고, 쓰러지면 기술이 꺼진다. 발동 조건·몫·지속·대기는 판을
   * 세울 때 정해 바뀌지 않는다(`setMateSkill`). 틱을 넘는 상태는 남은 대기(`mateCd`)와 남은 지속(`mateOn`)이다.
   */
  readonly mateWho = new Int32Array(MAX_SQUADS).fill(NO_TARGET);
  readonly mateTrigger = new Uint8Array(MAX_SQUADS);
  readonly mateAtk = new Int16Array(MAX_SQUADS);
  readonly mateCut = new Int16Array(MAX_SQUADS);
  readonly mateRally = new Uint8Array(MAX_SQUADS);
  readonly mateTicks = new Int16Array(MAX_SQUADS);
  readonly mateCdMax = new Int16Array(MAX_SQUADS);
  readonly mateCd = new Int16Array(MAX_SQUADS);
  readonly mateOn = new Int16Array(MAX_SQUADS);
  /**
   * 둘레에 거는 기술(지장의 계책·충언)의 반경(고정소수점). 0이면 제 분대에 거는 기술이다. 둘레 기술은 동료의 둘레 안에서
   * 아군 병사가 쓰러지면 걸리고(`mateFell`), 그 순간 둘레 안에 있던 아군에게 걸린다(`aid`).
   *
   * 처음에는 지장의 기술도 "제 분대의 병사가 쓰러지면 제 분대에"였다. 자동 편성에서 지장은 뒤에 선 궁수 분대를 맡아 한
   * 판에 0.1번꼴로만 걸렸다(`npm run mate-probe`). 사용자가 정했다: 전군을 보되 범위를 둔다.
   */
  readonly mateRadius = new Int32Array(MAX_SQUADS);
  /** 지난 틱에 그 동료의 둘레 안에서 아군 병사가 쓰러졌는가(`kill`이 세운다). 틱 시작에 읽고 비운다 — 틱을 넘는 상태. */
  readonly mateFell = new Uint8Array(MAX_SQUADS);
  /** 둘레 기술이 걸린 남은 틱과, 그것을 건 분대 자리(`mateWho`의 색인). */
  readonly aid = new Int16Array(MAX_UNITS);
  readonly aidBy = new Uint8Array(MAX_UNITS);
  /** 기술을 쓴 횟수. 그리기 전용(`roarCount`와 같은 까닭). */
  readonly mateCount = new Int32Array(MAX_SQUADS);
  /** 분대의 처음 병사 수(스폰할 때 센다). "절반 이하"의 분모다. */
  readonly sqStart = new Int16Array(MAX_SQUADS);
  /** 표적을 쥔 분대 병사 수. 틱 시작에 센다(`countSquads`) — 파생값이라 해시에 넣지 않는다. */
  private readonly sqFight = new Int16Array(MAX_SQUADS);
  /**
   * 분대에서 가장 느린 병사의 걸음(무너져 달아나는 병사는 빼고, 병사가 없으면 0). 공격 명령의 지휘관과 병사가 적이 멀 때 맞추는
   * 걸음이다(`ATTACK_CHARGE`). 군 전체(`armyPace`)가 아니라 제 분대다 — 기병 분대는 기병 걸음으로 먼저 가도 된다. 틱
   * 시작에 센 파생값이라 해시에 넣지 않는다.
   */
  private readonly sqPace = new Int32Array(MAX_SQUADS);
  /** 살아서 빠져나간 분대 병사 수(후퇴·패주로 가장자리를 넘었다). 틱 시작에 `exited`에서 센 파생값. */
  private readonly sqGone = new Int16Array(MAX_SQUADS);
  /** 명령을 받는 분대 비트 마스크. */
  selectedMask = ALL_SQUADS;
  /** 쓰러진 동료 수. 이들은 잃은 병력에 들어가지 않는다. */
  companionsDowned = 0;

  readonly stats: BattleStats = {
    kills: new Int32Array(2),
    killScore: new Int32Array(2),
    losses: new Int32Array(2),
    damage: new Int32Array(2),
    exited: new Int32Array(2),
  };

  readonly spawns: SpawnOrder[] = [];
  private spawnCursor = 0;

  private readonly hash: SpatialHash;
  /**
   * 공격 명령의 지휘관이 쓰는 길. 목표는 적 병사들의 무게중심이다.
   *
   * 옛 돌격 태세는 적장을 목표로 삼았다. 적장은 제 군대 뒤에 서므로, 그 길은
   * 적진 한복판을 가로지른다. "적을 찾아 섬멸한다"는 적 군대를 향해야 한다.
   */
  private readonly flowAttack: FlowField;
  private readonly flowEnemy: FlowField;
  /** 진영별 후퇴 길. 아군은 맨 아래 줄, 적군은 맨 위 줄이 목표다. 지형이 안 바뀌므로 한 번만 굽는다. */
  private readonly flowExit: FlowField[] = [];
  /**
   * 부대원이 지휘관을 따라가는 길. 분대마다 하나, 목표는 그 분대의 지휘관이다.
   *
   * 예전에는 부대원이 지휘관을 향해 **직선으로만** 걸었다. 그래서 사이에 바위가
   * 있으면 60초를 벽에 붙어 밀기만 했다 — 왼쪽 끝에 틈을 뚫어 둔 시험판에서
   * 여섯 중 0이 따라왔다. 그 뒤 장수를 목표로 하는 흐름장 하나를 뒀는데, 그때는
   * 동료도 장수를 따라다녀서 하나로 충분했다. 이제 동료가 명령대로 따로 움직이므로
   * 장수를 향하는 길은 동료 휘하 병사를 엉뚱한 데로 데려간다.
   */
  private readonly flowCmdr: FlowField[] = [];
  /** `freeSpot`이 쓰는 자리. 틱마다 새로 만들지 않는다. */
  private readonly spot = new Int32Array(2);
  /** 분대별 군기 흐름장. 군기는 꽂히면 움직이지 않으므로 꽂는 순간 한 번만 계산한다. */
  private readonly flowSquad: FlowField[] = [];
  private flowTimer = 0;

  private readonly near = new Int32Array(768);
  /** `crowdAround`가 도는 해시 칸 범위. */
  private readonly crowdCells = new Int32Array(4);
  /** 궁수에게 붙은 적을 찾을 때 도는 해시 칸 범위(`nearestFoe`). */
  private readonly closeCells = new Int32Array(4);
  /**
   * `crowdAround`의 틱 안 셈: 센 틱(`crowdTick`)과 그 값. 틱 시작의 모습이 아니라 **그 틱에 그 적을 처음 잰 때**의 값이다
   * (유닛은 색인 순서로 제자리에서 움직이고 죽는다) — 순서가 고정이라 결정론은 지켜진다. 같은 적을 병사 여럿이 재므로
   * 틱마다 한 번만 센다 — 처음에는
   * 부를 때마다 이웃을 다시 뒤져 1400명 `bench`의 p99가 7ms에서 35ms로 뛰었다(30Hz 예산 33ms). 틱마다 새로 세는 값이라
   * 체크섬에 넣지 않는다.
   */
  private readonly crowdTick = new Int32Array(MAX_UNITS);
  private readonly crowdN = new Int32Array(MAX_UNITS);
  /** 흐름장 목표 칸을 모으는 곳(`armyCells`). */
  private readonly srcCells = new Int32Array(MAX_UNITS);
  private readonly tmp = new Int32Array(2);
  private readonly tmp2 = new Int32Array(2);
  private readonly tmp3 = new Int32Array(2);
  /** `anchorPoint`가 대형이 지키는 점을 담는 자리. */
  private readonly anch = new Int32Array(2);
  /** `separation`이 적의 몸에 막혀 깎인 속도를 담는 자리. */
  private readonly tmpV = new Int32Array(2);
  /** `relaxCrowd`가 모은 떼어 놓기. 틱 안에서만 쓴다. */
  private readonly crowdX = new Int32Array(MAX_UNITS);
  private readonly crowdY = new Int32Array(MAX_UNITS);
  /** 이번 틱에 적의 몸과 맞닿았는가(`separation`이 적는다). `relaxCrowd`가 건너뛴다. 틱 안에서만 쓴다. */
  private readonly bodyPressed = new Uint8Array(MAX_UNITS);
  /** 가려는 쪽으로 나아가지 못한 틱이 이어진 수(`STUCK_TICKS`까지 센다). 틱을 넘는 상태라 해시에 든다. */
  readonly stuckT = new Uint8Array(MAX_UNITS);
  /**
   * 궁수에게 달려드는 기병이 벽을 도는 쪽(`flankPoint`). 0 = 돌지 않음, 1 = 왼쪽, 2 = 오른쪽. 한 번 고르면 우회가 끝날
   * 때까지 지킨다 — 처음에는 틱마다 새로 골라, 옆줄에 선 기병이 그쪽 길이 바위에 막히자 반대쪽을 골라 벽을 가로질러
   * 되돌아갔다. 틱을 넘는 상태라 해시에 든다.
   */
  readonly flankSide = new Uint8Array(MAX_UNITS);
  /** 남은 화살(`ARROWS_BY_TIER`). 궁수가 아니면 0. 틱을 넘는 상태라 해시에 든다. */
  readonly arrows = new Int16Array(MAX_UNITS);
  /** 화살통의 상태(`Quiver`). 다 쏜 순간 근접(1)이나 빠져나감(2)으로 정해지고 바뀌지 않는다. 해시에 든다. */
  readonly quiver = new Uint8Array(MAX_UNITS);
  /**
   * 방패가 더 막을 수 있는 화살 수(`SHIELD_BY_TIER`). 0이면 방패가 없거나 부서졌다 — 벌점(`SHIELD_PENALTY`)도 없다.
   * 틱을 넘는 상태라 해시에 든다. 화면은 이 값이 줄 때 막은 불꽃을, 0이 될 때 부서지는 모습을 그린다.
   */
  readonly shield = new Int16Array(MAX_UNITS);
  /**
   * 이번 틱에 갱신 중인 유닛이 활을 놓고 근접으로 싸우는가, 그리고 그때 닿는 거리(`updateUnit` 머리에서 정한다).
   * 표적 고르기·검사·치기가 같은 값을 본다. 그 유닛의 갱신 안에서만 쓴다.
   */
  private curMelee = false;
  private curRange = 0;
  /**
   * 다쳐서 물러났는가(`WITHDRAW_BY_BRANCH`). 0 = 아직 물러난 적 없음, 1 = 물러나 있음, 2 = 추슬러 돌아왔거나 마지막
   * 저항에 나섰다 — 다시는 물러나지 않는다. 틱을 넘는 상태라 해시에 든다.
   */
  readonly withdrawn = new Uint8Array(MAX_UNITS);
  /** 진영별(색인이 `Team`) 아직 싸울 만한(체력이 물러날 문턱 위이고 무너지지 않은) 병사 수. 틱 시작에 센다(`countFighters`). */
  private readonly fighters = new Int32Array(2);
  /**
   * 진영마다 궁수가 아닌 싸우는 몸(달아나지 않는)의 둘레 상자 — minX, maxX, minY, maxY(fp). 궁수를 노리는 기병이 이 벽을
   * 돌아 들어간다(`flankPoint`). 틱 시작 자리로 한 번 센다(`countFighters`). 틱마다 새로 세는 값이라 체크섬에 넣지 않는다.
   */
  private readonly wallBox = new Int32Array(8);
  // 표적 고르기(`acquireTarget` → `considerTarget`)의 문맥. 한 유닛의 표적 고르기 안에서만 쓴다.
  private acqAnchored = false;
  private acqAx = 0;
  private acqAy = 0;
  private acqNeedsSight = false;
  private acqWide = false;
  private acqCharging = false;
  private acqTouch2 = 0;
  private acqBest = NO_TARGET;
  private acqBestD = 0;
  /** 활을 쏘는 궁수가 고르는 중인가(`BOW_SPREAD`), 곧게 쏠 수 있는 반경의 제곱, 지금 쥔 표적. */
  private acqSpread = false;
  /** 근접 유닛이 고르는 중인가(`MELEE_SPREAD`) — 붐비는 적을 더 먼 것으로 친다. 모는 장수는 뺀다. */
  private acqMelee = false;
  private acqDirect2 = 0;
  private acqCur = NO_TARGET;
  /** 지금까지 고른 것(`acqBest`)이 곡사로만 닿는 적인가. 곧게 쏠 적이 하나라도 있으면 곡사의 적은 진다. */
  private acqBestLob = false;
  /**
   * 그 적을 겨누는 상대편 궁수의 수(`BOW_SPREAD`). 틱 시작에 표적에서 다시 센다(`countFighters`) — 파생값이라 체크섬에
   * 넣지 않는다. 틱 안에서는 궁수가 표적을 바꿀 때마다 고친다: 같은 틱에 고르는 궁수들이 앞 번호가 방금 고른 것을 보아야
   * 한꺼번에 같은 적으로 옮겨 가지 않는다. 색인 오름차순으로 도니 결정론은 그대로다.
   */
  private readonly aimers = new Uint16Array(MAX_UNITS);
  /**
   * 이 궁수가 지금 `aimers`에 얹어 둔 적(없으면 `NO_TARGET`). 표적이 바뀌는 길이 여럿이라(다시 고름, 붙은 적으로 바꿈,
   * 싸움을 피함, 가려져 놓음, 화살이 떨어져 빠져나감, 쓰러짐) 저마다 더하고 빼면 어긋난다 — 처음에는 `acquireTarget`에서만
   * 고쳐, 붙은 적으로 표적을 바꾼 궁수의 옛 표적이 그 틱 내내 한 명 더 겨눠진 것으로 남았다(리뷰가 짚었다). 얹은 것을
   * 적어 두고 한 곳(`syncAim`)에서 맞춘다. 틱 시작에 다시 세므로 파생값이다.
   */
  private readonly aimOf = new Int32Array(MAX_UNITS).fill(NO_TARGET);
  /**
   * 그 적을 쫓는 상대편 근접 유닛의 수(`MELEE_SPREAD`)와, 이 유닛이 지금 거기 얹어 둔 적(`aimers`·`aimOf`의 근접 짝).
   * 궁수는 세지 않는다 — 그쪽은 `aimers`다. 틱 시작에 표적에서 다시 세는 파생값이라 체크섬에 넣지 않는다.
   */
  private readonly chasers = new Uint16Array(MAX_UNITS);
  private readonly chaseOf = new Int32Array(MAX_UNITS).fill(NO_TARGET);
  /** 살아 있는 유닛의 명단(편마다, 색인 오름차순). 틱 시작에 뜬다(`countFighters`). 궁수가 표적을 고를 때 훑는다. */
  private readonly foeList = new Int32Array(MAX_UNITS * 2);
  private readonly foesOf = new Int32Array(2);
  /** 이 유닛이 이번 틱에 곡사로 쏘는가(`updateUnit` → `attack` → `fireArrow`). */
  private curLob = false;
  private readonly wallN = new Int32Array(2);
  /**
   * 틱 시작에 뜬 진영별 명단(`countFighters`), 색인 오름차순: 살아 있는 궁수(`bowList`, 수는 `bowsOf`), 그 진영 궁수를
   * 표적으로 쥔 적 기병(`hunterList`, 수는 `huntedOf`). 진영 t의 명단은 [t * MAX_UNITS, …) 자리다.
   * 본능의 표적은 이 짧은 명단만 훑는다 — 처음에는 반경 330px의 해시 칸을 돌아 1400명 `bench`가 평균 4.6 → 6.6ms로
   * 느려졌다. 틱마다 새로 뜨는 값이라 체크섬에 넣지 않는다.
   */
  private readonly bowsOf = new Int32Array(2);
  private readonly huntedOf = new Int32Array(2);
  private readonly bowList = new Int32Array(MAX_UNITS * 2);
  private readonly hunterList = new Int32Array(MAX_UNITS * 2);
  /**
   * 잔병 소탕 중인가(`MOPUP_MAX`). 틱 시작에 센 값에서 나오는 파생값이라 해시에 넣지 않는다. 적 병사가 조금만 남으면
   * 대형의 아군도 쫓아가 벤다(`senseOf`), 대형의 장수도 멈추지 않고 나아간다(`steerByOrder`).
   */
  private mopUp = false;
  /** 이 판에서 전장에 있던 적 병사 수의 최댓값(증원 포함). 잔병인지 가르는 데 쓴다. 틱을 넘는 상태라 해시에 든다. */
  private enemyPeak = 0;
  /**
   * 적이 제자리에서 기다리는 남은 틱(전장 특성 `Trait.HighGround`). 0이면 기다리지 않는다. 기다리는 동안 적은 닿는
   * 적만 치고(궁수는 사거리 안을 쏜다) 걷지 않는다 — 언덕을 내려오면 언덕의 값이 사라지기 때문이다. 적 하나라도
   * 맞으면(`applyDamage`) 모두 내려온다: 궁수로 끌어내리는 것이 답의 하나다. 틱을 넘는 상태라 해시에 든다.
   */
  enemyHold = 0;
  /**
   * 근접 교전이 시작됐는가(0·1) — 어느 쪽이든 화살이 아닌 판정이 한 번 났다(`applyDamage`). 공격 명령의 궁수가 지휘관
   * 뒤를 지키는 것은 이 전까지다(`BOW_REAR`). 누적 피해(`joined`)로는 화살과 가를 수 없어 따로 둔다. 틱을 넘는 상태라
   * 해시에 든다.
   */
  private meleeJoined = 0;
  /** 마름쇠가 남은 틱(군수품 `Supply.Caltrops`). 0보다 크면 적 기병에 돌격이 붙지 않는다. 틱을 넘는 상태라 해시에 든다. */
  caltrops = 0;
  /**
   * 적장 결전(`Trait.Warlords`): 적장이 여럿이고, 마지막 적장이 쓰러지면 적 병사가 모두 무너진다(`chiefFall`). 판을 만들 때
   * 정하고 바뀌지 않는다 — 명부(특성)에서 나오는 값이라 해시에 넣지 않는다.
   */
  warlords = false;
  /**
   * 결사대(`Trait.Diehard`): 적 병사가 무너지지 않는다(`rout`). 판을 만들 때 정하고 바뀌지 않는다 — 명부(특성)에서
   * 나오는 값이라 해시에 넣지 않는다.
   */
  diehard = false;
  /**
   * 지난 틱 이 유닛을 움직이기 직전의 자리. 한 틱 동안 실제로 옮긴 몫(밀림·떼어 놓기 포함)을 잰다. `prevX/Y`(그리기 보간용)는
   * 틱 시작에 `x`로 덮여 `updateUnit`에서는 늘 `x`와 같다 — 그래서 따로 둔다(일반 리뷰가 둘을 합치라고 했다가 틀렸다).
   */
  private readonly lastX = new Int32Array(MAX_UNITS);
  private readonly lastY = new Int32Array(MAX_UNITS);
  private readonly cell = new Int32Array(2);
  /** 이번 틱에 이 유닛이 낼 수 있는 속도 배율. steerByStance가 정한다. */
  private stepBoost = FP_ONE;
  /**
   * 용사의 호위(`Duty`, troops.ts)와 지킬 사람의 유닛 색인. 판을 세울 때 정해지고 판 도중에 바뀌지 않는다
   * (`setDuty`·`bindDuties`) — 명부와 같은 불변 입력이라 해시에 넣지 않는다. 지킬 사람이 쓰러진 뒤 장수로 옮겨 가는 것도
   * 이 값을 고치지 않고 틱마다 살아 있는지를 보고 정한다(`dutyPoint`) — 해시에 든 상태(`alive`)에서만 나온다.
   */
  readonly duty = new Uint8Array(MAX_UNITS);
  readonly dutyRef = new Int32Array(MAX_UNITS);
  /**
   * 지킬 자리로 가는 길이 막힌 틱 수(`ELITE_DUTY.stallTicks`)와, 임무를 내려놓고 쉬는 남은 틱(`restTicks`). 판 도중에
   * 바뀌는 상태다 — 해시에 넣는다.
   */
  readonly dutyStall = new Uint8Array(MAX_UNITS);
  readonly dutyRest = new Uint16Array(MAX_UNITS);
  /**
   * **적의 부대**(`FOE_SQUADS`, troops.ts). 적 유닛마다 든 부대(`foeSquad`, 0은 총대장의 부대), 부대마다 대장의 색인
   * (`foeCmd`)과 그 대장이 거는 지휘 보정(`foeAtkPct`·`foeGritBonus`). 판을 세울 때 한 번 정한다(`setFoeSquad`) — 명부와
   * 같은 불변 입력이라 해시에 넣지 않는다(`duty`와 같다). 대장이 살아 있는지는 틱마다 `alive`에서 읽는다.
   *
   * 우리 분대의 배열(`squad`·`sq*`)을 같이 쓰지 않는다: 그 배열들은 네 칸이고 `squad[i]`를 편을 가리지 않고 읽는 자리가
   * 있다(`tallyMorale`) — 적에게 4~7을 적으면 칸 밖을 읽는다. 뒤에 서는 적(매복·원군)은 0번 부대다.
   */
  readonly foeSquad = new Uint8Array(MAX_UNITS);
  readonly foeCmd = new Int32Array(MAX_FOE_SQUADS).fill(NO_TARGET);
  readonly foeAtkPct = new Int16Array(MAX_FOE_SQUADS).fill(100);
  readonly foeGritBonus = new Int16Array(MAX_FOE_SQUADS);
  /** 호위를 맡은 용사의 색인. 스폰이 끝난 뒤 지킬 사람을 찾아 묶는다(`bindDuties`). 그 뒤로는 쓰지 않는다. */
  private dutyWho: number[] = [];
  /** 이번 유닛이 지킬 자리와 그 사람(`dutyPoint`가 채운다). */
  private dutyX = 0;
  private dutyY = 0;
  private dutyWard = NO_TARGET;
  /**
   * 잔병을 쫓아 걷는 아군이 이번 틱에 가려던 쪽(단위 벡터, 아니면 0). `updateUnit`이 적고 같은 틱의 `relaxCrowd`가 읽는다
   * — 틱마다 다시 적는 임시값이라 해시에 넣지 않는다.
   */
  private readonly chaseX = new Int32Array(MAX_UNITS);
  private readonly chaseY = new Int32Array(MAX_UNITS);

  constructor(grid: TerrainGrid, seed: number) {
    this.grid = grid;
    this.seed = seed;
    this.rng = new Rng(seed);
    this.hash = new SpatialHash(
      grid.widthPx * FP_ONE,
      grid.heightPx * FP_ONE,
      fp(24),
      MAX_UNITS,
    );
    this.flowAttack = new FlowField(grid);
    this.flowEnemy = new FlowField(grid);
    for (let sq = 0; sq < MAX_SQUADS; sq++) this.flowCmdr.push(new FlowField(grid));
    // 색인이 곧 진영(Team.Player = 0, Team.Enemy = 1)이다.
    const exitPlayer = new FlowField(grid);
    exitPlayer.computeRow(grid.rows - 1);
    const exitEnemy = new FlowField(grid);
    exitEnemy.computeRow(0);
    this.flowExit.push(exitPlayer, exitEnemy);
    for (let i = 0; i < MAX_SQUADS; i++) this.flowSquad.push(new FlowField(grid));
    this.target.fill(NO_TARGET);
  }

  // ------------------------------------------------------------------
  // 생성
  // ------------------------------------------------------------------

  private specIndex(spec: CombatantSpec): number {
    // 레벨까지 열쇠에 넣는다. 병사가 레벨을 갖기 전에는 병종·나라면 스펙이 하나였다.
    // 레벨을 빼면 같은 병종의 레벨 3과 9가 먼저 세운 쪽 스펙으로 뭉친다.
    // 말에서 내린 몸(`dismounted`)은 같은 병종의 말 탄 몸과 다르다 — 한 판에 둘이 함께 서지는 않지만 섞이면 몸이 바뀐다.
    // 단련(`boost`, 깊이 30 너머의 적)도 몸이 다르다.
    // 용사(`elite`)도 몸이 다르다.
    const key = `${spec.troop}:${spec.culture}:${spec.level ?? 1}:${spec.retinue === true ? 1 : 0}:${spec.dismounted === true ? 1 : 0}:${spec.boost ?? 100}:${spec.elite === true ? 1 : 0}`;
    const found = this.specLookup.get(key);
    if (found !== undefined) return found;
    const idx = this.specs.length;
    this.specs.push(spec);
    this.specLookup.set(key, idx);
    return idx;
  }

  /**
   * 바위 밖으로 끄집어낸다.
   *
   * 자리를 난수로 뽑고 검사 없이 세우고 있었다. 깊이 3에서 **유닛의 7.4%가
   * 바위 안에서 시작한다.** `integrate`는 바위 안에서 출발한 유닛을 내보내지
   * 못한다 — 어느 쪽으로 가든 다음 칸이 바위라 막힌다. 그 자리에 굳는다.
   *
   * 전투가 끝나지 않는 진짜 이유가 이것이었다. 처음에는 바위가 고리를 이뤄
   * 안쪽이 갇힌 것이라 읽고 지형 쪽을 고쳤는데, 그건 시드당 두 칸쯤이었다.
   * 남아 있던 적은 갇힌 것이 아니라 **박혀 있던 것**이다. 적장이 박히면 그
   * 판은 3600틱을 돌려도 안 끝난다.
   *
   * 고정 나선으로 가장 가까운 열린 칸을 찾는다. **난수를 굴리지 않는 것이
   * 중요하다** — 여기서 굴리면 뒤따르는 모든 자리가 밀려 리플레이가 어긋난다.
   * 서버도 같은 코드를 돌리므로 답이 하나다.
   *
   * `spawn`이 직접 부르므로 모든 스폰 경로가 이 문을 지난다. 명부로 세우는
   * 길과 예약으로 세우는 길(`queueSpawn`)이 따로 있어서, 한쪽만 고치면 다른
   * 쪽이 조용히 옛 버그를 유지한다.
   */
  freeSpot(x: number, y: number, out: Int32Array): void {
    out[0] = x;
    out[1] = y;
    if (!this.grid.isBlockedFp(x, y)) return;
    // 한 칸씩 넓혀 가며 테두리만 본다. 순서가 고정이라 답이 하나다.
    for (let r = 1; r <= 6; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const nx = x + dx * TILE_FP;
          const ny = y + dy * TILE_FP;
          if (nx < 0 || ny < 0) continue;
          if (nx >= this.grid.widthPx * FP_ONE || ny >= this.grid.heightPx * FP_ONE) continue;
          if (this.grid.isBlockedFp(nx, ny)) continue;
          out[0] = nx;
          out[1] = ny;
          return;
        }
      }
    }
  }

  spawn(
    spec: CombatantSpec,
    team: Team,
    x: number,
    y: number,
    uid: number,
    squad = 0,
  ): number {
    // 어느 문으로 들어오든 바위 안에는 세우지 않는다.
    this.freeSpot(x, y, this.spot);
    x = this.spot[0];
    y = this.spot[1];
    if (this.count >= MAX_UNITS) return NO_TARGET;
    const i = this.count++;
    const s = spec.stats;

    this.x[i] = x;
    this.y[i] = y;
    this.prevX[i] = x;
    this.prevY[i] = y;
    this.knockX[i] = 0;
    this.knockY[i] = 0;
    this.faceX[i] = team === Team.Player ? FP_ONE : -FP_ONE;
    this.faceY[i] = 0;
    // 병사는 전장에서 체력이 더 붙는다(`SOLDIER_HP_PCT`).
    const hp = spec.role === Role.Soldier ? fieldHp(s.hp) : s.hp;
    this.hp[i] = hp;
    this.maxHp[i] = hp;
    this.alive[i] = 1;
    this.cd[i] = 0;
    this.target[i] = NO_TARGET;
    this.fatigue[i] = 0;
    this.fatGain[i] = 0;
    this.morale[i] = s.grit;
    this.routed[i] = 0;
    this.rallied[i] = 0;
    this.guard[i] = 0;
    this.volley[i] = 0;
    this.aid[i] = 0;
    this.aidBy[i] = 0;
    this.charge[i] = 0;
    this.flankSide[i] = 0;
    this.arrows[i] = spec.branch === BranchCode.Bow ? arrowsFor(spec.tier) : 0;
    this.quiver[i] = Quiver.Full;
    this.shield[i] = spec.shield ?? 0;
    this.hitFlash[i] = 0;
    this.killCount[i] = 0;
    this.team[i] = team;
    this.squad[i] = squad;
    this.downed[i] = 0;
    this.exited[i] = 0;
    this.uid[i] = uid;
    if (team === Team.Player) this.sqUsed[squad] = 1;

    this.sAtk[i] = s.atk;
    this.sDef[i] = s.def;
    this.sRange[i] = fp(s.range);
    this.sLead[i] = spec.branch === BranchCode.Bow ? arrowLeadPct(spec.tier, spec.level ?? 1) : 0;
    this.velX[i] = 0;
    this.velY[i] = 0;
    this.sSpeed[i] = speedToFp(s.speed);
    this.sCd[i] = s.cd;
    this.sRadius[i] = fp(s.radius);
    this.sMass[i] = s.mass;
    this.sGrit[i] = s.grit;
    this.sPierce[i] = s.pierce;
    // 지금은 색인을 재사용하지 않아 이미 0이지만, 재사용을 넣는 순간
    // 앞 주인의 포위 수를 물려받는다. 여기서 끊어 둔다.
    this.pressSides[i] = 0;
    this.pressSidesNext[i] = 0;
    this.stuckT[i] = 0;
    this.withdrawn[i] = 0;
    this.lastX[i] = x;
    this.lastY[i] = y;
    this.sPower[i] = spec.power;
    this.branch[i] = spec.branch;
    this.role[i] = spec.role;
    this.specIdx[i] = this.specIndex(spec);

    if (spec.role === Role.Soldier && team === Team.Player && squad >= 0 && squad < MAX_SQUADS) this.sqStart[squad]++;
    if (spec.role === Role.Leader) {
      this.leaderIdx = i;
      this.sqCommander[0] = i;
      this.sqUsed[0] = 1;
      this.sqHomeX[0] = x;
      this.sqHomeY[0] = y;
      // 장수도 지휘관이다. 맨몸이면 captain이 없어 아무것도 걸리지 않지만,
      // 군기나 이름난 칼을 들면 직할대가 그 값을 받는다.
      this.setCaptain(0, spec.captain);
    }
    if (spec.role === Role.Chief) this.chiefIdx = i;
    // 0번 분대는 장수가 이끈다. 장수 곁에 선 동료(`ScenarioConfig.escortSquad`)는 지휘하지 않는다 — 그 지휘 보정은
    // 장수의 보정에 이미 더해졌다.
    if (spec.role === Role.Companion && squad !== 0) {
      this.sqCommander[squad] = i;
      this.sqHomeX[squad] = x;
      this.sqHomeY[squad] = y;
      this.setCaptain(squad, spec.captain);
    }
    return i;
  }

  /**
   * 분대 보정을 건다. 상한도 여기서 건다.
   *
   * 상한은 리플레이 규칙의 일부다. 장비를 입히는 쪽(`createBattle`)에서만 걸면,
   * 그 경로를 타지 않는 스폰이 상한을 통째로 건너뛴다. 값이 들어오는 문 하나에
   * 걸어야 새는 곳이 없다.
   */
  /**
   * 공격자 `att`가 방어자 `dfd`의 어느 면에 서 있는가.
   *
   * 방어자가 바라보는 방향으로 사분면을 나눈다. 앞뒤 성분이 좌우 성분보다 크면
   * 정면 아니면 후면이고, 아니면 좌우다. 나눗셈도 삼각함수도 쓰지 않는다.
   * 정수 곱과 비교만으로 갈리므로 어느 기계에서 돌려도 같은 답이 나온다.
   */
  private sideOf(dfd: number, att: number): number {
    const dx = this.x[att] - this.x[dfd];
    const dy = this.y[att] - this.y[dfd];
    // 바라보는 방향으로의 사영과, 그것을 90도 돌린 축으로의 사영.
    // 방향은 틱 시작 시점의 것을 쓴다. 제자리 방향을 쓰면 같은 쪽에 선 둘이
    // 색인 순서에 따라 다른 면으로 갈린다.
    const fwd = dx * this.faceSnapX[dfd] + dy * this.faceSnapY[dfd];
    const side = dy * this.faceSnapX[dfd] - dx * this.faceSnapY[dfd];
    if (Math.abs(fwd) >= Math.abs(side)) return fwd >= 0 ? SIDE_FRONT : SIDE_REAR;
    return side >= 0 ? SIDE_RIGHT : SIDE_LEFT;
  }

  /** 지금 몇 면이 잡혀 있는가. 0에서 4까지다. */
  sidesPressed(i: number): number {
    const m = this.pressSides[i];
    return (m & 1) + ((m >> 1) & 1) + ((m >> 2) & 1) + ((m >> 3) & 1);
  }

  /**
   * 동료 분대의 대형 자리를 고정한다(출전 위치, `sim/deploy.ts`). 장수에게서 본 [옆, 앞], 고정소수점. 0번(장수 분대)은
   * 장수 자신이 자리라 받지 않는다. 시나리오가 판을 세울 때 한 번 부른다.
   */
  setPost(squad: number, side: number, front: number): void {
    if (squad <= 0 || squad >= MAX_SQUADS) return;
    this.sqPostX[squad] = side;
    this.sqPostY[squad] = front;
    this.sqPostSet[squad] = 1;
  }

  /**
   * 적 유닛을 부대에 넣는다(`createBattle`이 다 세운 뒤 부른다). 적장(`Role.Chief`)이면 그 부대의 대장이 되고 지휘 보정을
   * 건다 — 상한은 우리와 같은 문(`clampCaptain`)을 지난다. 적이 아니거나 칸 밖이면 받지 않는다.
   */
  setFoeSquad(i: number, squad: number): void {
    if (i < 0 || i >= this.count || this.team[i] !== Team.Enemy) return;
    if (!Number.isInteger(squad) || squad < 0 || squad >= MAX_FOE_SQUADS) return;
    this.foeSquad[i] = squad;
    if (this.role[i] !== Role.Chief) return;
    this.foeCmd[squad] = i;
    const c = this.specs[this.specIdx[i]].captain;
    if (!c) return;
    const capped = clampCaptain(c);
    this.foeAtkPct[squad] = capped.atkPct;
    this.foeGritBonus[squad] = capped.gritBonus;
  }

  /** 그 적 부대의 대장이 살아 있는가 — 지휘 보정은 그동안만 붙는다. */
  private foeCaptainActive(squad: number): boolean {
    const c = this.foeCmd[squad];
    return c !== NO_TARGET && this.alive[c] === 1;
  }

  private setCaptain(squad: number, c: CaptainBonus | undefined): void {
    if (!c) return;
    const capped = clampCaptain(c);
    this.sqAtkPct[squad] = capped.atkPct;
    this.sqDefBonus[squad] = capped.defBonus;
    this.sqGritBonus[squad] = capped.gritBonus;
    // 잘 부리는 병종의 몫. 명부(리플레이)가 실어 온 값이 아니라 정의에서 나온 값이지만 문 앞에서 한 번 더 자른다.
    const f = c.favor;
    if (f && Number.isInteger(f.branch) && f.branch >= 0 && f.branch <= 4) {
      this.sqFavBranch[squad] = f.branch;
      this.sqFavAtk[squad] = Math.max(0, Math.min(FAVOR_CAP.atkPct, f.atkPct | 0));
      this.sqFavDef[squad] = Math.max(0, Math.min(FAVOR_CAP.defBonus, f.defBonus | 0));
      this.sqFavGrit[squad] = Math.max(0, Math.min(FAVOR_CAP.gritBonus, f.gritBonus | 0));
    }
  }

  /** 예약 세우기(`processSpawns`)가 바위를 피한 자리를 받는 칸. */
  private readonly spawnSpot = new Int32Array(2);

  queueSpawn(order: SpawnOrder): void {
    this.spawns.push(order);
  }

  // ------------------------------------------------------------------
  // 한 틱
  // ------------------------------------------------------------------

  step(cmds: readonly Command[]): void {
    this.tick++;
    this.fx.fill(0);
    if (this.roarCd > 0) this.roarCd--;
    if (this.enemyHold > 0) this.enemyHold--;
    if (this.caltrops > 0) this.caltrops--;

    // 지난 틱에 센 접촉 수를 이번 틱이 읽는다. 세는 것은 updateUnit이 한다.
    //
    // 반드시 applyCommands보다 먼저다. 장수의 휩쓸기가 그 안에서 돌기 때문에,
    // 뒤로 밀면 장수만 한 틱 더 묵은 값을 보게 된다. 같은 규칙이 공격 경로에
    // 따라 달라지면 안 된다.
    this.pressSides.set(this.pressSidesNext.subarray(0, this.count));
    this.pressSidesNext.fill(0, 0, this.count);
    // 면 분류는 틱 내내 같은 방향을 봐야 한다. 이유는 faceSnapX에 적었다.
    this.faceSnapX.set(this.faceX.subarray(0, this.count));
    this.faceSnapY.set(this.faceY.subarray(0, this.count));

    this.applyCommands(cmds);
    this.processSpawns();
    this.ctrl = this.controlled();

    this.prevX.set(this.x.subarray(0, this.count));
    this.prevY.set(this.y.subarray(0, this.count));

    this.hash.build(this.count, this.x, this.y, this.alive);
    // 틱 시작 자리로 한 번만 센다. 유닛마다 세면 색인 순서에 따라 다른 값을 본다. 흐름장보다 먼저다 — 공격 흐름장이
    // 소탕 여부(`mopUp`)를 본다(`armyCells`). 뒤에 두면 지난 틱의 값을 보았다.
    this.countFighters();
    this.updateFlowFields();
    this.countSquads();
    this.planSweeps();
    this.advancePaths();
    // 자리도 틱 시작 자리로 한 번 짠다. 경로가 막 끝난 분대가 이번 틱부터 진을 지키도록
    // advancePaths 뒤다.
    this.updateFront();
    this.measureMain();
    this.placeSlots();

    this.bodyPressed.fill(0, 0, this.count);
    // 피로가 오르는 창이 바뀌었다(`FATIGUE_WINDOW`) — 이번 1초에 오른 몫을 비운다.
    if (this.tick % FATIGUE_WINDOW === 0) this.fatGain.fill(0, 0, this.count);
    this.tallyMorale();
    this.runMateSkills();
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0) continue;
      if (this.cd[i] > 0) this.cd[i]--;
      // 무작정 달아나는 시간(`ROUT_TICKS`)이 지나면 1에서 멈춰, 부대가 버티면 추스르고 흔들리면 계속 달아난다.
      if (this.routed[i] > 1) this.routed[i]--;
      else if (this.routed[i] === 1 && this.rallyPct(i) >= RALLY_PCT && !this.nearExit(i)) this.routed[i] = 0;
      if (this.rallied[i] > 0) this.rallied[i]--;
      if (this.guard[i] > 0) this.guard[i]--;
      if (this.aid[i] > 0) this.aid[i]--;
      if (this.volley[i] > 0) this.volley[i]--;
      if (this.hitFlash[i] > 0) this.hitFlash[i]--;
      this.updateUnit(i);
    }
    this.relaxCrowd();

    this.updateProjectiles();
    // 이번 틱에 옮긴 몫(`velX/Y`). 다음 틱의 궁수가 읽는다. **틱 끝에** 잰다 — 처음에는 다음 틱 시작에 `x − prevX`로
    // 쟀는데, 그러면 틱을 넘는 규칙 상태가 체크섬 밖의 `prevX`가 되어, 이전 자리만 다른 두 판이 같은 해시를 내고 다음
    // 틱에 화살이 갈렸다(리뷰가 짚었다). 여기서 재면 틱을 넘는 것은 해시에 든 `velX/Y`뿐이다.
    for (let i = 0; i < this.count; i++) {
      this.velX[i] = this.x[i] - this.prevX[i];
      this.velY[i] = this.y[i] - this.prevY[i];
    }
  }

  private applyCommands(cmds: readonly Command[]): void {
    // 장수가 쓰러진 뒤에는 명령하는 사람이 없다. 남은 병력은 후퇴만 한다(`leaderFall`).
    if (this.leaderFellAt !== 0) return;
    for (let i = 0; i < cmds.length; i++) {
      const c = cmds[i];
      switch (c.type) {
        case CmdType.MoveDir:
          this.inputDx = clamp(c.a, -1, 1);
          this.inputDy = clamp(c.b, -1, 1);
          // 몰고 있는 동안 장수의 경로를 지우는 것은 여기가 아니라 `advancePaths`다.
          break;
        case CmdType.SelectSquad:
          this.selectedMask = c.a & ALL_SQUADS;
          if (this.selectedMask === 0) this.selectedMask = ALL_SQUADS;
          break;
        case CmdType.Order: {
          const ord = clamp(c.a, 0, 3) as Order;
          this.forEachSelected((sq) => {
            this.sqOrder[sq] = ord;
            // 플레이어가 그 분대에 명령을 내렸다 — 대형의 기병 분대가 스스로 나서는 일은 이제 없다(`sqAuto`).
            this.sqAuto[sq] = 0;
            // 돌아가기는 명령을 내릴 때마다 처음부터 다시 고른다(`SWEEP`).
            this.sqSweep[sq] = Sweep.None;
            this.sqSweepLane[sq] = 0;
            this.sqSweepUp[sq] = 0;
            this.sqSweepFree[sq] = 0;
            // 대형은 지휘관이 지금 선 자리에 짠다. 지휘관이 없으면 장수 곁이다.
            // 동료 분대는 그 뒤 매 틱 장수 곁 제 자리로 진을 옮긴다(`placeSlots`).
            if (ord === Order.Formation) {
              this.sqStation[sq] = sq === 0 ? 0 : 1;
              let c = this.activeCommander(sq);
              if (c === NO_TARGET) c = this.leaderIdx;
              if (c !== NO_TARGET && this.alive[c] === 1) {
                this.sqHomeX[sq] = this.x[c];
                this.sqHomeY[sq] = this.y[c];
              }
            }
          });
          break;
        }
        case CmdType.Rally:
        case CmdType.Waypoint: {
          // 전장 밖 좌표를 그대로 받으면 분대가 벽에 붙어 영영 도착하지 못하고,
          // 아주 큰 값이 들어오면 거리 제곱이 고정소수점 예산을 넘어 조용히 틀어진다.
          // 입력은 화면에서 오지만 리플레이는 아무 숫자나 실어 올 수 있다.
          //
          // 음수를 버리지 않고 함께 자른다. 버리면 화면 왼쪽 여백을 우클릭했을
          // 때만 명령이 사라져서 좌우가 다르게 동작한다.
          const margin = TILE_PX;
          const rx = clamp(c.a, margin, this.grid.widthPx - margin) * FP_ONE;
          const ry = clamp(c.b, margin, this.grid.heightPx - margin) * FP_ONE;
          const start = c.type === CmdType.Rally;
          this.forEachSelected((sq) => {
            if (start) {
              this.sqPathLen[sq] = 0;
              this.sqPathAt[sq] = 0;
            }
            // 경로 없이 온 지점 추가는 시작으로 본다. 꽉 찼으면 버린다 — 화면이 이미
            // 솎아 보내므로 정상 입력은 여기 걸리지 않고, 리플레이가 무한히 늘릴 수 없다.
            const n = this.sqPathLen[sq];
            if (n >= MAX_WAYPOINTS) return;
            this.sqPathX[sq * MAX_WAYPOINTS + n] = rx;
            this.sqPathY[sq * MAX_WAYPOINTS + n] = ry;
            this.sqPathLen[sq] = n + 1;
            if (n === 0) this.bakePath(sq);
          });
          break;
        }
        case CmdType.Recall:
          this.forEachSelected((sq) => {
            this.sqPathLen[sq] = 0;
            this.sqPathAt[sq] = 0;
            this.bakeCamp(sq);
          });
          break;
        case CmdType.Strike: {
          // 휩쓸기는 조종하는 사람이 휘두른다(장수 또는 고른 분대의 동료).
          const who = this.controlled();
          if (who !== NO_TARGET) this.leaderStrike(who);
          break;
        }
        case CmdType.Roar:
          // 장수 기술(`Roar` 명령 번호는 함성 하나뿐이던 때의 것이다).
          if (this.leaderIdx !== NO_TARGET) this.leaderSkill(this.leaderIdx);
          break;
        default:
          break;
      }
    }
  }

  /** 지금 향하는 경로 지점의 흐름장을 굽는다. 지점이 바뀔 때만 다시 굽는다. */
  private bakePath(sq: number): void {
    const k = sq * MAX_WAYPOINTS + this.sqPathAt[sq];
    const cx = Math.floor(this.sqPathX[k] / TILE_FP);
    const cy = Math.floor(this.sqPathY[k] / TILE_FP);
    if (this.flowSquad[sq].needsRecompute(cx, cy)) this.flowSquad[sq].compute(cx, cy);
  }

  /**
   * 경로가 끝나지 않고 지워졌을 때, 경로 끝에 짠 진(`sqStation` 0)으로 돌아갈 길을 다시 굽는다.
   *
   * 진으로 돌아가는 동료는 막히면 이 분대의 경로 흐름장을 탄다(`steerByOrder`). 진을 짠 뒤
   * 새 경로를 주면 흐름장이 그 경로 지점으로 바뀌는데, 닿기 전에 지우면 흐름장이 지운 경로
   * 쪽을 향한 채 남아 동료가 진이 아니라 취소한 곳으로 갔다(리뷰가 짚었다).
   */
  private bakeCamp(sq: number): void {
    if (this.sqOrder[sq] !== Order.Formation || this.sqStation[sq] !== 0 || sq === 0) return;
    const cx = Math.floor(this.sqHomeX[sq] / TILE_FP);
    const cy = Math.floor(this.sqHomeY[sq] / TILE_FP);
    if (this.flowSquad[sq].needsRecompute(cx, cy)) this.flowSquad[sq].compute(cx, cy);
  }

  /**
   * 이 부대의 경로를 걷는 사람. 살아 있는 지휘관이다(0번 부대는 장수). 없으면 NO_TARGET이고,
   * 그때는 병사들이 경로를 직접 걷는다.
   *
   * 처음에는 경로를 병사들만 걸었고 장수는 따르지 않았다. 플레이한 사람이 "드래그 경로는
   * 장수의 경로다. 장수가 경로를 따라 걷고 휘하 병사가 따라와야 한다"고 했다. 병사
   * 수십 명이 저마다 경로를 걸으면 줄이 흩어지고, 무엇보다 누가 앞장서는지가 보이지
   * 않았다. 지휘관이 걷고 병사가 따라가면 부대가 한 덩어리로 돈다.
   */
  private pathWalker(sq: number): number {
    return this.activeCommander(sq);
  }

  /**
   * 분대를 지금 이끄는 지휘관(동료): 살아 있고 다쳐 물러나 있지 않다. 없으면 `NO_TARGET`. 경로를 걷는 이, 부대원이
   * 따르는 이, 대형을 짜는 자리, 분대 흐름장의 목표가 모두 이것을 본다. 처음에는 부대원이 따르는 쪽(`anchorOf`)만
   * 물러난 동료를 뺐다 — 경로를 준 분대는 물러나 추스르는 동료를 경로 걷는 이로 삼아 경로가 멈췄고(리뷰가 짚었다),
   * 대형은 물러난 동료 자리에 진을 짰다.
   */
  private activeCommander(sq: number): number {
    const c = this.sqCommander[sq];
    return c !== NO_TARGET && this.alive[c] === 1 && this.withdrawn[c] !== 1 ? c : NO_TARGET;
  }

  /**
   * 경로를 따라가는 부대가 지점에 닿았는지 본다. 틱 시작에 부대마다 한 번이다.
   *
   * 지휘관이 걸으면 지휘관의 자리로 잰다. 병사가 직접 걸으면(지휘관이 없으면) 병사의
   * 무게중심으로 잰다(`countSquads` 뒤여야 한다). 둘 다 없으면 경로를 지운다. 끝점에
   * 닿으면 경로가 사라지고 부대는 제 명령으로 돌아간다. 대형이면 그 자리에 진을
   * 짠다 — 출발한 자리로 걸어 돌아가면 경로를 그린 뜻이 없다.
   *
   * **장수를 몰고 있는 동안에는 장수의 경로가 없다.** 입력이 앞선다 — 손을 뗀 뒤 경로로
   * 걸어가 버리면 방금 몬 것을 되돌리는 것이 된다. 처음에는 MoveDir 명령을 받을 때 한
   * 번 지웠다. 그런데 이동 입력은 값이 바뀔 때만 기록되고 같은 틱에서는 경로 명령보다
   * 앞선다. 누르기 시작한 틱에 경로가 함께 들어오면 지운 뒤에 다시 생겼고, 누르는 동안
   * 남아 있다가 손을 떼자 장수가 그 경로로 돌아갔다(리뷰가 찾았다). 명령이 아니라
   * 상태로 지킨다 — 명령을 다 받은 뒤, 매 틱.
   */
  private advancePaths(): void {
    // 모는 사람의 분대 경로를 지운다(장수면 0번, 동료면 그 분대).
    if ((this.inputDx !== 0 || this.inputDy !== 0) && this.ctrl !== NO_TARGET) {
      const sq = this.squad[this.ctrl];
      this.sqPathLen[sq] = 0;
      this.sqPathAt[sq] = 0;
    }
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      const len = this.sqPathLen[sq];
      if (len === 0) continue;
      let ax: number;
      let ay: number;
      const walker = this.pathWalker(sq);
      const n = this.sqN[sq];
      if (walker !== NO_TARGET) {
        ax = this.x[walker];
        ay = this.y[walker];
      } else if (n > 0) {
        ax = Math.floor((this.sqCx[sq] * TILE_FP) / n) + (TILE_FP >> 1);
        ay = Math.floor((this.sqCy[sq] * TILE_FP) / n) + (TILE_FP >> 1);
      } else {
        this.sqPathLen[sq] = 0;
        this.sqPathAt[sq] = 0;
        continue;
      }
      const k = sq * MAX_WAYPOINTS + this.sqPathAt[sq];
      if (distSq(ax, ay, this.sqPathX[k], this.sqPathY[k]) > PATH_ARRIVE * PATH_ARRIVE) continue;
      if (this.sqPathAt[sq] + 1 < len) {
        this.sqPathAt[sq]++;
        this.bakePath(sq);
      } else {
        if (this.sqOrder[sq] === Order.Formation) {
          this.sqHomeX[sq] = this.sqPathX[k];
          this.sqHomeY[sq] = this.sqPathY[k];
          // 경로 끝에 짠 진은 지킨다. 장수 곁으로 돌아가면 경로를 그린 뜻이 없다.
          this.sqStation[sq] = 0;
        }
        this.sqPathLen[sq] = 0;
        this.sqPathAt[sq] = 0;
      }
    }
  }

  /** 선택된 분대에만 명령을 적용한다. 비트 순서가 고정이라 결정론이 유지된다. */
  private forEachSelected(fn: (squad: number) => void): void {
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      if ((this.selectedMask & (1 << sq)) === 0) continue;
      if (this.sqUsed[sq] === 0) continue;
      fn(sq);
    }
  }

  /** 지휘관이 살아 있어야 보정이 붙는다. 쓰러지면 그 자리에서 사라진다. */
  captainActive(squad: number): boolean {
    const c = this.sqCommander[squad];
    return c !== NO_TARGET && this.alive[c] === 1;
  }

  private processSpawns(): void {
    while (
      this.spawnCursor < this.spawns.length &&
      this.spawns[this.spawnCursor].tick <= this.tick
    ) {
      const o = this.spawns[this.spawnCursor++];
      for (let k = 0; k < o.count; k++) {
        const ox = o.x + this.rng.range(-o.spread, o.spread);
        const oy = o.y + this.rng.range(-o.spread, o.spread);
        const sx = clamp(ox, TILE_FP, (this.grid.widthPx - 32) * FP_ONE);
        const sy = clamp(oy, TILE_FP, (this.grid.heightPx - 32) * FP_ONE);
        // 바위를 피해 세운다(`freeSpot`). 처음에는 자리를 그대로 썼다 — 도구(`bench`)만 쓰던 길이라 드러나지 않다가,
        // 매복·원군(전장 특성)이 쓰며 바위 안에 선 적이 영영 나오지 못할 수 있었다(`deploy`와 같은 까닭).
        this.freeSpot(sx, sy, this.spawnSpot);
        this.spawn(o.spec, o.team, this.spawnSpot[0], this.spawnSpot[1], o.uidBase + k, o.squad ?? 0);
      }
    }
  }

  private updateFlowFields(): void {
    if (this.flowTimer > 0) {
      this.flowTimer--;
      return;
    }
    this.flowTimer = FLOW_INTERVAL;

    // 공격의 목표는 적 군대다 — 무게중심 한 점이 아니라 **그 둘레에서 가장 가까운 적 병사**(`FRONT_RADIUS_CELLS`, 흩어졌거나
    // 소탕 중이면 병사 전원 — `armyCells`). 군기는
    // 분대별 흐름장이 따로 맡는다. 주기마다 목표 칸을 다시 모은다 — 목록이 같으면 `computeCells`가 굽지 않고 건너뛴다.
    const nAttack = this.armyCells(Team.Enemy);
    if (nAttack > 0) this.flowAttack.computeCells(this.srcCells, nAttack);

    // 부대원이 따를 길. 목표를 고르는 규칙은 `anchorOf`와 같다 — 지휘관이
    // 쓰러졌으면 장수다. 가까이 붙은 뒤의 자리잡기는 직선이 맡는다.
    // 쓰지 않는 분대는 굽지 않는다. 칸이 그대로면 `needsRecompute`가 건너뛴다.
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      if (this.sqUsed[sq] === 0) continue;
      let a = this.activeCommander(sq);
      if (a === NO_TARGET) a = this.leaderIdx;
      if (a === NO_TARGET || this.alive[a] === 0) continue;
      const ax = Math.floor(this.x[a] / TILE_FP);
      const ay = Math.floor(this.y[a] / TILE_FP);
      if (this.flowCmdr[sq].needsRecompute(ax, ay)) this.flowCmdr[sq].compute(ax, ay);
    }

    // 적군은 장수 한 명이 아니라 아군 병사 쪽으로 흘러온다 — 아군 무게중심 둘레에서 저마다 가장 가까운 병사에게(흩어졌으면
    // 아군 병사 전원 중 가장 가까운 이에게).
    //
    // 장수를 목표로 두면 적 전원이 장수에게 터널을 파고 들어온다. 본대가 돌격으로
    // 빠져나간 순간 장수 혼자 수십 명에게 둘러싸여 몇 초 만에 죽는다. 검산에서
    // 깊이 2 이상이 전부 장수 전사로 끝난 원인이 이것이었다. 군대는 군대와 만나야 한다.
    // 그 뒤 아군 무게중심 한 점을 향하게 했더니 적이 줄지어 내려왔다(`computeCells`).
    const nEnemy = this.armyCells(Team.Player);
    if (nEnemy > 0) this.flowEnemy.computeCells(this.srcCells, nEnemy);
    // 횡대로 오는 적이 풀리는 줄(`LINE_RELEASE`).
    this.marchY = this.armyCenter(Team.Player, this.cell) ? this.cell[1] * TILE_FP + (TILE_FP >> 1) : -1;
  }

  /**
   * 흐름장 목표 칸을 `srcCells`에 모은다: 그 진영 무게중심 칸, 그리고 무게중심에서 `FRONT_RADIUS_CELLS` 안에 선
   * 병사의 칸(색인 오름차순). 병사가 다 쓰러졌으면 `armyCenter`가 우두머리(장수·적장) 칸을 주고 그 한 칸이다.
   *
   * **무게중심 둘레에 병사가 하나도 없으면(흩어졌거나 두 무리로 갈라졌다) 무게중심은 빼고 병사 전원의 칸을 목표로
   * 한다.** 처음에는 늘 무게중심 칸을 넣었다. 잔병 둘이 서로 다른 쪽으로 멀리 달아나면 무게중심은 둘 사이의 빈 땅이고
   * 둘 다 반경 밖이라, 흐름장의 목표는 빈 땅 한 칸뿐이었다. 장수가 거기까지 걸어가 서 버렸다 — 플레이한 사람 눈에
   * "누구를 쫓을지 몰라 머뭇거린다". 병사 칸을 목표로 두면 흐름장이 가장 가까운 한 명에게 이끈다.
   *
   * **잔병 소탕 중(`mopUp`)의 공격 흐름장은 늘 병사 전원이다.** 반경만 보면 셋째 잔병 하나가 무게중심 곁을 지날 때
   * 목표가 {양 끝의 둘}에서 {무게중심, 그 하나}로 뒤집혀, 한쪽을 쫓던 장수가 돌아섰다(리뷰가 짚었다).
   */
  private armyCells(team: Team): number {
    let n = 0;
    const cols = this.grid.cols;
    if (this.armyCenter(team, this.cell)) {
      const mx = this.cell[0];
      const my = this.cell[1];
      const rc = FRONT_RADIUS_CELLS;
      const scattered = team === Team.Enemy && this.mopUp;
      this.srcCells[n++] = my * cols + mx;
      for (let i = 0; i < this.count && !scattered; i++) {
        if (this.alive[i] === 0 || this.team[i] !== team || this.role[i] !== Role.Soldier) continue;
        const cx = Math.floor(this.x[i] / TILE_FP);
        const cy = Math.floor(this.y[i] / TILE_FP);
        if (!this.grid.inBounds(cx, cy)) continue;
        if ((cx - mx) * (cx - mx) + (cy - my) * (cy - my) > rc * rc) continue;
        this.srcCells[n++] = cy * cols + cx;
      }
      if (n === 1) {
        n = 0;
        for (let i = 0; i < this.count; i++) {
          if (this.alive[i] === 0 || this.team[i] !== team || this.role[i] !== Role.Soldier) continue;
          const cx = Math.floor(this.x[i] / TILE_FP);
          const cy = Math.floor(this.y[i] / TILE_FP);
          if (!this.grid.inBounds(cx, cy)) continue;
          this.srcCells[n++] = cy * cols + cx;
        }
        // 병사가 없거나(우두머리만 남았다) 모두 전장 밖이면 무게중심 한 칸으로 돌아간다.
        if (n === 0) this.srcCells[n++] = my * cols + mx;
      }
    }
    return n;
  }

  /**
   * 한 진영 병사들의 무게중심 칸. 적군은 아군의 것을, 공격 명령은 적군의 것을 향한다.
   * 칸 좌표로 더하므로 수백 명을 합쳐도 정수 범위를 넘지 않는다.
   */
  private armyCenter(team: Team, out: Int32Array): boolean {
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0) continue;
      if (this.team[i] !== team) continue;
      if (this.role[i] !== Role.Soldier) continue;
      sx += Math.floor(this.x[i] / TILE_FP);
      sy += Math.floor(this.y[i] / TILE_FP);
      n++;
    }
    if (n === 0) {
      // 병사가 다 쓰러졌으면 그때는 우두머리를 쫓는다.
      const l = team === Team.Player ? this.leaderIdx : this.chiefIdx;
      if (l === NO_TARGET || this.alive[l] === 0) return false;
      out[0] = Math.floor(this.x[l] / TILE_FP);
      out[1] = Math.floor(this.y[l] / TILE_FP);
      return true;
    }
    out[0] = Math.floor(sx / n);
    out[1] = Math.floor(sy / n);
    return true;
  }

  /** 부대원 무게중심을 센다. 병사만, 아군만, 전장 위에 있는 것만. */
  private countSquads(): void {
    this.sqCx.fill(0);
    this.sqCy.fill(0);
    this.sqN.fill(0);
    this.sqFight.fill(0);
    this.sqGone.fill(0);
    this.sqPace.fill(0);
    this.armyPace = 0;
    this.armyEngaged = 0;
    this.foePace = 0;
    this.sqHorse.fill(0);
    this.sqHorseCx.fill(0);
    this.sqHorseCy.fill(0);
    this.sqHorsePace.fill(0);
    for (let i = 0; i < this.count; i++) {
      if (this.team[i] !== Team.Player) {
        // 적 병사의 가장 느린 걸음(`foePace`). 달아나는 병사는 뺀다 — 무너진 병사의 걸음에 적장을 묶을 까닭이 없다.
        // **지친 만큼 느려진 걸음으로 센다**(`wearySpeed`). 처음에는 병종의 걸음(`sSpeed`)만 봤다: 적장은 지치지 않으므로,
        // 한 번 싸워 지친 병사들과 다음 적을 찾아갈 때 다시 앞질렀다(가득 지친 병사보다 8초에 92px — 리뷰가 재현했다).
        // 땅(숲·늪)은 넣지 않는다 — 선 자리마다 다르고 적장도 같은 땅을 밟는다.
        if (this.alive[i] === 1 && this.role[i] === Role.Soldier && this.routed[i] === 0) {
          const pace = this.wearySpeed(i, this.sSpeed[i]);
          if (this.foePace === 0 || pace < this.foePace) this.foePace = pace;
        }
        continue;
      }
      // 살아서 전장을 빠져나간 병사(`exited`)는 잃은 병사가 아니다 — 동료 기술의 "병사를 잃으면"이 세지 않게 따로 센다.
      if (this.exited[i] === 1 && this.role[i] === Role.Soldier) this.sqGone[this.squad[i]]++;
      if (this.alive[i] === 0) continue;
      const role = this.role[i];
      if (role === Role.Leader) continue;
      const sq = this.squad[i];
      // 장수를 따르는 무리만 센다: 내 부대와, 장수 곁에 진을 짜는 대형 분대(`sqStation` 1). 경로를 걷거나
      // 따로 공격하러 보낸 분대가 멀리서 싸운다고 본진이 서면 안 되고, 그 분대에 걸음을 맞출 까닭도 없다.
      // 무너져 달아나는 병사는 빼되, 아래 무게중심에는 예전처럼 넣는다.
      const follows = sq === 0 || (this.sqOrder[sq] === Order.Formation && this.sqStation[sq] === 1 && this.sqPathLen[sq] === 0);
      if (follows && this.routed[i] === 0) {
        if (this.armyPace === 0 || this.sSpeed[i] < this.armyPace) this.armyPace = this.sSpeed[i];
        if (role === Role.Soldier && this.target[i] !== NO_TARGET) this.armyEngaged++;
      }
      if (role !== Role.Soldier) continue;
      // 칸 좌표로 더한다. 고정소수점 그대로 수백 명을 더하면 2^31을 넘는다.
      this.sqCx[sq] += Math.floor(this.x[i] / TILE_FP);
      this.sqCy[sq] += Math.floor(this.y[i] / TILE_FP);
      this.sqN[sq]++;
      if (this.branch[i] === BranchCode.Horse) {
        this.sqHorse[sq]++;
        this.sqHorseCx[sq] += Math.floor(this.x[i] / TILE_FP);
        this.sqHorseCy[sq] += Math.floor(this.y[i] / TILE_FP);
        if (this.routed[i] === 0 && (this.sqHorsePace[sq] === 0 || this.sSpeed[i] < this.sqHorsePace[sq])) this.sqHorsePace[sq] = this.sSpeed[i];
      }
      if (this.routed[i] === 0 && (this.sqPace[sq] === 0 || this.sSpeed[i] < this.sqPace[sq])) this.sqPace[sq] = this.sSpeed[i];
      if (this.target[i] !== NO_TARGET && this.routed[i] === 0) this.sqFight[sq]++;
    }
  }

  /** 동료 고유 기술을 정한다(판을 세울 때, 동료를 세운 뒤). `who`가 쓰러지면 꺼진다. */
  setMateSkill(
    squad: number,
    who: number,
    trigger: number,
    atk: number,
    cut: number,
    rally: boolean,
    ticks: number,
    cd: number,
    radius = 0,
  ): void {
    if (squad < 0 || squad >= MAX_SQUADS || who < 0) return;
    this.mateRadius[squad] = radius;
    this.mateWho[squad] = who;
    this.mateTrigger[squad] = trigger;
    this.mateAtk[squad] = atk;
    this.mateCut[squad] = cut;
    this.mateRally[squad] = rally ? 1 : 0;
    this.mateTicks[squad] = ticks;
    this.mateCdMax[squad] = cd;
  }

  /** 이 분대의 동료 기술이 지금 걸려 있는가(동료가 살아 있고 지속이 남았다). */
  mateActive(squad: number): boolean {
    const who = this.mateWho[squad];
    return this.mateOn[squad] > 0 && who !== NO_TARGET && this.alive[who] === 1;
  }

  /**
   * 동료 고유 기술의 시계와 발동. 틱 시작에 센 값(`countSquads`·`tallyMorale`)으로 분대 색인 순서대로 본다. 조건이 맞고
   * 대기가 끝났으면 쓴다 — 분대 병사의 사기를 채우는 기술(`mateRally`)은 그 자리에서 채우고 달아나던 병사를 돌려세운다.
   *
   * 장수가 쓰러진 뒤(전군 퇴각)에는 **새로 쓰지 않는다. 시계는 계속 돈다** — 처음에는 함수 머리에서 돌아가, 걸려 있던
   * 기술의 남은 지속이 얼어붙어 6초짜리 기술이 30초 퇴각 내내 남았다(리뷰가 짚었다).
   *
   * "병사를 잃으면"은 쓰러진 병사만 센다. 살아서 빠져나간 병사(`sqGone`)는 남은 수에 넣는다 — 처음에는 전장에 선
   * 수(`sqN`)만 보아, 후퇴해 살아 돌아간 병사 하나로도 계책이 걸렸다(리뷰가 짚었다).
   */
  private runMateSkills(): void {
    const fell = this.leaderFellAt > 0;
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      const who = this.mateWho[sq];
      if (who === NO_TARGET) continue;
      if (this.alive[who] === 0) {
        this.mateOn[sq] = 0;
        continue;
      }
      // 지난 틱에 둘레에서 병사가 쓰러졌는가(`Fallen`). 읽고 비운다 — 대기 중에 쓰러진 병사가 대기가 끝난 뒤의 발동으로
      // 밀려 쌓이지 않게.
      const n = this.sqN[sq];
      const kept = n + this.sqGone[sq];
      const lost = this.mateFell[sq] === 1;
      this.mateFell[sq] = 0;
      if (this.mateOn[sq] > 0) this.mateOn[sq]--;
      if (this.mateCd[sq] > 0) {
        this.mateCd[sq]--;
        continue;
      }
      if (fell) continue;
      let fire = false;
      switch (this.mateTrigger[sq]) {
        case MateTrigger.Engage:
          // 병사 셋에 하나가 표적을 쥐었다. 병사가 없으면(단신 출전) 동료가 표적을 쥐었을 때.
          fire = n > 0 ? this.sqFight[sq] * 3 >= n : this.target[who] !== NO_TARGET;
          break;
        case MateTrigger.Half:
          fire = n > 0 && kept * 100 <= this.sqStart[sq] * MATE_LOSS_PCT;
          break;
        case MateTrigger.Fallen:
          fire = lost;
          break;
        case MateTrigger.Hurt:
          fire = this.hp[who] * 100 <= this.maxHp[who] * MATE_HURT_PCT;
          break;
        default:
          break;
      }
      if (!fire) continue;
      this.mateOn[sq] = this.mateTicks[sq];
      this.mateCd[sq] = this.mateCdMax[sq];
      this.mateCount[sq]++;
      // 둘레 기술이면 그 순간 둘레 안의 아군에게, 아니면 제 분대에. 유닛 색인 순으로 한 번 — 각자의 값만 쓴다.
      const r = this.mateRadius[sq];
      const r2 = r * r;
      const rally = this.mateRally[sq] === 1;
      if (r === 0 && !rally) continue;
      for (let j = 0; j < this.count; j++) {
        if (this.alive[j] === 0 || this.team[j] !== Team.Player) continue;
        if (r > 0) {
          if (distSq(this.x[j], this.y[j], this.x[who], this.y[who]) > r2) continue;
          this.aid[j] = this.mateTicks[sq];
          this.aidBy[j] = sq;
        } else if (this.squad[j] !== sq) continue;
        if (rally && this.role[j] === Role.Soldier) {
          this.morale[j] = this.sGrit[j];
          this.routed[j] = 0;
        }
      }
    }
  }

  // 공격 명령의 지휘관이 부대가 나란히 올 때까지 기다리던 판정(squadGathered)이 여기 있었다.
  // 장수(112)가 병사(74)보다 빨라 혼자 먼저 닿아 둘러싸이는 것을 막으려던 것인데, 맞붙기 전 시간의
  // 34%를 서 있어 "멈칫거리며 시원하게 못 나간다"는 말을 들었다. 플레이한 사람이 공격은 최대 속도로,
  // 부대와의 속도 공조는 대형에서만 하기로 정했다. 거쳐 온 길: 무게중심이 곁(110px)에 오면 걷기 →
  // 장수가 늘 110~130px 앞에서 걷고 서기를 되풀이 → 무게중심 한 점의 흐름장 거리 → 바위 위 무게중심에
  // 막혀 한 판 내내 섬 → 부대원 저마다의 거리 평균(궁수는 빼고) → 없앰.

  /**
   * 군의 앞을 정한다. **적 군대 쪽**이다(병사가 다 쓰러졌으면 적장 쪽 — `armyCenter`). 적이 아무도 없으면 장수를 모는 방향, 장수가 경로를
   * 걸으면 다음 지점 쪽이다.
   *
   * 처음에는 모는 방향만 앞으로 삼았다. 그러면 적에게서 물러서려고 장수를 뒤로 몰면 진이
   * 통째로 뒤집혀 궁수가 적 앞에 서고 기병이 반대쪽으로 건너갔다. 진은 움직이는 동안에도
   * 적을 보고 있어야 한다.
   *
   * 여덟 방향으로 끊고, 지금 앞에서 35도 넘게 벗어나야 바꾼다. 적 무게중심은 싸우는 동안
   * 조금씩 흔들리는데 그때마다 앞이 돌면 자리가 매 틱 돌아 진이 흔들린다. 장수의 얼굴
   * 방향(`faceX`)을 쓰지 않는 것도 같은 까닭이다 — 칠 때마다 표적 쪽으로 돈다.
   */
  private updateFront(): void {
    let dx = 0;
    let dy = 0;
    const l = this.leaderIdx;
    if (l === NO_TARGET || this.alive[l] === 0) return;
    if (this.armyCenter(Team.Enemy, this.cell)) {
      dx = this.cell[0] * TILE_FP + (TILE_FP >> 1) - this.x[l];
      dy = this.cell[1] * TILE_FP + (TILE_FP >> 1) - this.y[l];
      // 적 한가운데 들어가 있으면 앞이 없다. 그대로 둔다.
      if (dx * dx + dy * dy < SLOT_SPRINT * SLOT_SPRINT) return;
    } else if (this.ctrl === l && (this.inputDx !== 0 || this.inputDy !== 0)) {
      // 장수를 몰 때만 입력이 진의 앞이다. 동료를 몰면 장수의 진은 제 둘레를 본다.
      dx = this.inputDx * FP_ONE;
      dy = this.inputDy * FP_ONE;
    } else if (this.sqPathLen[0] > 0 && l !== NO_TARGET && this.alive[l] === 1) {
      // 0번 분대의 경로는 색인 0부터다(`sq * MAX_WAYPOINTS + at`).
      const k = this.sqPathAt[0];
      dx = this.sqPathX[k] - this.x[l];
      dy = this.sqPathY[k] - this.y[l];
      // 지점에 거의 닿았으면 방향이 몇 px 차이로 휙휙 돈다. 그때는 두고 넘어간다.
      if (dx * dx + dy * dy < SLOT_SPRINT * SLOT_SPRINT) return;
    }
    if (dx === 0 && dy === 0) return;
    fpNormalize(dx, dy, this.tmp);
    const ux = this.tmp[0];
    const uy = this.tmp[1];
    if (ux === 0 && uy === 0) return;
    if (ux * this.frontX + uy * this.frontY >= FRONT_KEEP_DOT) return;
    // 가장 가까운 여덟 방향. 같으면 앞선 방향(색인이 작은 쪽)이다.
    let best = 0;
    let bestDot = -Infinity;
    for (let k = 0; k < 8; k++) {
      const d = ux * OCTANT_X[k] + uy * OCTANT_Y[k];
      if (d > bestDot) {
        bestDot = d;
        best = k;
      }
    }
    this.frontX = OCTANT_X[best];
    this.frontY = OCTANT_Y[best];
  }

  /**
   * 대형 병사의 자리와, 장수 곁에 서는 동료 분대의 진을 짠다. 틱 시작에 한 번이다.
   *
   * 자리 번호는 색인 오름차순으로 매긴다. 누가 쓰러지면 뒤 번호가 한 칸씩 당겨져 빈자리를
   * 메운다 — 진이 구멍 난 채 서 있지 않는다. 순서가 색인이라 어디서 돌려도 같다.
   */
  private placeSlots(): void {
    this.slotOk.fill(0, 0, this.count);

    // 진을 겨누는 적. 적의 표적은 지난 틱에 고른 것이다(틱 시작 상태). 색인 오름차순에
    // 더 가까울 때만 바꾸므로 같으면 색인이 작은 쪽이다.
    this.sqThreat.fill(NO_TARGET);
    for (let e = 0; e < this.count; e++) {
      if (this.alive[e] === 0 || this.team[e] !== Team.Enemy) continue;
      const t = this.target[e];
      if (t === NO_TARGET || this.alive[t] === 0 || this.team[t] !== Team.Player) continue;
      // 겨누기만 하고 닿지 않는 적은 진을 치는 적이 아니다. 다가오는 중이면 곧 반경에 든다.
      const reach = this.sRange[e] + this.sRadius[t] + REACH_SLACK;
      if (distSq(this.x[e], this.y[e], this.x[t], this.y[t]) > reach * reach) continue;
      const sq = this.squad[t];
      if (this.sqOrder[sq] !== Order.Formation) continue;
      const cur = this.sqThreat[sq];
      const hx = this.sqHomeX[sq];
      const hy = this.sqHomeY[sq];
      if (cur === NO_TARGET || distSq(hx, hy, this.x[e], this.y[e]) < distSq(hx, hy, this.x[cur], this.y[cur])) {
        this.sqThreat[sq] = e;
      }
    }
    const fx = this.frontX;
    const fy = this.frontY;
    // 오른쪽은 앞을 시계 방향으로 90도 돌린 것이다(화면의 y는 아래로 는다).
    const rx = -fy;
    const ry = fx;
    const minX = TILE_FP;
    const minY = TILE_FP;
    const maxX = this.grid.widthPx * FP_ONE - TILE_FP;
    const maxY = this.grid.heightPx * FP_ONE - TILE_FP;

    // 분대마다 가지별 병사 수. 동료 분대의 자리(주력 가지)를 정하는 데 쓴다.
    this.slotCount.fill(0);
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0 || this.team[i] !== Team.Player || this.role[i] !== Role.Soldier) continue;
      this.slotCount[this.squad[i] * 3 + slotKind(this.branch[i])]++;
    }

    const l = this.leaderIdx;
    if (l !== NO_TARGET && this.alive[l] === 1) {
      let nInf = 0;
      let nBow = 0;
      let nHorse = 0;
      // 병사가 있는 분대가 먼저 자리를 고르고, 동료 혼자 선 분대(단신 출전)는 그 뒤 남은 자리를 받는다. 한 바퀴로
      // 돌면 색인이 앞선 단신 동료가 장수 옆 첫 자리를 차지해 몸이 있는 분대가 한 칸 먼 자리로 밀렸다(리뷰가 짚었다).
      for (let pass = 0; pass < 2; pass++)
      for (let sq = 1; sq < MAX_SQUADS; sq++) {
        if (this.sqUsed[sq] === 0 || this.sqOrder[sq] !== Order.Formation) continue;
        if (this.sqStation[sq] === 0 || this.sqPathLen[sq] > 0) continue;
        const inf = this.slotCount[sq * 3 + SLOT_INF];
        const bow = this.slotCount[sq * 3 + SLOT_BOW];
        const horse = this.slotCount[sq * 3 + SLOT_HORSE];
        if ((inf + bow + horse === 0) !== (pass === 1)) continue;
        // 같으면 보병으로 본다. 옆에 서는 것이 가장 덜 위험하다.
        // 출전 위치가 있으면 그 자리(`setPost`). 옆·앞을 낱값으로 읽는다 — 처음에는 매 틱 분대마다 배열을 새로 만들었다
        // (틱 안 할당, 리뷰가 짚었다).
        let side: number;
        let ahead: number;
        if (this.sqPostSet[sq] === 1) {
          side = this.sqPostX[sq];
          ahead = this.sqPostY[sq];
        } else {
          const at =
            horse > inf && horse >= bow
              ? STATION_HORSE[Math.min(nHorse++, STATION_HORSE.length - 1)]
              : bow > inf
                ? STATION_BOW[Math.min(nBow++, STATION_BOW.length - 1)]
                : STATION_INF[Math.min(nInf++, STATION_INF.length - 1)];
          side = at[0];
          ahead = at[1];
        }
        // **장수에게서 곧장 보이는 열린 자리여야 한다.** 바위 위거나 장수와 사이에 바위가 있으면
        // 장수 쪽으로 당겨(3/4, 1/2, 1/4) 다시 보고, 다 막히면 장수 자리다. 처음에는 막히면
        // 옮기지 않았다 — 궁수 분대의 자리가 바위면 동료가 명령한 자리에 영영 섰다(리뷰가
        // 짚었다). 장수에게서 보이는 자리여야 동료가 장수 쪽 길(`flowCmdr[0]`)로 돌아 닿는다.
        let hx = this.x[l];
        let hy = this.y[l];
        for (let k = 4; k >= 1; k--) {
          const cx = clamp(this.x[l] + Math.floor((fpMul(rx, side) + fpMul(fx, ahead)) * k / 4), minX, maxX);
          const cy = clamp(this.y[l] + Math.floor((fpMul(ry, side) + fpMul(fy, ahead)) * k / 4), minY, maxY);
          if (this.grid.isBlockedFp(cx, cy) || this.grid.blockedBetweenFp(this.x[l], this.y[l], cx, cy)) continue;
          hx = cx;
          hy = cy;
          break;
        }
        this.sqHomeX[sq] = hx;
        this.sqHomeY[sq] = hy;
      }
    }

    // 분대마다 보병이 채울 칸 수. 기병이 그 다음 칸부터 선다. 자리를 받지 못하는 보병(바위)이 있어도 칸은 비워 둔다 —
    // 기병이 그 칸으로 당겨 서면 보병이 돌아올 때 겹친다.
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      const inf = this.slotCount[sq * 3 + SLOT_INF];
      const horse = this.slotCount[sq * 3 + SLOT_HORSE];
      // 돌아가는 분대의 진에는 기병만 선다(`rides`) — 보병의 칸을 비워 두지 않는다.
      this.slotInfOf[sq] = this.sweepOn[sq] === 1 ? 0 : inf;
      // 마지막 줄의 깊이: 보병의 마지막 칸과 기병의 마지막 칸(보병 다음부터 센다) 가운데 더 뒤. 궁수 줄이 그 뒤에 선다.
      const infDeep = inf > 0 ? Math.floor(((inf - 1) >> 1) / SLOT_INF_PER_ROW) * SLOT_ROW_STEP : 0;
      const horseDeep = horse > 0 ? Math.floor(((inf + horse - 1) >> 1) / SLOT_INF_PER_ROW) * SLOT_HORSE_STEP : 0;
      this.slotBowBack[sq] = Math.max(SLOT_BOW_BACK, Math.max(infDeep, horseDeep) + SLOT_BOW_CLEAR);
    }
    this.slotCount.fill(0);
    // 제 자리가 막힌 병사의 명단(아래에서 줄 뒤의 빈 칸을 찾는다).
    let late = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0 || this.team[i] !== Team.Player || this.role[i] !== Role.Soldier) continue;
      const sq = this.squad[i];
      // 돌아가는 기병 분대도 자리를 받는다 — 지휘관 둘레의 걷는 진으로 간다(`SWEEP_PACK`).
      const packed = this.rides(i);
      if (this.sqOrder[sq] !== Order.Formation && !packed) continue;
      const a = this.anchorOf(i);
      if (a === NO_TARGET) continue;
      const kind = slotKind(this.branch[i]);
      // **기병은 제 분대의 줄 안에, 보병 다음 자리에 선다**(장수 부대도 동료 분대도). 보병이 채운 칸 수(`slotInfOf`)에서
      // 이어 세므로 기병은 줄의 바깥쪽·뒷줄에 모인다. 분대 밖 고정 자리에 따로 세우면 여섯 출전 칸 가운데 어느 칸의
      // 분대와는 겹친다(위 `SLOT_HORSE_STEP`의 설명).
      const horse = kind === SLOT_HORSE;
      const k = horse ? this.slotInfOf[sq] + this.slotCount[sq * 3 + SLOT_HORSE]++ : this.slotCount[sq * 3 + kind]++;
      let lat: number;
      let dep: number;
      if (kind !== SLOT_BOW) {
        const side = (k & 1) === 0 ? 1 : -1;
        const col = k >> 1;
        const step = horse ? SLOT_HORSE_STEP : SLOT_INF_STEP;
        lat = side * (SLOT_INF_GAP + (col % SLOT_INF_PER_ROW) * step);
        // 기병의 뒷줄은 기병 간격이다 — 보병 줄 간격(16px)이면 앞뒤 기병의 몸(18px)이 겹친다.
        dep = -Math.floor(col / SLOT_INF_PER_ROW) * (horse ? SLOT_HORSE_STEP : SLOT_ROW_STEP);
      } else {
        // 가운데서 바깥으로: 0, +1, -1, +2, -2, +3.
        const c = k % SLOT_BOW_PER_ROW;
        const off = (c + 1) >> 1;
        lat = ((c & 1) === 1 ? off : -off) * SLOT_BOW_STEP;
        dep = -(this.slotBowBack[sq] + Math.floor(k / SLOT_BOW_PER_ROW) * SLOT_ROW_STEP);
      }
      // **자리는 지휘관에게서 곧장 보이는 열린 땅이어야 한다.** 바위 건너 자리를 주면 병사가 지휘관 쪽 길(`flowCmdr`)
      // 로는 거기 닿지 못하고 바위를 밀며 섰다(리뷰가 짚었다). 지휘관에게서 보이는 자리면 막혀도 지휘관 쪽으로 돌아와
      // 곧장 간다(`steerByOrder`). 제 자리가 막힌 병사는 아래에서 줄 뒤의 빈 칸을 찾는다.
      // 돌아가는 분대의 진은 지휘관보다 `SWEEP_PACK.aheadPx`만큼 앞에서 시작한다 — 지휘관이 첫 줄이 아니라 줄 사이에 선다.
      if (packed) dep += SWEEP_AHEAD;
      const sx = this.x[a] + fpMul(rx, lat) + fpMul(fx, dep);
      const sy = this.y[a] + fpMul(ry, lat) + fpMul(fy, dep);
      if (this.grid.isBlockedFp(sx, sy) || this.grid.blockedBetweenFp(this.x[a], this.y[a], sx, sy)) {
        this.slotLate[late++] = i;
        continue;
      }
      this.slotX[i] = sx;
      this.slotY[i] = sy;
      this.slotOk[i] = 1;
      this.slotAlt[i] = 0;
    }

    // **제 자리가 막힌 병사는 줄 뒤의 빈 칸에 선다**(`SLOT_ALT_CELLS`). 처음에는 자리를 주지 않았다: 그 병사는 예전
    // 규칙대로 지휘관 곁 120px 안 아무 데나 서서 줄에서 빠져 보였다(QA가 짚었다 — 진이 전장 가장자리나 바위 곁에 붙으면
    // 분대마다 두셋). 재 보니 장수가 선 동안 병사 틱의 4.6%, 판 넷에 하나꼴이었다(`npm run wander`).
    //
    // 빈 칸은 그 분대 줄의 **마지막 줄 다음 줄부터**, 기병 간격(20px)의 칸을 차례로 본다. 열린 땅이고, 지휘관에게서
    // 보이고, 같은 분대의 다른 자리에서 기병 한 몸(`SLOT_ALT_GAP`) 넘게 떨어진 첫 칸을 준다 — 궁수 줄과 겹치는 칸은
    // 그래서 건너뛴다. 제 자리를 받은 병사를 먼저 다 세운 뒤에 고른다(뒤 번호의 자리와도 견줘야 한다).
    //
    // 두 번 틀렸다. 처음에는 제 자리를 지휘관 쪽으로 당겨 주었다(3/4, 1/2, 1/4): 줄의 칸이 촘촘해 당긴 자리가 남의 칸을
    // 밟았고(옆 60px의 3/4는 45px — 40px 칸에서 5px), 1/4는 지휘관의 몸속이었다. 그 칸의 병사가 밀렸다 돌아오기를
    // 되풀이해(리뷰: 제 자리에 서 있던 기병이 59틱에 56px을 오갔다) 섞인 부대의 헤매는 병사가 71 → 127명이 됐다(QA).
    // 남의 칸을 밟지 않는 당긴 자리만 주게 했더니 거의 주지 못했다(자리 없는 병사 4.59 → 4.14%).
    for (let n = 0; n < late; n++) {
      const i = this.slotLate[n];
      const a = this.anchorOf(i);
      const sq = this.squad[i];
      // 마지막 줄 다음 줄의 첫 칸(한 줄에 여섯).
      const line = this.slotInfOf[sq] + this.slotCount[sq * 3 + SLOT_HORSE];
      const base = Math.ceil(line / (SLOT_INF_PER_ROW * 2)) * (SLOT_INF_PER_ROW * 2);
      // **지난 틱에 고른 칸을 먼저 본다** — 서 있든 걷든. 바위 모서리에서는 칸이 틱마다 열렸다 막혔다 한다. 그때마다
      // 다시 고르면 자리가 오가고 병사가 따라 오간다. 처음에는 걷는 동안은 첫 칸부터 다시 골랐다: 걷는 진에서 빈 칸의
      // 병사가 칸을 바꿔 가며 달려 붙느라 걸음이 고르지 않았다(QA가 쟀다 — 틱 사이 걸음 변화 0.19 → 0.43px). 제 자리가
      // 다시 열리면 언제든 제 자리로 돌아간다(위의 첫 바퀴).
      const kept = this.slotAlt[i];
      let got = 0;
      for (let m = kept > 0 ? -1 : 0; m < SLOT_ALT_CELLS && got === 0; m++) {
        const c = m < 0 ? kept - 1 : m;
        const k = base + c;
        const side = (k & 1) === 0 ? 1 : -1;
        const col = k >> 1;
        const lat = side * (SLOT_INF_GAP + (col % SLOT_INF_PER_ROW) * SLOT_HORSE_STEP);
        // 돌아가는 분대는 빈 칸도 제 자리와 같은 기준점(지휘관 앞 `aheadPx`)에서 센다. 처음에는 빈 칸에만 빠뜨렸다:
        // 뒤 빈 칸이 마지막 줄 다음이 아니라 40px 더 뒤에 생겨, 제 자리가 막힐 때마다 목표가 100px을 건너뛰었다(리뷰).
        const dep = -Math.floor(col / SLOT_INF_PER_ROW) * SLOT_HORSE_STEP + (this.sweepOn[sq] === 1 ? SWEEP_AHEAD : 0);
        const sx = this.x[a] + fpMul(rx, lat) + fpMul(fx, dep);
        const sy = this.y[a] + fpMul(ry, lat) + fpMul(fy, dep);
        if (this.grid.isBlockedFp(sx, sy) || this.grid.blockedBetweenFp(this.x[a], this.y[a], sx, sy)) continue;
        let clear = true;
        for (let j = 0; j < this.count && clear; j++) {
          if (j === i || this.slotOk[j] === 0 || this.squad[j] !== sq || this.team[j] !== Team.Player) continue;
          if (distSq(sx, sy, this.slotX[j], this.slotY[j]) < SLOT_ALT_GAP * SLOT_ALT_GAP) clear = false;
        }
        if (!clear) continue;
        this.slotX[i] = sx;
        this.slotY[i] = sy;
        this.slotOk[i] = 1;
        got = c + 1;
      }
      this.slotAlt[i] = got;
    }
    // 자리를 잃은 병사(명령이 바뀌었다, 쓰러졌다, 자리가 바위다)는 붙든 것과 돌아가던 것도 놓는다.
    // 자리가 있는 병사는 돌아가는 시계를 한 틱 센다(`SLOT_DETOUR_TICKS`): 도는 동안 1까지 내려가면 쉬는 값으로 넘어가고,
    // 쉬는 값이 다 내려가면 0이다. 걷든 싸우든 붙들려 서 있든 센다 — 상한은 벽시계다.
    for (let i = 0; i < this.count; i++) {
      if (this.slotOk[i] === 0) {
        this.slotHeld[i] = 0;
        this.slotDetour[i] = 0;
        this.slotAlt[i] = 0;
        continue;
      }
      const d = this.slotDetour[i];
      if (d === 0) continue;
      if (d > SLOT_DETOUR_TICKS) this.slotDetour[i] = d - 1 === SLOT_DETOUR_TICKS ? 0 : d - 1;
      else this.slotDetour[i] = d === 1 ? SLOT_DETOUR_TICKS + SLOT_DETOUR_REST : d - 1;
    }
  }

  /** 적 군대까지의 흐름장 거리. 앞뒤를 가리는 데만 쓴다. 닿을 수 없으면 `UNREACHED`. */
  private attackDist(x: number, y: number): number {
    const cx = Math.floor(x / TILE_FP);
    const cy = Math.floor(y / TILE_FP);
    if (!this.grid.inBounds(cx, cy)) return UNREACHED;
    return this.flowAttack.dist[cy * this.grid.cols + cx];
  }

  // ------------------------------------------------------------------
  // 유닛 하나
  // ------------------------------------------------------------------

  /**
   * 활을 든 장수·부하장수가 **걷는 중**인가 — 그동안은 쏘지 않는다("서야 쏜다"). 몰고 있거나(`steering`), 분대의 경로를
   * 걷거나, 돌아가는 기병 분대를 이끄는 중(`rides`)이다. 처음에는 모는 것만 막았는데, 경로를 걷는 영웅은 표적이 닿으면
   * 치면서 걷는 규칙(`updateUnit`의 "지나가며 친다")을 타 걸으며 쏘았다(리뷰가 재현했다). 병사는 해당 없다 — 병사 궁수는
   * 전처럼 경로를 걸으며 쏜다.
   */
  private afoot(i: number, steering: boolean): boolean {
    if (this.role[i] === Role.Soldier || this.team[i] !== Team.Player) return false;
    return steering || this.sqPathLen[this.squad[i]] > 0 || this.rides(i);
  }

  /** 마름쇠에 걸린 적 기병인가(`caltrops`). */
  private caltropped(i: number): boolean {
    return this.caltrops > 0 && this.team[i] === Team.Enemy && this.branch[i] === BranchCode.Horse;
  }

  private updateUnit(i: number): void {
    this.stepBoost = FP_ONE;
    // 이번 틱에 쫓아 걷는지는 아래에서 정한다. 일찍 돌아가는 갈래(걷지 않는 유닛)에서 지난 틱의 값이 남지 않게 먼저 지운다.
    this.chaseX[i] = 0;
    this.chaseY[i] = 0;
    // 이 틱 동안 이 유닛이 선 땅. 속도와 돌격이 같은 값을 본다.
    const ground = this.grid.cellAtFp(this.x[i], this.y[i]);
    const br = this.branch[i];
    const isBow = br === BranchCode.Bow;
    const isHorse = br === BranchCode.Horse;
    const isLeader = this.role[i] === Role.Leader;
    // 몰고 있는가 — 조종하는 사람(`ctrl`, 장수 또는 고른 분대의 동료)만. 처음에는 장수만 몰 수 있었다. 플레이한 사람이
    // 청했다(Todo8): "부대 선택 버튼으로 부대 하나를 선택하면 바로 그 부대장으로 포커스 옮겨주고 해당 부대장을 조작하는
    // 상태가 되도록 해줘."
    const steering = i === this.ctrl && (this.inputDx !== 0 || this.inputDy !== 0);
    this.updateWithdraw(i);
    // 궁수가 활을 놓는가(`BOW_MELEE`): 다 쏘고 이기는 판이었거나, 화살이 남았어도 싸우는 적이 붙었다.
    const close = isBow && this.quiver[i] === Quiver.Full && this.routed[i] === 0 ? this.nearestFoe(i, BOW_CLOSE) : NO_TARGET;
    this.curMelee = isBow && (this.quiver[i] === Quiver.Melee || close !== NO_TARGET);
    this.curRange = this.curMelee ? BOW_MELEE_RANGE : this.sRange[i];
    // 호위하는 용사(`Duty`): 지킬 사람 곁이 제 자리고, 그 둘레의 적만 친다. 궁수인 용사는 곁에 서서 닿는 적을 쏜다.
    const onDuty = this.dutyPoint(i);
    const ordered = this.senseOf(i, steering);
    const sense = !onDuty || ordered === Sense.None ? ordered : isBow ? Sense.Reach : Sense.Free;

    if (sense === Sense.None) {
      this.target[i] = NO_TARGET;
    } else if (close !== NO_TARGET) {
      // 붙은 적과 싸운다. 멀리 겨누던 표적은 놓는다.
      this.target[i] = close;
    } else if (onDuty && !isBow) {
      if ((this.tick + i) % RETARGET_INTERVAL === 0 || !this.dutyKeeps(i)) this.acquireDuty(i);
    } else if ((this.tick + i) % RETARGET_INTERVAL === 0 || !this.targetValid(i, sense)) {
      this.acquireTarget(i, sense);
    }
    // 어느 갈래로 표적이 바뀌었든 겨누는 수·쫓는 수를 맞춘다(`aimOf`·`chaseOf`).
    if (isBow) this.syncAim(i);
    else this.syncChase(i);

    let dirX = 0;
    let dirY = 0;
    let wantMove = true;
    // 싸움을 피해 물러나는 중(다친 병사, 칼을 피하는 궁수). 막혀도 왼쪽으로 돌지 않는다 — 가는 쪽이 적이 아니다.
    let evading = false;

    if (this.routed[i] > 0) {
      const e = this.target[i];
      // 무작정 달아나는 동안(`ROUT_TICKS`)만 표적의 반대쪽으로 뛴다. 그 뒤(1에 멈춘, 추스르지 못한 병사)는 늘 제 출구로 —
      // 표적 반대쪽으로만 뛰게 두었더니, 제 출구 반대편 끝에서 무너진 적이 그 끝에 붙어 판이 끝나지 않았다(리뷰가 짚었다).
      if (this.routed[i] > 1 && e !== NO_TARGET && this.alive[e] === 1) {
        fpNormalize(this.x[i] - this.x[e], this.y[i] - this.y[e], this.tmp);
        dirX = this.tmp[0];
        dirY = this.tmp[1];
      } else {
        // 표적이 없으면 자기 진영 가장자리로. 닿으면 전장을 빠져나간다.
        //
        // 예전에는 곧장 아래(적은 위)로 달렸다. 가장자리가 벽이던 시절에는 어디로
        // 달리든 벽에 붙어 서는 것으로 끝났지만, 이제 가장자리가 출구다. 바로
        // 뒤가 바위인 병사가 바위에 붙어 서 있으면 빠져나갈 길이 있어도 못 나간다.
        this.exitDir(i);
        dirX = this.tmp2[0];
        dirY = this.tmp2[1];
      }
      // 무너진 병사는 뒤도 안 보고 달린다.
      this.stepBoost = Math.floor(FP_ONE * 1.25);
      this.charge[i] = 0;
    } else if (this.quiver[i] === Quiver.Leave) {
      // 화살을 다 쏜 지는 판의 궁수: 제 가장자리로 빠져나간다(`ARROWS_BY_TIER`). 닿으면 산 채로 나간다(`tryExit`).
      // 다쳐 물러난 것(아래)보다 앞선다 — 후퇴 명령처럼. 처음에는 뒤에 두어, 다친 궁수가 출구로 가지 않고 제자리에서
      // 추슬렀다(리뷰가 짚었다).
      this.exitDir(i);
      dirX = this.tmp2[0];
      dirY = this.tmp2[1];
      this.charge[i] = 0;
      evading = true;
    } else if (this.withdrawn[i] === 1 && !this.retreating(i)) {
      // 다쳐서 물러났다. 가까운 적에게서 멀어지고, 둘레가 비면 서서 추스른다(`WITHDRAW_BY_BRANCH`).
      // 후퇴 명령이 앞선다 — 물러난 병사가 제자리에서 추스르면 후퇴로 판을 끝낼 수 없었다(리뷰가 짚었다).
      this.charge[i] = 0;
      evading = true;
      if (this.awayFromEnemy(i, WITHDRAW_CLEAR, false)) {
        dirX = this.tmp2[0];
        dirY = this.tmp2[1];
        this.faceX[i] = dirX;
        this.faceY[i] = dirY;
      } else {
        wantMove = false;
        if ((this.tick + i) % REGEN_EVERY === 0 && this.hp[i] < this.maxHp[i]) {
          const per = WITHDRAW_BY_BRANCH[this.branch[i]].regenPctPerSec;
          const add = Math.max(1, Math.floor((this.maxHp[i] * per * REGEN_EVERY) / (100 * TICK_HZ)));
          this.hp[i] = Math.min(this.maxHp[i], this.hp[i] + add);
        }
      }
    } else {
      // 호위하는 용사가 지킬 사람에게서 너무 멀어졌으면 쥔 표적을 놓고 돌아간다(`ELITE_DUTY.leashPx`) — 칼이 닿는 적은 빼고.
      // 궁수인 용사는 서서 쏘므로 끈이 짧다(`sprintPx`): 지킬 사람이 멀어지면 쏘던 것을 놓고 따라붙는다. 활을 놓고 붙은
      // 적과 싸우는 중(`curMelee`)이면 그 싸움을 먼저 끝낸다.
      if (onDuty && this.target[i] !== NO_TARGET && !(isBow && this.curMelee)) {
        const leash = isBow ? DUTY_SPRINT : DUTY_LEASH;
        if (distSq(this.x[i], this.y[i], this.dutyX, this.dutyY) > leash * leash) {
          const t = this.target[i];
          const touch = this.curRange + this.sRadius[t];
          if (isBow || distSq(this.x[i], this.y[i], this.x[t], this.y[t]) > touch * touch) {
            this.target[i] = NO_TARGET;
            if (isBow) this.syncAim(i);
            else this.syncChase(i);
          }
        }
      }
      const e = this.target[i];
      if (e !== NO_TARGET) {
        const reach = this.curRange + this.sRadius[e];
        const d2 = distSq(this.x[i], this.y[i], this.x[e], this.y[e]);
        fpNormalize(this.x[e] - this.x[i], this.y[e] - this.y[i], this.tmp);
        this.faceX[i] = this.tmp[0];
        this.faceY[i] = this.tmp[1];
        if (d2 <= reach * reach) {
          wantMove = false;
          // 표적의 어느 면을 잡고 있는지 기록한다. 포위 판정이 이 면을 쓴다.
          //
          // 처음에는 밀어내기 쪽에서 몸이 겹친 적을 셌다. 그런데 접촉 판정은
          // 반지름 합(도수끼리 10px)이고 공격은 사거리(18px)에서 멈춘다.
          // 걸어와서 때리는 병사는 겹치지 않으므로 값이 늘 0이었고, 뒤 아군에
          // 밀려 앞줄이 적과 겹칠 때만 값이 붙었다.
          //
          // 쿨다운과 무관하게 잡는다. 때리는 순간이 아니라 붙어 있는 동안 내내
          // 그 면을 묶어 두는 것이 포위다.
          //
          // 활은 잡지 않는다. 250px 밖에서 쏘는 것은 막아낼 손을 뺏는 일이
          // 아니다. 무너져 달아나는 병사는 이 가지에 아예 들어오지 않는다.
          if (!isBow || this.curMelee) this.pressSidesNext[e] |= this.sideOf(e, i);
          // **몰고 있는 사람은 서야 쏜다**(활을 든 장수·부하장수). 플레이한 사람과 정했다(2026-10-09): 걸으며 쏘면
          // 닿지 않는 거리에서 끝없이 쏘며 달아날 수 있다 — 병사 궁수의 "물러서며 쏘기"를 없앤 것과 같은 까닭이다.
          // 활을 놓고 치는 것(`curMelee`)은 칼처럼 걸으면서도 한다.
          if (this.cd[i] === 0 && !(isBow && !this.curMelee && this.afoot(i, steering))) {
            // **장수도 스스로 친다.** 예전에는 휩쓸기가 플레이어 입력으로만
            // 나갔다. 목표 플레이어가 손기술이 아니라 지휘를 하는 사람으로
            // 정해졌고, 모바일 조이스틱으로는 치는 타이밍을 맞출 수 없다.
            // 움직이는 중에도 친다. 치는 순간은 표적 쪽을 본다(바로 위에서
            // 돌려 두었다). 입력 방향으로 치면 옆구리에 붙은 적을 못 벤다.
            // 활을 든 장수는 휩쓸지 않는다 — 병사 궁수와 같은 길(`attack`)로 쏘고, 붙으면 활을 놓고 한 명을 친다.
            // 휩쓸기는 사거리 안의 정면 반원을 한꺼번에 치는 것이라 활의 사거리로 휘두르면 화면 절반을 벤다.
            if (isLeader && !isBow) this.leaderStrike(i);
            else this.attack(i, e);
          }
        } else if (this.lobs(i, sense) && d2 > this.lobFrom(i, sense, e) && this.quiver[i] === Quiver.Full) {
          // **곡사**(`BOW_LOB`): 서서 지키는 궁수가 사거리 밖의 적을 비스듬히 위로 쏜다. 걸어 나가지 않는다. 사거리 + 30px
          // 안의 적은 전처럼 걸어 나가 곧게 쏜다(아래 가지) — 닿는 적만 치는 대기는 걷지 않으니 사거리 밖은 다 곡사다.
          wantMove = false;
          // 몰고 있는 사람은 곡사도 서야 쏜다(위와 같다 — 모는 동안은 닿는 적만 보므로 곡사의 가지에 든다).
          if (this.cd[i] === 0 && !this.afoot(i, steering)) {
            this.curLob = true;
            this.attack(i, e);
            this.curLob = false;
          }
        } else if (sense === Sense.Reach && !(this.curMelee && this.role[i] !== Role.Soldier)) {
          // 닿는 적만 치라는 명령이다. 쫓아가지 않는다.
          //
          // 활을 놓은 장수·부하장수는 붙은 적에게 마저 다가선다(아래 가지). 영웅은 몸이 커서(반지름 8) 적의 칼은 닿는데
          // 활을 놓은 손(`BOW_MELEE.rangePx` + 적의 반지름)은 2~3px 모자랐다 — 대기 중인 활 든 장수가 20px 앞의 칼잡이에게
          // 맞기만 하고 한 번도 치지 못했다(시험이 잡았다). 병사 궁수는 몸집이 적과 같아 이 틈이 없다.
          wantMove = false;
        } else {
          dirX = this.tmp[0];
          dirY = this.tmp[1];
          // **궁수에게 달려드는 기병은 벽을 돌아간다**(`flankPoint`). 처음에는 곧장 달려, 적 앞줄 한가운데를 뚫다가 칼에
          // 깎이고 궁수 여럿의 과녁이 되어 닿기 전에 물러났다 — 기병이 궁수를 노리게 해도 우리 궁수는 한 명도 죽지 않았다.
          let gx = this.x[e];
          let gy = this.y[e];
          const hunting = isHorse && this.instinctFor(i, e) === Instinct.Hunt;
          if (!hunting) this.flankSide[i] = 0;
          if (hunting && !this.flankPoint(i, e)) this.flankSide[i] = 0;
          if (hunting && this.flankSide[i] !== 0) {
            gx = this.tmp2[0];
            gy = this.tmp2[1];
            fpNormalize(gx - this.x[i], gy - this.y[i], this.tmp);
            dirX = this.tmp[0];
            dirY = this.tmp[1];
            // 도는 동안은 가는 쪽을 본다. 표적 쪽을 보고 옆으로 달리면 배후 판정(`sideOf`)이 어긋난다.
            this.faceX[i] = dirX;
            this.faceY[i] = dirY;
          }
          // **표적과 사이에 바위가 있으면 곧장 가지 않고 돌아간다.**
          //
          // 표적을 쫓을 때는 늘 곧장 걸었다. 바위 하나를 사이에 두면 양쪽이 서로를
          // 향해 바위를 밀기만 했다. 깊이 3의 한 시드에서 마지막 적 하나와 장수·병사
          // 쉰넷이 바위를 사이에 두고 100초를 서 있었다(전투가 끝나지 않았다). 이럴 때는
          // 제 군대의 흐름장을 탄다 — 아군은 적 군대 쪽, 적군은 아군 본대 쪽이다.
          // 남은 적이 적으면 흐름장의 목표가 그 병사들이라(`armyCells`) 돌아서 닿는다.
          if (this.grid.blockedBetweenFp(this.x[i], this.y[i], gx, gy)) {
            const field = this.team[i] === Team.Player ? this.flowAttack : this.flowEnemy;
            this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
            field.sample(this.cell[0], this.cell[1], this.tmp2);
            if (this.tmp2[0] !== 0 || this.tmp2[1] !== 0) {
              dirX = this.tmp2[0];
              dirY = this.tmp2[1];
            }
          }
          // 숲과 늪에서는 돌격이 붙지 않는다. 나무 사이를, 진창을 뚫고
          // 속도를 붙일 수는 없다. 창이 없는 편성이 기병에 대응할 자리다.
          // 마름쇠를 밟는 적 기병도 속도를 붙이지 못한다(`caltrops`).
          if (roughGround(ground) || this.caltropped(i)) this.charge[i] = 0;
          else if (isHorse && this.charge[i] < 255) this.charge[i]++;
        }
      } else {
        this.charge[i] = 0;
        // 임무 중인 용사는 분대의 명령이 아니라 지킬 자리로 간다.
        if (onDuty) this.steerToPost(i);
        else this.steerByOrder(i, isBow);
        dirX = this.tmp2[0];
        dirY = this.tmp2[1];
      }
    }

    // **경로를 따르는 병사는 지나가며 친다.** 표적이 있어도 경로대로 걷는다.
    //
    // 처음에는 표적이 닿으면 서서 치고, 닿지 않으면(대기와 같은 규칙으로) 서 있게 했다.
    // 그런데 표적을 고르는 반경(사거리 + 16px)이 실제로 닿는 거리(사거리 + 상대 몸
    // 반지름, 5~8px)보다 넓다. 그 틈에 선 적을 잡으면 때리지도 못하고 걷지도 못한 채
    // 멈췄다 — 리뷰가 옆 25px에 선 적 하나로 경로 병사를 60틱 동안 0px 세웠다.
    // 닿으면 치고(쿨다운은 위에서 이미 썼다), 발은 경로대로 옮긴다. 경로를 걷는 장수도
    // 같다. 플레이어가 몰고 있으면 입력이 정한다(아래).
    if (
      !steering &&
      !onDuty &&
      this.team[i] === Team.Player &&
      this.routed[i] === 0 &&
      this.target[i] !== NO_TARGET &&
      (this.sqPathLen[this.squad[i]] > 0 || this.rides(i))
    ) {
      this.steerByOrder(i, isBow);
      dirX = this.tmp2[0];
      dirY = this.tmp2[1];
      wantMove = true;
    }

    if (steering && !isLeader) {
      // 동료를 몬다: 장수를 몰 때와 같다. 누르는 동안은 입력대로, 떼면 제 분대의 명령대로. 진의 자리도 따라온다.
      fpNormalize(this.inputDx * FP_ONE, this.inputDy * FP_ONE, this.tmp);
      dirX = this.tmp[0];
      dirY = this.tmp[1];
      this.faceX[i] = dirX;
      this.faceY[i] = dirY;
      wantMove = true;
      this.sqHomeX[this.squad[i]] = this.x[i];
      this.sqHomeY[this.squad[i]] = this.y[i];
    }

    if (isLeader) {
      // **플레이어 입력이 우선이다.** 누르는 동안은 입력대로 가고, 떼면 다시
      // 명령대로 스스로 싸운다.
      //
      // 옛 설계는 장수를 입력으로만 움직였다. 알아서 걷게 두면 손을 놓은 사이에
      // 적진으로 걸어 들어가 둘러싸여 죽기 때문이었다. 그 위험은 지금도 있다 — 공격 명령의
      // 장수는 이제 부대를 기다리지 않고 돌격한다. 부대와 붙어 다니려면 대형을 건다.
      if (steering) {
        fpNormalize(this.inputDx * FP_ONE, this.inputDy * FP_ONE, this.tmp);
        dirX = this.tmp[0];
        dirY = this.tmp[1];
        this.faceX[i] = dirX;
        this.faceY[i] = dirY;
        wantMove = true;
        // 몰고 가는 동안은 진의 자리가 장수를 따라간다. 손을 뗐을 때 대형이면
        // 뗀 자리에서 진을 짠다. 옛 자리로 걸어 돌아가면 조작을 되돌리는 것이다.
        this.sqHomeX[0] = this.x[i];
        this.sqHomeY[0] = this.y[i];
      } else if (this.sqOrder[0] === Order.Retreat) {
        // 후퇴하는 장수는 닿는 적을 베면서도 계속 물러난다. 베려고 서면
        // 가장자리에 닿기 전에 둘러싸인다. 가장자리 줄에 닿으면 흐름이 0이라 선다.
        this.exitDir(i);
        dirX = this.tmp2[0];
        dirY = this.tmp2[1];
        wantMove = true;
      } else if (this.target[i] === NO_TARGET && (dirX !== 0 || dirY !== 0)) {
        // 스스로 걸을 때는 걷는 쪽을 본다. 아래의 방향 갱신은 장수를 빼므로
        // (밀려서 방향이 돌면 안 되므로) 여기서 가는 방향으로 돌려 둔다.
        this.faceX[i] = dirX;
        this.faceY[i] = dirY;
      }
    }

    // 공격하러 가는 길이 막혀 나아가지 못하면 왼쪽으로 돈다(`FLANK_TURN`, `STUCK_TICKS`). 모는 장수, 달아나는 병사,
    // 경로를 걷는 분대는 빼고, 아군은 공격 명령일 때만 — 대형은 진을 지키는 명령이다.
    //
    // 나아감은 지난 틱에 실제로 옮긴 몫(밀림과 같은 편 떼어 놓기까지)을 이번에 가려는 쪽으로 잰다. 비껴 도는 동안은
    // 앞으로 제 걸음의 1/4(76°의 코사인)쯤밖에 못 가므로 셈이 이어지고, 앞이 비면(`allyAhead`) 그 틱에 앞으로 돌아온다.
    const moved = Math.trunc(((this.x[i] - this.lastX[i]) * dirX + (this.y[i] - this.lastY[i]) * dirY) / FP_ONE);
    this.lastX[i] = this.x[i];
    this.lastY[i] = this.y[i];
    // 지킬 자리로 걷는데 나아가지 못하면(바위·벽) 임무를 잠깐 내려놓는다(`ELITE_DUTY.stallTicks`·`restTicks`).
    if (onDuty && this.target[i] === NO_TARGET && wantMove && (dirX !== 0 || dirY !== 0)) {
      if (moved < this.sSpeed[i] >> 1) {
        if (++this.dutyStall[i] >= ELITE_DUTY.stallTicks) {
          this.dutyStall[i] = 0;
          this.dutyRest[i] = ELITE_DUTY.restTicks;
        }
      } else if (this.dutyStall[i] > 0) this.dutyStall[i]--;
    } else if (this.dutyStall[i] > 0) this.dutyStall[i]--;
    if (wantMove && !steering && !evading && this.routed[i] === 0 && (dirX !== 0 || dirY !== 0) && this.flanks(i)) {
      // 절반. 1/4로 두면 앞줄 뒤에서 비비며 조금씩 밀고 들어가는 병사가 늘 문턱을 넘어, 열 판에 도는 틱이 91뿐이었다
      // (막혀도 돌지 않았다). 절반이면 2600틱 — 앞줄 뒤에 몰린 병사만 돈다.
      if (moved < this.sSpeed[i] >> 1) {
        if (this.stuckT[i] < STUCK_TICKS) this.stuckT[i]++;
      } else {
        this.stuckT[i] = 0;
      }
      if (this.stuckT[i] >= STUCK_TICKS && this.allyAhead(i, dirX, dirY)) {
        // 화면 좌표는 y가 아래다. 가는 쪽 (x, y)의 왼쪽은 (y, -x) — 위(0,-1)로 가면 왼쪽은 서(-1,0)다.
        const lx = dirX + Math.trunc((dirY * FLANK_TURN) / FP_ONE);
        const ly = dirY - Math.trunc((dirX * FLANK_TURN) / FP_ONE);
        fpNormalize(lx, ly, this.tmp3);
        dirX = this.tmp3[0];
        dirY = this.tmp3[1];
      }
    } else if (evading && this.withdrawn[i] === 1 && wantMove) {
      // **몰려서 물러날 수 없으면 돌아서 싸운다.** 물러나는 쪽으로 제 걸음의 절반도 못 가는 틱이 1초(`CORNERED_TICKS`)
      // 이어지면 가장자리·바위·같은 편에 막힌 것이다. 그대로 두면 추스르지도(둘레에 적) 돌아오지도(체력) 못하고 영영
      // 도망만 다닐 수 있었다(리뷰가 짚었다). 다시는 물러나지 않는다(2).
      if (moved < this.sSpeed[i] >> 1) {
        if (this.stuckT[i] < CORNERED_TICKS) this.stuckT[i]++;
      } else {
        this.stuckT[i] = 0;
      }
      if (this.stuckT[i] >= CORNERED_TICKS) {
        this.withdrawn[i] = 2;
        this.stuckT[i] = 0;
      }
    } else {
      // 서 있거나(치는 중) 걷지 않는 틱은 막힌 것이 아니다. 싸우다 다시 걸을 때 오래전의 멈춤으로 돌면 안 된다.
      this.stuckT[i] = 0;
    }

    // 적장은 맞서는 적이 멀면 제 병사 걸음으로 간다(`CHIEF_CHARGE`). 표적을 쫓든 흐름장을 타든 같다 — 표적은 전장 어디서나
    // 잡히므로(`Sense.Free`) 쫓는 걸음에 걸지 않으면 판을 열자마자 달려 나간다.
    if (
      this.chiefMarch &&
      this.role[i] === Role.Chief &&
      wantMove &&
      this.foePace > 0 &&
      this.foePace < this.sSpeed[i] &&
      !this.enemyWithin(i, CHIEF_CHARGE)
    ) {
      this.stepBoost = Math.floor((this.foePace * FP_ONE) / this.sSpeed[i]);
    }

    // **표적을 쥔 공격의 지휘관도 돌격 거리 밖에서는 제 분대의 걸음이다**(`ATTACK_CHARGE`). 처음에는 표적 없이 걷는
    // 갈래(`steerByOrder`)에만 걸었다 — 지휘관이 표적을 잡는 반경(170px)이 돌격 거리(150px)보다 커서, 150~170px의 적을
    // 표적으로 쥔 지휘관은 첫 피해 전에도 제 걸음으로 달렸다(리뷰가 재현했다: 165px 앞의 적에게 3.46px/틱, 병사는 1.98).
    // 돌아가는 무리의 지휘관은 기병의 걸음에 맞춘다(위) — 여기서 걸지 않는다.
    //
    // **병사도 같다**(REPLAY 109). 플레이한 사람이 정했다(2026-10-07): "지금 혹시 장수들만 보조 맞춰서 이동하고 일반
    // 보병 중에 발걸음이 좀 빠른 보병들은 제 속도로 이동해? 이 규칙을 일반 병사들에게도 적용해줘. 우회 기병 부대에는
    // 적용하지말고." 그 전에는 지휘관만 묶여, 걸음이 빠른 병사(도검·기병)가 느린 병사(창·궁)를 두고 먼저 닿았다.
    // 이제 공격 분대의 병사도 첫 피해 전·적이 돌격 거리 밖이면 제 분대의 가장 느린 걸음이다. 돌아가는 기병 분대의
    // 기병과 지휘관(`rides`·`sweepFreed`)은 묶지 않는다. 뒤처져 달려 붙는 병사(걸음을 올려 받은 틱)는 묶지 않는다 —
    // 묶으면 영영 못 따라붙는다.
    const soldier = this.role[i] === Role.Soldier;
    if (wantMove && (soldier ? this.stepBoost <= FP_ONE : this.target[i] !== NO_TARGET) && !this.rides(i)) {
      const held = this.chargeHeld(i);
      if (held > 0) {
        const boost = Math.floor((held * FP_ONE) / this.sSpeed[i]);
        if (boost < this.stepBoost) this.stepBoost = boost;
      }
    }

    // 따로 공격하러 보낸 분대는 첫 칼 전까지 주력과 나란히 간다(`ATTACK_ABREAST`). 앞으로 가는 걸음을 남은 거리에 맞게
    // 줄인다 — 앞쪽으로 가려던 것이 아니면(옆·뒤) 건드리지 않는다.
    // **돌아가는 무리(`rides`)는 묶지 않는다** — 주력이 얼마나 나갔든 곧장 돌아 들어간다. 플레이한 사람이 정했다
    // (2026-10-06): "본대의 전투를 기다리는 기능은 끄자. 그냥 우회해서 바로 공격하는 걸로. 본대가 얼마나 나갔는지에
    // 상관없이." 그 전에는 주력이 적의 벽에 닿을 때 궁수의 높이에 닿도록 지휘관의 위쪽 걸음을 줄였다(옛 `SWEEP.pacePct`):
    // 돌 줄에 서서 몇 초씩 주력을 기다렸다.
    if (wantMove && this.abreast(i) && !this.rides(i)) {
      const sq = this.squad[i];
      const fwd = fpMul(dirX, this.frontX) + fpMul(dirY, this.frontY);
      if (fwd > 0) {
        // 한 틱에 갈 수 있는 앞쪽 걸음은 남은 거리의 `ATTACK_ABREAST_EASE`분의 일이다. 처음에는 남은 거리를 통째로 줬다 —
        // 주력의 맨 앞 병사가 한 걸음 가면 제 걸음으로 따라붙고 서기를 되풀이해, 걷는 틱의 34~38%를 서 있었고 100틱에
        // 7~8번 서고 걸었다(QA). 나눠 주면 주력의 걸음에 맞춰 조금 뒤에서 고르게 걷는다.
        //
        // **분대를 한 자리로 잰다** — 분대가 함께 서고 함께 걷는다: 맨 앞 사람(`sqFront`). 처음에는 사람마다 제
        // 자리로 쟀다: 맨 앞 병사가 묶여 서면 뒤따르던 병사가 계속 걸어와 그 등을 밀었고, 밀린 병사는 선을 넘어 다시
        // 묶였다 — 주력이 서 있는 동안 분대가 30px을 밀려 올라가며 100틱에 서너 번씩 서고 걸었다(QA: 남은 거리를 나눠
        // 걷게 고친 뒤에도 4.1 → 4.0번으로 그대로였다).
        const lead = this.sqFront[sq] - this.mainFront - ATTACK_ABREAST;
        const room = Math.floor(-lead / ATTACK_ABREAST_EASE);
        const want = fpMul(fpMul(this.sSpeed[i], this.stepBoost), fwd);
        if (room < want) {
          const keep = room <= 0 || want <= 0 ? 0 : Math.floor((fwd * room) / want);
          if (keep === 0) {
            // 선다. 처음에는 앞쪽 몫만 지우고 옆걸음은 남겼는데, 주력을 기다리는 동안 병사들이 흐름의
            // 작은 옆 몫을 따라 좌우로 서성였다(주력이 선 100틱 동안 열일곱 가운데 열쯤이 늘 움직였다).
            wantMove = false;
          } else {
            // 가는 쪽은 그대로 두고 걸음만 줄인다.
            this.stepBoost = Math.floor((this.stepBoost * keep) / fwd);
          }
          // 걸음을 묶인 틱은 달린 틱이 아니다 — 돌격의 속도(`charge`)를 쌓지 않는다. 처음에는 표적을 쥔 기병이 선 채로
          // 기다리는 동안에도 쌓여, 한 걸음도 달리지 않고 돌격의 덧피해와 밀치기를 얻었다(리뷰가 재현했다: 41틱 서 있고 41).
          this.charge[i] = 0;
        }
      }
    }

    const pct = movePct(ground, this.branch[i] as BranchCode);
    let speed = Math.floor((fpMul(this.sSpeed[i], this.stepBoost) * pct) / 100);
    // 방진은 느리다(`PHALANX_SPEED_PCT`).
    if (this.guard[i] > 0) speed = Math.floor((speed * PHALANX_SPEED_PCT) / 100);
    // 지친 병사는 느리다(`FATIGUE_SPEED_PCT`).
    speed = this.wearySpeed(i, speed);
    let vx = wantMove ? fpMul(dirX, speed) : 0;
    let vy = wantMove ? fpMul(dirY, speed) : 0;

    // 밀어내기. 겹친 만큼만 민다. 이게 덩어리감을 만든다.
    // 같은 조회에서 적의 몸에 막힌 만큼 가려던 속도도 깎는다.
    // 잔병을 쫓아 걷는 아군(`mopUp`)은 포개진 몸을 떼어 놓을 때 앞뒤로 옮겨지지 않는다 — 가려는 쪽을 적어 둔다(`relaxCrowd`).
    const chase = wantMove && this.mopUp && this.team[i] === Team.Player && this.routed[i] === 0;
    this.chaseX[i] = chase ? dirX : 0;
    this.chaseY[i] = chase ? dirY : 0;
    this.separation(i, vx, vy);
    vx = this.tmpV[0];
    vy = this.tmpV[1];
    vx += fpMul(this.tmp[0], SEPARATION_SPEED);
    vy += fpMul(this.tmp[1], SEPARATION_SPEED);

    const cap = speed + (this.sSpeed[i] >> 1);
    fpClampLen(vx, vy, cap, this.tmp);
    vx = this.tmp[0];
    vy = this.tmp[1];

    if (vx !== 0 || vy !== 0) {
      const l = isqrt(vx * vx + vy * vy);
      // 무너져 달아나는 병사도 달리는 쪽을 본다. 그래야 뒤쫓는 적이 배후를
      // 잡는다. 대열이 한 번 무너지면 학살로 이어지는 이유가 여기서 나온다.
      //
      // 장수만은 제외한다. 제자리에 선 장수가 옆 병사에게 밀리는 것만으로
      // 방향이 돌아가면, 휩쓸기가 플레이어가 보고 있던 쪽이 아닌 데로 나간다.
      // faceX는 이제 배후 판정에도 쓰이므로 더더욱 흔들리면 안 된다.
      if (l > 0 && this.role[i] !== Role.Leader && (this.routed[i] > 0 || this.target[i] === NO_TARGET)) {
        this.faceX[i] = Math.floor((vx * FP_ONE) / l);
        this.faceY[i] = Math.floor((vy * FP_ONE) / l);
      }
    }

    this.integrate(i, vx + this.knockX[i], vy + this.knockY[i], this.sRadius[i]);

    this.knockX[i] = (this.knockX[i] * 3) >> 2;
    this.knockY[i] = (this.knockY[i] * 3) >> 2;

    this.tryExit(i);
  }

  /**
   * 다쳐서 물러날지, 추슬러 돌아올지 정한다(`WITHDRAW_BY_BRANCH`, `RETURN_HP_PCT`). 장수와 적장은 물러나지 않는다 —
   * 장수는 플레이어가 모는 몸이고, 적장이 물러나면 판이 끝나지 않는다.
   */
  private updateWithdraw(i: number): void {
    const role = this.role[i];
    if (role === Role.Leader || role === Role.Chief) return;
    // **싸울 사람이 없으면 물러나지 않는다(마지막 저항).** 한 진영에 싸울 만한 병사가 하나도 없으면 물러난 이들도 돌아와
    // 끝까지 싸운다. 없으면 판이 끝나지 않았다 — 마지막 적 하나가 추슬러 홀로 돌아오다 궁수에게 맞아 문턱 밑으로
    // 떨어지면 또 물러나기를 되풀이해, 대형으로 지키는 판의 셋에 하나가 2분을 넘겼다. 아군도 같다 — 병사가 다 다쳐
    // 물러나 장수 혼자 남는 일이 없다.
    if (this.fighters[this.team[i]] === 0) {
      if (this.withdrawn[i] === 1) this.withdrawn[i] = 2;
      return;
    }
    const hp100 = this.hp[i] * 100;
    if (this.withdrawn[i] === 0) {
      if (this.belowWithdraw(i)) {
        this.withdrawn[i] = 1;
        this.target[i] = NO_TARGET;
      }
    } else if (this.withdrawn[i] === 1 && hp100 >= this.maxHp[i] * RETURN_HP_PCT) {
      // **한 판에 한 번만 물러난다.** 추슬러 돌아온 병사는 다시 다쳐도 끝까지 싸운다. 몇 번이고 물러나게 두었더니
      // 판이 끝나지 않았다 — 적이 물러나 추스르고 돌아와 궁수에게 맞아 또 물러나기를 되풀이하고, 대형으로 서서
      // 활만 주고받는 판(대형의 장수는 적이 가까우면 나아가지 않는다)이 2분을 넘겼다.
      this.withdrawn[i] = 2;
    }
  }

  /**
   * 진영마다 싸울 만한 병사(살아 있고, 무너지지 않았고, 체력이 제 병종의 물러날 문턱 위)를 센다. 동료는 세지 않는다
   * — 병사가 몸이다.
   *
   * 물러났는지(`withdrawn`)로 세면 안 된다. 홀로 남은 다친 병사가 "싸울 사람이 없다 → 돌아온다 → 다음 틱 제가 싸우는
   * 사람으로 세여 → 물러난다"를 틱마다 되풀이해 제자리에서 떨기만 했다. 체력으로 세면 판정이 틱마다 같다.
   */
  private countFighters(): void {
    this.fighters[0] = 0;
    this.fighters[1] = 0;
    this.wallN[0] = 0;
    this.wallN[1] = 0;
    this.bowsOf[0] = 0;
    this.bowsOf[1] = 0;
    this.huntedOf[0] = 0;
    this.huntedOf[1] = 0;
    let enemies = 0;
    this.aimers.fill(0, 0, this.count);
    this.aimOf.fill(NO_TARGET, 0, this.count);
    this.chasers.fill(0, 0, this.count);
    this.chaseOf.fill(NO_TARGET, 0, this.count);
    this.foesOf[0] = 0;
    this.foesOf[1] = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0) continue;
      this.foeList[this.team[i] * MAX_UNITS + this.foesOf[this.team[i]]++] = i;
      if (this.branch[i] === BranchCode.Bow) {
        const t = this.team[i];
        this.bowList[t * MAX_UNITS + this.bowsOf[t]++] = i;
        // 겨누는 궁수를 센다(`aimers`). 활을 놓은 궁수도 센다 — 붙은 적을 치는 중이어도 그 적을 겨누는 한 명이다.
        this.syncAim(i);
      } else {
        // 쫓는 근접 유닛을 센다(`chasers`).
        this.syncChase(i);
      }
      if (this.branch[i] === BranchCode.Horse) {
        const t = this.target[i];
        if (t !== NO_TARGET && this.alive[t] === 1 && this.team[t] !== this.team[i] && this.branch[t] === BranchCode.Bow) {
          const v = this.team[t];
          this.hunterList[v * MAX_UNITS + this.huntedOf[v]++] = i;
        }
      }
      // 벽(`wallBox`): 궁수가 아니고 달아나지 않는 몸. 장수·동료도 벽이다.
      if (this.branch[i] !== BranchCode.Bow && this.routed[i] === 0) {
        const t = this.team[i];
        const b = t * 4;
        const x = this.x[i];
        const y = this.y[i];
        if (this.wallN[t]++ === 0) {
          this.wallBox[b] = x;
          this.wallBox[b + 1] = x;
          this.wallBox[b + 2] = y;
          this.wallBox[b + 3] = y;
        } else {
          if (x < this.wallBox[b]) this.wallBox[b] = x;
          if (x > this.wallBox[b + 1]) this.wallBox[b + 1] = x;
          if (y < this.wallBox[b + 2]) this.wallBox[b + 2] = y;
          if (y > this.wallBox[b + 3]) this.wallBox[b + 3] = y;
        }
      }
      if (this.role[i] !== Role.Soldier) continue;
      if (this.team[i] === Team.Enemy) enemies++;
      if (this.routed[i] > 0) continue;
      if (this.belowWithdraw(i)) continue;
      this.fighters[this.team[i]]++;
    }
    // 적 병사(무너지거나 물러난 이도 센다 — 그들이 바로 잔병이다). 적장은 세지 않는다. 적 병사가 하나도 없으면 적장만
    // 남은 것이고 그때도 쫓는다.
    if (enemies > this.enemyPeak) this.enemyPeak = enemies;
    // 아군 쪽은 전장의 병사 수(물러난 이도 센다)와 견준다. 체력 문턱으로 센 `fighters`는 물러나 추스르면 오르고 맞으면
    // 내려 틱마다 오가, 경계에서 대형 병사가 쫓다 돌아서기를 되풀이했다(리뷰가 짚었다).
    this.mopUp =
      this.enemyPeak > 0 &&
      enemies * 4 <= this.enemyPeak &&
      (enemies <= MOPUP_MAX || enemies * 4 <= this.soldiersOnField(Team.Player));
  }

  /**
   * `r` 안의 가장 가까운 적에게서 멀어지는 방향을 `tmp2`에 담는다. 적이 없으면 false.
   * 무너져 달아나거나 물러난 적은 세지 않는다 — 쫓아오지 않는다. `meleeOnly`면 활도 뺀다(궁수가 궁수를 피하지 않는다).
   * 같은 거리면 색인이 작은 쪽. 조회 순서와 무관하다.
   */
  private awayFromEnemy(i: number, r: number, meleeOnly: boolean): boolean {
    const team = this.team[i];
    // 조회는 `near`(768칸)에서 잘린다. 200px 안에 그보다 많은 유닛이 몰리면 가장 가까운 적을 놓칠 수 있다 — 잘리는
    // 자리도 격자 순서로 정해져 결정론은 지켜진다(`acquireTarget`과 같은 한계).
    const n = this.hash.query(this.x[i], this.y[i], r, this.near);
    let best = NO_TARGET;
    let bestD = r * r;
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (this.alive[j] === 0 || this.team[j] === team) continue;
      if (this.routed[j] > 0 || this.withdrawn[j] === 1) continue;
      if (meleeOnly && this.branch[j] === BranchCode.Bow) continue;
      const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
      if (d2 < bestD || (d2 === bestD && best !== NO_TARGET && j < best)) {
        bestD = d2;
        best = j;
      }
    }
    if (best === NO_TARGET) return false;
    if (this.x[i] === this.x[best] && this.y[i] === this.y[best]) {
      // 포개졌으면 제 진영 쪽으로(아군은 아래, 적은 위).
      this.tmp2[0] = 0;
      this.tmp2[1] = team === Team.Player ? FP_ONE : -FP_ONE;
      return true;
    }
    fpNormalize(this.x[i] - this.x[best], this.y[i] - this.y[best], this.tmp2);
    return true;
  }

  /**
   * `r` 안의 가장 가까운 싸우는 적(궁수가 아니고 무너져 달아나거나 물러나지 않은). 없으면 `NO_TARGET`. 같은 거리면
   * 색인이 작은 쪽 — 조회 순서와 무관하다. 궁수가 활을 놓을지 본다(`BOW_MELEE`).
   */
  private nearestFoe(i: number, r: number): number {
    // 해시 칸을 직접 돈다. 처음에는 `query`로 768칸 그릇에 받아, 떼 한가운데서는 그릇이 다른 후보로 차 실제로 붙은
    // 칼을 놓치고 활을 쏘았다(리뷰가 짚었다).
    const h = this.hash;
    const b = this.closeCells;
    h.cellsAround(this.x[i], this.y[i], r, b);
    let best = NO_TARGET;
    let bestD = r * r + 1;
    for (let cy = b[2]; cy <= b[3]; cy++) {
      for (let cx = b[0]; cx <= b[1]; cx++) {
        const cell = cy * h.cols + cx;
        const end = h.cellEnd(cell);
        for (let k = h.cellStart(cell); k < end; k++) {
          const j = h.itemAt(k);
          if (this.alive[j] === 0 || this.team[j] === this.team[i] || this.routed[j] > 0 || this.withdrawn[j] === 1) continue;
          if (this.branch[j] === BranchCode.Bow) continue;
          const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
          if (d2 < bestD || (d2 === bestD && j < best)) {
            bestD = d2;
            best = j;
          }
        }
      }
    }
    return best;
  }

  /** 후퇴 명령을 받은 아군인가. 물러나기·물러서기보다 앞선다 — 가장자리로 빠져나가야 판을 끝낼 수 있다. */
  private retreating(i: number): boolean {
    return this.team[i] === Team.Player && this.sqOrder[this.squad[i]] === Order.Retreat;
  }

  /**
   * 궁수 `e`에게 달려드는 기병 `i`가 곧장 가면 `e` 편의 벽(`wallBox`)을 지나는가. 지나면 벽 옆으로 도는 한 점을 `tmp2`에
   * 담고 true — 벽 가장자리에서 `FLANK_MARGIN`의 두 배 떨어진 세로줄 위의 점이다(벽 폭 안이면 벽 모서리 높이, 옆줄에
   * 섰으면 궁수 높이). 양옆 중 기병과 궁수를 잇는
   * 가로 거리가 짧은 쪽으로 돈다(같으면 왼쪽). 궁수가 벽 안에 섞여 있으면(뒤에 숨지 않았으면) 돌 것이 없다.
   * 전장 가장자리 밖이나 바위에 막힌 쪽으로는 돌지 않는다 — 양쪽 다 안 되면 곧장 간다.
   *
   * 전장은 아군이 아래, 적이 위로 마주 선다. 그래서 벽은 가로로 넓고, 도는 것은 옆(x)이다. 모두 정수 연산이다 —
   * 선분과 상자의 교차를 나눗셈 없이 상자 모서리가 선의 한쪽에만 있는지로 가린다(fp 좌표의 곱은 2^53 안이다).
   */
  private flankPoint(i: number, e: number): boolean {
    const t = this.team[e];
    if (this.wallN[t] === 0) return false;
    const b = t * 4;
    const wx0 = this.wallBox[b];
    const wx1 = this.wallBox[b + 1];
    const wy0 = this.wallBox[b + 2];
    const wy1 = this.wallBox[b + 3];
    const ax = this.x[i];
    const ay = this.y[i];
    const ex = this.x[e];
    const ey = this.y[e];
    // 안에 있는지는 벽 상자 그대로 본다. 처음에는 `FLANK_MARGIN`만큼 넓힌 상자로 보아, 앞줄 20px 뒤에 선 궁수까지 벽
    // 안에 섞인 것으로 쳐서 기병이 하나도 돌지 않았다.
    if (ex >= wx0 && ex <= wx1 && ey >= wy0 && ey <= wy1) return false;
    // 기병이 이미 벽 안(난전 한가운데)이면 돌아 나가지 않고 곧장 간다.
    if (ax >= wx0 && ax <= wx1 && ay >= wy0 && ay <= wy1) return false;
    // 길이 벽을 스치는지는 조금만 넓혀 본다(`FLANK_GRAZE`). 도는 줄은 벽에서 `FLANK_MARGIN` 두 배 떨어져, 그 줄에서
    // 궁수로 꺾어 드는 길은 이 상자에 걸리지 않는다.
    const x0 = wx0 - FLANK_GRAZE;
    const x1 = wx1 + FLANK_GRAZE;
    const y0 = wy0 - FLANK_GRAZE;
    const y1 = wy1 + FLANK_GRAZE;
    // 선분의 둘레 상자가 벽 상자와 겹치지 않으면 지나지 않는다.
    if (Math.max(ax, ex) < x0 || Math.min(ax, ex) > x1 || Math.max(ay, ey) < y0 || Math.min(ay, ey) > y1) return false;
    // 상자 네 모서리가 모두 선의 한쪽에 있으면 지나지 않는다.
    const dx = ex - ax;
    const dy = ey - ay;
    const s0 = Math.sign(dx * (y0 - ay) - dy * (x0 - ax));
    const s1 = Math.sign(dx * (y0 - ay) - dy * (x1 - ax));
    const s2 = Math.sign(dx * (y1 - ay) - dy * (x0 - ax));
    const s3 = Math.sign(dx * (y1 - ay) - dy * (x1 - ax));
    if ((s0 > 0 && s1 > 0 && s2 > 0 && s3 > 0) || (s0 < 0 && s1 < 0 && s2 < 0 && s3 < 0)) return false;
    const lo = FLANK_MARGIN;
    const hi = this.grid.cols * TILE_FP - FLANK_MARGIN;
    const left = wx0 - 2 * FLANK_MARGIN;
    const right = wx1 + 2 * FLANK_MARGIN;
    // 두 걸음으로 돈다. 아직 벽의 가로 폭 안이면 먼저 벽 앞(또는 뒤) 모서리 바깥으로 옆걸음하고, 옆줄에 서면 그 줄을
    // 따라 궁수 높이까지 간다. 처음에는 곧장 옆줄의 궁수 높이 점을 겨눠, 비스듬한 길이 벽 모서리(가장자리 분대)를 가로질렀다.
    const beside = ax < wx0 - FLANK_MARGIN || ax > wx1 + FLANK_MARGIN;
    const wy = beside ? ey : ay < wy0 ? wy0 - FLANK_MARGIN : wy1 + FLANK_MARGIN;
    // 도는 길이 바위에 막힌 쪽은 버린다. 처음에는 막혀도 그쪽을 골라, 바위를 피하는 흐름장(적 본대 쪽)이 방향을 덮어
    // 결국 벽 한가운데로 달렸다(깊이 18 시드 777의 오른쪽 바위).
    // 이미 도는 중이면 그쪽만 본다(`flankSide`). 막히면 반대쪽으로 벽을 가로지르지 않고 곧장 간다.
    const was = this.flankSide[i];
    // 끝점 칸도 본다 — `blockedBetweenFp`는 끝 칸을 보지 않아, 바위 칸 위의 점을 골라 그리로 달렸다(리뷰가 짚었다).
    const canLeft =
      was !== 2 && left >= lo && !this.grid.isBlockedFp(left, wy) && !this.grid.blockedBetweenFp(ax, ay, left, wy);
    const canRight =
      was !== 1 && right <= hi && !this.grid.isBlockedFp(right, wy) && !this.grid.blockedBetweenFp(ax, ay, right, wy);
    if (!canLeft && !canRight) {
      this.flankSide[i] = 0;
      return false;
    }
    const costLeft = Math.abs(ax - left) + Math.abs(ex - left);
    const costRight = Math.abs(ax - right) + Math.abs(ex - right);
    const goLeft = canLeft && (!canRight || costLeft <= costRight);
    this.flankSide[i] = goLeft ? 1 : 2;
    this.tmp2[0] = goLeft ? left : right;
    this.tmp2[1] = wy;
    return true;
  }

  /** 체력이 제 병종의 물러날 문턱 밑인가. `updateWithdraw`와 `countFighters`가 같은 식을 써야 떨지 않는다. */
  private belowWithdraw(i: number): boolean {
    return this.hp[i] * 100 < this.maxHp[i] * WITHDRAW_BY_BRANCH[this.branch[i]].withdrawPct;
  }

  /**
   * 막히면 왼쪽으로 도는 유닛인가(`FLANK_TURN`). 적은 늘 공격하러 온다. 아군은 공격 명령이고 경로가 없을 때.
   * 장수는 돌지 않는다 — 스스로 걸을 때 보는 쪽(`faceX`)을 걷는 쪽보다 먼저 정해 두므로, 돌면 도는 동안 보는
   * 쪽과 걷는 쪽이 갈려 배후 판정(`sideOf`)이 어긋났다(리뷰가 짚었다). 장수는 플레이어가 모는 몸이기도 하다.
   */
  private flanks(i: number): boolean {
    if (this.role[i] === Role.Leader) return false;
    if (this.team[i] !== Team.Player) return true;
    const sq = this.squad[i];
    return this.sqOrder[sq] === Order.Attack && this.sqPathLen[sq] === 0;
  }

  /** 가는 쪽(`dx`, `dy`, 단위 벡터) 바로 앞을 아군이 막고 있나. 결과만 보므로 이웃 순서와 무관하다. */
  private allyAhead(i: number, dx: number, dy: number): boolean {
    const ri = this.sRadius[i];
    const team = this.team[i];
    const n = this.hash.query(this.x[i], this.y[i], ri + CROWD_BODY_MAX + FLANK_GAP, this.near);
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (j === i || this.alive[j] === 0 || this.team[j] !== team) continue;
      // 같이 걸어가는 앞사람은 막지 않는다. 싸우는(표적을 잡은) 앞사람만 길을 막는다. 달아나는 이는 비킨다.
      if (this.target[j] === NO_TARGET || this.routed[j] > 0) continue;
      const rx = this.x[j] - this.x[i];
      const ry = this.y[j] - this.y[i];
      const reach = ri + this.sRadius[j] + FLANK_GAP;
      const d2 = rx * rx + ry * ry;
      if (d2 >= reach * reach || d2 === 0) continue;
      const d = isqrt(d2);
      if (rx * dx + ry * dy > d * FLANK_COS) return true;
    }
    return false;
  }

  /** 표적이 없을 때의 행동. 결과는 tmp2에 담는다. */
  private steerByOrder(i: number, isBow: boolean): void {
    this.tmp2[0] = 0;
    this.tmp2[1] = 0;

    if (this.team[i] !== Team.Player) {
      // 언덕에서 기다리는 적은 서 있다(`enemyHold`).
      if (this.enemyHold > 0) return;
      this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
      // 멀리 선 적 병사는 횡대로 온다(`LINE_RELEASE`): 곧장, 가장 느린 병사의 걸음으로. 적장은 제 규칙이 있다(`CHIEF_CHARGE`).
      if (
        this.lineMarch &&
        this.role[i] === Role.Soldier &&
        this.marchY >= 0 &&
        this.y[i] < this.marchY - LINE_RELEASE
      ) {
        // 곧장 아래로 — 이 칸과 바로 아래 칸의 흐름(아군 가장자리 줄이 목표)이 둘 다 아래를 볼 때만. 아니면 바위 뒤라
        // 흐름을 타고 돌아 나온다.
        const exit = this.flowExit[Team.Player];
        exit.sample(this.cell[0], this.cell[1], this.tmp2);
        exit.sample(this.cell[0], this.cell[1] + 1, this.tmp3);
        if (this.tmp2[1] > 0 && this.tmp3[1] > 0) {
          this.tmp2[0] = 0;
          this.tmp2[1] = FP_ONE;
        }
        if (this.tmp2[0] !== 0 || this.tmp2[1] !== 0) {
          // 제 걸음은 **지친 만큼 깎은 값**으로 견준다(`foePace`도 그렇게 센 값이다). 처음에는 병종의 걸음(`sSpeed`)에 견줘
          // 배율을 냈다 — 이동이 그 뒤에 피로를 한 번 더 깎으므로(`wearySpeed`) 지친 병사는 두 번 깎여 뒤처졌다(리뷰가
          // 재현했다: 가득 지친 창병이 120틱에 새 창병보다 35px 덜 왔다). 싸운 적과 새 원군이 함께 다시 걸을 때 벌어진다.
          const mine = this.wearySpeed(i, this.sSpeed[i]);
          if (this.foePace > 0 && this.foePace < mine) {
            this.stepBoost = Math.floor((this.foePace * FP_ONE) / mine);
          }
          return;
        }
      }
      // 가까우면 아군 본대 쪽으로 흘러온다.
      this.flowEnemy.sample(this.cell[0], this.cell[1], this.tmp2);
      return;
    }

    const sq = this.squad[i];
    const order = this.sqOrder[sq];
    const isLeader = this.role[i] === Role.Leader;

    // 후퇴는 군기보다 앞선다. 깃발을 꽂아 둔 채 물러나라고 했으면 물러나는 것이 나중 명령이다.
    if (order === Order.Retreat) {
      this.exitDir(i);
      return;
    }

    // 이동경로가 있으면 명령보다 우선한다. 경로는 지휘관이 걷고(`pathWalker`) 병사는
    // 지휘관을 따른다. 지휘관이 없는 부대만 병사가 경로를 직접 걷는다.
    // 돌아가는 기병 분대(`SWEEP`)도 같다: 지휘관이 걷고 병사는 지휘관을 따른다.
    const sweeping = this.rides(i);
    const walker = this.sqPathLen[sq] > 0 ? this.pathWalker(sq) : sweeping ? this.activeCommander(sq) : NO_TARGET;
    const followsWalker = walker !== NO_TARGET && walker !== i;
    if (this.sqPathLen[sq] > 0 && !followsWalker && (walker === i || !isLeader)) {
      const k = sq * MAX_WAYPOINTS + this.sqPathAt[sq];
      const px = this.sqPathX[k];
      const py = this.sqPathY[k];
      const d2 = distSq(this.x[i], this.y[i], px, py);
      if (d2 <= BANNER_ARRIVE * BANNER_ARRIVE) return; // 닿았다. 부대가 넘어갈 때까지 선다.
      this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
      this.flowSquad[sq].sample(this.cell[0], this.cell[1], this.tmp2);
      if (this.tmp2[0] === 0 && this.tmp2[1] === 0) {
        // 흐름장이 닿지 않는 칸이면 직선으로라도 간다.
        fpNormalize(px - this.x[i], py - this.y[i], this.tmp2);
      }
      return;
    }

    // 지휘관. 공격이면 적 군대를 향해 간다 — 멀면 제 분대와 함께, 가까우면 제 걸음으로 돌격(`ATTACK_CHARGE`). 대형이면
    // 진의 자리로 돌아간다(나가 싸운 뒤다). 대기면 선다.
    if (isLeader || this.role[i] === Role.Companion) {
      if (order === Order.Attack) {
        this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
        this.flowAttack.sample(this.cell[0], this.cell[1], this.tmp2);
        // **공격의 지휘관은 서지 않는다.** 예전에는 부대가 나란히 올 때까지 섰다 — 맞붙기 전 시간의 34%를 서 있었고
        // 한 판에 서너 번 섰다 걸어, 플레이한 사람 눈에 "멈칫거리며 시원하게 못 나간다"였다. 병사 걸음에 맞춰 늦춰
        // 걷게도 해 봤는데(멈춤 0%), 플레이한 사람이 "속도 공조는 대형일 때만, 공격은 최대 속도로"를 정했다.
        //
        // **멀면 함께, 가까우면 돌격**(2026-10-02에 다시 정했다, `ATTACK_CHARGE`). 전장이 길어지자 처음부터 제 걸음으로
        // 가는 말 탄 장수가 병사보다 245px 앞서 닿아 혼자 쓰러졌다. 맞서는 적이 그 거리 밖이면 제 분대의 가장 느린
        // 병사 걸음으로 가고, 안에 들면 제 걸음이다 — 서지 않으므로 멈칫거리지 않고, 마지막은 여전히 돌격이다.
        // 몰고 있으면 입력이 정한다(대형과 같다 — 여기서 늦추면 플레이어가 모는 장수까지 병사 걸음이 된다).
        // 한 번 피가 난 뒤로는(`joined`) 늘 제 걸음이다 — 무너져 달아나는 적을 쫓을 때 병사 걸음으로 느려지면 안 된다.
        const driven = i === this.ctrl && (this.inputDx !== 0 || this.inputDy !== 0);
        //
        // **돌격 거리를 450 → 150px로 줄였다**(REPLAY 108, `ATTACK_CHARGE`). 플레이한 사람이 정했다(2026-10-07): "첫 데미지
        // 발생 전(근거리, 원거리 피해 둘다)에는 공격모드에서도 장수들만 혼자 튀어나가지 않게 해줘 … 첫 데미지 발생 전 +
        // 적이 … 보다 멀리 있는 경우로 하자 … 150 px로 하자. 맥궁 명사수 사거리의 절반 정도면 좋을 것 같아." 450px에서는
        // 마지막 구간에서 장수가 병사를 떼어 놓고 먼저 닿았다.
        const held = this.chargeHeld(i);
        if (held > 0) this.stepBoost = Math.floor((held * FP_ONE) / this.sSpeed[i]);
        // 돌아가는 기병 분대의 지휘관은 적 군대가 아니라 돌 곳으로 간다(`SWEEP`). 바위가 가로막으면 위의 흐름을 그대로 탄다.
        // 돌 곳이 바위 속이어도 흐름을 탄다 — 닿을 수 없는 점을 겨누면 그 앞의 바위를 밀며 선다.
        if (
          sweeping &&
          !this.grid.isBlockedFp(this.sweepX[sq], this.sweepY[sq]) &&
          !this.grid.blockedBetweenFp(this.x[i], this.y[i], this.sweepX[sq], this.sweepY[sq])
        ) {
          fpNormalize(this.sweepX[sq] - this.x[i], this.sweepY[sq] - this.y[i], this.tmp);
          if (this.tmp[0] !== 0 || this.tmp[1] !== 0) {
            this.tmp2[0] = this.tmp[0];
            this.tmp2[1] = this.tmp[1];
          }
        }
        // 도는 동안 지휘관은 기병과 뭉쳐 간다(`SWEEP_PACK`): 기병의 가장 느린 걸음을 넘지 않고, 기병의 무게중심이 제 뒤
        // 16px(`lagPx`)보다 처져 있으면 더 늦춰서. 칼이 닿으면 돌기가 끝난다. 몰고 있으면 입력이 정한다.
        // 걸음도 무게중심도 **기병만으로** 잰다 — 딸린 보병은 돌지 않는다(`rides`).
        const ride = this.sqHorsePace[sq];
        if (sweeping && !driven && ride > 0 && this.sqHorse[sq] > 0) {
          const cx = Math.floor(this.sqHorseCx[sq] / this.sqHorse[sq]) * TILE_FP + (TILE_FP >> 1);
          const cy = Math.floor(this.sqHorseCy[sq] / this.sqHorse[sq]) * TILE_FP + (TILE_FP >> 1);
          const ahead = fpMul(cx - this.x[i], this.sweepDirX[sq]) + fpMul(cy - this.y[i], this.sweepDirY[sq]);
          const want = ahead >= SWEEP_LAG ? ride : Math.floor((ride * SWEEP_PACK.waitPct) / 100);
          // 위에서 분대의 가장 느린 걸음(딸린 보병)으로 줄여 놓은 것은 푼다.
          this.stepBoost = FP_ONE;
          const boost = Math.floor((want * FP_ONE) / this.sSpeed[i]);
          if (boost < this.stepBoost) this.stepBoost = boost;
        }
      } else if (order === Order.Formation) {
        // **대형의 장수는 손을 놓으면 진을 끌고 나아간다**(자동전투). 아군이 아무도 싸우지 않을 때만,
        // 가장 느린 병사의 걸음으로 적 군대 쪽 흐름장을 탄다. 진의 자리(`sqHome`)가 장수를 따라가므로
        // 동료 분대의 진과 병사의 자리도 함께 움직이고, 적이 진 가까이 오면 저마다 나가 벤다(`Sense.Anchor`).
        // 누가 싸우기 시작하면 선다 — 싸우는 병사를 두고 가면 진 반경 밖으로 밀려 표적을 놓는다. 적이
        // 가까이(`ADVANCE_HALT`) 보여도 선다: 진을 적 떼 한가운데로 끌고 들어가지 않고 맞으러 오게 둔다.
        // 처음에는 싸움만 보고 섰는데, 기병을 예비로 뒤에 묶어 둔 판(tactic '대형 · 기병 망치')에서 장수가
        // 앞장서 적 떼로 걸어 들어가 장수 전사가 1/15에서 9/15가 됐다.
        // 공격과 다른 점: 공격은 제 걸음으로 돌격하고 부대원도 제 둘레의 적을 다 쫓는다.
        // 몰고 있으면 입력이 정한다 — 여기서 걸음을 늦추면 플레이어가 모는 장수까지 병사 걸음이 됐다(리뷰가 짚었다).
        const steering = i === this.ctrl && (this.inputDx !== 0 || this.inputDy !== 0);
        // 잔병만 남았으면 싸우는 중이어도, 적이 가까워도 나아간다 — 서서 맞을 적이 없다(`MOPUP_MAX`).
        // 언덕에서 기다리는 적(`enemyHold`)이 가까워도 나아간다 — 맞으러 오지 않는다. 처음에는 여기서도 서서, 양쪽이
        // 서로 쳐다보며 기다림이 끝날 때까지(30초) 아무 일도 없었다. 대형으로 두면 언덕 밑으로 올라가 불리하게
        // 싸운다 — 대기로 버티거나 돌아 오르는 것은 플레이어가 고른다.
        const halt = this.enemyHold === 0 && this.enemyWithin(i, ADVANCE_HALT);
        if (isLeader && !steering && (this.mopUp || (this.armyEngaged === 0 && !halt))) {
          this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
          this.flowAttack.sample(this.cell[0], this.cell[1], this.tmp2);
          if (this.tmp2[0] !== 0 || this.tmp2[1] !== 0) {
            if (this.armyPace > 0 && this.armyPace < this.sSpeed[i]) {
              this.stepBoost = Math.floor((this.armyPace * FP_ONE) / this.sSpeed[i]);
            }
            this.sqHomeX[0] = this.x[i];
            this.sqHomeY[0] = this.y[i];
            return;
          }
        }
        const hx = this.sqHomeX[sq];
        const hy = this.sqHomeY[sq];
        const dx = hx - this.x[i];
        const dy = hy - this.y[i];
        // 장수 곁 제 자리를 따르는 동료도 진이 걷는 동안에는 자리와 같은 걸음으로 걷는다(`SLOT_MARCH`). 말 탄 동료가
        // 가다 서다 하면 그 분대의 자리가 통째로 따라 흔들린다 — 병사만 고쳤을 때 기병 분대는 여전히 틱의 25%를 서 있었다.
        const l = this.leaderIdx;
        // 직접 모는 동료는 뺀다 — 걸음을 줄인 채로 입력 가지에 가면 방향만 입력으로 바뀌고 걸음은 줄어든 그대로였다
        // (리뷰가 재현했다: 장수가 걷는 중에 동료를 골라 몰면 2.55 → 1.0px/틱).
        const marching =
          !steering &&
          sq !== 0 &&
          this.sqStation[sq] === 1 &&
          l !== NO_TARGET &&
          this.velX[l] * this.velX[l] + this.velY[l] * this.velY[l] >= SLOT_MARCH * SLOT_MARCH;
        const h2 = dx * dx + dy * dy;
        if (marching ? h2 === 0 : h2 <= HOME_ARRIVE * HOME_ARRIVE) return;
        if (marching) {
          const d = isqrt(h2);
          if (d < this.sSpeed[i]) this.stepBoost = Math.floor((d * FP_ONE) / this.sSpeed[i]);
        }
        // **진까지 바위가 막으면 돌아간다.** 동료의 진은 장수를 따라 움직여 바위 건너로 옮겨
        // 갈 수 있고, 곧장만 걸으면 동료가 바위를 밀며 서고 휘하도 따라 섰다(리뷰가 짚었다).
        // 장수 곁 진은 장수에게서 보이는 자리라(`placeSlots`) 장수 쪽 길로 가면 닿는다.
        // 경로 끝에 짠 진은 그 분대의 경로 흐름장이 아직 그 점을 향한다.
        if (sq !== 0 && this.grid.blockedBetweenFp(this.x[i], this.y[i], hx, hy)) {
          this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
          (this.sqStation[sq] === 1 ? this.flowCmdr[0] : this.flowSquad[sq]).sample(this.cell[0], this.cell[1], this.tmp2);
        }
        if (this.tmp2[0] === 0 && this.tmp2[1] === 0) fpNormalize(dx, dy, this.tmp2);
      }
      return;
    }

    // 경로를 걷는 지휘관을 따르는 병사는 명령과 상관없이 따라간다. 대기라도 지휘관이
    // 경로로 떠나면 따라가야 부대가 한 덩어리로 돈다.
    if (order === Order.Hold && !followsWalker) return;

    // **대형의 병사는 제 자리로 간다**(`placeSlots`). 자리는 지휘관을 기준으로 하므로
    // 지휘관이 걸으면(몰든 경로든) 진째 따라온다. 닿으면 군의 앞을 보고 선다 — 옆과 뒤를
    // 가르는 판정(`sideOf`)이 얼굴 방향을 쓰므로, 진이 앞을 봐야 앞에서 온 적을 앞으로 받는다.
    // 자리가 없으면(바위, 따를 사람 없음) 아래의 예전 규칙대로 지휘관 곁을 따른다.
    if ((order === Order.Formation || sweeping) && this.slotOk[i] === 1) {
      const sx = this.slotX[i];
      const sy = this.slotY[i];
      const s2 = distSq(this.x[i], this.y[i], sx, sy);
      // **진이 걷는 동안에는 자리와 같은 걸음으로 따라 걷는다**(`SLOT_MARCH`). 지휘관이 지난 틱에 걸었으면 닿았다고 서지
      // 않고, 자리까지 남은 만큼만 걷는다(아래 `stepBoost`).
      const lead = this.anchorOf(i);
      const marching =
        lead !== NO_TARGET && this.velX[lead] * this.velX[lead] + this.velY[lead] * this.velY[lead] >= SLOT_MARCH * SLOT_MARCH;
      // **서 있는 진에서 한번 자리에 선 병사는 조금 밀려도 되돌아 걷지 않는다**(`slotHeld`, `SLOT_LEAVE`). 진이 걷는
      // 동안에는 붙들지 않는다 — 자리와 같은 걸음으로 따라 걷는다.
      if (marching) this.slotHeld[i] = 0;
      else if (s2 <= SLOT_ARRIVE * SLOT_ARRIVE) this.slotHeld[i] = 1;
      else if (s2 > SLOT_LEAVE * SLOT_LEAVE) this.slotHeld[i] = 0;
      if (marching ? s2 === 0 : this.slotHeld[i] === 1) {
        this.faceX[i] = this.frontX;
        this.faceY[i] = this.frontY;
        return;
      }
      // 자리까지 바위가 막으면 지휘관 쪽으로 돈다. 자리는 지휘관에게서 보이는 곳이라
      // (`placeSlots`) 지휘관 곁에서는 곧장 보인다. 지휘관과 같은 칸이라 흐름장이 0이면
      // 지휘관을 향해 곧장 걷는다 — 자리를 향해 곧장 걸으면 바위를 민다.
      // **돌아가기 시작하면 돌 곳에 닿을 때까지 돌아간다**(`slotDetour`). 바위 모서리에서는 한 걸음마다 자리가 가렸다
      // 보였다 한다 — 틱마다 다시 고르면 "지휘관 쪽으로 한 걸음, 자리 쪽으로 한 걸음"을 오가며 영영 닿지 못한다.
      // 돌 곳은 **지휘관에게서 자리 쪽으로 `SLOT_DETOUR_OUT`만큼 나간 점**이다. 자리는 지휘관에게서 곧장 보이는 곳이라
      // (`placeSlots`) 그 선 위에 서면 자리가 보인다. 지휘관의 몸 한가운데를 향하면 몸에 부딪혀 튕기고, 곁에 왔다고
      // 그만 돌면(처음에 그렇게 했다) 선에서 벗어난 채라 자리가 여전히 가려 다시 오갔다.
      //
      // 시계는 **돌기 시작할 때 한 번만** 채운다. 처음에는 자리가 가릴 때마다 다시 채워, 돌 곳을 밟지 못하는 병사
      // (지휘관·다른 병사·바위에 막힌)가 상한을 넘겨 돌았다(리뷰가 자연 전장에서 100틱 연속을 쟀다). 다 세고도 닿지
      // 못했으면 `SLOT_DETOUR_REST`틱 동안은 예전 규칙으로 걷는다(가리면 지휘관 쪽 길, 보이면 곧장) — 곧바로 다시
      // 돌기 시작하면 상한이 없는 것과 같다. 쉬는 동안의 값은 `SLOT_DETOUR_TICKS`보다 크다(한 배열에 두 시계).
      const blocked = this.grid.blockedBetweenFp(this.x[i], this.y[i], sx, sy);
      // 시계는 여기서 세지 않는다 — `placeSlots`가 틱마다 센다. 여기는 표적이 없고 붙들리지 않은 틱에만 오므로, 여기서
      // 세면 싸우는 동안에는 시계가 멈춰 상한이 벽시계 3초가 아니게 된다(리뷰가 짚었다).
      let detour = this.slotDetour[i];
      if (detour === 0 && blocked) {
        detour = SLOT_DETOUR_TICKS;
        this.slotDetour[i] = detour;
      }
      const detouring = detour > 0 && detour <= SLOT_DETOUR_TICKS;
      if (!detouring && blocked) {
        // 쉬는 동안(또는 지휘관이 없을 때) 가린 자리: 지휘관 쪽 길을 탄다. 흐름장이 0이면 지휘관을 향해 곧장.
        this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
        this.flowCmdr[sq].sample(this.cell[0], this.cell[1], this.tmp2);
        if (this.tmp2[0] === 0 && this.tmp2[1] === 0 && lead !== NO_TARGET) {
          fpNormalize(this.x[lead] - this.x[i], this.y[lead] - this.y[i], this.tmp2);
        }
      }
      if (detouring && lead !== NO_TARGET) {
        // 돌 곳. 자리가 지휘관에게서 그보다 가까우면 자리 자체다.
        const ax = this.x[lead];
        const ay = this.y[lead];
        const span = fpNormalize(sx - ax, sy - ay, this.tmp3);
        const out = Math.min(span, SLOT_DETOUR_OUT);
        const wx = ax + fpMul(this.tmp3[0], out);
        const wy = ay + fpMul(this.tmp3[1], out);
        // 돌 곳 곁에 왔고 **자리가 보이면** 그만 돈다. 곁에 왔다고만 끝냈더니, 선에서 몇 px 비낀 자리에서는 모서리에
        // 가려 다음 틱에 다시 돌기 시작했다 — 돌 곳으로 두 걸음, 자리 쪽으로 한 걸음을 되풀이했다. 가렸으면 돌 곳의 한가운데
        // 까지 간다(그 점은 지휘관과 자리를 잇는 선 위라 자리가 보인다).
        if (!blocked && distSq(this.x[i], this.y[i], wx, wy) <= SLOT_DETOUR_NEAR * SLOT_DETOUR_NEAR) {
          this.slotDetour[i] = 0;
        } else if (this.grid.blockedBetweenFp(this.x[i], this.y[i], wx, wy)) {
          // 돌 곳도 가렸으면 지휘관 쪽 길을 탄다. 지휘관과 같은 칸이라 흐름장이 0이면 돌 곳으로 곧장 걷는다.
          this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
          this.flowCmdr[sq].sample(this.cell[0], this.cell[1], this.tmp2);
          if (this.tmp2[0] === 0 && this.tmp2[1] === 0) fpNormalize(wx - this.x[i], wy - this.y[i], this.tmp2);
        } else {
          fpNormalize(wx - this.x[i], wy - this.y[i], this.tmp2);
        }
      }
      if (this.tmp2[0] === 0 && this.tmp2[1] === 0) fpNormalize(sx - this.x[i], sy - this.y[i], this.tmp2);
      if (s2 > SLOT_SPRINT * SLOT_SPRINT) this.stepBoost = FP_ONE * 2;
      else if (marching) {
        // 한 걸음보다 가까우면 남은 만큼만 걷는다 — 자리를 지나치지 않고 자리가 걷는 걸음에 맞는다.
        const d = isqrt(s2);
        if (d < this.sSpeed[i]) this.stepBoost = Math.floor((d * FP_ONE) / this.sSpeed[i]);
      }
      return;
    }

    // 공격과 대형의 부대원은 지휘관을 따른다. 둘의 차이는 표적을 고르는 범위다
    // (`senseOf`). 공격은 제 둘레의 적을 다 치러 가고, 대형은 지휘관 가까이 온 적만 친다.
    const anchor = this.anchorOf(i);
    if (anchor === NO_TARGET) return;

    const d2 = distSq(this.x[i], this.y[i], this.x[anchor], this.y[anchor]);

    // 공격이면 지휘관과 함께 나아간다. 이유는 `ATTACK_LEASH`에 적었다.
    // 궁수도 함께 간다. 칼이 닿기 전에는 지휘관 뒤에서만이다(`BOW_REAR`) — 앞서 나가면 칼 든 적과 먼저 맞붙는다.
    // 경로를 걷는 지휘관을 따를 때는 앞서 나가지 않는다 — 적 쪽이 아니라 지휘관 쪽으로 간다.
    if (order === Order.Attack && !followsWalker) {
      // 지휘관이 선 칸의 공격 흐름을 앞으로 삼아, 지휘관에게서 앞뒤(`ahead`)와 옆(`side`)으로 얼마나 떨어졌나
      // 잰다(`ATTACK_FLANK`). 지휘관이 흐름이 없는 칸(목표 칸)에 섰으면 예전처럼 원으로 잰다.
      this.grid.cellOfFp(this.x[anchor], this.y[anchor], this.cell);
      this.flowAttack.sample(this.cell[0], this.cell[1], this.tmp3);
      let near: boolean;
      let wait: boolean;
      const rear = isBow && this.meleeJoined === 0;
      // 첫 칼이 닿은 뒤로는 끈을 매지 않는다(`ATTACK_LEASH`) — 어디에 있든 제 칸의 흐름을 타고 적 쪽으로 나아간다.
      const loose = this.meleeJoined !== 0;
      if (loose) {
        near = true;
        wait = false;
      } else if (this.tmp3[0] !== 0 || this.tmp3[1] !== 0) {
        const rx = this.x[i] - this.x[anchor];
        const ry = this.y[i] - this.y[anchor];
        const ahead = Math.trunc((rx * this.tmp3[0] + ry * this.tmp3[1]) / FP_ONE);
        const side = Math.abs(Math.trunc((rx * this.tmp3[1] - ry * this.tmp3[0]) / FP_ONE));
        // 칼이 닿기 전의 궁수는 지휘관 뒤에서만 나아간다(`BOW_REAR`).
        near = side <= ATTACK_FLANK && ahead <= (rear ? -BOW_REAR : ATTACK_LEASH);
        wait = side <= ATTACK_FLANK && ahead <= ATTACK_LEASH_MAX;
      } else if (rear) {
        // 지휘관이 흐름이 없는 칸(목표 칸)에 섰다 — 칼이 닿기 전의 궁수는 예전처럼 곁을 따른다.
        near = false;
        wait = false;
      } else {
        near = d2 <= ATTACK_LEASH * ATTACK_LEASH;
        wait =
          d2 <= ATTACK_LEASH_MAX * ATTACK_LEASH_MAX &&
          this.attackDist(this.x[i], this.y[i]) < this.attackDist(this.x[anchor], this.y[anchor]);
      }
      if (near) {
        this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
        this.flowAttack.sample(this.cell[0], this.cell[1], this.tmp2);
        if (this.tmp2[0] !== 0 || this.tmp2[1] !== 0) return;
      } else if (wait) {
        return; // 앞서 나갔다. 지휘관이 올 때까지 선다.
      }
    }

    // 궁수는 조금 더 뒤에 선다.
    const keep = isBow ? FOLLOW_RADIUS + fp(40) : FOLLOW_RADIUS;
    if (d2 <= keep * keep) return;

    // **막혔을 때만 길을 찾는다.**
    //
    // 직선으로만 걸으면 바위를 못 돌아 영영 못 따라온다. 그렇다고 늘
    // 흐름장을 쓰면 안 된다 — 흐름장이 데려가는 곳과 멈추는 거리(`keep`)를 재는
    // 곳이 어긋나면 병사들이 목표를 지나쳐 몰려가 멈춘다. 예전에 흐름장의 목표를
    // 장수로, 멈추는 거리를 분대장까지로 쟀더니 분대가 통째로 장수 위로 무너져
    // 깊이 3 전과가 2181에서 1331로 떨어졌다. 지금은 흐름장도 분대마다 지휘관을
    // 목표로 삼지만(`flowCmdr`), 칸 단위라 가까이서는 여전히 거칠다.
    //
    // 그래서 지휘관이 보이면 곧장 간다. 사이에 바위가 있을 때만 흐름장에 길을 묻는다.
    if (this.grid.blockedBetweenFp(this.x[i], this.y[i], this.x[anchor], this.y[anchor])) {
      this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
      this.flowCmdr[sq].sample(this.cell[0], this.cell[1], this.tmp2);
    }
    if (this.tmp2[0] === 0 && this.tmp2[1] === 0) {
      fpNormalize(this.x[anchor] - this.x[i], this.y[anchor] - this.y[i], this.tmp2);
    }
    // 많이 뒤처졌으면 달려서 따라붙는다. 장수가 기병이라 평속으로는 영영 못 따라간다.
    // 배율이 장수 속도를 넘어서야 벌어진 간격이 실제로 좁혀진다.
    const sprint = keep * 2;
    if (d2 > sprint * sprint) this.stepBoost = FP_ONE * 2;
  }

  /**
   * 대형이 지키는 점을 nch에 담는다. 지휘관에게는 진의 자리이고, 부대원에게는
   * 지휘관이 선 곳이다. 부대원은 지휘관을 따라 서므로 진의 한가운데는 지휘관이다.
   * 지킬 점이 없으면(따를 사람이 다 쓰러졌으면) false.
   */
  private anchorPoint(i: number): boolean {
    const role = this.role[i];
    if (role === Role.Leader || role === Role.Companion) {
      const sq = this.squad[i];
      this.anch[0] = this.sqHomeX[sq];
      this.anch[1] = this.sqHomeY[sq];
      return true;
    }
    const a = this.anchorOf(i);
    if (a === NO_TARGET) return false;
    this.anch[0] = this.x[a];
    this.anch[1] = this.y[a];
    return true;
  }

  /**
   * 부대원이 따를 사람. 자기 부대의 지휘관이고, 지휘관이 쓰러졌거나 빠져나갔으면 장수다.
   * 흐름장(`flowCmdr`)도 같은 규칙으로 목표를 잡는다.
   */
  private anchorOf(i: number): number {
    // 물러난 동료는 따르지 않는다(`activeCommander`). 따르면 분대가 통째로 뒤로 끌려간다. 지휘 보정은 그대로 붙는다
    // (`captainActive`).
    const cmdr = this.activeCommander(this.squad[i]);
    if (cmdr !== NO_TARGET && cmdr !== i) return cmdr;
    const l = this.leaderIdx;
    if (l === NO_TARGET || l === i || this.alive[l] === 0) return NO_TARGET;
    return l;
  }

  /**
   * 표적을 어떻게 고르는가. 명령과 역할에서 정한다.
   *
   * 적군은 예전 그대로 제 둘레를 본다. 무너진 병사도 그렇다 — 도망치는 방향을
   * 정하려면 쫓아오는 적을 알아야 한다.
   */
  private senseOf(i: number, steering: boolean): Sense {
    // 물러난 병사는 싸움을 피한다. 표적을 잡지 않는다(무너져 달아나는 병사는 따로 — 그쪽이 먼저다).
    if (this.withdrawn[i] === 1 && this.routed[i] === 0) return Sense.None;
    // 화살을 다 쏘고 빠져나가는 궁수도 싸우지 않는다.
    if (this.quiver[i] === Quiver.Leave && this.routed[i] === 0) return Sense.None;
    // 언덕에서 기다리는 적은 닿는 적만 친다(`enemyHold`).
    if (this.team[i] !== Team.Player && this.enemyHold > 0 && this.routed[i] === 0) return Sense.Reach;
    if (this.team[i] !== Team.Player || this.routed[i] > 0) return Sense.Free;
    // 몰고 있는 사람(장수·동료)은 닿는 적만 본다 — 멀리 있는 적을 표적으로 잡으면 곁의 적을 두고 휩쓸기가 안 나간다.
    if (steering) return Sense.Reach;
    const sq = this.squad[i];
    const order = this.sqOrder[sq];
    const role = this.role[i];
    if (role === Role.Leader) {
      // 장수는 후퇴 중에도 닿는 적은 벤다. 가장자리에 남아 엄호하는 사람이다.
      // 플레이어가 몰고 있을 때도 닿는 적만 본다. 멀리 있는 적을 표적으로 잡으면
      // 곁에 붙은 적이 있어도 "닿지 않았다"고 보고 휩쓸기가 안 나간다.
      if (steering) return Sense.Reach;
      // 경로를 걷는 장수도 닿는 적만 치며 지나간다. 병사가 경로를 걸을 때와 같은 이유다.
      if (this.sqPathLen[sq] > 0) return Sense.Reach;
      if (order === Order.Attack) return Sense.Free;
      return order === Order.Formation ? Sense.Anchor : Sense.Reach;
    }
    if (order === Order.Retreat) return Sense.None;
    if (order === Order.Hold) return Sense.Reach;
    // 경로를 따르는 동안은 닿는 적만 친다. 제 둘레의 적을 다 쫓으면 우회하러 간
    // 기병이 첫 적 앞에서 멈춘다. 치는 동안에도 걷는다(`updateUnit`). 끝점에 닿으면
    // 경로가 지워지고 명령대로 싸운다.
    // (옛 군기는 제 둘레를 다 쳤다. 군기는 서서 지키는 것이었고 경로는 가는 것이다.)
    if (this.sqPathLen[sq] > 0) return Sense.Reach;
    // 돌아가는 기병 분대도 같다(`SWEEP`) — 스치는 적마다 쫓으면 적 줄의 끝에서 붙들린다.
    if (this.rides(i)) return Sense.Reach;
    // 잔병만 남으면 대형도 제 둘레의 적을 쫓는다(`MOPUP_MAX`).
    if (order === Order.Formation) return this.mopUp ? Sense.Free : Sense.Anchor;
    // 공격의 지휘관은 표적이 생기면 부대를 기다리지 않고 쫓는다(부대를 기다리는 것은 표적이
    // 없을 때만 건다). 그래서 장수와 동료가 병사보다 앞서 한 덩어리로 들어간다.
    //
    // 이것을 막아 봤다. 부대가 떨어져 있으면 닿는 적만 치게 하자 깊이 4의 전군 공격이
    // 장수 전사 8/15에서 15/15로 나빠졌다. 지휘관들이 앞에서 휩쓸며 길을 여는 것이
    // 이기는 몫이었고, 세워 두니 둘러싸여 베지도 못하고 맞기만 했다. 되돌렸다.
    return Sense.Free;
  }

  /**
   * 싸움이 붙었는가 — 어느 쪽이든 한 번이라도 피해를 입혔다(화살 포함). 누적 피해(`stats.damage`, 체크섬에 든 상태)에서
   * 읽는다. 공격의 지휘관이 걸음을 맞추는 것은 이 전까지다(`ATTACK_CHARGE`).
   */
  private joined(): boolean {
    return this.stats.damage[Team.Player] + this.stats.damage[Team.Enemy] > 0;
  }

  /**
   * i 둘레 r 안에 맞서는 적(무너져 달아나는 적은 빼고)이 있는가. 대형의 장수가 나아가기를 멈출지, 공격의 지휘관이
   * 돌격할지(`ATTACK_CHARGE`) 정한다.
   *
   * 해시 칸을 직접 돌며 찾는 대로 멈춘다. 처음에는 `query`로 768칸 그릇(`near`)에 받아 걸렀고, "그릇이 차면 뒤는
   * 잘리지만 그만큼 붐비는 곳이면 앞의 것만으로 적을 찾는다"고 적어 두었다 — 틀렸다. 그릇은 거르기 전에 차므로, 무너져
   * 달아나는 적 800명이 앞 칸을 채우면 400px 앞의 싸우는 적을 못 보고 공격의 장수가 병사 걸음으로 걸었다(리뷰가
   * 재현했다). `nearestFoe`가 같은 까닭으로 먼저 고쳐진 자리다.
   */
  private enemyWithin(i: number, r: number): boolean {
    const h = this.hash;
    const b = this.closeCells;
    h.cellsAround(this.x[i], this.y[i], r, b);
    const r2 = r * r;
    for (let cy = b[2]; cy <= b[3]; cy++) {
      for (let cx = b[0]; cx <= b[1]; cx++) {
        const cell = cy * h.cols + cx;
        const end = h.cellEnd(cell);
        for (let k = h.cellStart(cell); k < end; k++) {
          const j = h.itemAt(k);
          if (this.alive[j] === 0 || this.team[j] === this.team[i] || this.routed[j] > 0) continue;
          if (distSq(this.x[i], this.y[i], this.x[j], this.y[j]) <= r2) return true;
        }
      }
    }
    return false;
  }

  /** 제 진영 가장자리로 가는 방향을 tmp2에. 가장자리 줄에 서 있으면 (0, 0)이다. */
  private exitDir(i: number): void {
    const team = this.team[i];
    this.grid.cellOfFp(this.x[i], this.y[i], this.cell);
    this.flowExit[team].sample(this.cell[0], this.cell[1], this.tmp2);
    if (this.tmp2[0] === 0 && this.tmp2[1] === 0) {
      // 흐름장이 닿지 않는 칸이면 곧장 간다. 가장자리 줄이면 다 온 것이다.
      const edge = team === Team.Player ? this.grid.rows - 1 : 0;
      if (this.cell[1] !== edge) this.tmp2[1] = team === Team.Player ? FP_ONE : -FP_ONE;
    }
  }

  /**
   * 가장자리에 닿은 후퇴·패주 병력을 전장에서 뺀다.
   *
   * **장수는 빠지지 않는다.** 장수가 먼저 빠지면 남은 병력을 버리는 것이 버튼
   * 하나로 되고, 조종할 대상도 사라진다. 적장도 빠지지 않는다 — 무너지지 않는다.
   *
   * 빠져나간 병사는 처치가 아니다(`stats.kills`에 들어가지 않는다). 쫓아가 베지
   * 못한 적은 전과가 아니다.
   */
  private tryExit(i: number): void {
    if (this.alive[i] === 0) return;
    const role = this.role[i];
    if (role === Role.Leader || role === Role.Chief) return;
    const team = this.team[i];
    const leaving =
      this.routed[i] > 0 ||
      this.quiver[i] === Quiver.Leave ||
      (team === Team.Player && this.sqOrder[this.squad[i]] === Order.Retreat);
    if (!leaving) return;
    const edge = team === Team.Player ? this.grid.rows - 1 : 0;
    if (Math.floor(this.y[i] / TILE_FP) !== edge) return;
    this.alive[i] = 0;
    // 쓰러지거나 빠져나간 궁수는 아무도 겨누지 않는다(`aimOf`). 근접 유닛도 같다(`chaseOf`).
    this.syncAim(i);
    this.syncChase(i);
    this.exited[i] = 1;
    this.target[i] = NO_TARGET;
    if (role === Role.Soldier) this.stats.exited[team]++;
  }

  /**
   * 반경 안의 아군·적군 모두에게서 밀려나는 벡터를 tmp에 담는다.
   *
   * 가려던 속도(`vx`, `vy`)도 받아, **적의 몸에 막힌 만큼 깎은 속도**를 tmpV에
   * 담는다. 둘을 한 번의 이웃 조회로 끝내려고 같은 자리에 둔다.
   */
  private separation(i: number, vx: number, vy: number): void {
    const radius = this.sRadius[i];
    const mass = this.sMass[i];
    const myTeam = this.team[i];
    const n = this.hash.query(this.x[i], this.y[i], radius * 2, this.near);
    let sx = 0;
    let sy = 0;
    // 적의 몸이 막아 깎아 낼 속도. 합을 모았다가 한 번에 뺀다.
    let rx = 0;
    let ry = 0;
    const moving = vx !== 0 || vy !== 0;
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (j === i || this.alive[j] === 0) continue;
      const dx = this.x[i] - this.x[j];
      const dy = this.y[i] - this.y[j];
      const want = radius + this.sRadius[j];
      const d2 = dx * dx + dy * dy;
      if (d2 >= want * want) continue;
      // 적의 몸과 맞닿았다. `relaxCrowd`가 이 유닛을 건너뛴다. 한 점에 포개진 적도 맞닿은 것이라 아래
      // `d2 === 0`보다 앞에 둔다.
      if (this.team[j] !== myTeam) this.bodyPressed[i] = 1;
      if (d2 === 0) {
        // 완전히 겹쳤을 때는 인덱스로 방향을 갈라 결정론을 지킨다.
        sx += i < j ? FP_ONE : -FP_ONE;
        continue;
      }
      const d = isqrt(d2);
      const overlap = want - d;
      // 무거운 상대일수록 내가 더 밀린다.
      const other = this.sMass[j];
      const w = Math.floor((overlap * FP_ONE * other) / (want * (mass + other)));
      sx += Math.floor((dx * w) / d);
      sy += Math.floor((dy * w) / d);

      // **적의 몸은 벽이다.**
      //
      // 밀어내기는 끝에서 길이 1로 자른다. 그래야 밀집 대열에서 속도가 폭발해
      // 떨리지 않는다. 그런데 그 탓에 몇 명이 미는지가 아무 값도 하지 못했다.
      // 장수 몸에 적이 마흔 명까지 동시에 닿아 있어도 빈 땅과 같은 속도로
      // 지나갔다(30초에 1045px 대 1048px). 창벽이 장수를 몸으로 막지 못하면
      // 대열을 짜는 의미가 반으로 준다.
      //
      // 밀어내기를 키우는 대신 **가려던 쪽으로의 전진을 깎는다.** 겹친 적
      // 하나마다, 그 적을 향한 속도 성분을 (적의 질량 비) × (겹친 깊이)만큼
      // 덜어 낸다. 여럿이면 합이 되므로 수가 값을 한다 — 민병 하나는 장수를
      // 거의 못 세우고, 철기 둘이 깊이 붙으면 세운다. 스치듯 닿은 적은 조금만,
      // 몸을 붙여 막은 적은 많이 막는다. 가장자리에서 떨리지 않게 하는 몫이다.
      //
      // **적에게만** 건다. 규칙을 "적의 몸"으로 좁게 두는 편이 설명하기 쉽다.
      //
      // 처음에는 "아군끼리 걸면 행군하는 대열의 뒷사람이 앞사람에게 막혀 통째로
      // 느려진다"고 적었다. 재 보니 틀렸다 — 아군에게도 걸어 종대를 8초 걷게 하면
      // 717.7px 대 711.7px, 1%도 안 느려진다. 가만히 선 아군 사이를 장수가 지나가도
      // 차이가 없다. 같이 걷거나 밀려 비켜서기 때문이다. 아군을 빼는 것은 막을
      // 이유가 있어서가 아니라 걸 이유가 없어서다. 그래서 이 구분을 지키는 시험도
      // 두지 않았다 — 어느 쪽이든 통과해 아무것도 지키지 못한다. (같은 편끼리 포개지는 것은 따로
      // `relaxCrowd`가 막는다. 그것은 속도가 아니라 자리를 옮기고, 적에 눌린 유닛은 건드리지 않는다.)
      if (!moving || this.team[j] === myTeam) continue;
      // 나에게서 그 적 쪽을 향한 단위 벡터.
      //
      // 부호 있는 성분은 floor가 아니라 trunc로 자른다. floor는 음수를 한 칸 더
      // 내려, 좌우 대칭으로 붙은 두 적의 깎기가 -25와 +24로 어긋난다. 그 1단위가
      // 매 틱 한쪽으로 쌓이면 장수가 대열 앞에서 옆으로 흐른다. trunc는 0 쪽으로
      // 잘라 부호만 바꾼 두 값이 정확히 상쇄된다. 정수 연산이므로 결정론은 같다.
      const ux = Math.trunc((-dx * FP_ONE) / d);
      const uy = Math.trunc((-dy * FP_ONE) / d);
      const toward = Math.floor((vx * ux + vy * uy) / FP_ONE);
      if (toward <= 0) continue; // 멀어지는 중이면 막을 것이 없다.
      const k2 = Math.floor(
        (other * overlap * FP_ONE * BODY_BLOCK_PCT) / ((mass + other) * want * 100),
      );
      const cut = Math.floor((toward * k2) / FP_ONE);
      rx += Math.trunc((ux * cut) / FP_ONE);
      ry += Math.trunc((uy * cut) / FP_ONE);
    }
    fpClampLen(sx, sy, FP_ONE, this.tmp);

    // 합을 한 번에 뺀다. 이웃마다 차례로 빼면 이웃을 도는 순서에 따라 답이
    // 달라진다. 정수 덧셈은 순서와 무관하므로 모았다 빼면 결정론이 지켜진다.
    let nx = vx - rx;
    let ny = vy - ry;
    // 뒤로 밀려나지는 않는다. 세우는 데서 그친다 — 밀어내는 것은 밀어내기의 몫이다.
    if (nx * vx + ny * vy <= 0) {
      nx = 0;
      ny = 0;
    }
    // 옆의 적 둘이 기울어 막으면 옆으로 비껴 나갈 수 있다. 그래도 원래보다
    // 빨라지지는 않게 한다.
    const lim = isqrt(vx * vx + vy * vy);
    fpClampLen(nx, ny, lim, this.tmpV);
  }

  /**
   * 아군끼리 겹친 몸을 떼어 놓는다. 모두 움직인 뒤 한 번, 겹친 깊이의 `CROWD_PUSH_PCT`만큼 질량 비로 나눠
   * 자리를 직접 옮긴다(속도가 아니라 위치). 밀어내기(`separation`)는 그대로 둔다 — 그 힘이 덩어리감을
   * 만들고, 이것은 그 힘이 걸음에 져서 몸이 포개지는 것만 막는다.
   *
   * - **같은 편끼리만, 양쪽 모두.** 적의 몸이 막는 규칙(창벽이 장수를 세운다)은 `separation`의 몫이다. 아군만
   *   떼어 보니 모든 전술이 약해졌다 — 이 시뮬레이션에서는 뭉친 것이 곧 전투력이라, 한쪽만 풀면 다른 쪽이
   *   국지적으로 수가 많아진다.
   * - **적의 몸에 눌린 유닛은 빼고.** 부딪친 줄까지 떼면 창벽이 느슨해졌다(아래 `bodyPressed`).
   * - **장수는 옮기지 않는다.** 모는 장수가 부하에게 밀려 흔들리면 조작이 무뎌진다. 장수와 겹친 병사가
   *   겹친 몫을 혼자 진다(비켜선다).
   * - **결정론:** 쌍마다 (i < j) 한 번, 합을 모았다가 색인 오름차순으로 한 번에 옮긴다. 정수 덧셈은 순서와
   *   무관하다. 옮기기는 `integrate`를 지나 바위와 전장 끝을 넘지 않는다.
   */
  private relaxCrowd(): void {
    const n = this.count;
    this.crowdX.fill(0, 0, n);
    this.crowdY.fill(0, 0, n);
    for (let i = 0; i < n; i++) {
      // 적의 몸에 눌린 유닛은 손대지 않는다. 떼어 놓으면 창벽이 장수를 세우는 힘이 빠졌다 — 여섯 겹이 장수를
      // 1.8배 넘게 늦추던 것이 1.4배로 줄었다(`combat.test.ts` 적의 몸). 부딪친 줄은 몸으로 버티고, 그 뒤에서
      // 포개진 무리만 푼다. 조회 전에 거른다 — 눌린 유닛은 가장 붐비는 칸에 있다.
      if (this.alive[i] === 0 || this.bodyPressed[i] === 1) continue;
      const ri = this.sRadius[i];
      const team = this.team[i];
      // 상대가 가장 큰 몸(기병·장수 8px)이어도 닿는 거리 + 격자를 지은 뒤 움직인 몫. 처음에는 `ri * 2`라 보병과
      // 기병 쌍의 여유가 1px뿐이었고, 기병이 큰 색인이면 칸 경계에서 그 틱을 놓쳤다(리뷰가 짚었다).
      const found = this.hash.query(this.x[i], this.y[i], ri + CROWD_BODY_MAX + CROWD_GAP + CROWD_SLACK, this.near);
      for (let k = 0; k < found; k++) {
        const j = this.near[k];
        if (j <= i || this.alive[j] === 0 || this.team[j] !== team || this.bodyPressed[j] === 1) continue;
        const dx = this.x[i] - this.x[j];
        const dy = this.y[i] - this.y[j];
        const want = ri + this.sRadius[j] + CROWD_GAP;
        const d2 = dx * dx + dy * dy;
        if (d2 >= want * want) continue;
        // 장수는 판에 하나라 같은 편 쌍의 둘 다 장수일 일은 없다. 적장(Chief)은 고정하지 않는다 — 모는 사람이
        // 없으니 흔들려도 조작이 무뎌지지 않는다.
        // **동료(분대 지휘관)도 병사에게는 밀리지 않는다.** 병사의 자리는 지휘관의 몸을 기준으로 짠다(`placeSlots`) —
        // 병사가 지휘관을 밀면 자리가 통째로 옮겨 가고, 밀려난 지휘관은 제 자리로 되돌아오며 다시 병사를 민다. 장수가 서
        // 있는데 분대 하나가 통째로 앞뒤로 흔들렸다(기병 분대 12명이 45틱에 방향을 26번 뒤집었다). 동료끼리, 동료와
        // 장수 사이는 전과 같다(동료끼리는 무게대로 나눠 지고, 장수 앞에서는 동료가 다 진다) — 둘 다 고정하면 포개진 채
        // 풀리지 않는다. 대형만이 아니라 어느 명령에서나 걸린다.
        const iFixed = this.role[i] === Role.Leader || (this.role[i] === Role.Companion && this.role[j] === Role.Soldier);
        const jFixed = this.role[j] === Role.Leader || (this.role[j] === Role.Companion && this.role[i] === Role.Soldier);
        let d = isqrt(d2);
        // 완전히 겹쳤으면 방향이 없다. 색인으로 가른다(작은 쪽이 +x).
        let ux = FP_ONE;
        let uy = 0;
        if (d > 0) {
          ux = Math.trunc((dx * FP_ONE) / d);
          uy = Math.trunc((dy * FP_ONE) / d);
        } else {
          d = 0;
        }
        const push = Math.floor(((want - d) * CROWD_PUSH_PCT) / 100);
        const mi = this.sMass[i];
        const mj = this.sMass[j];
        // i가 지는 몫: 상대가 무거울수록 크다. 한쪽이 장수면 다른 쪽이 다 진다.
        const shareI = iFixed ? 0 : jFixed ? push : Math.floor((push * mj) / (mi + mj));
        const shareJ = jFixed ? 0 : push - shareI;
        this.crowdX[i] += Math.trunc((ux * shareI) / FP_ONE);
        this.crowdY[i] += Math.trunc((uy * shareI) / FP_ONE);
        this.crowdX[j] -= Math.trunc((ux * shareJ) / FP_ONE);
        this.crowdY[j] -= Math.trunc((uy * shareJ) / FP_ONE);
      }
    }
    for (let i = 0; i < n; i++) {
      let cx = this.crowdX[i];
      let cy = this.crowdY[i];
      if (cx === 0 && cy === 0) continue;
      // **잔병을 쫓아 걷는 아군은 앞뒤로 옮기지 않는다(`chaseX`).** 옮길 몫에서 가려는 쪽(앞뒤) 성분을 빼고 옆 성분만
      // 남긴다 — 옆으로는 그대로 벌어진다. 플레이한 사람이 짚었다: "잔병이 남았을 때 기병들이 시원하게 달려가지 않아 …
      // 적을 찾아 움직이는 기병들이 무리를 이루고 이동하는데 그게 최고 속도가 아닌 느낌이었어." 걸음을 늦추는 규칙은
      // 없었고, 남은 적 몇을 향해 모두가 같은 흐름을 타 무리가 되면 포개진 뒷사람이 틱마다 여기서 뒤로 옮겨졌다 — 잔병
      // 구간의 느린 틱(제 걸음의 80% 미만) 가운데 84~89%가 이것이었다(QA가 걸음의 단계마다 손실을 쟀다; 한 번에
      // 0.24~0.27걸음).
      //
      // 두 번 고쳤다. (1) 처음에는 밀어내기(`separation`)에서 뒤로 미는 성분을 뺐다. 내가 여덟 판으로 잰 것은 좋아
      // 보였지만(1초 창 33 → 9%), QA가 마흔 판으로 다시 재니 차이가 없었다 — 밀어내기가 앗아 가는 걸음은 0.01걸음
      // 뿐이었다. 그쪽은 걷어 냈다. (2) 다음에는 여기서 **뒤로 옮기는 것만** 뺐다. 걸음은 풀렸지만(기병 97~102%), 느린
      // 보병 뒤에 빠른 기병이 붙은 줄에서 앞의 보병이 계속 앞으로 옮겨져 제 걸음보다 66% 빨리 갔다 — 포개짐이 풀리지
      // 않는 평형이라 "잠깐"이 아니었다(리뷰가 150틱으로 쟀다). 앞으로 옮기는 것도 함께 뺀다: 여기서는 아무도 빨라지지도
      // 느려지지도 않는다. (3) 앞뒤로 옮기려던 몫은 버리지 않고 옆으로 돌린다(아래) — 버리면 한 줄로 선 둘이 포개진 채 갔다.
      // 소탕 중에만, 걷는 아군만. 여느 때 맞붙은 줄의 두께는 그대로다. 적의 몸에 눌린 유닛은 애초에 여기 오지 않는다.
      const hx = this.chaseX[i];
      const hy = this.chaseY[i];
      if (hx !== 0 || hy !== 0) {
        const along = Math.trunc((cx * hx + cy * hy) / FP_ONE);
        if (along !== 0) {
          cx -= Math.trunc((hx * along) / FP_ONE);
          cy -= Math.trunc((hy * along) / FP_ONE);
          // 앞뒤로 옮기려던 몫은 **옆으로 돌린다**: 앞으로 옮겨질 사람은 가는 쪽의 한편으로, 뒤로 옮겨질 사람은 그
          // 반대편으로(앞뒤 벡터를 90° 돌린 것 — 부호가 반대라 둘이 서로 다른 쪽으로 비킨다). 그냥 빼기만 했더니, 똑바로
          // 한 줄로 선 느린 도수와 빠른 기병이 중심 1.9px까지 포개진 채 계속 걸었다 — 옆 성분이 0이라 벌어질 힘이
          // 없었다(리뷰가 150틱으로 쟀다). 돌리면 빠른 뒷사람이 비켜서 지나간다. 걸음(가는 쪽 성분)은 건드리지 않는다.
          //
          // 돌린 쪽이 막혔으면(전장 끝·바위) 반대편으로 돌리고, 양쪽이 다 막혔으면 앞뒤 몫을 되돌려 놓는다. 늘 한쪽으로만
          // 돌렸더니, 왼쪽 끝에 붙어 걷는 장수(여기서 옮겨지지 않는다) 바로 뒤의 기병이 막힌 쪽으로만 비키려다 중심
          // 3.9px로 포개진 채 갔고 장수는 제 걸음보다 22% 빨랐다 — 열린 오른쪽을 고르지 않았다(리뷰가 150틱으로 쟀다).
          const sx = -Math.trunc((hy * along) / FP_ONE);
          const sy = Math.trunc((hx * along) / FP_ONE);
          if (this.sideOpen(i, sx, sy)) {
            cx += sx;
            cy += sy;
          } else if (this.sideOpen(i, -sx, -sy)) {
            cx -= sx;
            cy -= sy;
          } else {
            cx += Math.trunc((hx * along) / FP_ONE);
            cy += Math.trunc((hy * along) / FP_ONE);
          }
          if (cx === 0 && cy === 0) continue;
        }
      }
      fpClampLen(cx, cy, CROWD_MAX_STEP, this.tmp);
      this.integrate(i, this.tmp[0], this.tmp[1], this.sRadius[i]);
    }
  }

  /** 유닛 `i`가 (`dx`, `dy`)쪽으로 한 몸만큼 비켜설 수 있는가 — 전장 안이고 바위가 아니다. 옆 공간의 **근사** 검사다: 축마다 ±두 반지름을 더한 끝점 하나만 본다. `integrate`(축을 나눠 몸 반지름까지 본다)와 같은 판정이 아니어서 대각 모서리에서는 어긋날 수 있다 — 어긋나도 그 틱의 옮김이 덜 될 뿐이다(리뷰). */
  private sideOpen(i: number, dx: number, dy: number): boolean {
    const r = this.sRadius[i];
    const px = this.x[i] + (dx > 0 ? 2 * r : dx < 0 ? -2 * r : 0);
    const py = this.y[i] + (dy > 0 ? 2 * r : dy < 0 ? -2 * r : 0);
    if (px < 0 || py < 0 || px > this.grid.widthPx * FP_ONE || py > this.grid.heightPx * FP_ONE) return false;
    return !this.grid.isBlockedFp(px, py);
  }

  /** 지형과 경계를 고려해 위치를 옮긴다. 축을 나눠 처리해 벽에 붙어 미끄러지게 한다. */
  private integrate(i: number, vx: number, vy: number, radius: number): void {
    const maxX = this.grid.widthPx * FP_ONE - radius;
    const maxY = this.grid.heightPx * FP_ONE - radius;

    if (vx !== 0) {
      const nx = clamp(this.x[i] + vx, radius, maxX);
      if (!this.grid.isBlockedFp(nx + (vx > 0 ? radius : -radius), this.y[i])) {
        this.x[i] = nx;
      }
    }
    if (vy !== 0) {
      const ny = clamp(this.y[i] + vy, radius, maxY);
      if (!this.grid.isBlockedFp(this.x[i], ny + (vy > 0 ? radius : -radius))) {
        this.y[i] = ny;
      }
    }
  }

  // ------------------------------------------------------------------
  // 표적과 전투
  // ------------------------------------------------------------------

  /**
   * 지금 표적을 계속 쥐고 있어도 되는가. 고를 때(`acquireTarget`)와 같은 규칙에
   * 여유를 조금 얹는다. 여유가 없으면 경계에 선 적을 틱마다 잡았다 놓는다.
   */
  private targetValid(i: number, sense: Sense): boolean {
    const e = this.target[i];
    if (e === NO_TARGET) return false;
    if (this.alive[e] === 0) return false;
    const d2 = distSq(this.x[i], this.y[i], this.x[e], this.y[e]);
    // 곡사로 겨누는 적은 곡사가 닿는 데까지 쥔다 — 놓으면 틱마다 다시 고른다.
    if (this.lobs(i, sense)) {
      const r = this.lobReach(i) + fp(8);
      return d2 <= r * r;
    }
    if (sense === Sense.Reach) {
      const r = this.curRange + REACH_SLACK + fp(8);
      return d2 <= r * r;
    }
    // 본능으로 쥔 표적은 본능의 반경까지 놓지 않는다(`acquireTarget`). 놓으면 다음 틱에 다시 골라 쥐었다 놓기를 되풀이한다.
    const instinct = this.instinctFor(i, e);
    if (sense === Sense.Anchor && this.branch[i] !== BranchCode.Bow && instinct !== Instinct.Guard && this.anchorPoint(i)) {
      // 진을 쏘는 적은 반경 밖이어도, 제 둘레(`SENSE_RADIUS`)보다 멀어도 쥔다. 놓으면
      // 다음 틱에 다시 그 적을 골라, 잡았다 놓기를 되풀이하며 한 걸음도 못 나간다.
      if (e === this.sqThreat[this.squad[i]]) return true;
      const r = FORMATION_RADIUS + fp(40);
      if (distSq(this.anch[0], this.anch[1], this.x[e], this.y[e]) > r * r) return false;
    }
    const leash =
      (instinct === Instinct.Hunt ? HUNT_RADIUS : instinct === Instinct.Guard ? GUARD_RADIUS : this.freeSense(i)) + fp(80);
    return d2 <= leash * leash;
  }

  /**
   * `i`가 `e`를 본능으로 노리는가(`HUNT_RADIUS_PX`). 기병 병사 → 적 궁수는 `Hunt`, 창 병사 → 제 편 궁수를 표적으로 쥔 적
   * 기병은 `Guard`. 닿는 적만 치는 명령(`Sense.Reach`)이나 무너진 병사는 이 함수를 부르기 전에 걸러진다.
   */
  private instinctFor(i: number, e: number): Instinct {
    if (this.role[i] !== Role.Soldier || this.routed[i] > 0) return Instinct.None;
    const mine = this.branch[i];
    if (mine === BranchCode.Horse) return this.branch[e] === BranchCode.Bow ? Instinct.Hunt : Instinct.None;
    if (mine !== BranchCode.Spear || this.branch[e] !== BranchCode.Horse) return Instinct.None;
    // 그 기병이 지금 쥔 표적이 제 편 궁수인가. 인덱스 오름차순으로 도는 틱 안에서 앞 번호는 이번 틱에 고른 값을 본다 —
    // 순서가 고정이라 결정론은 그대로다.
    const t = this.target[e];
    return t !== NO_TARGET && this.alive[t] === 1 && this.team[t] === this.team[i] && this.branch[t] === BranchCode.Bow
      ? Instinct.Guard
      : Instinct.None;
  }

  /** `j` 둘레(`CROWD_RADIUS`)에 선 같은 편의 수(자신 빼고). `CROWD_MAX`를 넘으면 `CROWD_MAX + 1`에서 멈춘다. */
  private crowdAround(j: number): number {
    if (this.crowdTick[j] === this.tick) return this.crowdN[j];
    // 해시 칸을 직접 돈다. 처음에는 `query`로 목록을 베꼈는데, 목록이 768칸이라 떼 한가운데서는 반경 밖 후보로 차서
    // 곁의 병사를 못 보고 떼를 한산하다고 셌다(리뷰가 짚었다: 774명 배치에서 곁 셋을 0으로). 전원을 담게 키우니 1400명
    // `bench`가 4.3ms에서 7ms로 느려졌다 — 넘었는지만 알면 되므로 `CROWD_MAX`를 넘는 순간 멈춘다.
    const h = this.hash;
    const b = this.crowdCells;
    h.cellsAround(this.x[j], this.y[j], CROWD_RADIUS, b);
    const r2 = CROWD_RADIUS * CROWD_RADIUS;
    let c = 0;
    scan: for (let cy = b[2]; cy <= b[3]; cy++) {
      for (let cx = b[0]; cx <= b[1]; cx++) {
        const cell = cy * h.cols + cx;
        const end = h.cellEnd(cell);
        for (let k = h.cellStart(cell); k < end; k++) {
          const o = h.itemAt(k);
          if (o === j || this.alive[o] === 0 || this.team[o] !== this.team[j]) continue;
          if (distSq(this.x[o], this.y[o], this.x[j], this.y[j]) > r2) continue;
          if (++c > CROWD_MAX) break scan;
        }
      }
    }
    this.crowdTick[j] = this.tick;
    this.crowdN[j] = c;
    return c;
  }

  /** 제 둘레의 적을 다 쫓는(`Sense.Free`) 유닛이 표적을 찾는 반경. 공격 명령의 아군 병사는 넓다(`ATTACK_SENSE`). */
  private freeSense(i: number): number {
    // 궁수는 뺀다 — 표적을 고르는 반경은 따로(사거리 + 30px)지만, 쥔 표적을 놓는 거리(`targetValid`)가 이것을 읽어
    // 사거리 밖으로 달아난 적을 더 오래 쫓았다(리뷰가 짚었다).
    return this.team[i] === Team.Player &&
      this.role[i] === Role.Soldier &&
      this.branch[i] !== BranchCode.Bow &&
      this.routed[i] === 0 &&
      this.sqOrder[this.squad[i]] === Order.Attack &&
      this.sqPathLen[this.squad[i]] === 0
      ? ATTACK_SENSE
      : SENSE_RADIUS;
  }

  private acquireTarget(i: number, sense: Sense): void {
    const myTeam = this.team[i];
    // **궁수는 보이는 적만 고른다.** 바위 너머의 적을 겨누면 화살이 바위에
    // 박혀 쿨다운만 버린다. 근접은 걸어가서 돌아 붙을 수 있으므로 상관없다.
    // 활을 놓은 궁수(`curMelee`)는 근접이라 시야를 보지 않고 제 둘레를 본다.
    const needsSight = this.branch[i] === BranchCode.Bow && !this.curMelee;
    // 곧게 쏠 적을 고르는 반경: 닿는 적만 치는 명령이면 사거리(+틈), 아니면 사거리 + 30px(그만큼은 걸어 나간다).
    const direct = sense === Sense.Reach ? this.curRange + REACH_SLACK : this.sRange[i] + fp(30);
    // 서서 지키는 궁수는 그 밖의 적도 본다 — 곡사로 쏜다(`BOW_LOB`). 곧게 쏠 적이 있으면 그쪽이 먼저다(`considerTarget`).
    const senseRadius = needsSight ? (this.lobs(i, sense) ? this.lobReach(i) : direct) : sense === Sense.Reach ? direct : this.freeSense(i);
    // 대형의 부대원은 지휘관 가까이 온 적만 고른다. 궁수는 예외다 — 제자리에서
    // 사거리 안을 쏘는 것이 곧 진을 지키는 일이다.
    const anchored = sense === Sense.Anchor && !needsSight && this.anchorPoint(i);
    // 병종 본능(`HUNT_RADIUS_PX`): 기병 병사는 적 궁수를, 창 병사는 제 편 궁수를 노리는 기병을 더 넓게 본다. 닿는 적만
    // 치라는 명령(`Sense.Reach`)이면 본능도 쉰다.
    const soldier = this.role[i] === Role.Soldier && this.routed[i] === 0 && sense !== Sense.Reach;
    const instinctR =
      soldier && this.branch[i] === BranchCode.Horse
        ? HUNT_RADIUS
        : soldier && this.branch[i] === BranchCode.Spear
          ? GUARD_RADIUS
          : 0;
    const sr2 = senseRadius * senseRadius;
    const ir2 = instinctR * instinctR;
    // 후보를 견주는 규칙은 `considerTarget` 하나다. 두 갈래(제 둘레의 적, 본능의 표적)가 같은 규칙을 쓰게 문맥을 필드에 둔다
    // — 틱 경로에서 클로저나 객체를 만들지 않는다.
    this.acqAnchored = anchored;
    this.acqAx = this.anch[0];
    this.acqAy = this.anch[1];
    this.acqNeedsSight = needsSight;
    this.acqSpread = needsSight;
    this.acqDirect2 = direct * direct;
    this.acqWide = !needsSight && sense === Sense.Free && senseRadius > SENSE_RADIUS;
    // **궁수에게 달려드는 기병은 곁눈질하지 않는다.** 이미 적 궁수를 쥐었으면 궁수가 아닌 적은 몸이 닿을 때만 본다 — 창은
    // 예외라 창이 가로막으면 멈춰 싸운다(창이 기병을 세운다). 처음에는 이 예외 없이 가장 가까운 쪽을 골라, 달려들던 기병이
    // 앞줄의 칼·민병 곁을 지나며 표적을 그리 바꾸고 맞아 물러났다. 창이 없는 부대의 궁수도 한 명도 죽지 않았다(깊이 18).
    const cur = this.target[i];
    // 궁수: 제가 겨누던 몫은 빼고 견준다(`aimers`) — 넣으면 쥔 표적이 늘 한 명 더 붐벼 보여 틱마다 옮겨 다닌다.
    if (needsSight && this.aimOf[i] !== NO_TARGET) {
      this.aimers[this.aimOf[i]]--;
      this.aimOf[i] = NO_TARGET;
    }
    // 근접: 제가 쫓던 몫은 빼고 견준다(`chasers`) — 궁수와 같은 까닭. 모는 장수(`Role.Leader`)는 플레이어가 고른 쪽을
    // 치므로 값을 매기지 않는다(세기는 한다 — 병사들이 장수가 치는 적을 붐비는 것으로 본다).
    this.acqMelee = !needsSight && this.branch[i] !== BranchCode.Bow && this.role[i] !== Role.Leader;
    if (this.chaseOf[i] !== NO_TARGET) {
      this.chasers[this.chaseOf[i]]--;
      this.chaseOf[i] = NO_TARGET;
    }
    this.acqCur = cur;
    this.acqBestLob = false;
    this.acqCharging =
      instinctR === HUNT_RADIUS &&
      cur !== NO_TARGET &&
      this.alive[cur] === 1 &&
      this.team[cur] !== myTeam &&
      this.branch[cur] === BranchCode.Bow &&
      distSq(this.x[i], this.y[i], this.x[cur], this.y[cur]) <= ir2;
    const touch = this.sRange[i] + REACH_SLACK;
    this.acqTouch2 = touch * touch;
    this.acqBest = NO_TARGET;
    // 비교 값은 거리의 제곱이고, 본능의 표적은 그 4분의 1(거리 절반)로 친다. fp 거리의 제곱은 2^31을 넘으므로 시프트가
    // 아니라 나눗셈으로 줄인다.
    this.acqBestD = Number.MAX_SAFE_INTEGER;

    // 제 둘레의 적. 궁수(기병의 본능)는 아래 명단에서만 본다. 창이 막을 기병은 여기서도 본다 — 이번 틱에 막 궁수로 표적을
    // 바꾼 기병은 틱 시작에 뜬 명단에 없어, 여기서 빼면 한 틱 동안 누구에게도 보이지 않는다. 명단에서 한 번 더 보아도
    // 같은 값이라 앞선 쪽이 남는다.
    if (needsSight) {
      // **활을 쏘는 궁수는 적의 명단을 훑는다**(`foeList`). 곡사까지 보는 반경은 400px을 넘는다 — 해시 조회(`query`)는
      // 그릇이 768칸이라, 반경을 감싼 네모 안에 선 유닛이 그보다 많으면 거리·편을 거르기도 전에 차서 뒤의 적을 못 본다.
      // 리뷰가 재현했다: 곡사 거리 밖 구석의 적 768명이 그릇을 채워 110px 앞의 적을 놓쳤다. 명단은 색인 오름차순이라
      // 같은 값이면 색인이 작은 쪽이 남는다. 궁수에게는 병종 본능이 없다(`instinctR`는 기병·창의 것).
      const other = myTeam === Team.Player ? Team.Enemy : Team.Player;
      const base = other * MAX_UNITS;
      const m = this.foesOf[other];
      for (let k = 0; k < m; k++) {
        const j = this.foeList[base + k];
        if (this.alive[j] === 0) continue;
        const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
        if (d2 <= sr2) this.considerTarget(i, j, d2, Instinct.None);
      }
    }
    const n = needsSight ? 0 : this.hash.query(this.x[i], this.y[i], senseRadius, this.near);
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (this.alive[j] === 0 || this.team[j] === myTeam) continue;
      const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
      if (d2 > sr2) continue;
      const instinct = instinctR > 0 ? this.instinctFor(i, j) : Instinct.None;
      if (instinct === Instinct.Hunt) continue;
      this.considerTarget(i, j, d2, instinct);
    }

    // 본능의 표적은 해시 칸을 직접 돈다. 처음에는 넓힌 반경으로 `query`를 불렀는데, 그릇(`near`)이 768칸이라 떼 한가운데서는
    // 반경 밖·같은 편 후보로 차서 목록 뒤의 궁수를 아예 보지 못했다(리뷰가 짚었다: 같은 편 768명 너머 300px의 궁수를 놓침).
    // 본능의 표적은 틱 시작에 뜬 명단(`bowList`, `hunterList`)만 훑는다. 처음에는 넓힌 반경으로 `query`를 불렀는데, 그릇
    // (`near`)이 768칸이라 떼 한가운데서는 반경 밖·같은 편 후보로 차서 목록 뒤의 궁수를 아예 보지 못했다(리뷰가 짚었다:
    // 같은 편 768명 너머 300px의 궁수를 놓침). 명단은 색인 오름차순이라 동점이면 색인이 작은 쪽이 남는다. 틱 시작에 뜬
    // 명단이라 이번 틱에 막 표적을 바꾼 기병은 다음 틱부터 보인다 — 순서가 고정이라 결정론은 그대로다.
    if (instinctR > 0) {
      const hunts = instinctR === HUNT_RADIUS;
      const other = myTeam === Team.Player ? Team.Enemy : Team.Player;
      const list = hunts ? this.bowList : this.hunterList;
      const base = (hunts ? other : myTeam) * MAX_UNITS;
      const m = hunts ? this.bowsOf[other] : this.huntedOf[myTeam];
      for (let k = 0; k < m; k++) {
        const j = list[base + k];
        if (this.alive[j] === 0) continue;
        const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
        if (d2 > ir2) continue;
        const instinct = this.instinctFor(i, j);
        if (instinct !== Instinct.None) this.considerTarget(i, j, d2, instinct);
      }
    }
    let best = this.acqBest;
    // 반경 안에 적이 없으면 진을 쏘는 적에게 나간다(`sqThreat`).
    if (best === NO_TARGET && anchored) best = this.sqThreat[this.squad[i]];
    this.target[i] = best;
    // 방금 고른 것을 뒤 번호의 궁수가 본다. 고르지 않았으면(가려졌다, 적이 없다) 아무도 겨누지 않는다.
    this.syncAim(i);
    this.syncChase(i);
  }

  /**
   * 근접 유닛 `i`가 쫓는 적을 `chasers`에 맞춘다(`chaseOf`). `syncAim`의 근접 짝 — 궁수는 세지 않는다. 표적을 바꾸거나
   * 놓은 뒤에 부른다. 몇 번을 불러도 같다.
   */
  private syncChase(i: number): void {
    if (this.branch[i] === BranchCode.Bow) return;
    const e = this.target[i];
    const now = this.alive[i] === 1 && e !== NO_TARGET && this.alive[e] === 1 && this.team[e] !== this.team[i] ? e : NO_TARGET;
    const was = this.chaseOf[i];
    if (was === now) return;
    if (was !== NO_TARGET) this.chasers[was]--;
    if (now !== NO_TARGET) this.chasers[now]++;
    this.chaseOf[i] = now;
  }

  /**
   * 궁수 `i`가 겨누는 적을 `aimers`에 맞춘다(`aimOf`). 표적을 바꾸거나 놓은 뒤에 부른다 — 몇 번을 불러도 같다.
   * 궁수가 아니거나 쓰러졌거나 표적이 없으면(죽은 적, 제 편) 아무도 겨누지 않는 것으로 친다.
   */
  private syncAim(i: number): void {
    if (this.branch[i] !== BranchCode.Bow) return;
    const e = this.target[i];
    const now = this.alive[i] === 1 && e !== NO_TARGET && this.alive[e] === 1 && this.team[e] !== this.team[i] ? e : NO_TARGET;
    const was = this.aimOf[i];
    if (was === now) return;
    if (was !== NO_TARGET) this.aimers[was]--;
    if (now !== NO_TARGET) this.aimers[now]++;
    this.aimOf[i] = now;
  }

  /**
   * 곡사를 쓰는 궁수인가(`BOW_LOB`): 활을 쏘는 중이고(`curMelee` 아님), 서서 지키라는 명령이다 — 대형의 부대원
   * (`Sense.Anchor`)과 닿는 적만 치는 대기(`Sense.Reach`). 제 둘레의 적을 쫓는 궁수(공격 명령, 적 궁수, 잔병 소탕)는
   * 사거리까지 걸어가 곧게 쏜다. 고지에서 기다리는 적 궁수도 대기와 같아 곡사를 쓴다.
   */
  private lobs(i: number, sense: Sense): boolean {
    return this.branch[i] === BranchCode.Bow && !this.curMelee && (sense === Sense.Anchor || sense === Sense.Reach);
  }

  /**
   * 곡사가 닿는 거리(`BOW_LOB.rangePct`). 곧게 쏠 적을 고르는 반경(사거리 + 30px)보다 작지는 않다 — 배율을 낮게 잡아도
   * 서서 지키는 궁수가 전보다 덜 보게 되지는 않는다.
   */
  private lobReach(i: number): number {
    return Math.max(this.sRange[i] + fp(30), Math.floor((this.sRange[i] * BOW_LOB.rangePct) / 100));
  }

  /**
   * 후보 `j`를 견준다(`acquireTarget`). 앞서면 `acqBest`로. 같은 값이면 먼저 본 쪽이 남는다 — 도는 순서(해시 조회·칸 순서,
   * 칸 안은 색인 순)가 고정이라 결정론은 그대로다.
   */
  private considerTarget(i: number, j: number, d2: number, instinct: Instinct): void {
    if (this.acqCharging && instinct === Instinct.None && this.branch[j] !== BranchCode.Spear && d2 > this.acqTouch2) return;
    let score = instinct === Instinct.None ? d2 : Math.floor(d2 / 4);
    // 곡사로만 닿는 적인가. 곧게 쏠 적이 하나라도 있으면 곡사의 적은 고르지 않는다 — **갈래를 먼저 견주고** 같은 갈래
    // 안에서만 점수를 견준다. 처음에는 곡사의 적에 4,000px을 얹어 한 점수로 견줬는데, 겨누는 궁수가 150명이면 곧게 쏠
    // 적(110 + 150 × 30 = 4,610px)이 곡사의 적(360 + 4,000px)보다 멀어져 순서가 뒤집혔다(리뷰가 재현했다).
    const lob = this.acqSpread && d2 > this.acqDirect2;
    if (this.acqSpread) {
      // 궁수: 이미 겨누는 제 편 궁수 한 명마다 그만큼 더 먼 것으로 친다(`BOW_SPREAD`). 쥔 표적은 조금 가깝게.
      // 거리의 제곱이 아니라 거리에 더해야 "몇 px 더 먼 셈"이 된다.
      let d = isqrt(d2) + this.aimers[j] * BOW_SPREAD_STEP;
      if (j === this.acqCur) d = Math.max(0, d - BOW_STICK);
      score = d * d;
    } else if (this.acqMelee) {
      // 근접: 이미 `free`명이 쫓는 적은 넘는 한 명마다 그만큼 더 먼 것으로 친다(`MELEE_SPREAD`). 쥔 표적은 조금 가깝게.
      // 붐비지도 않고 쥔 표적도 아니면 예전 점수 그대로다 — 제곱근을 내지 않는다(후보 대부분이 그렇다).
      const role = this.role[j];
      const free = role === Role.Soldier ? MELEE_SPREAD.free : MELEE_SPREAD.freeOnLeader;
      // 지금 닿는 적(`acqTouch2`)은 붐벼도 값을 매기지 않는다 — 이미 붙어 있는 사람이 여럿이 한 명을 치는 것은 몰림이
      // 아니라 에워싼 것이다. 값은 **걸어가야 하는** 적에게만 매긴다(닿지 못하는 뒷사람이 줄을 서는 것이 몰림이다).
      //
      // `chasers`는 **저 말고** 쫓는 사람의 수다(`acquireTarget`이 제 몫을 빼고 부른다). 그러니 저까지 넣어 `free`를 넘는
      // 몫이 값이다: 넷이 쫓는 적에 붙으려는 다섯째는 한 명 넘는다. 처음에는 저를 빼고 셌더니 다섯째까지 값이 없고
      // 여섯째부터 매겨졌다 — "네명까지 같은적 허용. 다섯 째부터"와 한 명 어긋났고, 시험도 고르는 병사를 빼고 세어 그
      // 어긋남을 그대로 지켰다(리뷰가 잡았다).
      const over = d2 > this.acqTouch2 ? this.chasers[j] + 1 - free : 0;
      // 쥔 표적을 가깝게 쳐 주는 것(`stickPx`)은 그 적이 **붐비기 직전이거나 붐빌 때만**이다(저까지 넣어 `free - 1`명 이상이
      // 쫓는다) — 나누기 때문에 둘 사이를 오가는 것을 막으려는 것이라, 붐비지 않는 곳에서는 예전의 견주기를 그대로 둔다.
      // 처음에는 늘 가깝게 쳤더니, 칼을 쥐고 있던 창이 궁수를 노리는 기병(본능의 표적, 거리 절반 셈)으로 갈아타지 않았다
      // — 90px의 칼이 78px 셈이 되어 80px 셈의 기병을 이겼다(`instinct.test.ts`가 잡았다).
      const stick = j === this.acqCur && this.chasers[j] + 1 >= free - 1;
      if (over > 0 || stick) {
        let d = isqrt(score) + (over > 0 ? over * MELEE_SPREAD_STEP : 0);
        if (stick) d = Math.max(0, d - MELEE_STICK);
        score = d * d;
      }
    }
    const has = this.acqBest !== NO_TARGET;
    if (has && lob && !this.acqBestLob) return;
    if (!(has && !lob && this.acqBestLob) && score >= this.acqBestD) return;
    // 대형의 부대원은 지휘관 가까이 온 적만 고른다. 궁수를 노리는 기병을 막으러 가는 창은 진의 반경 밖까지 나간다.
    if (
      this.acqAnchored &&
      instinct !== Instinct.Guard &&
      distSq(this.acqAx, this.acqAy, this.x[j], this.y[j]) > FORMATION_RADIUS * FORMATION_RADIUS
    ) {
      return;
    }
    // 시야 검사는 가장 가까운 후보에만 하고 싶지만, 그러면 그 하나가 가렸을
    // 때 뒤의 보이는 적을 놓친다. 후보가 앞설 때만 재므로 횟수는 적다.
    if (this.acqNeedsSight && this.grid.blockedBetweenFp(this.x[i], this.y[i], this.x[j], this.y[j])) return;
    // 넓힌 반경(`ATTACK_SENSE`)의 바깥 띠에서는 떼 속의 적을 고르지 않는다(`CROWD_MAX`). 넓힌 반경일 때만 — 궁수의
    // 반경(사거리 + 30px)도 170px을 넘어, 처음에는 대형의 궁수까지 걸려 대형 판이 바뀌었다.
    if (this.acqWide && instinct === Instinct.None && d2 > SENSE_RADIUS * SENSE_RADIUS && this.crowdAround(j) > CROWD_MAX) return;
    this.acqBestD = score;
    this.acqBest = j;
    this.acqBestLob = lob;
  }

  /**
   * 이 거리(제곱)보다 먼 적은 곡사로 쏜다. 대형의 궁수는 사거리 + 30px까지는 걸어 나가 곧게 쏘고(표적을 고르는 반경과
   * 같다), 대기는 걷지 않으니 닿는 거리 밖이 다 곡사다.
   */
  private lobFrom(i: number, sense: Sense, e: number): number {
    const r = sense === Sense.Reach ? this.curRange + this.sRadius[e] : this.sRange[i] + fp(30);
    return r * r;
  }

  private attack(i: number, e: number): void {
    // 방패를 든 동안은 더디다(`SHIELD_PENALTY`). 부서지면 벌점이 사라진다.
    this.cd[i] = shieldedCd(this.sCd[i], this.shield[i] > 0);

    if (this.branch[i] === BranchCode.Bow) {
      if (!this.curMelee) {
        // 일제사격(`volley`) 동안 궁수는 두 배로 빨리 **쏜다**(`VOLLEY_RATE_PCT`). 화살을 쏠 때만이다 — 처음에는 활을 놓고
        // 칠 때(`curMelee`)도 간격이 반으로 줄어, 화살을 다 쓴 궁수의 칼질까지 빨라졌다(리뷰가 짚었다).
        if (this.volley[i] > 0) this.cd[i] = Math.floor((this.cd[i] * VOLLEY_RATE_PCT) / 100);
        // 화살의 세기는 **쏘는 순간의** 피로로 정해 싣는다(`fatigued`) — 맞는 순간 사수의 피로로 깎았더니 첫 화살부터 깎이고,
        // 날아가는 동안 사수가 지치면 이미 떠난 화살까지 약해졌다(리뷰가 짚었다). 쏘지 못했으면(가려짐·자리 없음) 지치지 않는다.
        // 일제사격(`volley`) 동안의 화살은 더 아프다.
        // 방패를 든 동안은 화살도 약하다(`SHIELD_PENALTY`) — 병사 궁수는 방패가 없어 값이 그대로고, 활과 방패를 함께 든
        // 장수·부하장수에게만 걸린다. 처음에는 간격 벌점만 붙고 공격 벌점이 빠져, 카드가 말하는 −10%가 거짓이었다(리뷰).
        let arrow = this.fatigued(i, shieldedAtk(this.sAtk[i], this.shield[i] > 0));
        if (this.volley[i] > 0) arrow = Math.floor((arrow * (100 + this.skillPct)) / 100);
        if (this.fireArrow(i, e, arrow)) this.tire(i, FATIGUE_SWING);
        return;
      }
      // 활을 놓고 친다(`BOW_MELEE`). 화살이 아니므로 화살의 규칙은 붙지 않는다.
      this.applyDamage(i, e, Math.floor((shieldedAtk(this.sAtk[i], this.shield[i] > 0) * BOW_MELEE.atkPct) / 100), false);
      this.tire(i, FATIGUE_SWING);
      return;
    }

    // 치는 순간의 발밑도 본다. 붙이는 쪽에만 두면 가장자리에서 샌다.
    //
    // 예전에는 접근하는 가지에서만 돌격을 버렸다. 그러면 기병이 평지에서
    // 속도를 붙인 채 숲 가장자리에 선 적을 그대로 들이받는다. 재 보니 표적이
    // 숲 칸 안 20px까지 들어가 있어도, 심지어 기병이 숲 칸 안에서 쳐도
    // 돌격이 들어갔다. 숲 덩어리가 반지름 두 칸이라 대부분이 가장자리이고,
    // 그러면 "창 없는 편성이 기병에 대응할 자리"가 안쪽 서너 칸만 남는다.
    //
    // 둘 중 하나만 거친 땅이어도 버린다. 미는 쪽도 밀리는 쪽도 나무 사이에서는
    // 충격을 주고받을 자리가 없다.
    if (
      roughGround(this.grid.cellAtFp(this.x[i], this.y[i])) ||
      roughGround(this.grid.cellAtFp(this.x[e], this.y[e]))
    ) {
      this.charge[i] = 0;
    }

    let dmg = shieldedAtk(this.sAtk[i], this.shield[i] > 0);
    if (this.charge[i] >= CHARGE_TICKS) {
      dmg = Math.floor((dmg * CHARGE_PCT) / 100);
      this.charge[i] = 0;
      this.knockback(i, e, fp(9));
    }
    this.applyDamage(i, e, dmg, false);
    // 치면 지친다(활을 쏘아도) — 친 **뒤에**. 앞에 두었더니 첫 칼부터 지친 몸으로 쳐 판정식(`hitDamage`)과 어긋났다.
    this.tire(i, FATIGUE_SWING);
  }

  /**
   * 장수의 함성. 둘레(`ROAR_RADIUS`)의 적 병사는 사기가 기개의 `ROAR_PCT`만큼 깎여, 바닥에 닿으면 무너져 달아난다.
   * 아군 병사는 사기를 채우고, 달아나던 이는 돌아선다. 장수·동료·적장은 사기가 없으므로 닿지 않는다.
   * 이웃은 격자 칸 순서로 한 번 돈다 — 각자의 사기만 바꾸므로 순서가 결과를 바꾸지 않는다. 한 번에 닿는 수에 상한을
   * 두려면(휩쓸기의 `CLEAVE_MAX`처럼) 이 순서가 누가 먼저인지를 정하게 되므로 그때 다시 본다.
   */
  /** 장수 기술을 정한다(판을 세울 때 한 번, `sim/skills.ts`). */
  setSkill(code: number, level: number, p: SkillParams): void {
    this.skillCode = code;
    this.skillLevel = level;
    this.skillCdMax = p.cd;
    this.skillTicks = p.ticks;
    this.skillPct = p.pct;
    this.skillRadius = p.radius > 0 ? p.radius : ROAR_RADIUS;
  }

  /** 장수 기술을 쓴다. 쓸 수 없으면(쓰러짐·대기 중·첫 틱) 아무것도 하지 않는다. */
  private leaderSkill(i: number): void {
    // 첫 틱에는 격자가 아직 지어지지 않아(명령은 격자를 짓기 전에 판정한다) 둘레에 아무도 없다. 대기만 쓰고
    // 헛외치지 않게 막는다(리뷰가 짚었다).
    if (this.alive[i] === 0 || this.roarCd > 0 || this.tick <= 1) return;
    // 걸 대상이 없으면 쓰지 않는다 — 처음에는 궁수 없는 분대를 고르고 일제사격을 쓰면 아무 일 없이 대기만 썼다(QA).
    if (this.skillTargets() === 0) return;
    this.roarCd = this.skillCdMax;
    this.roarCount++;
    if (this.skillCode === SKILL_CODE.phalanx) this.leaderPhalanx();
    else if (this.skillCode === SKILL_CODE.volley) this.leaderVolley();
    else this.leaderRoar(i);
  }

  /**
   * 지금 장수 기술이 걸릴 아군 수. 함성은 장수 둘레에 거는 것이라 늘 1(장수 자신), 방진은 고른 분대의 아군, 일제사격은
   * 고른 분대의 궁수. 0이면 기술을 써도 아무 일도 없다(`leaderSkill`) — 화면이 단추를 흐리게 하는 데도 쓴다. 상태를
   * 바꾸지 않는다.
   */
  skillTargets(): number {
    if (this.skillCode !== SKILL_CODE.phalanx && this.skillCode !== SKILL_CODE.volley) return 1;
    const bowsOnly = this.skillCode === SKILL_CODE.volley;
    let n = 0;
    for (let j = 0; j < this.count; j++) {
      if (this.alive[j] === 0 || this.team[j] !== Team.Player) continue;
      if (bowsOnly && this.branch[j] !== BranchCode.Bow) continue;
      if ((this.selectedMask & (1 << this.squad[j])) !== 0) n++;
    }
    return n;
  }

  /**
   * 방진. 고른 분대(`selectedMask`)의 아군 모두 — 병사·동료, 0번 분대면 장수도 — 가 `skillTicks` 동안 덜 아프게 맞고
   * 밀리지 않으며 느리다. 유닛 색인 순으로 한 번 — 각자의 값만 쓰므로 순서가 결과를 바꾸지 않는다.
   */
  private leaderPhalanx(): void {
    for (let j = 0; j < this.count; j++) {
      if (this.alive[j] === 0 || this.team[j] !== Team.Player) continue;
      if ((this.selectedMask & (1 << this.squad[j])) === 0) continue;
      this.guard[j] = this.skillTicks;
    }
  }

  /**
   * 일제사격. 고른 분대의 궁수가 곧바로 쏠 수 있고(대기를 비운다), `skillTicks` 동안 화살이 더 아프다. 활을 든 걷는
   * 장수·동료도 궁수의 가지라 같이 걸린다(121부터 — 그 전에는 영웅이 활을 들 수 없었다).
   */
  private leaderVolley(): void {
    for (let j = 0; j < this.count; j++) {
      if (this.alive[j] === 0 || this.team[j] !== Team.Player || this.branch[j] !== BranchCode.Bow) continue;
      if ((this.selectedMask & (1 << this.squad[j])) === 0) continue;
      this.cd[j] = 0;
      this.volley[j] = this.skillTicks;
    }
  }

  private leaderRoar(i: number): void {
    const team = this.team[i];
    // **외친 장수 자신과 둘레의 동료도 기세를 받는다**(타격 +`ROAR_ATK_PCT`%). 처음에는 병사에게만 걸었다 — 장수·동료는
    // 사기가 없어(사기를 채우는 몫이 뜻이 없다) 뺐는데, 기세는 사기와 무관한 타격이다. 플레이한 사람이 청했다: "함성,
    // 장수와 동료들에게도 적용되게 해줘." 외치고 앞장서 휩쓸기로 몰아치는 한 수가 생긴다. 적의 사기 꺾기는 그대로
    // 병사에게만이다.
    this.rallied[i] = this.skillTicks;
    const n = this.hash.query(this.x[i], this.y[i], this.skillRadius, this.near);
    const r2 = this.skillRadius * this.skillRadius;
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (this.alive[j] === 0) continue;
      const dx = this.x[j] - this.x[i];
      const dy = this.y[j] - this.y[i];
      if (dx * dx + dy * dy > r2) continue;
      if (this.role[j] === Role.Companion && this.team[j] === team) {
        this.rallied[j] = this.skillTicks;
        continue;
      }
      if (this.role[j] !== Role.Soldier) continue;
      if (this.team[j] === team) {
        this.morale[j] = this.sGrit[j];
        this.routed[j] = 0;
        this.rallied[j] = this.skillTicks;
        continue;
      }
      if (this.routed[j] > 0) continue;
      this.shakenTick[j] = this.tick;
      this.morale[j] -= Math.floor((this.sGrit[j] * ROAR_PCT) / 100);
      if (this.morale[j] <= 0) this.rout(j);
    }
  }

  /** 장수의 휩쓸기. 정면 반원 안의 적을 한꺼번에 친다. 일당백의 근거다. */
  private leaderStrike(i: number): void {
    if (this.alive[i] === 0 || this.cd[i] > 0) return;
    // 활을 든 사람은 휩쓸지 않는다(명령으로 불러도) — 사거리가 활의 것이라 정면 300px를 한꺼번에 벤다.
    if (this.branch[i] === BranchCode.Bow) return;
    // 방패를 든 장수도 병사처럼 더디고 약하다(`SHIELD_PENALTY`). 부서지면 벌점이 사라진다.
    const shielded = this.shield[i] > 0;
    this.cd[i] = shieldedCd(this.sCd[i], shielded);
    const hit = shieldedAtk(this.sAtk[i], shielded);
    this.strikeCount++;
    this.strikeBy = i;
    this.strikeFaceX = this.faceX[i];
    this.strikeFaceY = this.faceY[i];
    const reach = this.sRange[i] + fp(10);
    const n = this.hash.query(this.x[i], this.y[i], reach + fp(8), this.near);
    let hits = 0;
    for (let k = 0; k < n && hits < CLEAVE_MAX; k++) {
      const j = this.near[k];
      if (this.alive[j] === 0 || this.team[j] === this.team[i]) continue;
      const dx = this.x[j] - this.x[i];
      const dy = this.y[j] - this.y[i];
      const want = reach + this.sRadius[j];
      if (dx * dx + dy * dy > want * want) continue;
      // 정면 반원: 바라보는 방향과의 내적이 음수면 등 뒤다.
      if (dx * this.faceX[i] + dy * this.faceY[i] < 0) continue;
      this.applyDamage(i, j, hit, false);
      this.knockback(i, j, fp(7));
      hits++;
    }
  }

  /**
   * 피해 판정. troops.ts의 hitDamage와 같은 식이어야 한다.
   * 한쪽만 고치면 설계 페이지의 검산이 거짓말이 된다.
   */
  /** `ranged`: 화살인가. 화살만 방어를 절반 받고(`ARROW_ARMOR_PCT`), 포위가 붙지 않고, 숲에 가린다. 활을 놓고 친 궁수는 아니다. */
  private applyDamage(att: number, dfd: number, base: number, ranged: boolean): void {
    // 칼이 닿았다(`meleeJoined`) — 화살이 아닌 첫 판정에서 켜지고 꺼지지 않는다.
    if (!ranged) this.meleeJoined = 1;
    const ab = this.branch[att];
    const pct = COUNTER_TABLE[ab * 5 + this.branch[dfd]];

    // 지휘 보정. 동료가 살아 있는 동안만 휘하 분대에 붙는다.
    let hit = base;
    // 함성을 들은 병사는 기세가 올라 더 세게 친다.
    // 기세는 함성만 건다(`rallied`) — 기술이 함성일 때의 몫(`skillPct`).
    if (this.rallied[att] > 0) hit = Math.floor((hit * (100 + this.skillPct)) / 100);
    // 지친 병사는 약하게 친다(`FATIGUE_ATK_PCT`). 화살은 쏠 때 이미 깎아 실었다(`attack`) — 여기서 다시 깎지 않는다.
    if (!ranged) hit = this.fatigued(att, hit);
    if (this.team[att] === Team.Player) {
      const sq = this.squad[att];
      if (this.sqAtkPct[sq] !== 100 && this.captainActive(sq)) {
        hit = Math.floor((hit * this.sqAtkPct[sq]) / 100);
      }
      // 지휘관이 잘 부리는 병종의 병사는 더 세게 친다(`Favor`).
      if (this.sqFavAtk[sq] > 0 && this.sqFavBranch[sq] === this.branch[att] && this.role[att] === Role.Soldier && this.captainActive(sq)) {
        hit = Math.floor((hit * (100 + this.sqFavAtk[sq])) / 100);
      }
      // 동료 고유 기술의 타격 몫(`mateAtk`). 분대의 누구에게나 — 병사·동료, 0번 분대면 장수도.
      if (this.mateAtk[sq] > 0 && this.mateRadius[sq] === 0 && this.mateActive(sq)) hit = Math.floor((hit * (100 + this.mateAtk[sq])) / 100);
      // 둘레 기술(`aid`)은 분대가 아니라 걸린 사람에게 붙는다. 건 동료가 쓰러지면 꺼진다.
      if (this.aid[att] > 0) {
        const by = this.aidBy[att];
        const w = this.mateWho[by];
        if (this.mateAtk[by] > 0 && w !== NO_TARGET && this.alive[w] === 1) hit = Math.floor((hit * (100 + this.mateAtk[by])) / 100);
      }
    } else if (this.role[att] === Role.Soldier) {
      // 적의 병사도 제 부대의 대장이 살아 있으면 더 세게 친다(`FOE_SQUADS`).
      const fs = this.foeSquad[att];
      if (this.foeAtkPct[fs] !== 100 && this.foeCaptainActive(fs)) hit = Math.floor((hit * this.foeAtkPct[fs]) / 100);
    }

    // 화살은 한 점에 힘을 모으므로 방어를 절반만 적용받는다.
    let armor =
      ranged
        ? Math.floor((this.sDef[dfd] * ARROW_ARMOR_PCT) / 100)
        : this.sDef[dfd];
    if (this.team[dfd] === Team.Player) {
      const sq = this.squad[dfd];
      if (this.sqDefBonus[sq] !== 0 && this.captainActive(sq)) armor += this.sqDefBonus[sq];
      if (this.sqFavDef[sq] > 0 && this.sqFavBranch[sq] === this.branch[dfd] && this.role[dfd] === Role.Soldier && this.captainActive(sq)) {
        armor += this.sqFavDef[sq];
      }
    }
    // 관통은 방어를 깎는다. 지휘 보정으로 올린 방어도 같이 깎인다.
    // 뺄셈형 방어라 약한 공격이 통째로 막히는 문제를 여기서 푼다.
    armor = Math.max(0, armor - this.sPierce[att]);
    // 여울에 선 사람은 막아낼 손이 굼뜨다(`FORD_DEF_MALUS`).
    const attGround = this.grid.cellAtFp(this.x[att], this.y[att]);
    const dfdGround = this.grid.cellAtFp(this.x[dfd], this.y[dfd]);
    if (dfdGround === Cell.Ford) armor = Math.max(0, armor - FORD_DEF_MALUS);
    // 언덕에서 기다리던 적이 맞으면 모두 내려온다(`enemyHold`).
    if (this.team[dfd] === Team.Enemy) this.enemyHold = 0;

    // 포위. 여러 면이 동시에 잡히면 막아낼 손이 모자란다. 한 면은 정면으로
    // 맞서면 되는 것이므로 하나를 뺀다. 갈라서 감싸는 편성이 뭉쳐 미는 것보다
    // 세지는 자리가 여기다.
    //
    // 화살에는 붙지 않는다. 포위가 뺏는 것은 막아낼 손인데 화살은 막는 것이
    // 아니다. 여기에 붙이면 난전 한복판에 대고 쏘는 것만으로 궁수가 통째로
    // 세지고, 활의 값어치를 사거리에서 재기로 한 판단이 무너진다.
    const sides = ranged ? 0 : this.sidesPressed(dfd);
    if (sides > 1) {
      hit = Math.floor((hit * (100 + (sides - 1) * SURROUND_PCT)) / 100);
    }

    // 지형. 선 자리가 타격을 바꾼다.
    //
    // 언덕은 위에서 칠 때만 값이 붙는다. 양쪽 다 언덕이면 서로 +15%를
    // 주고받아 아무것도 바뀌지 않으므로 아예 빼고, 언덕을 차지하는 것이
    // 이득이 되게 둔다. 숲은 반대로 맞는 쪽을 돕되 화살에만 그렇다. 나무가
    // 칼을 막아 주지는 않는다.
    if (attGround === Cell.Hill && dfdGround !== Cell.Hill) {
      hit = Math.floor((hit * HILL_PCT) / 100);
    }
    if (ranged && dfdGround === Cell.Forest) {
      hit = Math.floor((hit * FOREST_COVER_PCT) / 100);
    }

    // 배후 공격. 방어자가 바라보는 방향의 반대쪽에서 들어오면 크게 들어간다.
    const bx = this.x[att] - this.x[dfd];
    const by = this.y[att] - this.y[dfd];
    const facing = bx * this.faceX[dfd] + by * this.faceY[dfd];
    if (facing < 0) {
      hit = Math.floor((hit * FLANK_PCT) / 100);
      if (this.role[dfd] === Role.Soldier) this.morale[dfd] -= FLANK_MORALE;
    }

    let dmg = Math.floor((hit * pct) / 100) - armor;
    // 작은 요동. 시드 난수라 재현된다.
    dmg += this.rng.range(-1, 1);
    // 방진(`guard`): 받는 피해를 줄인다. 방어 뒤에 — 방어처럼 약한 공격을 통째로 막지는 않는다(최소 1은 그대로).
    if (this.guard[dfd] > 0) dmg = Math.floor((dmg * (100 - this.skillPct)) / 100);
    // 동료 고유 기술의 덜 맞는 몫(`mateCut`). 방진과 겹치면 곱으로 준다.
    if (this.team[dfd] === Team.Player) {
      const dq = this.squad[dfd];
      if (this.mateCut[dq] > 0 && this.mateRadius[dq] === 0 && this.mateActive(dq)) dmg = Math.floor((dmg * (100 - this.mateCut[dq])) / 100);
      if (this.aid[dfd] > 0) {
        const by = this.aidBy[dfd];
        const w = this.mateWho[by];
        if (this.mateCut[by] > 0 && w !== NO_TARGET && this.alive[w] === 1) dmg = Math.floor((dmg * (100 - this.mateCut[by])) / 100);
      }
    }
    if (dmg < 1) dmg = 1;

    this.hp[dfd] -= dmg;
    this.hitFlash[dfd] = 4;
    this.fxAdd(ranged ? FX.ArrowHit : ab === BranchCode.Spear || ab === BranchCode.Horse ? FX.Thrust : FX.Cut, dfd);
    if (att === this.leaderIdx) this.leaderDealt[dfd] += dmg;
    this.hitsDealt[att]++;
    this.hitsTaken[dfd]++;
    // 맞으면 지친다.
    this.tire(dfd, FATIGUE_HIT);
    this.stats.damage[this.team[att]] += dmg;

    if (this.hp[dfd] <= 0) this.kill(dfd, att);
  }

  /** 소리 전용 셈(`fx`)에 한 건을 더한다. 자리는 그 유닛이 선 곳(px). */
  private fxAdd(kind: number, i: number): void {
    const o = kind * 3;
    this.fx[o]++;
    this.fx[o + 1] += this.x[i] >> 8;
    this.fx[o + 2] += this.y[i] >> 8;
  }

  private knockback(att: number, dfd: number, power: number): void {
    // 방진은 밀리지 않는다.
    if (this.guard[dfd] > 0) return;
    fpNormalize(this.x[dfd] - this.x[att], this.y[dfd] - this.y[att], this.tmp);
    const am = this.sMass[att];
    const dm = this.sMass[dfd];
    const scale = Math.floor((power * am) / (am + dm));
    this.knockX[dfd] += fpMul(this.tmp[0], scale);
    this.knockY[dfd] += fpMul(this.tmp[1], scale);
  }

  private kill(i: number, by: number): void {
    this.alive[i] = 0;
    this.fxAdd(FX.Fall, i);
    // 쓰러지거나 빠져나간 궁수는 아무도 겨누지 않는다(`aimOf`). 근접 유닛도 같다(`chaseOf`).
    this.syncAim(i);
    this.syncChase(i);
    this.target[i] = NO_TARGET;
    const victimTeam = this.team[i];
    const killerTeam = this.team[by];
    // 둘레 기술을 가진 동료(지장)의 둘레 안에서 아군 병사가 쓰러졌는가(`mateFell`). 다음 틱 시작에 본다.
    if (victimTeam === Team.Player && this.role[i] === Role.Soldier) {
      for (let sq = 0; sq < MAX_SQUADS; sq++) {
        const r = this.mateRadius[sq];
        const w = this.mateWho[sq];
        if (r === 0 || w === NO_TARGET || this.alive[w] === 0) continue;
        if (distSq(this.x[i], this.y[i], this.x[w], this.y[w]) <= r * r) this.mateFell[sq] = 1;
      }
    }

    if (this.role[i] === Role.Companion) {
      // 동료는 죽지 않는다. 쓰러져 실려 나가고 다음 출정 몇 번을 쉰다.
      this.downed[i] = 1;
      this.companionsDowned++;
    }
    // 잃은 병력은 **병사만** 센다. 생환 배율이 재는 것이 병사의 영구 사망이기
    // 때문이다. 동료는 쓰러질 뿐이고 장수는 데려간 병력이 아니다.
    //
    // 예전에는 동료만 빼고 장수는 셌다. 아무도 이 값을 읽지 않아서 드러나지
    // 않다가, 지형이 전투를 바꾸면서 장수가 죽는 판이 생겨 시험이 걸렸다.
    if (this.role[i] === Role.Soldier) this.stats.losses[victimTeam]++;

    this.stats.kills[killerTeam]++;
    this.stats.killScore[killerTeam] += this.sPower[i];
    if (this.killCount[by] < 0xffff) this.killCount[by]++;
    this.killPower[by] += this.sPower[i];
    if (by === this.leaderIdx) this.leaderKilled[i] = 1;
    // 적장의 죽음이 먼저다. 둘레 충격(-9)을 먼저 치면 거기서 막 무너진 곁의 병사가 "이미 달아나는 이"로 빠져 ▼가 없었다
    // (리뷰가 짚었다).
    if (this.role[i] === Role.Chief) this.chiefFall(victimTeam, i);
    if (i === this.leaderIdx) this.leaderFall();
    this.shockMorale(i, victimTeam);
  }

  /**
   * 장수가 쓰러졌다: 모든 분대가 후퇴한다(경로도 지운다). 뒤로 오는 명령은 받지 않는다(`applyCommands`). 병사는 아래
   * 가장자리로 달려 빠져나가고(`tryExit`), 빠져나간 병사는 살아 돌아온다(`meta/run.ts`).
   */
  private leaderFall(): void {
    this.leaderFellAt = this.tick;
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      this.sqOrder[sq] = Order.Retreat;
      this.sqPathLen[sq] = 0;
      this.sqPathAt[sq] = 0;
    }
  }

  /**
   * 적장이 쓰러졌다: 그 편의 모든 병사가 사기를 크게 잃는다(`CHIEF_FALL_PCT`). 이미 달아나는 이는 건너뛴다(함성과 같다).
   * 적장 결전(`warlords`)에서 마지막 적장이면 모두 무너진다 — "적장을 모두 베면 끝".
   */
  private chiefFall(team: number, who: number): void {
    // **부장이 쓰러지면 그 부대만 흔들린다**(`FOE_SQUADS.fallPct`): 그 부대의 병사가 기개의 40%를 잃는다(114에서는 60%). 적장
    // (총대장, 결전은 0·1·2번 부대의 대장 셋 — `foeHeadSquad`)이 쓰러지면 아래의 예전 규칙대로 전군이 잃는다.
    // 117까지 결전에는 부장이 없어 이 갈래를 통째로 건너뛰었다 — 결전에 부장을 세우며(118) 부대 번호로 가른다. 그대로
    // 두었다면 부장 하나가 쓰러질 때마다 전군이 40%를 잃었다.
    if (team === Team.Enemy && !foeHeadSquad(this.foeSquad[who], this.warlords) && this.foeCmd[this.foeSquad[who]] === who) {
      const fs = this.foeSquad[who];
      for (let j = 0; j < this.count; j++) {
        if (this.alive[j] === 0 || this.team[j] !== team || this.role[j] !== Role.Soldier || this.foeSquad[j] !== fs) continue;
        if (this.routed[j] > 0) continue;
        this.shakenTick[j] = this.tick;
        this.morale[j] -= Math.floor((this.sGrit[j] * FOE_SQUADS.fallPct) / 100);
        if (this.morale[j] <= 0) this.rout(j);
      }
      return;
    }
    // 마지막 적장인가: 산 **적장**만 센다(`headsAlive`). 117까지는 살아 있는 대장을 모두 셌다 — 부장이 산 채로는 적장 셋을
    // 다 베어도 무너지지 않았을 것이다.
    const last = this.warlords && this.headsAlive() === 0;
    for (let j = 0; j < this.count; j++) {
      if (this.alive[j] === 0 || this.team[j] !== team || this.role[j] !== Role.Soldier) continue;
      if (this.routed[j] > 0) continue;
      this.shakenTick[j] = this.tick;
      this.morale[j] -= last ? this.morale[j] : Math.floor((this.sGrit[j] * CHIEF_FALL_PCT) / 100);
      if (this.morale[j] <= 0) this.rout(j);
    }
  }

  /** 무너진다: 한동안 달아나고, 돌아설 때는 사기가 절반이다. 전우의 죽음(`shockMorale`)과 함성이 같이 쓴다. */
  /**
   * 편·분대마다 사기를 센다(`grpMorale`, `grpGrit`). 달아나는 병사는 사기 0으로 친다 — 사기 값(`rout`이 기개의 절반으로
   * 둔다)이 아니라 버티는지가 부대의 사기다. 적은 모두 0번 분대로 선다(한 군대).
   */
  private tallyMorale(): void {
    this.grpMorale.fill(0);
    this.grpGrit.fill(0);
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 0 || this.role[i] !== Role.Soldier) continue;
      const g = this.team[i] * MAX_SQUADS + Math.min(this.squad[i], MAX_SQUADS - 1);
      this.grpGrit[g] += this.sGrit[i];
      if (this.routed[i] === 0) this.grpMorale[g] += Math.max(0, this.morale[i]);
    }
  }

  /** 제 편 출구(가장자리)에서 `RALLY_EDGE_ROWS`줄 안인가. */
  private nearExit(i: number): boolean {
    const row = Math.floor(this.y[i] / TILE_FP);
    return this.team[i] === Team.Player ? row >= this.grid.rows - RALLY_EDGE_ROWS : row < RALLY_EDGE_ROWS;
  }

  /** 병사가 속한 부대의 평균 사기(기개 대비 백분율, 정수). 부대가 비었으면 0. */
  private rallyPct(i: number): number {
    const g = this.team[i] * MAX_SQUADS + Math.min(this.squad[i], MAX_SQUADS - 1);
    const grit = this.grpGrit[g];
    return grit > 0 ? Math.floor((this.grpMorale[g] * 100) / grit) : 0;
  }

  /**
   * 기병 분대마다 이번 틱에 돌아갈 곳을 정한다(`SWEEP`). 틱 시작에 한 번 — `countFighters`(벽·궁수 명단)와
   * `countSquads`(분대의 기병 수) 뒤여야 한다. 분대 번호 오름차순, 궁수 명단은 색인 오름차순이라 순서가 고정이다.
   *
   * 전장은 아군이 아래, 적이 위로 마주 선다(`flankPoint`와 같은 전제). 벽의 앞은 y가 큰 쪽이고, 도는 것은 옆(x)이다.
   */
  private planSweeps(): void {
    const foe = Team.Enemy;
    const b = foe * 4;
    for (let sq = 1; sq < MAX_SQUADS; sq++) {
      this.sweepOn[sq] = 0;
      if (this.sqSweep[sq] === Sweep.Done) continue;
      if (this.sqPathLen[sq] !== 0) continue;
      // 대형의 기병 분대도 스스로 나선다(`sqAuto`): 장수 곁에 진을 짠 채이고 플레이어가 아직 그 분대에 명령을 내리지
      // 않았을 때. 돌 수 있다고 정해지는 틱에 명령이 공격으로 바뀐다(아래).
      // **칼이 닿기 전에만, 플레이어가 장수나 부하장수를 직접 몰고 있지 않을 때만.** 처음에는 조건이 맞는 첫 틱에 늘 나섰다 — 장수를
      // 앞으로 몰고 달리는 판에서 곁을 지키던 기병이 따로 돌아가 버려, 깊이 20 너머의 이김이 104판에 86 → 65로 내렸다
      // (QA). 몰고 있는 플레이어는 손수 지휘하는 사람이다 — 기병도 그의 명령을 기다린다. 누구를 몰든 같다(장수든, 고른
      // 분대의 부하장수든 — 모든 분대가 기다린다). 한 번 싸움이 붙은 뒤에는 진에서 싸우던 기병을 빼내지 않는다.
      const driving = this.ctrl !== NO_TARGET && (this.inputDx !== 0 || this.inputDy !== 0);
      const auto =
        this.sqOrder[sq] === Order.Formation && this.sqAuto[sq] === 1 && this.sqStation[sq] === 1 && this.meleeJoined === 0 && !driving;
      if (this.sqOrder[sq] !== Order.Attack && !auto) continue;
      const c = this.activeCommander(sq);
      // 기병의 분대만: 병사의 절반 넘게가 기병. 지휘관이 없으면 따를 사람이 없다 — 병사 저마다의 본능에 맡긴다.
      if (c === NO_TARGET || this.sqHorse[sq] * 2 <= this.sqN[sq]) continue;
      // 잔병만 남았으면(`mopUp`) 돌아갈 벽이 없다 — 저마다 쫓는다.
      if (this.mopUp || this.wallN[foe] === 0 || this.bowsOf[foe] === 0) continue;
      // 어디서든 칼이 닿았으면 그만 돈다 — 여느 공격으로 싸운다(위 `SWEEP`의 설명). 아직 고르지 않은 분대도 돌지 않는다.
      if (this.meleeJoined !== 0) {
        this.sqSweep[sq] = Sweep.Done;
        continue;
      }
      // 요격이 다가왔으면 그만 돌고 맞선다(`SWEEP_MEET`). 이미 돌고 있는 분대만 본다.
      if (this.sqSweep[sq] !== Sweep.None && this.sweepMet(sq)) {
        this.sqSweep[sq] = Sweep.Done;
        this.sqSweepFree[sq] = 1;
        continue;
      }
      const wx0 = this.wallBox[b];
      const wx1 = this.wallBox[b + 1];
      const wy1 = this.wallBox[b + 3];
      // 적 궁수의 가운데(달아나는 궁수는 뺀다). 칸 좌표로 더한다 — 고정소수점 그대로 더하면 2^31을 넘는다.
      let n = 0;
      let sx = 0;
      let sy = 0;
      const m = this.bowsOf[foe];
      for (let k = 0; k < m; k++) {
        const j = this.bowList[foe * MAX_UNITS + k];
        if (this.alive[j] === 0 || this.routed[j] > 0 || this.role[j] !== Role.Soldier) continue;
        sx += Math.floor(this.x[j] / TILE_FP);
        sy += Math.floor(this.y[j] / TILE_FP);
        n++;
      }
      if (n === 0) continue;
      const bx = Math.floor(sx / n) * TILE_FP + (TILE_FP >> 1);
      const by = Math.floor(sy / n) * TILE_FP + (TILE_FP >> 1);
      // 궁수가 벽의 앞줄에 섞여 있으면(뒤에 숨지 않았으면) 돌 것이 없다(`SWEEP.mixedPx`). **쪽을 고르기 전에만 묻는다** —
      // 한 번 돌기 시작했으면 끝까지 돈다. 처음에는 틱마다 물었다: 적 줄이 걸어오는 동안 궁수와 벽 앞의 거리가 문턱을
      // 오가면 돌기가 켜졌다 꺼졌다 했고(QA가 전부터 짚었다: 90판 가운데 26판), 꺼진 틱에는 도는 무리가 여느 공격
      // 병사로 돌아가 걸음이 묶였다. 적의 부대가 서며 적 줄이 얕아지자(114) 더 자주 걸렸다(시험의 판: 60틱에 128px
      // 갈 것을 83px).
      if (this.sqSweep[sq] === Sweep.None && by > wy1 - SWEEP_MIXED) continue;
      const ax = this.x[c];
      const ay = this.y[c];
      const lo = FLANK_MARGIN;
      const hi = this.grid.cols * TILE_FP - FLANK_MARGIN;
      if (this.sqSweep[sq] === Sweep.None) {
        // 쪽을 고른다: 지휘관과 궁수를 잇는 가로 거리가 짧은 쪽(같으면 왼쪽). 전장 밖으로 나가는 쪽은 버린다.
        const left = wx0 - SWEEP_OUT;
        const right = wx1 + SWEEP_OUT;
        // 줄의 절반 넘게가 바위인 쪽도 버린다(`laneOpen`). 처음에는 전장 안인지만 봤다 — 협곡의 판에서 벼랑 속의 줄을
        // 골라 지휘관이 바위를 밀며 섰다(QA: 협곡 130판의 이김 119 → 115). 바위 하나가 걸친 줄은 버리지 않는다 — 그때는
        // 적 쪽 흐름을 타다가 트이면 다시 줄로 간다. 줄의 두 점만 보고 걸렀더니 들길의 판에서 바위 하나 때문에 먼 쪽으로
        // 돌았다.
        const canLeft = left >= lo && this.laneOpen(left, by, ay);
        const canRight = right <= hi && this.laneOpen(right, by, ay);
        if (!canLeft && !canRight) {
          // 스스로 나서려던 대형의 분대는 그대로 진을 지킨다 — 다음 틱에 다시 본다.
          if (!auto) this.sqSweep[sq] = Sweep.Done;
          continue;
        }
        const costLeft = Math.abs(ax - left) + Math.abs(bx - left);
        const costRight = Math.abs(ax - right) + Math.abs(bx - right);
        const goesLeft = canLeft && (!canRight || costLeft <= costRight);
        this.sqSweep[sq] = goesLeft ? Sweep.Left : Sweep.Right;
        this.sqSweepLane[sq] = goesLeft ? left : right;
        if (auto) {
          // **대형의 기병 분대가 스스로 공격에 나선다.** 명령이 공격으로 바뀐다 — 화면의 명령 표시도 공격이 되고, 다 돈
          // 뒤에도 공격으로 싸운다. 한 판에 한 번뿐이다(`sqAuto`).
          this.sqOrder[sq] = Order.Attack;
          this.sqAuto[sq] = 0;
          this.sqAutoOut[sq] = 1;
        }
      }
      const goLeft = this.sqSweep[sq] === Sweep.Left;
      const lane = this.sqSweepLane[sq];
      // 줄에 섰는지·다 돌았는지는 **병사의 무게중심**으로 본다. 지휘관은 무리의 가운데·뒤에 있다(`SWEEP_PACK`) — 처음에는
      // 지휘관의 자리로 봐서, 뭉쳐 가게 고친 뒤 앞선 병사들이 궁수의 높이에 닿고도 지휘관이 닿을 때까지 닿는 적만 쳤다
      // (계단 90판: 다음 관문 30의 이김이 고·백·신 65·61·23 → 43·42·7).
      // 기병만의 무게중심이다 — 딸린 보병은 돌지 않는다(`rides`). 기병의 분대이므로 기병은 늘 있다.
      const rx = Math.floor(this.sqHorseCx[sq] / this.sqHorse[sq]) * TILE_FP + (TILE_FP >> 1);
      const ry = Math.floor(this.sqHorseCy[sq] / this.sqHorse[sq]) * TILE_FP + (TILE_FP >> 1);
      // 줄에 섰는가: 병사의 무게중심이나 지휘관이 줄 곁에 오면 선 것이고, 한 번 서면 그대로다(`sqSweepUp`).
      if (this.sqSweepUp[sq] === 0) {
        const near = goLeft ? Math.min(rx, ax) <= lane + SWEEP_BESIDE : Math.max(rx, ax) >= lane - SWEEP_BESIDE;
        if (near) this.sqSweepUp[sq] = 1;
      }
      // **줄까지는 적 쪽으로 비스듬히 간다**(REPLAY 111). 플레이한 사람이 짚었다(2026-10-07): "우회 기병이 처음에
      // 우회로를 향해 직선으로 이동하고 있어. 적 방향으로 대각선으로 이동하게 해줘." 그 전에는 제 높이에서 옆으로만 가
      // 줄에 선 뒤 올라갔다 — 주력을 기다리던 때(옛 `pacePct`) 기다리는 동안 줄에 서 있게 하려던 것인데, 기다림을 없앤
      // 뒤(107)로는 옆으로 갔다가 꺾어 올라가는 ㄱ자 걸음만 남았다. 이제 줄 위의 점을 비스듬히 겨눠 줄에 서고, 거기서
      // 궁수의 높이로 올라간다. 겨눈 점이 바위 속이면 흐름을 타고 간다(`steerByOrder`) — 그러다 벽의 앞 높이에 닿으면 줄
      // 곁이 아니어도 올라가기로 넘어간다(옆걸음이던 때의 함정: 줄의 제 높이가 바위 속이면 분대가 13초를 서 있었다).
      //
      // **기울기를 눕혔다**(REPLAY 112, `SWEEP.slantPct`): 줄까지 남은 옆 거리의 절반만큼만 위를 겨눈다(옆 2에 위 1).
      // 111에서는 벽의 앞 높이를 곧장 겨눴다 —
      // 줄은 옆으로 가깝고(100px 남짓) 벽은 위로 멀어(600px) 거의 곧장 위로 달렸고, 적 앞줄의 끝에 주력보다 먼저 닿았다
      // (기병의 첫 칼 7.0 → 5.2초, 전체 첫 칼 5.1초). 기병 쓰러짐이 고·백·신 0.9·3.3·3.9 → 10.5·22.2·22.6%, 계단의 관문
      // 25가 89·85·76 → 59·55·39/90, 쓰러진 부하장수가 15 → 100/1044이 됐다(idb-test — 줄을 130px에 둔 채 비스듬히만
      // 가게 한 판이 그 늘어난 몫의 77~90%였다: 줄의 거리가 아니라 겨눈 점이 원인). 눕히면 옆으로 벌어지며 조금씩만
      // 올라가 줄에 서고, 거기서부터는 예전처럼 벽의 끝 바깥을 타고 올라간다.
      // 값은 좁게 재어 골랐다(고구려 관문 부대 40판, 600틱 — 기병의 첫 칼이 전체 첫 칼보다 늦은 시간 · **기병 분대의
      // 병사** 가운데 쓰러진 몫): 옆걸음(0%) +0.81초 · 2.1%, 25% +0.55초 · 6.3%, **50% +0.38초 · 6.1%**, 100%(45도)
      // +0.20초 · 9.6%, 111 +0.08초 · 17.2%. 분모에는 산골 길에서 말을 내린 병사도 든다(40판의 429명 가운데 78명 —
      // 리뷰가 쟀다: 말 탄 병사만 세면 50%는 7.4%). 첫 칼의 시차는 그 분대의 기병이 칼을 댄 판(50%는 27판)의 평균이다.
      // 크게 기울수록 기병이 일찍 닿고 더 잃는 경향이다(25%와 50%는 가르지 못했다) — 비스듬히 가라는 요청과 손실
      // 사이에서 50%를 골랐다(내가 고른 값이다).
      const sideGap = Math.abs(lane - ax);
      const slantY = Math.max(by, Math.min(ay, Math.max(wy1, ay - Math.floor((sideGap * SWEEP.slantPct) / 100))));
      // 벽의 앞 높이에 닿았으면 줄 곁이 아니어도 올라가기다.
      if (this.sqSweepUp[sq] === 0 && ay <= Math.max(by, wy1) + SWEEP_ARRIVE) this.sqSweepUp[sq] = 1;
      const beside = this.sqSweepUp[sq] === 1;
      // 무게중심은 진의 맨 앞줄보다 `SWEEP_PACK.aheadPx`쯤 뒤다 — 그만큼 일찍 닿은 것으로 친다(맨 앞이 닿으면 다 돌았다).
      if (beside && ry <= by + SWEEP_ARRIVE + SWEEP_AHEAD) {
        // 궁수의 높이에 닿았다. 다 돌았다.
        this.sqSweep[sq] = Sweep.Done;
        this.sqSweepFree[sq] = 1;
        continue;
      }
      // 올라갈 때는 **바로 앞 구간**(`SWEEP.stepPx`)만 겨눈다. 처음에는 궁수의 높이(수백 px 위)를 곧장 겨눠, 줄 어디에든
      // 바위가 하나 있으면 그 바위를 지날 때까지 내내 "길이 막혔다"가 되어 적 쪽 흐름을 탔다. 앞 구간만 보면 바위가 코앞일
      // 때만 흐름을 타고 돌아, 지나면 다시 줄로 든다.
      this.sweepX[sq] = lane;
      this.sweepY[sq] = beside ? Math.max(by, ay - SWEEP_STEP) : slantY;
      fpNormalize(this.sweepX[sq] - ax, this.sweepY[sq] - ay, this.tmp);
      this.sweepDirX[sq] = this.tmp[0];
      this.sweepDirY[sq] = this.tmp[1];
      this.sweepOn[sq] = 1;
    }
  }

  /**
   * 세로줄 `x`의 `y0`~`y1` 구간이 걸친 칸을 모두 짚어, 바위가 절반을 넘지 않으면 열린 줄이다(`SWEEP`). 전장 밖의 칸은
   * 바위로 센다(`TerrainGrid.isBlocked`).
   *
   * **칸의 줄 번호로 돈다** — 두 끝의 칸까지. 처음에는 위 끝에서 한 칸 길이씩 건너뛰며 점을 찍었는데, 끝점이 칸의
   * 가운데가 아니면 마지막 칸을 빼먹었다(리뷰가 재현했다: 세 칸 가운데 아래 둘이 바위인 구간에서 두 점만 보고 열렸다고
   * 했다).
   */
  private laneOpen(x: number, y0: number, y1: number): boolean {
    const first = Math.floor(Math.min(y0, y1) / TILE_FP);
    const last = Math.floor(Math.max(y0, y1) / TILE_FP);
    let rock = 0;
    for (let row = first; row <= last; row++) {
      if (this.grid.isBlockedFp(x, row * TILE_FP + (TILE_FP >> 1))) rock++;
    }
    return rock * 2 <= last - first + 1;
  }

  /**
   * 주력의 맨 앞 병사를 잰다(`ATTACK_ABREAST`): 장수를 따르는 무리(`countSquads`의 `follows`와 같은 무리)의 병사 가운데
   * 군의 앞쪽으로 가장 나간 자리. 무너졌거나 다쳐 물러난 병사는 뺀다 — 그 자리에 맞춰 설 까닭이 없다.
   *
   * **앞축을 고친 뒤(`updateFront`)에 잰다.** 처음에는 `countSquads` 안에서 쟀는데, 그 뒤에 앞축이 바뀌는 틱에는 주력은
   * 옛 축으로, 공격 분대의 사람은 새 축으로 재어 뺐다 — 거리가 아닌 값이 나와 그 틱에 기병이 한 걸음도 못 갔다(리뷰가
   * 빈 평지에서 재현했다: 축이 위에서 오른쪽으로 바뀌는 틱에 0px, 같은 축으로 재면 2.6px).
   */
  private measureMain(): void {
    this.mainFront = 0;
    this.mainAny = false;
    this.sqFrontAny.fill(0);
    for (let i = 0; i < this.count; i++) {
      if (this.team[i] !== Team.Player || this.alive[i] === 0 || this.exited[i] === 1) continue;
      if (this.routed[i] > 0 || this.withdrawn[i] === 1) continue;
      const sq = this.squad[i];
      // 부하 부대의 맨 앞 사람(지휘관 포함). 장수는 0번이라 들지 않는다.
      // 돌아가는 무리(`rides`)는 뺀다 — 그 분대에 딸린 보병이 주력과 나란히 서는 자리는 보병끼리 잰다.
      // 돌기를 마쳐 풀린 기병과 지휘관(`sqSweepFree`)도 뺀다. 처음에는 도는 동안만 뺐다: 돌기가 끝나면 앞서간 기병이
      // 다시 분대의 맨 앞이 되어, 주력보다 뒤에 있는 딸린 보병이 그 때문에 섰다(리뷰가 재현했다: 창병 0px/틱).
      if (sq !== 0 && sq < MAX_SQUADS && !this.rides(i) && !this.sweepFreed(i)) {
        const g = this.frontOf(i);
        if (this.sqFrontAny[sq] === 0 || g > this.sqFront[sq]) this.sqFront[sq] = g;
        this.sqFrontAny[sq] = 1;
      }
      if (this.role[i] !== Role.Soldier) continue;
      const follows = sq === 0 || (this.sqOrder[sq] === Order.Formation && this.sqStation[sq] === 1 && this.sqPathLen[sq] === 0);
      if (!follows) continue;
      const f = this.frontOf(i);
      if (!this.mainAny || f > this.mainFront) this.mainFront = f;
      this.mainAny = true;
    }
  }

  /** 군의 앞쪽 축(`frontX`)에 내린 자리(고정소수점 px). 클수록 앞이다. */
  private frontOf(i: number): number {
    return fpMul(this.x[i], this.frontX) + fpMul(this.y[i], this.frontY);
  }

  /**
   * 이 사람이 지금 돌아가는 무리인가(`SWEEP`): 돌아가는 분대의 **기병 병사와 지휘관**. 그 분대에 딸린 보병·궁수는 아니다.
   */
  private rides(i: number): boolean {
    if (this.team[i] !== Team.Player || this.sweepOn[this.squad[i]] !== 1) return false;
    return this.role[i] !== Role.Soldier || this.branch[i] === BranchCode.Horse;
  }

  /**
   * 돌아가는 분대 `sq`의 무리(기병 병사·지휘관)를 표적으로 쥔 적의 근접 병사가 `SWEEP_MEET` 안에 있는가.
   * 적을 색인 순서로 훑는다 — 있다·없다만 보므로 순서가 결과를 바꾸지 않는다.
   */
  private sweepMet(sq: number): boolean {
    for (let e = 0; e < this.count; e++) {
      if (this.team[e] !== Team.Enemy || this.alive[e] === 0 || this.routed[e] > 0 || this.branch[e] === BranchCode.Bow) continue;
      const t = this.target[e];
      if (t === NO_TARGET || this.alive[t] === 0 || this.team[t] !== Team.Player || this.squad[t] !== sq) continue;
      if (this.role[t] === Role.Soldier && this.branch[t] !== BranchCode.Horse) continue;
      if (distSq(this.x[e], this.y[e], this.x[t], this.y[t]) <= SWEEP_MEET * SWEEP_MEET) return true;
    }
    return false;
  }

  /**
   * 공격 분대의 사람(지휘관·병사)이 지금 제 분대의 걸음에 묶이는가(`ATTACK_CHARGE`): 묶이면 그 걸음(분대의 가장 느린
   * 병사), 아니면 0. 첫 피해 전이고, 맞서는 적이 돌격 거리 밖이고, 직접 몰고 있지 않고, 경로를 걷지 않을 때다. 병사가
   * 없으면 맞출 걸음이 없다. 무너졌거나 물러나는 병사, 돌기를 마친 기병 분대의 기병과 지휘관(`sweepFreed`)은 묶지 않는다
   * (도는 중인 무리는 부르는 쪽이 거른다 — `rides`).
   */
  private chargeHeld(i: number): number {
    if (this.team[i] !== Team.Player) return 0;
    const role = this.role[i];
    if (role !== Role.Leader && role !== Role.Companion && role !== Role.Soldier) return 0;
    if (role === Role.Soldier && (this.routed[i] > 0 || this.withdrawn[i] === 1 || this.quiver[i] === Quiver.Leave)) return 0;
    if (this.sweepFreed(i)) return 0;
    const sq = this.squad[i];
    if (this.sqOrder[sq] !== Order.Attack || this.sqPathLen[sq] !== 0) return 0;
    if (i === this.ctrl && (this.inputDx !== 0 || this.inputDy !== 0)) return 0;
    const pace = this.sqPace[sq];
    // 칼이 닿았으면(`meleeJoined`) 피해가 0이었어도 풀린다 — 싸움은 붙었다.
    if (pace <= 0 || pace >= this.sSpeed[i] || this.joined() || this.meleeJoined !== 0 || this.enemyWithin(i, ATTACK_CHARGE)) return 0;
    return pace;
  }

  /** 돌기를 마쳐 걸음 맞추기에서 풀린 사람인가(`sqSweepFree`): 그 분대의 기병 병사와 지휘관. */
  private sweepFreed(i: number): boolean {
    if (this.team[i] !== Team.Player || this.sqSweepFree[this.squad[i]] !== 1) return false;
    return this.role[i] !== Role.Soldier || this.branch[i] === BranchCode.Horse;
  }

  /** 이 사람이 지금 주력과 나란히 가야 하는가(`ATTACK_ABREAST`). */
  private abreast(i: number): boolean {
    if (this.meleeJoined !== 0 || !this.mainAny || this.team[i] !== Team.Player) return false;
    const sq = this.squad[i];
    if (sq === 0 || this.sqOrder[sq] !== Order.Attack || this.sqPathLen[sq] !== 0) return false;
    // 돌기를 마친 분대의 기병과 지휘관은 묶지 않는다(`sqSweepFree`) — 딸린 보병·궁수는 여전히 주력과 나란히 간다.
    if (this.sweepFreed(i)) return false;
    const lead = this.sqOrder[0];
    if (lead !== Order.Formation && lead !== Order.Attack) return false;
    if (this.routed[i] > 0 || this.withdrawn[i] === 1 || this.quiver[i] === Quiver.Leave) return false;
    return !(i === this.ctrl && (this.inputDx !== 0 || this.inputDy !== 0));
  }

  /** 지친 만큼 깎은 걸음(`FATIGUE_SPEED_PCT`). 지치지 않았으면 그대로. 이동(`updateUnit`)과 적장의 걸음(`foePace`)이 같이 쓴다. */
  private wearySpeed(i: number, speed: number): number {
    const f = this.fatigue[i];
    return f > 0 ? Math.floor((speed * (FATIGUE_MAX - Math.floor((f * FATIGUE_SPEED_PCT) / 100))) / FATIGUE_MAX) : speed;
  }

  /** 지친 만큼 깎은 세기(`FATIGUE_ATK_PCT`). 지치지 않았으면 그대로. */
  private fatigued(i: number, hit: number): number {
    const f = this.fatigue[i];
    return f > 0 ? Math.floor((hit * (FATIGUE_MAX - Math.floor((f * FATIGUE_ATK_PCT) / 100))) / FATIGUE_MAX) : hit;
  }

  /** 병사를 지치게 한다. 1초에 오르는 몫(`FATIGUE_WINDOW_CAP`)과 끝(`FATIGUE_MAX`)까지. 병사만. */
  private tire(i: number, n: number): void {
    if (this.role[i] !== Role.Soldier) return;
    const room = Math.min(FATIGUE_WINDOW_CAP - this.fatGain[i], FATIGUE_MAX - this.fatigue[i]);
    const add = Math.min(n, room);
    if (add <= 0) return;
    this.fatigue[i] += add;
    this.fatGain[i] += add;
  }

  /**
   * 이미 선 **적 병사**의 몸을 용사의 몸(`spec` — `foeEliteSpec`이 올린 것)으로 바꾸고 호위를 맡긴다(`createBattle`,
   * 판을 세울 때만). 적의 용사는 부대마다 고르는데(`FOE_ELITE`) 부대는 병사가 모두 선 뒤 자리로 나누므로, 세울 때는
   * 누가 용사인지 모른다. 용사의 몸이 여느 몸과 다른 것은 체력·공격·격뿐이다(`foeEliteSpec`) — 그 셋과 스펙 색인만 고친다.
   * 우리 병사, 병사가 아닌 사람, 이미 용사인 사람, 다른 병종의 몸은 받지 않는다.
   */
  promoteElite(i: number, spec: CombatantSpec): void {
    if (i < 0 || i >= this.count || this.team[i] !== Team.Enemy || this.role[i] !== Role.Soldier) return;
    const was = this.specs[this.specIdx[i]];
    if (was.elite === true || spec.elite !== true || spec.troop !== was.troop || spec.role !== Role.Soldier) return;
    const hp = fieldHp(spec.stats.hp);
    this.hp[i] = hp;
    this.maxHp[i] = hp;
    this.sAtk[i] = spec.stats.atk;
    this.sPower[i] = spec.power;
    this.specIdx[i] = this.specIndex(spec);
    this.setDuty(i);
  }

  /** 용사에게 호위를 맡긴다(`createBattle`). 지킬 사람은 스폰이 끝나면 찾는다(`bindDuties`). 용사의 몸이 아니면 받지 않는다. */
  setDuty(i: number): void {
    if (i < 0 || i >= this.count || this.role[i] !== Role.Soldier || this.specs[this.specIdx[i]].elite !== true) return;
    this.dutyWho.push(i);
  }

  /**
   * 지킬 사람을 찾아 묶는다: 용사는 **제가 든 부대의 대장**을 지킨다 — 우리는 0번 부대가 장수, 부하 부대는 그 부하장수
   * (`sqCommander`), 적은 0번 부대가 총대장, 나머지는 그 부장(`foeCmd`, REPLAY 117 — 그 전에는 적의 용사가 모두 총대장을
   * 지켰다). 플레이한 사람이 정했다(2026-10-06): "용사가 소속한 부대의 대장(장수, 혹은 부하장수)를 호위하면 돼".
   * 대장이 없는 부대(없을 수 없다 — 대장 없는 부대는 출정하지 않는다)면 장수를 지킨다. 장수도 없으면 호위는 없던 것이
   * 된다. 스폰이 모두 끝난 뒤 한 번 부른다.
   *
   * 처음에는 명부가 지킬 사람의 이름표(`ward`: 장수가 기본, 고르면 부하장수)를 실어 왔다 — 어느 부대에 앉았든 고른 사람
   * 곁으로 갔다. 고르는 칸이 하나 더 있었고, 용사가 앉은 자리와 서는 자리가 달랐다.
   *
   * 제 편의 장수는 `dutyLead`가 준다. 처음에는 여기서 색인 오름차순으로 처음 만난 장수·적장을 골랐는데, 적장이 셋인
   * 결전에서는 먼저 선 옆 적장을 지키게 됐다(리뷰가 깊이 30 결전에서 146·147·148 가운데 146을 봤다) — 판이 적장으로
   * 치는 것은 `chiefIdx`다.
   */
  bindDuties(): void {
    for (const at of this.dutyWho) {
      const team = this.team[at];
      let ward = NO_TARGET;
      if (team === Team.Player) {
        const sq = this.squad[at];
        const c = sq < MAX_SQUADS ? this.sqCommander[sq] : NO_TARGET;
        if (c !== NO_TARGET && this.team[c] === team && this.alive[c] === 1) ward = c;
      } else {
        const c = this.foeCmd[this.foeSquad[at]];
        if (c !== NO_TARGET && this.team[c] === team && this.alive[c] === 1) ward = c;
      }
      if (ward === NO_TARGET) ward = this.dutyLead(team);
      if (ward === NO_TARGET) continue;
      this.duty[at] = Duty.Escort;
      this.dutyRef[at] = ward;
    }
    this.dutyWho = [];
  }

  /** 그 편의 장수(우리는 장수, 적은 적장)의 색인. 없으면 `NO_TARGET`. */
  private dutyLead(team: number): number {
    return team === Team.Player ? this.leaderIdx : this.chiefIdx;
  }

  /**
   * 이 유닛이 지금 호위 중인가. 그렇다면 지킬 자리를 `dutyX`·`dutyY`에, 그 사람을 `dutyWard`에 둔다. 무너졌거나 다쳐
   * 물러났거나 빠져나가는 중이면 아니다(그쪽 규칙이 먼저다). 후퇴 명령과 장수가 쓰러진 뒤의 퇴각도 따른다.
   *
   * 지킬 부하장수(제 부대의 대장)가 쓰러지면 **장수를 지킨다.** 처음에는 여느 병사로 돌아가 분대의 명령을 따랐는데, 플레이한 사람의 말은
   * "용사는 무조건 장수를 호위"였다 — QA가 온달이 쓰러진 판에서 용사 다섯이 흩어져 싸우는 것을 봤다. 장수까지 쓰러지면
   * 호위는 끝난다(여느 병사로 싸우거나 퇴각을 따른다).
   *
   * **틱마다 한 유닛에 한 번만 부른다**(`updateUnit`) — 쉬는 시계(`dutyRest`)를 여기서 줄인다.
   */
  private dutyPoint(i: number): boolean {
    if (this.duty[i] === Duty.None) return false;
    if (this.routed[i] > 0 || this.withdrawn[i] === 1 || this.quiver[i] === Quiver.Leave) return false;
    if (this.retreating(i)) return false;
    // 고지에서 기다리는 적(`enemyHold`)은 용사도 함께 서 있는다 — 호위로 걸어 나가면 기다림이 깨진다(시험이 잡았다).
    if (this.team[i] !== Team.Player && this.enemyHold > 0) return false;
    // 길이 막혀 호위를 내려놓고 쉬는 중이다(`ELITE_DUTY.restTicks`).
    if (this.dutyRest[i] > 0) {
      this.dutyRest[i]--;
      return false;
    }
    let w = this.dutyRef[i];
    if (this.alive[w] !== 1 || this.exited[w] === 1) {
      w = this.dutyLead(this.team[i]);
      if (w === NO_TARGET || this.alive[w] !== 1 || this.exited[w] === 1) return false;
    }
    this.dutyWard = w;
    this.dutyX = this.x[w];
    this.dutyY = this.y[w];
    return true;
  }

  /** 호위의 표적을 계속 쥐어도 되는가: 살아 있고, 지킬 사람 둘레에 있거나 지금 칼이 닿는다. */
  private dutyKeeps(i: number): boolean {
    const e = this.target[i];
    if (e === NO_TARGET || this.alive[e] === 0) return false;
    const touch = this.sRange[i] + REACH_SLACK + fp(8);
    if (distSq(this.x[i], this.y[i], this.x[e], this.y[e]) <= touch * touch) return true;
    const r = DUTY_RADIUS + fp(40);
    return distSq(this.dutyX, this.dutyY, this.x[e], this.y[e]) <= r * r;
  }

  /**
   * 호위의 표적을 고른다(`dutyPoint`가 참인 근접 용사). 지킬 사람에게서 `ELITE_DUTY.radiusPx` 안의 적 가운데 제게 가장
   * 가까운 적 — 다만 **그 사람을 표적으로 쥔 적**은 거리를 절반으로 친다(병종 본능과 같은 셈). 쥔 표적은 조금 가깝게 쳐
   * 뒤집히지 않게 한다. 둘레에 적이 없으면 제 칼이 닿는 적만 본다(제 몸은 지킨다). 적의 명단(`foeList`)은 색인
   * 오름차순이라 같은 값이면 색인이 작은 쪽이 남는다.
   */
  private acquireDuty(i: number): void {
    const other = this.team[i] === Team.Player ? Team.Enemy : Team.Player;
    const base = other * MAX_UNITS;
    const m = this.foesOf[other];
    const r2 = DUTY_RADIUS * DUTY_RADIUS;
    const ward = this.dutyWard;
    const cur = this.target[i];
    let best = NO_TARGET;
    let bestScore = Number.MAX_SAFE_INTEGER;
    for (let k = 0; k < m; k++) {
      const j = this.foeList[base + k];
      if (this.alive[j] === 0) continue;
      if (distSq(this.dutyX, this.dutyY, this.x[j], this.y[j]) > r2) continue;
      const d2 = distSq(this.x[i], this.y[i], this.x[j], this.y[j]);
      let score = this.target[j] === ward ? Math.floor(d2 / 4) : d2;
      if (j === cur) score = Math.floor((score * 3) / 4);
      if (score < bestScore) {
        bestScore = score;
        best = j;
      }
    }
    if (best === NO_TARGET) {
      this.acquireTarget(i, Sense.Reach);
      return;
    }
    this.target[i] = best;
  }

  /**
   * 호위하는 용사가 지킬 자리로 가는 쪽을 `tmp2`에 담는다. 가까우면 선다(0, 0). 멀면 뛴다 — 장수는 말을 타고, 모는 대로
   * 움직인다. 바위는 돌아가지 않고 미끄러진다(`integrate`) — 막힌 채 2초가 쌓이면 호위를 잠깐 내려놓는다(`dutyStall`).
   */
  private steerToPost(i: number): void {
    const d2 = distSq(this.x[i], this.y[i], this.dutyX, this.dutyY);
    if (d2 <= DUTY_NEAR * DUTY_NEAR) {
      this.tmp2[0] = 0;
      this.tmp2[1] = 0;
      return;
    }
    fpNormalize(this.dutyX - this.x[i], this.dutyY - this.y[i], this.tmp);
    this.tmp2[0] = this.tmp[0];
    this.tmp2[1] = this.tmp[1];
    this.faceX[i] = this.tmp[0];
    this.faceY[i] = this.tmp[1];
    if (d2 > DUTY_SPRINT * DUTY_SPRINT) this.stepBoost = FP_ONE + (FP_ONE >> 1);
  }

  /** 앞선 판에서 쌓인 피로로 병사를 세운다(`createBattle`, 명부의 `RosterEntry.fatigue`). 범위 밖은 자른다. */
  setFatigue(i: number, v: number): void {
    if (i < 0 || i >= this.count || this.role[i] !== Role.Soldier) return;
    this.fatigue[i] = Math.max(0, Math.min(FATIGUE_MAX, Math.floor(v) || 0));
  }

  private rout(j: number): void {
    // 결사대의 적은 무너지지 않는다. 사기는 바닥에 머물러 부대 평균(`rallyPct`)에는 그대로 든다.
    if (this.diehard && this.team[j] === Team.Enemy) return;
    this.routed[j] = ROUT_TICKS;
    this.morale[j] = this.sGrit[j] >> 1;
    this.target[j] = NO_TARGET;
    // 무너진 궁수는 아무도 겨누지 않는다(`aimOf`). 처음에는 여기를 빠뜨려, 틱 중에 전우가 쓰러져 무너진 궁수의 옛 표적이
    // 그 틱의 뒤 번호 궁수들에게 한 명 더 겨눠진 것으로 보였다(리뷰가 재현했다).
    this.syncAim(j);
    // 근접도 같다(`chaseOf`). 여기를 또 빠뜨려, 틱 중에 무너진 병사가 옛 적을 쫓는 것으로 남아 그 틱의 뒤 번호가 붐비지
    // 않는 적을 붐빈다고 보고 먼 적을 골랐다(리뷰가 실제 판의 388틱에서 봤다).
    this.syncChase(j);
  }

  /** 전우의 죽음은 주변 사기를 깎고 적의 사기를 올린다. */
  private shockMorale(i: number, victimTeam: number): void {
    const n = this.hash.query(this.x[i], this.y[i], MORALE_RADIUS, this.near);
    for (let k = 0; k < n; k++) {
      const j = this.near[k];
      if (this.alive[j] === 0) continue;
      if (this.role[j] !== Role.Soldier) continue;
      if (this.team[j] === victimTeam) {
        this.morale[j] -= 9;
        // 지장이 붙은 분대는 같은 충격에도 늦게 무너진다.
        const floorLine =
          this.team[j] === Team.Player
            ? this.captainActive(this.squad[j])
              ? -this.sqGritBonus[this.squad[j]] - (this.sqFavBranch[this.squad[j]] === this.branch[j] ? this.sqFavGrit[this.squad[j]] : 0)
              : 0
            : this.foeCaptainActive(this.foeSquad[j])
              ? -this.foeGritBonus[this.foeSquad[j]]
              : 0;
        if (this.morale[j] <= floorLine && this.routed[j] === 0) this.rout(j);
      } else {
        this.morale[j] = Math.min(this.sGrit[j], this.morale[j] + 3);
      }
    }
  }

  // ------------------------------------------------------------------
  // 투사체
  // ------------------------------------------------------------------

  /** 화살을 쏜다. 쏘았으면 true — 가려졌거나(쿨다운을 되돌린다) 자리가 없으면 false. */
  private fireArrow(i: number, e: number, dmg: number): boolean {
    // **바위 너머로는 쏘지 않는다.**
    //
    // 예전에는 표적이 보이든 말든 쐈다. 화살은 바위에 박혀 사라지므로 피해는
    // 없었지만, 화면에서는 적 하나 없는 바위밭으로 화살이 계속 날아갔다.
    // 플레이어가 제일 먼저 이상하게 본 자리다. 그리고 궁수 입장에서는 닿지도
    // 않을 곳에 쿨다운을 통째로 버리는 것이라 실제로 약해진다.
    if (this.grid.blockedBetweenFp(this.x[i], this.y[i], this.x[e], this.y[e])) {
      // **쿨다운을 되돌리고 표적을 푼다.** 겨눈 뒤에 서로 움직여 가려진
      // 경우다. 그냥 돌아가면 쏘지도 않고 쿨다운만 통째로 버린다.
      //
      // 무한히 겨눌 걱정은 없다. `acquireTarget`이 이미 시야를 거르므로 다음
      // 틱에는 보이는 적을 고른다. 보이는 적이 없으면 표적이 없는 것이다.
      this.cd[i] = 0;
      this.target[i] = NO_TARGET;
      this.syncAim(i);
      return false;
    }

    // 화살이 닿을 때 적이 설 자리를 내다보고 겨눈다(`sLead`). 날아가는 틱 수는 지금 거리로 어림하고, 그동안 적이 지난 틱의
    // 걸음(`velX/Y`)대로 간다고 본다. 내다보는 몫만큼만 앞을 겨누므로 낮은 티어는 움직이는 적을 여전히 놓친다 — 빗나갈
    // 수 있어야 활 맛이 난다. 처음에는 쏘는 순간의 자리만 겨눴다(티어와 무관하게 옆으로 달리는 기병은 거의 못 맞혔다).
    // 곡사(`BOW_LOB`)는 느리게 날고 떨어질 자리가 흩어진다. 내다보는 것은 같다 — 더 오래 나니 움직이는 적은 더 비껴 간다.
    const lob = this.curLob;
    const speed = lob ? LOB_SPEED : fp(15);
    const reach = isqrt(distSq(this.x[i], this.y[i], this.x[e], this.y[e]));
    const flight = Math.floor(reach / speed);
    const lead = this.sLead[i];
    let ax = this.x[e] + Math.trunc((this.velX[e] * flight * lead) / 100);
    let ay = this.y[e] + Math.trunc((this.velY[e] * flight * lead) / 100);
    // 자리가 없어 못 쏘는 판(아래)에서는 난수를 쓰지 않도록, 흩어짐은 자리를 얻은 뒤에 굴린다.
    const scatter = lob;
    let slot = NO_TARGET;
    for (let k = 0; k < MAX_PROJECTILES; k++) {
      const c = (this.pjCursor + k) % MAX_PROJECTILES;
      if (this.pjAlive[c] === 0) {
        slot = c;
        break;
      }
    }
    if (slot === NO_TARGET) return false;
    if (scatter) {
      ax += this.rng.range(-LOB_SCATTER, LOB_SCATTER);
      ay += this.rng.range(-LOB_SCATTER, LOB_SCATTER);
    }
    const dx = ax - this.x[i];
    const dy = ay - this.y[i];
    const len = fpNormalize(dx, dy, this.tmp);
    if (len === 0) return false;
    this.pjCursor = (slot + 1) % MAX_PROJECTILES;

    this.pjX[slot] = this.x[i];
    this.pjY[slot] = this.y[i];
    this.pjPrevX[slot] = this.x[i];
    this.pjPrevY[slot] = this.y[i];
    this.pjVX[slot] = fpMul(this.tmp[0], speed);
    this.pjVY[slot] = fpMul(this.tmp[1], speed);
    // 곡사는 겨눈 자리에서 떨어진다 — 수명이 곧 날아가는 틱 수다(적어도 한 틱). 곧은 화살은 표적을 지나 세 틱 더 난다.
    this.pjTtl[slot] = lob ? Math.max(1, Math.min(120, Math.round(len / speed))) : Math.min(120, Math.floor(len / speed) + 3);
    this.pjLob[slot] = lob ? 1 : 0;
    this.pjLife[slot] = this.pjTtl[slot];
    this.pjDmg[slot] = dmg;
    this.pjTeam[slot] = this.team[i];
    this.pjOwner[slot] = i;
    this.pjAlive[slot] = 1;
    this.fxAdd(FX.ArrowOut, i);

    // 화살 한 발을 쓴다(`ARROWS_BY_TIER`). 다 쓴 순간 판세를 한 번 보고 정한다 — 이기는 판이면 근접, 지는 판이면 빠져나감.
    if (this.arrows[i] > 0 && --this.arrows[i] === 0) {
      const team = this.team[i] as Team;
      const other = team === Team.Player ? Team.Enemy : Team.Player;
      // 장수·부하장수·적장은 화살을 다 써도 전장을 떠나지 않는다 — 늘 활을 놓고 싸운다. 병사처럼 판세를 보고 빠져나가면
      // 지는 판에서 장수가 제 발로 걸어 나가 판이 끝난다.
      this.quiver[i] =
        this.role[i] !== Role.Soldier || this.alivePower(team) >= this.alivePower(other) ? Quiver.Melee : Quiver.Leave;
      if (this.quiver[i] === Quiver.Leave) this.target[i] = NO_TARGET;
      this.syncAim(i);
    }
    return true;
  }

  private updateProjectiles(): void {
    const hitR = fp(7);
    for (let p = 0; p < MAX_PROJECTILES; p++) {
      if (this.pjAlive[p] === 0) continue;
      this.pjPrevX[p] = this.pjX[p];
      this.pjPrevY[p] = this.pjY[p];
      this.pjX[p] += this.pjVX[p];
      this.pjY[p] += this.pjVY[p];
      this.pjTtl[p]--;

      // 곡사(`BOW_LOB`)는 위로 난다: 날아가는 동안에는 바위에도 사람에도 닿지 않고, 수명이 다하는 틱에 떨어진 자리의 적만
      // 맞는다(아래 판정을 그 틱에 한 번 지난다). 떨어진 자리가 바위면 맞을 사람이 없다.
      const landing = this.pjLob[p] === 1 && this.pjTtl[p] <= 0;
      if (this.pjLob[p] === 1 && !landing) continue;
      if ((this.pjTtl[p] <= 0 && !landing) || this.grid.isBlockedFp(this.pjX[p], this.pjY[p])) {
        this.pjAlive[p] = 0;
        continue;
      }

      const n = this.hash.query(this.pjX[p], this.pjY[p], hitR + fp(8), this.near);
      for (let k = 0; k < n; k++) {
        const j = this.near[k];
        if (this.alive[j] === 0 || this.team[j] === this.pjTeam[p]) continue;
        const want = hitR + this.sRadius[j];
        if (distSq(this.pjX[p], this.pjY[p], this.x[j], this.y[j]) > want * want) continue;
        const owner = this.pjOwner[p];
        // 쏜 사람이 죽었으면 화살은 힘을 잃는다(예전 규칙). 빠져나간 사람은 죽은 것이
        // 아니다 — 쏘고 나서 후퇴한 궁수의 화살이 허공에서 사라지면 안 된다.
        // **방패가 앞에서 날아온 화살을 막는다**(`SHIELD_BY_TIER`). 화살이 가는 쪽과 맞는 이가 보는 쪽이 마주 보면(내적이
        // 음수) 앞이다. 막은 화살은 피해가 없고 방패가 한 번 닳는다. 등 뒤로 날아든 화살은 막지 못한다.
        // 힘을 잃은 화살(쏜 사람이 죽었다)은 방패도 닳리지 않는다 — 처음에는 막기를 먼저 보아, 죽은 궁수의 화살 네 발이
        // 창의 방패를 부수고 뒤의 산 화살을 맞게 했다(리뷰가 짚었다).
        if (this.alive[owner] === 1 || this.exited[owner] === 1) {
          if (this.shield[j] > 0 && this.pjVX[p] * this.faceX[j] + this.pjVY[p] * this.faceY[j] < 0) {
            this.shield[j]--;
          } else {
            this.applyDamage(owner, j, this.pjDmg[p], true);
          }
        }
        this.pjAlive[p] = 0;
        break;
      }
      // 떨어진 곡사 화살은 맞히지 못했어도 사라진다.
      if (landing) this.pjAlive[p] = 0;
    }
  }

  // ------------------------------------------------------------------
  // 조회
  // ------------------------------------------------------------------

  aliveCount(team: Team): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 1 && this.team[i] === team) n++;
    }
    return n;
  }

  /** 전장 위에 선 병사 수. 장수·동료·적장은 빼고 센다. 빠져나간 병사는 없다. */
  soldiersOnField(team: Team): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 1 && this.team[i] === team && this.role[i] === Role.Soldier) n++;
    }
    return n;
  }

  /**
   * 살아남은 병사 수. 전장 위에 선 병사에 **빠져나간 병사를 더한다.**
   *
   * 생환 배율과 경험치가 이 값을 본다. 후퇴로 빠져나간 병사를 빼면 물러나는
   * 것이 죽는 것과 같아져, 후퇴 명령이 있을 이유가 사라진다.
   */
  soldiersSurviving(team: Team): number {
    // 장수가 쓰러진 판은 빠져나간 병사만 산 것이다 — 전장에 남은 병사는 퇴각 시간이 지나면 잃는다(`leaderFall`). 처음에는
    // 여기가 전장의 병사까지 세어, 서버 요약(`summarize`)이 계정 정산(`applyBattle`)과 달랐다(리뷰가 짚었다).
    if (team === Team.Player && this.leaderFellAt !== 0) return this.stats.exited[team];
    return this.soldiersOnField(team) + this.stats.exited[team];
  }

  /** 살아 있는 병사의 전투력 합. 장수와 적장은 빼고 센다. */
  alivePower(team: Team): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 1 && this.team[i] === team && this.role[i] === Role.Soldier) {
        n += this.sPower[i];
      }
    }
    return n;
  }

  /**
   * 적장이 아직 서 있는가.
   *
   * 토벌은 이것으로 끝난다. 사기가 낮은 도적은 불리해지면 흩어져 달아나는데,
   * 그걸 끝까지 쫓아 마지막 하나까지 베는 것은 재미도 없고 일당백의 그림도
   * 아니다. 두령을 베면 나머지는 무너진 것으로 친다.
   */
  /**
   * 플레이어가 조종하는 사람. 한 분대만 골랐고(`selectedMask`) 그 분대의 지휘관이 살아 있는 동료면 그 동료, 아니면
   * 장수다 — 내 부대·전 부대를 고르거나, 동료가 쓰러지거나 빠져나가면 장수로 돌아온다.
   */
  controlled(): number {
    const m = this.selectedMask;
    if (m !== 0 && (m & (m - 1)) === 0) {
      const sq = 31 - Math.clz32(m);
      const c = sq > 0 ? this.sqCommander[sq] : NO_TARGET;
      if (c !== NO_TARGET && this.alive[c] === 1 && this.role[c] === Role.Companion && this.team[c] === Team.Player) return c;
    }
    return this.leaderIdx;
  }

  chiefAlive(): boolean {
    return this.chiefIdx !== NO_TARGET && this.alive[this.chiefIdx] === 1;
  }

  /**
   * 살아 있는 **적장**의 수: 여느 판은 총대장(0 또는 1), 적장 결전은 0·1·2번 부대의 대장 가운데 산 사람(`foeHeadSquad`).
   * 부장은 세지 않는다. 결전이 끝났는지("적장을 모두 베었다")와 화면의 "남은 적장"이 이것을 본다.
   */
  headsAlive(): number {
    let n = 0;
    for (let sq = 0; sq < MAX_FOE_SQUADS; sq++) {
      if (!foeHeadSquad(sq, this.warlords)) continue;
      const c = this.foeCmd[sq];
      if (c !== NO_TARGET && this.alive[c] === 1) n++;
    }
    return n;
  }

  /** 살아 있는 적의 대장 수(적장 + 부장). `chiefAlive`는 총대장 하나만, `headsAlive`는 적장만 본다. */
  chiefsAlive(): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 1 && this.team[i] === Team.Enemy && this.role[i] === Role.Chief) n++;
    }
    return n;
  }

  leaderAlive(): boolean {
    return this.leaderIdx !== NO_TARGET && this.alive[this.leaderIdx] === 1;
  }

  /**
   * 장수가 **물러나 전장의 아래 끝에 닿았는가**: 살아 있고, 제 분대의 명령이 후퇴이고, 맨 아래 줄에 섰다. 읽기만 한다.
   *
   * 단신 토벌을 접는 조건이다(화면 `main.ts`·도구 `battle-end.ts`가 본다). 토벌에는 물러날 길이 없었다 — 후퇴를 눌러도
   * 장수가 가장자리에 가서 설 뿐 판은 두령이나 장수가 쓰러져야 끝났다. 도적 무리가 커져 못 이길 판이 생기자 눈에 띄었고
   * (QA), 플레이한 사람이 정했다(2026-10-09): "토벌에서 후퇴 누르면 장수가 아래쪽 끝까지 도망가면서 전장 이탈하고 끝내면
   * 돼." 출정에서는 쓰지 않는다 — 장수가 먼저 빠지면 남은 병력을 버리는 것이 단추 하나로 된다(`tryExit`).
   */
  leaderFled(): boolean {
    const l = this.leaderIdx;
    if (l === NO_TARGET || this.alive[l] !== 1) return false;
    if (this.sqOrder[this.squad[l]] !== Order.Retreat) return false;
    return Math.floor(this.y[l] / TILE_FP) === this.grid.rows - 1;
  }

  /**
   * 장수가 쓰러진 뒤의 후퇴가 끝났나: 전장에 아군(장수 빼고)이 남지 않았거나, 쓰러진 뒤 `FALL_RETREAT_TICKS`가 지났다.
   * 장수가 살아 있으면 false. 판을 끝내는 쪽(화면·도구·서버)이 모두 이것을 본다 — 끝나는 틱이 같아야 누가 빠져나왔는지가
   * 같다.
   */
  fallOver(): boolean {
    if (this.leaderAlive() || this.leaderFellAt === 0) return false;
    if (this.tick - this.leaderFellAt >= FALL_RETREAT_TICKS) return true;
    for (let i = 0; i < this.count; i++) {
      if (this.alive[i] === 1 && this.team[i] === Team.Player && this.role[i] !== Role.Leader) return false;
    }
    return true;
  }

  /**
   * 서버 검증용 상태 해시.
   *
   * **틱을 넘어 다음 틱을 바꾸는 것은 다 넣는다.** 빠진 것이 있으면 거기서 갈라진 두 판이
   * 같은 해시를 내고, 눈에 보이는 값이 갈릴 때까지 검증이 모른다. 한 번에 다 찾지 못해
   * 리뷰가 여러 차례 찾았다(명령·진 자리, 잡힌 면, 이동 입력, 붕괴, 밀림·방향·돌격,
   * 투사체의 속도·수명·피해·편·쏜 이·슬롯, 흐름장).
   *
   * 넣지 않는 것과 그 까닭:
   * - 렌더링 전용: `prevX/Y`, `pjPrevX/Y`(틱 시작에 현재 값으로 덮는다), `hitFlash`(읽는 규칙이 없다),
   *   `strikeCount`·`strikeFaceX/Y`(칼바람 그림용), `leaderDealt`·`leaderKilled`(장수 데미지 숫자용),
   *   `shakenTick`(사기가 크게 꺾인 적 표시용).
   * - 결과 전용: `killCount`(유닛별 처치 수), `killPower`·`hitsDealt`·`hitsTaken`(전투 성과 경험치). 읽는 규칙이 없고
   *   팀 합계 `stats.kills`는 넣는다.
   *   넣으면 해시가 바뀌어 규칙은 그대로인데 옛 리플레이가 검증에서 떨어진다.
   * - 매 틱 다시 만드는 것: `pressSides`(`pressSidesNext`에서 온다 — 그쪽을 넣는다), `crowdTick`·`crowdN`(틱 안 셈).
   * - 스폰 때 설정에서 정해져 바뀌지 않는 것: 능력치(`s*`), 병종·역할·편·부대, 부대 보정,
   *   지휘관. 설정은 리플레이에 함께 실린다.
   * - 흐름장의 내용: 고정된 지형과 목표 칸만으로 정해진다. 목표 칸을 넣는다.
   */
  checksum(): number {
    let h = hashInit();
    h = hashNumber(h, this.tick);
    h = hashNumber(h, this.count);
    // 언덕에서 기다리는 남은 틱. 적이 걷는지가 갈린다.
    h = hashNumber(h, this.enemyHold);
    h = hashNumber(h, this.meleeJoined);
    // 횡대로 오는 적이 풀리는 줄. 흐름장을 다시 볼 때만 바뀌므로 그 사이의 틱에는 자리에서 다시 낼 수 없다.
    h = hashNumber(h, this.marchY);
    // 마름쇠가 남은 틱. 적 기병의 돌격이 갈린다.
    h = hashNumber(h, this.caltrops);
    h = hashArray(h, this.x, this.count);
    h = hashArray(h, this.velX, this.count);
    h = hashArray(h, this.velY, this.count);
    h = hashArray(h, this.y, this.count);
    h = hashArray(h, this.hp, this.count);
    h = hashArray(h, this.alive, this.count);
    // 빠져나간 것과 죽은 것은 `alive`로는 같다. 계정에 돌아갈 때 갈린다.
    h = hashArray(h, this.exited, this.count);
    h = hashArray(h, this.cd, this.count);
    h = hashArray(h, this.morale, this.count);
    // 붕괴는 사기와 별개로 남은 틱을 센다. 사기가 같아도 도망치는 중인지가 다르면
    // 다음 틱 조향이 갈린다. 이동 입력이 빠져 있던 것(아래)을 고치다 함께 찾았다.
    h = hashArray(h, this.routed, this.count);
    // 함성의 기세도 틱을 넘는다. 남은 틱이 다르면 다음 타격의 피해가 갈린다.
    h = hashArray(h, this.rallied, this.count);
    h = hashArray(h, this.target, this.count);
    // 잡힌 면은 틱을 넘어가는 상태다. 해시에 없으면 여기서 갈라진 재생이
    // 피해가 달라질 때까지 들키지 않는다.
    h = hashArray(h, this.pressSidesNext, this.count);
    // 밀림은 다음 이동에, 방향은 배후 판정에, 돌격은 기병 돌격 피해에 들어간다.
    // 쓰러진 동료는 결과에 들어간다.
    h = hashArray(h, this.knockX, this.count);
    h = hashArray(h, this.knockY, this.count);
    h = hashArray(h, this.faceX, this.count);
    h = hashArray(h, this.faceY, this.count);
    h = hashArray(h, this.charge, this.count);
    // 막힌 틱 수와 지난 자리. 다음 틱에 도는지가 갈린다.
    h = hashArray(h, this.stuckT, this.count);
    // 벽을 도는 쪽. 다음 틱 걸음이 갈린다.
    h = hashArray(h, this.flankSide, this.count);
    // 남은 화살과 화살통 상태. 쏠지, 근접할지, 빠져나갈지가 갈린다.
    h = hashArray(h, this.arrows, this.count);
    h = hashArray(h, this.quiver, this.count);
    // 방패가 더 막을 수 있는 화살. 다음 화살이 막히는지와 치는 세기·간격이 갈린다.
    h = hashArray(h, this.shield, this.count);
    // 물러나 있는지. 다음 틱 표적과 걸음이 갈린다.
    h = hashArray(h, this.withdrawn, this.count);
    h = hashArray(h, this.lastX, this.count);
    h = hashArray(h, this.lastY, this.count);
    // 바위를 돌아가는 남은 틱. 다음 틱에 자리로 곧장 가는지 지휘관 쪽으로 도는지가 갈린다.
    h = hashArray(h, this.slotDetour, this.count);
    // 자리에 서 있는가. 다음 틱에 조금 밀린 병사가 되돌아 걷는지가 갈린다.
    h = hashArray(h, this.slotHeld, this.count);
    // 줄 뒤의 어느 빈 칸에 섰나. 서 있는 진은 다음 틱에 이 칸을 먼저 본다.
    h = hashArray(h, this.slotAlt, this.count);
    h = hashArray(h, this.downed, this.count);
    // 피로와 이번 1초에 오른 몫. 치는 세기·걸음과 다음에 오를 몫이 갈린다.
    h = hashArray(h, this.fatigue, this.count);
    h = hashArray(h, this.fatGain, this.count);
    // 투사체는 자리와 살았는지만 넣었다. 속도가 다르면 다음 틱 자리가, 수명·피해·편·쏜
    // 이가 다르면 소멸 시점·피해량·피아 판정·피해 귀속이, 슬롯 커서가 다르면 다음
    // 발사가 들어갈 자리가 갈린다.
    h = hashArray(h, this.pjX, MAX_PROJECTILES);
    h = hashArray(h, this.pjY, MAX_PROJECTILES);
    h = hashArray(h, this.pjVX, MAX_PROJECTILES);
    h = hashArray(h, this.pjVY, MAX_PROJECTILES);
    h = hashArray(h, this.pjTtl, MAX_PROJECTILES);
    h = hashArray(h, this.pjDmg, MAX_PROJECTILES);
    h = hashArray(h, this.pjTeam, MAX_PROJECTILES);
    // 곡사인가. 날아가는 동안 맞는지와 떨어지는 틱의 판정이 갈린다.
    h = hashArray(h, this.pjLob, MAX_PROJECTILES);
    h = hashArray(h, this.pjOwner, MAX_PROJECTILES);
    h = hashArray(h, this.pjAlive, MAX_PROJECTILES);
    h = hashNumber(h, this.pjCursor);
    // 흐름장을 언제 다시 짤지(타이머), 어디를 향해 짰는지(목표 칸), 증원이 어디까지 왔는지.
    h = hashNumber(h, this.flowTimer);
    h = hashNumber(h, this.spawnCursor);
    // 함성의 재사용 대기는 틱을 넘는 상태다. 없으면 외칠 수 있는지가 다른 두 재생이 같은 해시를 낸다.
    h = hashNumber(h, this.roarCd);
    h = hashNumber(h, this.skillCode);
    h = hashNumber(h, this.skillLevel);
    h = hashArray(h, this.mateCd, MAX_SQUADS);
    h = hashArray(h, this.mateOn, MAX_SQUADS);
    h = hashArray(h, this.mateFell, MAX_SQUADS);
    h = hashArray(h, this.mateRadius, MAX_SQUADS);
    h = hashArray(h, this.aid, this.count);
    h = hashArray(h, this.aidBy, this.count);
    h = hashArray(h, this.mateWho, MAX_SQUADS);
    h = hashArray(h, this.mateTrigger, MAX_SQUADS);
    h = hashArray(h, this.mateAtk, MAX_SQUADS);
    h = hashArray(h, this.mateCut, MAX_SQUADS);
    h = hashArray(h, this.guard, this.count);
    h = hashArray(h, this.volley, this.count);
    h = hashNumber(h, this.enemyPeak);
    h = hashNumber(h, this.leaderFellAt);
    h = hashArray(h, this.dutyStall, this.count);
    h = hashArray(h, this.dutyRest, this.count);
    h = this.hashFlowTarget(h, this.flowAttack);
    h = this.hashFlowTarget(h, this.flowEnemy);
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      h = this.hashFlowTarget(h, this.flowCmdr[sq]);
      h = this.hashFlowTarget(h, this.flowSquad[sq]);
    }
    h = hashArray(h, this.stats.kills, 2);
    h = hashArray(h, this.stats.damage, 2);
    h = hashArray(h, this.stats.exited, 2);
    // **부대 상태도 넣는다.** 명령과 진의 자리는 위치도 체력도 아니지만 다음 틱부터
    // 조향을 바꾼다. 빠져 있으면 명령만 다른 두 월드가 같은 해시를 내고, 몇 틱 뒤에
    // 갈라질 때까지 검증이 모른다 — 리뷰가 첫 틱에 공격과 대기를 건 두 월드로
    // 보였다(해시는 같고 30틱 뒤 장수 위치가 달랐다). 군기도 같은 처지라 함께 넣는다.
    h = hashArray(h, this.sqOrder, MAX_SQUADS);
    h = hashArray(h, this.sqHomeX, MAX_SQUADS);
    h = hashArray(h, this.sqHomeY, MAX_SQUADS);
    h = hashArray(h, this.sqPathLen, MAX_SQUADS);
    h = hashArray(h, this.sqSweep, MAX_SQUADS);
    h = hashArray(h, this.sqSweepLane, MAX_SQUADS);
    h = hashArray(h, this.sqSweepUp, MAX_SQUADS);
    h = hashArray(h, this.sqSweepFree, MAX_SQUADS);
    h = hashArray(h, this.sqAuto, MAX_SQUADS);
    h = hashArray(h, this.sqPathAt, MAX_SQUADS);
    // 대형의 자리는 매 틱 새로 짜지만, 그 재료인 군의 앞과 동료 분대가 장수를 따르는지는
    // 틱을 넘어가는 상태다. 자리(slotX)는 이 둘과 위치에서 나오므로 넣지 않는다.
    h = hashArray(h, this.sqStation, MAX_SQUADS);
    h = hashArray(h, this.sqPostX, MAX_SQUADS);
    h = hashArray(h, this.sqPostY, MAX_SQUADS);
    h = hashArray(h, this.sqPostSet, MAX_SQUADS);
    h = hashNumber(h, this.frontX);
    h = hashNumber(h, this.frontY);
    h = hashArray(h, this.sqPathX, MAX_SQUADS * MAX_WAYPOINTS);
    h = hashArray(h, this.sqPathY, MAX_SQUADS * MAX_WAYPOINTS);
    h = hashNumber(h, this.selectedMask);
    // 이동 입력도 틱을 넘어간다. 값이 바뀔 때만 명령으로 오므로, 누르고 있는 동안은
    // 이것만이 "장수를 몰고 있다"를 기억한다. 몰고 있으면 장수의 경로가 매 틱 지워진다
    // (`advancePaths`). 빠져 있을 때 리뷰가 한쪽만 MoveDir를 받은 두 월드로 보였다 —
    // 해시는 같았고, 다음 틱 같은 Rally를 받자 경로 길이가 0과 1로 갈렸다.
    h = hashNumber(h, this.inputDx);
    h = hashNumber(h, this.inputDy);
    h = hashArray(h, this.rng.save(), 4);
    return h >>> 0;
  }

  private hashFlowTarget(h: number, f: FlowField): number {
    const t = f.target;
    return hashNumber(hashNumber(h, t[0]), t[1]);
  }
}
